/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const xi = globalThis, Do = xi.ShadowRoot && (xi.ShadyCSS === void 0 || xi.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Lo = Symbol(), hs = /* @__PURE__ */ new WeakMap();
let mn = class {
  constructor(e, i, o) {
    if (this._$cssResult$ = !0, o !== Lo) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = i;
  }
  get styleSheet() {
    let e = this.o;
    const i = this.t;
    if (Do && e === void 0) {
      const o = i !== void 0 && i.length === 1;
      o && (e = hs.get(i)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), o && hs.set(i, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const Ar = (t) => new mn(typeof t == "string" ? t : t + "", void 0, Lo), Ye = (t, ...e) => {
  const i = t.length === 1 ? t[0] : e.reduce((o, s, n) => o + ((r) => {
    if (r._$cssResult$ === !0) return r.cssText;
    if (typeof r == "number") return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(s) + t[n + 1], t[0]);
  return new mn(i, t, Lo);
}, kr = (t, e) => {
  if (Do) t.adoptedStyleSheets = e.map((i) => i instanceof CSSStyleSheet ? i : i.styleSheet);
  else for (const i of e) {
    const o = document.createElement("style"), s = xi.litNonce;
    s !== void 0 && o.setAttribute("nonce", s), o.textContent = i.cssText, t.appendChild(o);
  }
}, us = Do ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let i = "";
  for (const o of e.cssRules) i += o.cssText;
  return Ar(i);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: Sr, defineProperty: Tr, getOwnPropertyDescriptor: _r, getOwnPropertyNames: Cr, getOwnPropertySymbols: jr, getPrototypeOf: zr } = Object, xt = globalThis, ps = xt.trustedTypes, Rr = ps ? ps.emptyScript : "", gs = xt.reactiveElementPolyfillSupport, Ht = (t, e) => t, Ci = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? Rr : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let i = t;
  switch (e) {
    case Boolean:
      i = t !== null;
      break;
    case Number:
      i = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        i = JSON.parse(t);
      } catch {
        i = null;
      }
  }
  return i;
} }, Po = (t, e) => !Sr(t, e), fs = { attribute: !0, type: String, converter: Ci, reflect: !1, hasChanged: Po };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), xt.litPropertyMetadata ?? (xt.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let ft = class extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, i = fs) {
    if (i.state && (i.attribute = !1), this._$Ei(), this.elementProperties.set(e, i), !i.noAccessor) {
      const o = Symbol(), s = this.getPropertyDescriptor(e, o, i);
      s !== void 0 && Tr(this.prototype, e, s);
    }
  }
  static getPropertyDescriptor(e, i, o) {
    const { get: s, set: n } = _r(this.prototype, e) ?? { get() {
      return this[i];
    }, set(r) {
      this[i] = r;
    } };
    return { get() {
      return s == null ? void 0 : s.call(this);
    }, set(r) {
      const a = s == null ? void 0 : s.call(this);
      n.call(this, r), this.requestUpdate(e, a, o);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? fs;
  }
  static _$Ei() {
    if (this.hasOwnProperty(Ht("elementProperties"))) return;
    const e = zr(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(Ht("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(Ht("properties"))) {
      const i = this.properties, o = [...Cr(i), ...jr(i)];
      for (const s of o) this.createProperty(s, i[s]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const i = litPropertyMetadata.get(e);
      if (i !== void 0) for (const [o, s] of i) this.elementProperties.set(o, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [i, o] of this.elementProperties) {
      const s = this._$Eu(i, o);
      s !== void 0 && this._$Eh.set(s, i);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const i = [];
    if (Array.isArray(e)) {
      const o = new Set(e.flat(1 / 0).reverse());
      for (const s of o) i.unshift(us(s));
    } else e !== void 0 && i.push(us(e));
    return i;
  }
  static _$Eu(e, i) {
    const o = i.attribute;
    return o === !1 ? void 0 : typeof o == "string" ? o : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((i) => this.enableUpdating = i), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((i) => i(this));
  }
  addController(e) {
    var i;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((i = e.hostConnected) == null || i.call(e));
  }
  removeController(e) {
    var i;
    (i = this._$EO) == null || i.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), i = this.constructor.elementProperties;
    for (const o of i.keys()) this.hasOwnProperty(o) && (e.set(o, this[o]), delete this[o]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return kr(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostConnected) == null ? void 0 : o.call(i);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostDisconnected) == null ? void 0 : o.call(i);
    });
  }
  attributeChangedCallback(e, i, o) {
    this._$AK(e, o);
  }
  _$EC(e, i) {
    var o;
    const s = this.constructor.elementProperties.get(e), n = this.constructor._$Eu(e, s);
    if (n !== void 0 && s.reflect === !0) {
      const r = (((o = s.converter) == null ? void 0 : o.toAttribute) !== void 0 ? s.converter : Ci).toAttribute(i, s.type);
      this._$Em = e, r == null ? this.removeAttribute(n) : this.setAttribute(n, r), this._$Em = null;
    }
  }
  _$AK(e, i) {
    var o;
    const s = this.constructor, n = s._$Eh.get(e);
    if (n !== void 0 && this._$Em !== n) {
      const r = s.getPropertyOptions(n), a = typeof r.converter == "function" ? { fromAttribute: r.converter } : ((o = r.converter) == null ? void 0 : o.fromAttribute) !== void 0 ? r.converter : Ci;
      this._$Em = n, this[n] = a.fromAttribute(i, r.type), this._$Em = null;
    }
  }
  requestUpdate(e, i, o) {
    if (e !== void 0) {
      if (o ?? (o = this.constructor.getPropertyOptions(e)), !(o.hasChanged ?? Po)(this[e], i)) return;
      this.P(e, i, o);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, i, o) {
    this._$AL.has(e) || this._$AL.set(e, i), o.reflect === !0 && this._$Em !== e && (this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Set())).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (i) {
      Promise.reject(i);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [n, r] of this._$Ep) this[n] = r;
        this._$Ep = void 0;
      }
      const s = this.constructor.elementProperties;
      if (s.size > 0) for (const [n, r] of s) r.wrapped !== !0 || this._$AL.has(n) || this[n] === void 0 || this.P(n, this[n], r);
    }
    let i = !1;
    const o = this._$AL;
    try {
      i = this.shouldUpdate(o), i ? (this.willUpdate(o), (e = this._$EO) == null || e.forEach((s) => {
        var n;
        return (n = s.hostUpdate) == null ? void 0 : n.call(s);
      }), this.update(o)) : this._$EU();
    } catch (s) {
      throw i = !1, this._$EU(), s;
    }
    i && this._$AE(o);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var i;
    (i = this._$EO) == null || i.forEach((o) => {
      var s;
      return (s = o.hostUpdated) == null ? void 0 : s.call(o);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((i) => this._$EC(i, this[i]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
ft.elementStyles = [], ft.shadowRootOptions = { mode: "open" }, ft[Ht("elementProperties")] = /* @__PURE__ */ new Map(), ft[Ht("finalized")] = /* @__PURE__ */ new Map(), gs == null || gs({ ReactiveElement: ft }), (xt.reactiveElementVersions ?? (xt.reactiveElementVersions = [])).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const ji = globalThis, zi = ji.trustedTypes, ms = zi ? zi.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, wn = "$lit$", Pe = `lit$${Math.random().toFixed(9).slice(2)}$`, vn = "?" + Pe, Dr = `<${vn}>`, it = document, Wt = () => it.createComment(""), Jt = (t) => t === null || typeof t != "object" && typeof t != "function", Io = Array.isArray, Lr = (t) => Io(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", no = `[ 	
\f\r]`, Mt = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, ws = /-->/g, vs = />/g, We = RegExp(`>|${no}(?:([^\\s"'>=/]+)(${no}*=${no}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), bs = /'/g, ys = /"/g, bn = /^(?:script|style|textarea|title)$/i, Pr = (t) => (e, ...i) => ({ _$litType$: t, strings: e, values: i }), _ = Pr(1), ot = Symbol.for("lit-noChange"), M = Symbol.for("lit-nothing"), xs = /* @__PURE__ */ new WeakMap(), Xe = it.createTreeWalker(it, 129);
function yn(t, e) {
  if (!Io(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return ms !== void 0 ? ms.createHTML(e) : e;
}
const Ir = (t, e) => {
  const i = t.length - 1, o = [];
  let s, n = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", r = Mt;
  for (let a = 0; a < i; a++) {
    const l = t[a];
    let c, d, h = -1, p = 0;
    for (; p < l.length && (r.lastIndex = p, d = r.exec(l), d !== null); ) p = r.lastIndex, r === Mt ? d[1] === "!--" ? r = ws : d[1] !== void 0 ? r = vs : d[2] !== void 0 ? (bn.test(d[2]) && (s = RegExp("</" + d[2], "g")), r = We) : d[3] !== void 0 && (r = We) : r === We ? d[0] === ">" ? (r = s ?? Mt, h = -1) : d[1] === void 0 ? h = -2 : (h = r.lastIndex - d[2].length, c = d[1], r = d[3] === void 0 ? We : d[3] === '"' ? ys : bs) : r === ys || r === bs ? r = We : r === ws || r === vs ? r = Mt : (r = We, s = void 0);
    const f = r === We && t[a + 1].startsWith("/>") ? " " : "";
    n += r === Mt ? l + Dr : h >= 0 ? (o.push(c), l.slice(0, h) + wn + l.slice(h) + Pe + f) : l + Pe + (h === -2 ? a : f);
  }
  return [yn(t, n + (t[i] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), o];
};
let vo = class xn {
  constructor({ strings: e, _$litType$: i }, o) {
    let s;
    this.parts = [];
    let n = 0, r = 0;
    const a = e.length - 1, l = this.parts, [c, d] = Ir(e, i);
    if (this.el = xn.createElement(c, o), Xe.currentNode = this.el.content, i === 2 || i === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (s = Xe.nextNode()) !== null && l.length < a; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const h of s.getAttributeNames()) if (h.endsWith(wn)) {
          const p = d[r++], f = s.getAttribute(h).split(Pe), g = /([.?@])?(.*)/.exec(p);
          l.push({ type: 1, index: n, name: g[2], strings: f, ctor: g[1] === "." ? Or : g[1] === "?" ? Br : g[1] === "@" ? Ur : Hi }), s.removeAttribute(h);
        } else h.startsWith(Pe) && (l.push({ type: 6, index: n }), s.removeAttribute(h));
        if (bn.test(s.tagName)) {
          const h = s.textContent.split(Pe), p = h.length - 1;
          if (p > 0) {
            s.textContent = zi ? zi.emptyScript : "";
            for (let f = 0; f < p; f++) s.append(h[f], Wt()), Xe.nextNode(), l.push({ type: 2, index: ++n });
            s.append(h[p], Wt());
          }
        }
      } else if (s.nodeType === 8) if (s.data === vn) l.push({ type: 2, index: n });
      else {
        let h = -1;
        for (; (h = s.data.indexOf(Pe, h + 1)) !== -1; ) l.push({ type: 7, index: n }), h += Pe.length - 1;
      }
      n++;
    }
  }
  static createElement(e, i) {
    const o = it.createElement("template");
    return o.innerHTML = e, o;
  }
};
function $t(t, e, i = t, o) {
  var s, n;
  if (e === ot) return e;
  let r = o !== void 0 ? (s = i._$Co) == null ? void 0 : s[o] : i._$Cl;
  const a = Jt(e) ? void 0 : e._$litDirective$;
  return (r == null ? void 0 : r.constructor) !== a && ((n = r == null ? void 0 : r._$AO) == null || n.call(r, !1), a === void 0 ? r = void 0 : (r = new a(t), r._$AT(t, i, o)), o !== void 0 ? (i._$Co ?? (i._$Co = []))[o] = r : i._$Cl = r), r !== void 0 && (e = $t(t, r._$AS(t, e.values), r, o)), e;
}
let Mr = class {
  constructor(e, i) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = i;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: i }, parts: o } = this._$AD, s = ((e == null ? void 0 : e.creationScope) ?? it).importNode(i, !0);
    Xe.currentNode = s;
    let n = Xe.nextNode(), r = 0, a = 0, l = o[0];
    for (; l !== void 0; ) {
      if (r === l.index) {
        let c;
        l.type === 2 ? c = new Mo(n, n.nextSibling, this, e) : l.type === 1 ? c = new l.ctor(n, l.name, l.strings, this, e) : l.type === 6 && (c = new Nr(n, this, e)), this._$AV.push(c), l = o[++a];
      }
      r !== (l == null ? void 0 : l.index) && (n = Xe.nextNode(), r++);
    }
    return Xe.currentNode = it, s;
  }
  p(e) {
    let i = 0;
    for (const o of this._$AV) o !== void 0 && (o.strings !== void 0 ? (o._$AI(e, o, i), i += o.strings.length - 2) : o._$AI(e[i])), i++;
  }
}, Mo = class $n {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, i, o, s) {
    this.type = 2, this._$AH = M, this._$AN = void 0, this._$AA = e, this._$AB = i, this._$AM = o, this.options = s, this._$Cv = (s == null ? void 0 : s.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const i = this._$AM;
    return i !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = i.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, i = this) {
    e = $t(this, e, i), Jt(e) ? e === M || e == null || e === "" ? (this._$AH !== M && this._$AR(), this._$AH = M) : e !== this._$AH && e !== ot && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Lr(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== M && Jt(this._$AH) ? this._$AA.nextSibling.data = e : this.T(it.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var i;
    const { values: o, _$litType$: s } = e, n = typeof s == "number" ? this._$AC(e) : (s.el === void 0 && (s.el = vo.createElement(yn(s.h, s.h[0]), this.options)), s);
    if (((i = this._$AH) == null ? void 0 : i._$AD) === n) this._$AH.p(o);
    else {
      const r = new Mr(n, this), a = r.u(this.options);
      r.p(o), this.T(a), this._$AH = r;
    }
  }
  _$AC(e) {
    let i = xs.get(e.strings);
    return i === void 0 && xs.set(e.strings, i = new vo(e)), i;
  }
  k(e) {
    Io(this._$AH) || (this._$AH = [], this._$AR());
    const i = this._$AH;
    let o, s = 0;
    for (const n of e) s === i.length ? i.push(o = new $n(this.O(Wt()), this.O(Wt()), this, this.options)) : o = i[s], o._$AI(n), s++;
    s < i.length && (this._$AR(o && o._$AB.nextSibling, s), i.length = s);
  }
  _$AR(e = this._$AA.nextSibling, i) {
    var o;
    for ((o = this._$AP) == null ? void 0 : o.call(this, !1, !0, i); e && e !== this._$AB; ) {
      const s = e.nextSibling;
      e.remove(), e = s;
    }
  }
  setConnected(e) {
    var i;
    this._$AM === void 0 && (this._$Cv = e, (i = this._$AP) == null || i.call(this, e));
  }
}, Hi = class {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, i, o, s, n) {
    this.type = 1, this._$AH = M, this._$AN = void 0, this.element = e, this.name = i, this._$AM = s, this.options = n, o.length > 2 || o[0] !== "" || o[1] !== "" ? (this._$AH = Array(o.length - 1).fill(new String()), this.strings = o) : this._$AH = M;
  }
  _$AI(e, i = this, o, s) {
    const n = this.strings;
    let r = !1;
    if (n === void 0) e = $t(this, e, i, 0), r = !Jt(e) || e !== this._$AH && e !== ot, r && (this._$AH = e);
    else {
      const a = e;
      let l, c;
      for (e = n[0], l = 0; l < n.length - 1; l++) c = $t(this, a[o + l], i, l), c === ot && (c = this._$AH[l]), r || (r = !Jt(c) || c !== this._$AH[l]), c === M ? e = M : e !== M && (e += (c ?? "") + n[l + 1]), this._$AH[l] = c;
    }
    r && !s && this.j(e);
  }
  j(e) {
    e === M ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}, Or = class extends Hi {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === M ? void 0 : e;
  }
}, Br = class extends Hi {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== M);
  }
}, Ur = class extends Hi {
  constructor(e, i, o, s, n) {
    super(e, i, o, s, n), this.type = 5;
  }
  _$AI(e, i = this) {
    if ((e = $t(this, e, i, 0) ?? M) === ot) return;
    const o = this._$AH, s = e === M && o !== M || e.capture !== o.capture || e.once !== o.once || e.passive !== o.passive, n = e !== M && (o === M || s);
    s && this.element.removeEventListener(this.name, this, o), n && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var i;
    typeof this._$AH == "function" ? this._$AH.call(((i = this.options) == null ? void 0 : i.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}, Nr = class {
  constructor(e, i, o) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = i, this.options = o;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    $t(this, e);
  }
};
const $s = ji.litHtmlPolyfillSupport;
$s == null || $s(vo, Mo), (ji.litHtmlVersions ?? (ji.litHtmlVersions = [])).push("3.2.1");
const Hr = (t, e, i) => {
  const o = (i == null ? void 0 : i.renderBefore) ?? e;
  let s = o._$litPart$;
  if (s === void 0) {
    const n = (i == null ? void 0 : i.renderBefore) ?? null;
    o._$litPart$ = s = new Mo(e.insertBefore(Wt(), n), n, void 0, i ?? {});
  }
  return s._$AI(t), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let J = class extends ft {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const i = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore ?? (e.renderBefore = i.firstChild), i;
  }
  update(e) {
    const i = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Hr(i, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return ot;
  }
};
var Es;
J._$litElement$ = !0, J.finalized = !0, (Es = globalThis.litElementHydrateSupport) == null || Es.call(globalThis, { LitElement: J });
const As = globalThis.litElementPolyfillSupport;
As == null || As({ LitElement: J });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Te = (t) => (e, i) => {
  i !== void 0 ? i.addInitializer(() => {
    customElements.define(t, e);
  }) : customElements.define(t, e);
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Fr = { attribute: !0, type: String, converter: Ci, reflect: !1, hasChanged: Po }, qr = (t = Fr, e, i) => {
  const { kind: o, metadata: s } = i;
  let n = globalThis.litPropertyMetadata.get(s);
  if (n === void 0 && globalThis.litPropertyMetadata.set(s, n = /* @__PURE__ */ new Map()), n.set(i.name, t), o === "accessor") {
    const { name: r } = i;
    return { set(a) {
      const l = e.get.call(this);
      e.set.call(this, a), this.requestUpdate(r, l, t);
    }, init(a) {
      return a !== void 0 && this.P(r, void 0, t), a;
    } };
  }
  if (o === "setter") {
    const { name: r } = i;
    return function(a) {
      const l = this[r];
      e.call(this, a), this.requestUpdate(r, l, t);
    };
  }
  throw Error("Unsupported decorator location: " + o);
};
function E(t) {
  return (e, i) => typeof i == "object" ? qr(t, e, i) : ((o, s, n) => {
    const r = s.hasOwnProperty(n);
    return s.constructor.createProperty(n, r ? { ...o, wrapped: !0 } : o), r ? Object.getOwnPropertyDescriptor(s, n) : void 0;
  })(t, e, i);
}
var Yr = Object.defineProperty, Vr = Object.getOwnPropertyDescriptor, Oo = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Vr(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Yr(e, i, s), s;
};
let Gt = class extends J {
  constructor() {
    super(...arguments), this.docsHint = "Tips: 点击Logo了解更多", this.count = 0;
  }
  render() {
    return _`
      <div>
        <a href="https://demo-docs.jackery.com" target="_blank">
          <img
            src="../assets/jds-logo.svg"
            class="logo"
            alt="Jackery Design System Logo"
          />
        </a>
      </div>
      <slot></slot>
      <div class="card">
        <button @click=${this._onClick} part="button">
          count is ${this.count}
        </button>
      </div>
      <p class="read-the-docs">${this.docsHint}</p>
    `;
  }
  _onClick() {
    this.count++;
  }
};
Gt.styles = Ye`
    :host {
      max-width: 1280px;
      margin: 0 auto;
      padding: 2rem;
      text-align: center;
    }

    .logo {
      height: 6em;
      padding: 1.5em;
      will-change: filter;
      transition: filter 300ms;
    }
    .logo:hover {
      filter: drop-shadow(0 0 1px #fd5000);
    }

    .card {
      padding: 2em;
    }

    .read-the-docs {
      color: #888;
    }

    ::slotted(h2) {
      font-size: 1.2em;
      line-height: 1.1;
    }

    a {
      font-weight: 500;
      color: #646cff;
      text-decoration: inherit;
    }

    a:hover {
      color: #535bf2;
    }

    button {
      border-radius: 8px;
      border: 1px solid transparent;
      padding: 0.6em 1.2em;
      font-size: 1em;
      font-weight: 500;
      font-family: inherit;
      background-color: #1a1a1a;
      cursor: pointer;
      transition: border-color 0.25s;
    }
    button:hover {
      border-color: #fd5000;
    }
    button:focus,
    button:focus-visible {
      outline: 4px auto #fd5000;
    }

    @media (prefers-color-scheme: light) {
      a:hover {
        color: #fd5000;
      }
      button {
        background-color: #f9f9f9;
      }
    }
  `;
Oo([
  E()
], Gt.prototype, "docsHint", 2);
Oo([
  E({ type: Number })
], Gt.prototype, "count", 2);
Gt = Oo([
  Te("jds-info")
], Gt);
const Kr = Ye`
  :host {
    display: inline-block;
    max-width: 100%;
    width: max-content;
  }

  .badge {
    --bg-color: var(
      --jds-badge-bg-color,
      var(--jds-color-primary-contrast)
    ); // 徽章背景颜色
    --text-color: var(
      --jds-badge-color,
      var(--jds-color-primary)
    ); // 徽章文字颜色
    --font: var(--jds-font-title-4-medium); // 徽章字体
    --padding-vertical: var(--jds-size-4xs); // 徽章垂直内边距
    --padding-horizontal: var(--jds-size-3xs); // 徽章水平内边距
    --margin-icon: var(--jds-size-3xs); // 图标外边距
    --icon-size: var(--jds-size-s); // 图标大小
    --height: var(--jds-size-xl); // 徽章高度

    display: flex;
    gap: var(--margin-icon);
    justify-content: center;
    align-items: center;

    box-sizing: border-box;
    width: 100%;
    border: none;
    border-radius: var(--jds-size-4xs);
    margin: 0;
    padding: var(--padding-vertical) var(--padding-horizontal);
    background-color: var(--bg-color);
    color: var(--jds-badge-color, var(--jds-color-primary));
    /* font: var(--font); */
    font-kerning: none;
    height: var(--height);
  }

  // 小号徽章样式
  :host([size="small"]) .badge {
    --font: var(--jds-font-caption);
    --height: var(--jds-size-m);
  }

  // 大号徽章样式
  :host([size="large"]) .badge {
    --font: var(--jds-font-title-3-medium);
    --padding-vertical: var(--jds-size-2xs);
    --padding-horizontal: var(--jds-size-2xs);
    --height: var(--jds-size-2xl);
    --icon-size: var(--jds-size-m);
  }

  // 插槽中的图标样式
  :host ::slotted(bl-icon) {
    font-size: var(--icon-size);
  }

  // 小号徽章中不显示图标
  :host([size="small"]) bl-icon {
    display: none;
  }
`;
var Wr = Object.defineProperty, Jr = Object.getOwnPropertyDescriptor, Bo = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Jr(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Wr(e, i, s), s;
};
let Xt = class extends J {
  constructor() {
    super(...arguments), this.size = "medium";
  }
  // 组件的渲染方法
  render() {
    const e = this.icon ? _`<jds-icon name=${this.icon}></jds-icon>` : "";
    return _`<span class="badge">
      <slot name="icon">${e}</slot>
      <slot></slot>
    </span>`;
  }
};
Xt.styles = [Kr];
Bo([
  E({ type: String, reflect: !0 })
], Xt.prototype, "size", 2);
Bo([
  E({ type: String })
], Xt.prototype, "icon", 2);
Xt = Bo([
  Te("jds-badge")
], Xt);
var Gr = Object.defineProperty, Xr = Object.getOwnPropertyDescriptor, li = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Xr(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Gr(e, i, s), s;
};
let st = class extends J {
  constructor() {
    super(...arguments), this.size = "var(--jds-font-size-m)", this.disabled = !1, this.overlay = !1, this.color = "var(--jds-color-primary)";
  }
  // 渲染组件的HTML模板
  render() {
    return _`<jds-icon
      class="spinner"
      name="loading"
      style="color: ${this.disabled ? "var(--jds-color-neutral-light)" : this.color}; font-size: ${this.size};"
    ></jds-icon>`;
  }
};
st.styles = Ye`
    :host {
      cursor: wait; // 设置鼠标悬停时的光标样式为等待状态
      display: inline-flex;
      width: max-content;
      align-items: center;
    }

    .spinner {
      animation: spin 1s linear infinite; // 设置旋转动画
    }

    :host([overlay]) {
      position: absolute;
      top: 0;
      inset-inline-start: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(39 49 66 / 70%); // 设置背景颜色和透明度
      z-index: 10; // 设置层叠顺序
    }

    @keyframes spin {
      from {
        transform: rotate(0deg); // 动画开始时的旋转角度
      }

      to {
        transform: rotate(360deg); // 动画结束时的旋转角度
      }
    }
  `;
li([
  E({ type: String, reflect: !0 })
], st.prototype, "size", 2);
li([
  E({ type: Boolean, reflect: !0 })
], st.prototype, "disabled", 2);
li([
  E({ type: Boolean, reflect: !0 })
], st.prototype, "overlay", 2);
li([
  E({ type: String, reflect: !0 })
], st.prototype, "color", 2);
st = li([
  Te("jds-spinner")
], st);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function Uo(t) {
  return E({ ...t, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Qr = (t, e, i) => (i.configurable = !0, i.enumerable = !0, Reflect.decorate && typeof e != "object" && Object.defineProperty(t, e, i), i);
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function _e(t, e) {
  return (i, o, s) => {
    const n = (r) => {
      var a;
      return ((a = r.renderRoot) == null ? void 0 : a.querySelector(t)) ?? null;
    };
    return Qr(i, o, { get() {
      return n(this);
    } });
  };
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Zr = { ATTRIBUTE: 1 }, ea = (t) => (...e) => ({ _$litDirective$: t, values: e });
let ta = class {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, i, o) {
    this._$Ct = e, this._$AM = i, this._$Ci = o;
  }
  _$AS(e, i) {
    return this.update(e, i);
  }
  update(e, i) {
    return this.render(...i);
  }
};
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const No = ea(class extends ta {
  constructor(t) {
    var e;
    if (super(t), t.type !== Zr.ATTRIBUTE || t.name !== "class" || ((e = t.strings) == null ? void 0 : e.length) > 2) throw Error("`classMap()` can only be used in the `class` attribute and must be the only part in the attribute.");
  }
  render(t) {
    return " " + Object.keys(t).filter((e) => t[e]).join(" ") + " ";
  }
  update(t, [e]) {
    var i, o;
    if (this.st === void 0) {
      this.st = /* @__PURE__ */ new Set(), t.strings !== void 0 && (this.nt = new Set(t.strings.join(" ").split(/\s/).filter((n) => n !== "")));
      for (const n in e) e[n] && !((i = this.nt) != null && i.has(n)) && this.st.add(n);
      return this.render(e);
    }
    const s = t.element.classList;
    for (const n of this.st) n in e || (s.remove(n), this.st.delete(n));
    for (const n in e) {
      const r = !!e[n];
      r === this.st.has(n) || (o = this.nt) != null && o.has(n) || (r ? (s.add(n), this.st.add(n)) : (s.remove(n), this.st.delete(n)));
    }
    return ot;
  }
});
/**
 * @license
 * Copyright 2018 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Le = (t) => t ?? M;
function ia(t, e) {
  return function(i, o) {
    const s = new CustomEvent(e, {
      detail: i,
      // 事件的详细信息
      bubbles: !0,
      // 默认允许冒泡
      cancelable: !1,
      // 默认不可取消
      composed: !0,
      // 默认允许穿透到 shadow DOM
      ...o
      // 允许覆盖默认选项
    });
    return t.dispatchEvent(s), s;
  };
}
function Ce(t) {
  return (e, i) => {
    Object.defineProperty(e, i, {
      get() {
        return ia(this, t || i);
      },
      enumerable: !0,
      // 使属性可枚举
      configurable: !0
      // 使属性可配置
    });
  };
}
const oa = globalThis.SubmitEvent = typeof globalThis.SubmitEvent < "u" ? SubmitEvent : Event, sa = (t) => {
  if (!(!t.noValidate && !t.reportValidity())) {
    const e = new oa("submit", {
      bubbles: !0,
      cancelable: !0
    });
    t.dispatchEvent(e), e.defaultPrevented || t.submit();
  }
}, na = Ye`
  :host {
    display: var(--jds-button-display, inline-block);
    max-width: 100%;
    width: max-content;
    position: relative;
  }

  .button {
    --main-color: var(--jds-color-primary);
    --main-hover-color: var(--jds-color-primary-highlight);
    --text-hover-color: var(--jds-color-neutral-lightest);
    --content-color: var(--jds-color-neutral-full);
    --bg-color: var(--main-color);
    --border-color: var(--main-color);
    --padding-vertical: var(--jds-size-2xs);
    --padding-horizontal: var(--jds-size-m);
    --margin-icon: var(--padding-vertical);
    --icon-size: var(--jds-size-m);
    --font: var(--jds-font-title-3-medium);
    --height: var(--jds-size-2xl);
    --border-radius: var(--jds-border-radius-pill);

    display: flex;
    gap: var(--margin-icon);
    justify-content: var(--jds-button-justify, center);
    align-items: center;
    box-sizing: border-box;
    width: max-content;
    height: var(--height);
    border: solid 1px var(--border-color);
    border-radius: var(--border-radius);
    text-decoration: none;
    padding: var(--padding-vertical) var(--padding-horizontal);
    cursor: pointer;
    background-color: var(--bg-color);
    color: var(--content-color);
    font: var(--font);
    font-kerning: none;
    user-select: none;
  }

  :host(:hover) .button {
    --bg-color: var(--main-hover-color);
    --border-color: var(--main-hover-color);
  }

  .label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  :host([size="small"]) .button {
    --font: var(--jds-font-title-4-medium);
    --padding-vertical: var(--jds-size-3xs);
    --padding-horizontal: var(--jds-size-2xs);
    --icon-size: var(--jds-size-s);
    --height: var(--jds-size-xl);
  }

  :host([size="large"]) .button {
    --font: var(--jds-font-title-3-medium);
    --padding-vertical: var(--jds-size-xs);
    --padding-horizontal: var(--jds-size-xl);
    --margin-icon: var(--jds-size-2xs);
    --height: var(--jds-size-3xl);
  }

  .button:focus {
    outline: none;
  }

  .button:focus-visible {
    position: relative;
    outline: none;
  }

  .button:focus-visible::after {
    border: 2px solid var(--jds-button-focus-border-color, var(--main-color));
    border-radius: var(--border-radius);
    content: "";
    position: absolute;
    inset: -4px;
  }

  :host ::slotted(bl-icon) {
    font-size: var(--icon-size);
  }

  :host([loading]) ::slotted(bl-icon) {
    display: none;
  }

  :host .has-icon:not(.has-content) {
    --padding-horizontal: var(--padding-vertical);
    --margin-icon: 0;
  }

  :host([variant="secondary"]) .button {
    --bg-color: transparent;
    --content-color: var(--main-color);
  }

  :host([variant="tertiary"]) .button {
    --content-color: var(--main-color);
    --border-color: transparent;
    --bg-color: transparent;
  }

  :host([kind="neutral"]) .button {
    --main-color: var(--jds-color-neutral-darker);
    --main-hover-color: var(--jds-color-neutral-darkest);
  }

  :host([kind="success"]) .button {
    --main-color: var(--jds-color-success);
    --main-hover-color: var(--jds-color-success-highlight);
  }

  :host([kind="danger"]) .button {
    --main-color: var(--jds-color-danger);
    --main-hover-color: var(--jds-color-danger-highlight);
  }

  :host([disabled]) {
    cursor: not-allowed;
  }

  :host([loading]) {
    cursor: wait;
  }

  :host .button[aria-disabled="true"] {
    --main-color: var(--jds-color-neutral-lightest);
    --main-hover-color: var(--jds-color-neutral-lightest);
    --content-color: var(--jds-color-neutral-lighter);
    --bg-color: var(--main-color);

    pointer-events: none;
    text-decoration: none;
  }

  :host([variant="tertiary"]) .button[aria-disabled="true"] {
    --main-color: transparent;
  }

  :host([variant="secondary"]:hover) .button[aria-disabled="false"] {
    --content-color: var(--jds-color-neutral-full);
    --bg-color: var(--main-hover-color);
  }

  :host([variant="tertiary"]:hover) .button[aria-disabled="false"] {
    --content-color: var(--main-hover-color);
    --bg-color: var(--text-hover-color);
  }

  :host([dropdown]) .open {
    display: none;
  }

  :host([dropdown]) .active .open {
    display: inline-block;
  }

  :host([dropdown]) .active .close {
    display: none;
  }

  :host .active.button {
    --bg-color: var(--main-hover-color);
    --border-color: var(--main-hover-color);
  }

  :host([variant="secondary"]) .active.button {
    --content-color: var(--jds-color-neutral-full);
    --bg-color: var(--main-hover-color);
  }

  :host([variant="tertiary"]) .active.button {
    --content-color: var(--main-color);
    --bg-color: var(--jds-color-neutral-lightest);
    --border-color: transparent;
  }
`;
var ra = Object.defineProperty, aa = Object.getOwnPropertyDescriptor, U = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? aa(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && ra(e, i, s), s;
};
let I = class extends J {
  constructor() {
    super(...arguments), this.variant = "primary", this.kind = "default", this.size = "medium", this.loading = !1, this.disabled = !1, this.target = "_self", this.dropdown = !1, this.autofocus = !1, this.active = !1;
  }
  // 获取按钮的激活状态
  get _isActive() {
    return this.active;
  }
  // 组件连接到文档DOM时的回调
  connectedCallback() {
    super.connectedCallback();
  }
  // 下拉菜单的图标模板
  caretTemplate() {
    return _`
      <jds-icon class="open" name="arrow-up"></jds-icon>
      <jds-icon class="close" name="arrow-down"></jds-icon>
    `;
  }
  // 处理按钮点击事件
  _handleClick() {
    if (this.type === "submit") {
      let e;
      this.form instanceof HTMLFormElement ? e = this.form : typeof this.form == "string" ? e = document.getElementById(this.form) : e = this.closest("form"), e && sa(e);
    }
    this.onClick("Click event fired!");
  }
  // 使按钮获取焦点
  focus() {
    var e;
    (e = this.button) == null || e.focus();
  }
  // 获取是否有图标插槽
  get _hasIconSlot() {
    return this.querySelector(':scope > [slot="icon"]') !== null;
  }
  // 获取是否有默认插槽
  get _hasDefaultSlot() {
    return [...this.childNodes].some((e) => {
      var i;
      const o = e.nodeType;
      return o === e.TEXT_NODE && ((i = e.textContent) == null ? void 0 : i.trim()) !== "" || o === e.ELEMENT_NODE && !e.hasAttribute("slot");
    });
  }
  // 渲染组件的HTML模板
  render() {
    const e = this.loading || this.disabled, i = this.loading && this.loadingLabel ? this.loadingLabel : _`<slot></slot>`, o = !!this.href, s = this.icon ? _`<jds-icon name=${this.icon}></jds-icon>` : "", n = _`
      <jds-spinner
        class="loading-spinner"
        ?disabled="${e}"
        size="${this.size}"
      ></jds-spinner>
    `, r = _`
      <slot name="icon">${this.loading ? n : s}</slot>
      <span class="label">${i}</span>
    `, a = this.dropdown ? this.caretTemplate() : "", l = No({
      button: !0,
      "has-icon": this.icon || this._hasIconSlot,
      "has-content": this._hasDefaultSlot,
      active: !o && this._isActive
    });
    return o ? _`
          <a
            part="button"
            class=${l}
            ?autofocus=${this.autofocus}
            aria-disabled="${Le(e)}"
            aria-label="${Le(this.label)}"
            href=${Le(this.href)}
            target=${Le(this.target)}
            role="button"
          >
            ${r}
          </a>
        ` : _`
          <button
            part="button"
            class=${l}
            ?autofocus=${this.autofocus}
            aria-disabled="${Le(e)}"
            aria-label="${Le(this.label)}"
            ?disabled=${e}
            @click="${this._handleClick}"
          >
            ${r} ${a}
          </button>
        `;
  }
};
I.styles = [na];
U([
  E({ type: String, reflect: !0 })
], I.prototype, "variant", 2);
U([
  E({ type: String, reflect: !0 })
], I.prototype, "kind", 2);
U([
  E({ type: String, reflect: !0 })
], I.prototype, "size", 2);
U([
  E({ type: String })
], I.prototype, "label", 2);
U([
  E({ type: String, attribute: "loading-label" })
], I.prototype, "loadingLabel", 2);
U([
  E({ type: Boolean, reflect: !0 })
], I.prototype, "loading", 2);
U([
  E({ type: Boolean, reflect: !0 })
], I.prototype, "disabled", 2);
U([
  E({ type: String })
], I.prototype, "href", 2);
U([
  E({ type: String })
], I.prototype, "icon", 2);
U([
  E({ type: String })
], I.prototype, "target", 2);
U([
  E({ type: String })
], I.prototype, "type", 2);
U([
  E({ type: Boolean })
], I.prototype, "dropdown", 2);
U([
  E({ type: Boolean, reflect: !0 })
], I.prototype, "autofocus", 2);
U([
  E({ type: String })
], I.prototype, "form", 2);
U([
  Uo({})
], I.prototype, "active", 2);
U([
  _e(".button")
], I.prototype, "button", 2);
U([
  Ce("jds-click")
], I.prototype, "onClick", 2);
I = U([
  Te("jds-button")
], I);
const la = Ye`
  :host {
    display: contents; /* 使宿主元素不影响布局 */
    width: max-content;
  }

  .container {
    --background-color: var(--jds-color-neutral-full);

    display: flex;
    flex-direction: column;
    background: var(--background-color);
    width: var(--jds-dialog-width, auto);
    max-width: calc(100vw - var(--jds-size-4xl));
    max-height: calc(100vh - var(--jds-size-4xl));
    min-width: 424px;
    padding: 0;
    border: 0;
    border-radius: var(--jds-border-radius-l);
  }

  .dialog,
  .dialog-polyfill .container {
    padding: 0;
    border: 0;
    border-radius: var(--jds-border-radius-l);
  }

  .dialog-polyfill .container {
    position: fixed;
    z-index: var(--jds-index-dialog);
  }

  .dialog::backdrop {
    background-color: #273142b3; /* 对话框背景色 */
  }

  .dialog-polyfill {
    display: none;
    position: fixed;
    inset-inline-start: 0;
    top: 0;
    width: 100vw;
    height: 100vh;
    align-items: center;
    justify-content: center;
    z-index: var(--jds-index-dialog);
    background-color: #273142b3; /* 遮罩层背景色 */
  }

  :host([open]) .dialog-polyfill {
    display: flex; /* 打开对话框时显示遮罩 */
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--jds-size-2xs);
    padding: var(--jds-size-m) var(--jds-size-m) 0 var(--jds-size-m);
  }

  header jds-icon {
    cursor: pointer;
  }

  header h2 {
    font: var(--jds-font-title-1-medium);
    color: var(--jds-color-neutral-darker);
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(
      --jds-dialog-caption-line-clamp,
      1
    ); /* 限制标题行数 */
    margin: 0;
    padding: 0;
  }

  .content {
    margin-right: var(--jds-size-2xs);
    margin-bottom: var(--jds-size-xl);
    padding:0 calc(var(--jds-size-4xl) - 10px) 0 var(--jds-size-4xl);
    overflow-y: auto; /* 内容溢出时可滚动 */
  }

  /* 滚动条滑块 */
  .content::-webkit-scrollbar-thumb {
    /* 滑块颜色 */
    background-color: #C9C9CB;
    /* 滑块圆角 */
    border-radius: 10px;
  }

  .content::-webkit-scrollbar-track {
    /* 滚动条轨道透明 */
    background-color: transparent;
  }

  @media screen and (min-width: 1440px) {
    .content::-webkit-scrollbar {
      /* 滚动条宽度 */
      width: 6px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px){
    .content::-webkit-scrollbar {
      /* 滚动条宽度 */
      width: 0.416666666666667vw;
    }
  }

  @media screen and (max-width: 1023.98px) {
    .content::-webkit-scrollbar {
      /* 滚动条宽度 */
      width: 1.06666666666667vw;
    }
  }

  :host([critical]) .content {
    padding-top: 0; /* 关键对话框顶部无内边距 */
  }

  .container.has-footer .content {
    padding-bottom: 0; /* 若有页脚则底部无内边距 */
  }

  footer {
    padding: var(--jds-size-xl);
    display: flex;
    flex-flow: row-reverse wrap; /* 反向排列页脚按钮 */
    gap: var(--jds-size-m);
  }

  footer.shadow {
    box-shadow: 0 -4px 15px #27314226; /* 页脚阴影 */
  }

  @media only screen and (max-width: 471px) {
    .container {
      max-width: calc(100vw - var(--jds-size-2xl));
      max-height: calc(100vh - var(--jds-size-2xl));
      min-width: auto;
      min-height: auto;
    }

    footer {
      flex-flow: column wrap; /* 小屏幕下页脚按钮竖排 */
    }
  }

  ::slotted(bl-tab-group) {
    display: block;
    transform: translateX(calc(var(--jds-size-xl) * -1));
    width: calc(100% + calc(var(--jds-size-xl) * 2)); /* 适应宽度 */
  }
`;
var ca = Object.defineProperty, da = Object.getOwnPropertyDescriptor, ee = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? da(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && ca(e, i, s), s;
};
let V = class extends J {
  constructor() {
    super(...arguments), this.open = !1, this.critical = !1, this.closable = !0, this.polyfilled = !0, this.clickOutsideHandler = (e) => {
      this.critical || e.composedPath().includes(this.container) || this.closeDialog("backdrop");
    }, this.onKeydown = (e) => {
      e.code === "Escape" && this.open && !this.critical && (e.preventDefault(), this.closeDialog("keyboard"));
    }, this.toggleFooterShadow = () => {
      var e, i, o, s, n, r, a;
      const l = (e = this.content) == null ? void 0 : e.scrollTop, c = (i = this.content) == null ? void 0 : i.scrollHeight, d = (o = this.content) == null ? void 0 : o.clientHeight;
      l + d >= c ? (n = (s = this.footer) == null ? void 0 : s.classList) == null || n.remove("shadow") : (a = (r = this.footer) == null ? void 0 : r.classList) == null || a.add("shadow");
    };
  }
  // 当属性发生变化时执行的逻辑
  updated(e) {
    (e.has("open") || e.has("polyfilled")) && this.toggleDialogHandler();
  }
  // 判断是否有页脚
  get _hasFooter() {
    return [...this.childNodes].some((e) => {
      if (e.nodeType === Node.ELEMENT_NODE) {
        const i = e;
        return i.nodeName === "JDS-BUTTON" && i.hasAttribute("slot");
      }
      return !1;
    });
  }
  // 切换对话框的处理程序
  toggleDialogHandler() {
    var e, i, o, s, n, r;
    this.open ? ((i = (e = this.dialog) == null ? void 0 : e.showModal) == null || i.call(e), this.onOpen({ isOpen: !0 }), document.body.style.overflow = "hidden", this.toggleFooterShadow(), window == null || window.addEventListener("keydown", this.onKeydown), window == null || window.addEventListener("resize", this.toggleFooterShadow), (o = this.content) == null || o.addEventListener("scroll", this.toggleFooterShadow)) : ((n = (s = this.dialog) == null ? void 0 : s.close) == null || n.call(s), this.onClose({ isOpen: !1 }, { bubbles: !1 }), document.body.style.overflow = "auto", window == null || window.removeEventListener("keydown", this.onKeydown), window == null || window.removeEventListener("resize", this.toggleFooterShadow), (r = this.content) == null || r.removeEventListener("scroll", this.toggleFooterShadow), this.dispatchEvent(new Event("closed", { bubbles: !0 })));
  }
  // 关闭对话框的方法
  closeDialog(e) {
    this.onRequestClose(
      { source: e },
      { cancelable: !0 }
    ).defaultPrevented || (this.open = !1);
  }
  // 渲染页脚
  renderFooter() {
    return this._hasFooter ? _`<footer part="dialog-footer">
          <slot name="primary-action"></slot>
          <slot name="secondary-action"></slot>
          <slot name="tertiary-action"></slot>
        </footer>` : "";
  }
  // 渲染对话框容器
  renderContainer() {
    const e = this.caption ? _`<h2 id="dialog-caption">${this.caption}</h2>` : "", i = this.closable ? _`<jds-icon
          @click="${() => this.closeDialog("close-button")}"
          name="close-fill"
          label="close"
          style="font-size:var(--jds-size-2xl);color:var(--jds-color-text-l3);"
        ></jds-icon>` : null, o = {
      container: !0,
      "has-footer": this._hasFooter
    };
    return _`<div part="dialog-container" class="${No(o)}">
      <header>${e} ${i}</header>
      <section part="dialog-content" class="content"><slot></slot></section>
      ${this.renderFooter()}
    </div>`;
  }
  // 渲染组件
  render() {
    return this.polyfilled || !window.HTMLDialogElement ? _`<div
          part="dialog-polyfill"
          class="dialog-polyfill"
          role="dialog"
          aria-labelledby="dialog-caption"
          @click=${this.clickOutsideHandler}
        >
          ${this.renderContainer()}
        </div>` : _`
          <dialog
            part="dialog"
            class="dialog"
            aria-labelledby="dialog-caption"
            @click=${this.clickOutsideHandler}
          >
            ${this.renderContainer()}
          </dialog>
        `;
  }
};
V.styles = [la];
ee([
  E({
    type: Boolean,
    reflect: !0,
    hasChanged(t, e) {
      return t === !1 && e === void 0 ? !1 : t !== e;
    }
  })
], V.prototype, "open", 2);
ee([
  E({ type: String })
], V.prototype, "caption", 2);
ee([
  E({ type: Boolean, reflect: !0 })
], V.prototype, "critical", 2);
ee([
  E({ type: Boolean, reflect: !0 })
], V.prototype, "closable", 2);
ee([
  E({ type: Boolean, reflect: !0 })
], V.prototype, "polyfilled", 2);
ee([
  _e(".dialog")
], V.prototype, "dialog", 2);
ee([
  _e("footer")
], V.prototype, "footer", 2);
ee([
  _e(".container")
], V.prototype, "container", 2);
ee([
  _e(".content")
], V.prototype, "content", 2);
ee([
  Ce("jds-dialog-open")
], V.prototype, "onOpen", 2);
ee([
  Ce("jds-dialog-request-close")
], V.prototype, "onRequestClose", 2);
ee([
  Ce("jds-dialog-close")
], V.prototype, "onClose", 2);
V = ee([
  Te("jds-dialog")
], V);
const ha = () => ({
  /**
   * 从属性值转换为对应的 JavaScript 类型
   *
   * @param value - HTML 属性的字符串值
   * @returns 返回布尔值或原始字符串
   */
  fromAttribute: (t) => !t || t === "true" ? !0 : t === "false" ? !1 : t
}), ua = Ye`
  :host {
    display: block; /* 设置为块级元素 */
    width: max-content;
  }

  .toast {
    --padding: var(--jds-size-m); /* 设置内边距 */
    --main-color: var(--jds-color-info); /* 主颜色 */
    --main-bg-color: var(--jds-color-info-contrast); /* 背景颜色 */

    position: relative;
    display: flex; /* 使用弹性布局 */
    align-items: flex-start;
    justify-content: space-between;
    width: 560px;
    background-color: var(--main-bg-color);
    color: var(--jds-color-neutral-darker);
    box-shadow: inset 0 0 0 1px var(--main-color);
    padding: calc(var(--padding) / 2) var(--padding);
    padding-inline-end: calc(var(--padding) / 2);
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .toast {
      width: calc(560vw / 1440 * 100);
    }
  }
  @media screen and (max-width: 1023.98px) {
    .toast {
      width: calc(640vw / 750 * 100);
    }
  }

  .description {
    font: var(--jds-font-body-text-2); /* 描述文本样式 */
  }

  .wrapper {
    display: flex;
    flex-flow: column; /* 垂直排列 */
    flex-wrap: wrap; /* 换行 */
    justify-content: space-between;
    flex: auto;
  }

  .content {
    display: flex;
    margin-inline-end: var(--jds-size-2xs);
    flex: 20 1 70%; /* 设置弹性缩放 */
    padding: calc(var(--padding) / 2) 0;
  }

  .icon {
    padding: calc(var(--padding) / 2) 0;
    margin-inline-end: var(--jds-size-2xs);
    color: var(--main-color);
  }

  .text-content {
    display: flex;
    flex-direction: column; /* 垂直排列文本内容 */
  }

  .caption {
    color: var(--jds-color-neutral-darker);
    font: var(--jds-font-title-3-medium); /* 标题样式 */
  }

  .actions {
    display: none; /* 默认不显示操作区域 */
    flex-wrap: wrap; /* 换行 */
    gap: var(--jds-size-m); /* 间距 */
    padding: calc(var(--padding) / 2) 0;
  }

  .close {
    --jds-color-neutral-lightest: transparent; /* 关闭按钮透明 */
  }

  .caption + .description {
    margin-top: var(--jds-size-2xs); /* 标题与描述之间的间距 */
  }

  :host([closed]) {
    display: none; /* 关闭时不显示 */
  }

  /* 根据不同变体设置颜色 */
  :host([variant="success"]) .toast {
    --main-color: var(--jds-color-success);
    --main-bg-color: var(--jds-color-success-contrast);
  }

  :host([variant="warning"]) .toast {
    --main-color: var(--jds-color-warning);
    --main-bg-color: var(--jds-color-warning-contrast);
  }

  :host([variant="danger"]) .toast {
    --main-color: var(--jds-color-danger);
    --main-bg-color: var(--jds-color-danger-contrast);
  }
`;
var pa = Object.defineProperty, ga = Object.getOwnPropertyDescriptor, je = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? ga(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && pa(e, i, s), s;
};
let ae = class extends J {
  constructor() {
    super(...arguments), this.variant = "info", this.closable = !1, this.closed = !1, this.countdownTime = 0;
  }
  //存储倒计时的定时器
  /**
   * 打开轻提示组件
   */
  open() {
    this.closed = !1;
  }
  /**
   * 关闭轻提示组件
   */
  close() {
    this.closed = !0;
  }
  connectedCallback() {
    super.connectedCallback(), this.startCountdown();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.stopCountdown();
  }
  /**
   * 启动倒计时
   */
  startCountdown() {
    this.countdownTime > 0 && (this.countdownInterval = window.setInterval(() => {
      this.countdownTime--, this.countdownTime <= 0 && this.close();
    }, 1e3));
  }
  /**
   * 停止倒计时
   */
  stopCountdown() {
    this.countdownInterval && (clearInterval(this.countdownInterval), this.countdownInterval = void 0);
  }
  // 检查是否有轻提示标题插槽
  get _hasToastCaptionSlot() {
    return this.querySelector(':scope > [slot="caption"]') !== null;
  }
  // 关闭处理事件
  _closeHandler() {
    this.closed = !0, this.onClose(!0);
  }
  // 根据变体返回预定义图标
  _predefinedIcons() {
    switch (this.variant) {
      case "info":
        return "info-fill";
      case "warning":
        return "alert-fill";
      case "success":
        return "check-fill";
      case "danger":
        return "close-fill";
      default:
        return this.variant;
    }
  }
  // 获取图标
  _getIcon() {
    return this.icon ? this.icon === !0 ? this._predefinedIcons() : this.icon : "";
  }
  // 初始化轻提示操作插槽
  _initToastActionSlot(e) {
    const i = e.target;
    i.assignedElements({ flatten: !0 }).forEach((o) => {
      var s, n;
      if (o.tagName !== "JDS-BUTTON") {
        (s = o.parentNode) == null || s.removeChild(o);
        return;
      }
      ((n = this.shadowRoot) == null ? void 0 : n.querySelector(".actions")).style.display = "flex";
      const r = i.name === "action-secondary" ? "secondary" : "primary", a = {
        info: "neutral",
        warning: "neutral",
        success: "success",
        danger: "danger"
      };
      o.setAttribute("variant", r), o.setAttribute("kind", a[this.variant]), o.setAttribute("size", "medium"), o.removeAttribute("icon");
    });
  }
  render() {
    const e = this.caption || this._hasToastCaptionSlot ? _`<span class="caption">
            <slot name="caption"> ${this.caption} </slot>
          </span>` : null, i = this._getIcon() ? _`<jds-icon
          class="icon"
          name=${Le(this._getIcon())}
        ></jds-icon>` : null, o = this.closable ? _`<jds-button
          class="close"
          label="close"
          kind="neutral"
          icon="close"
          variant="tertiary"
          @click=${this._closeHandler}
        ></jds-button>` : null, s = _`<span class="description">
      <slot> ${this.description} </slot>
    </span>`;
    return _`
      <div class="toast">
        ${i}
        <div class="wrapper">
          <div class="content">
            <div class="text-content">${e} ${s}</div>
          </div>
          <div class="actions">
            <slot
              class="action"
              name="action"
              @slotchange=${this._initToastActionSlot}
            ></slot>
            <slot
              class="action-secondary"
              name="action-secondary"
              @slotchange=${this._initToastActionSlot}
            ></slot>
          </div>
        </div>
        ${o}
      </div>
    `;
  }
};
ae.styles = [ua];
je([
  E({ reflect: !0 })
], ae.prototype, "variant", 2);
je([
  E()
], ae.prototype, "description", 2);
je([
  E({ converter: ha() })
], ae.prototype, "icon", 2);
je([
  E({ type: Boolean, reflect: !0 })
], ae.prototype, "closable", 2);
je([
  E()
], ae.prototype, "caption", 2);
je([
  E({ type: Boolean, reflect: !0 })
], ae.prototype, "closed", 2);
je([
  E({ type: Number })
], ae.prototype, "countdownTime", 2);
je([
  Ce("jds-close")
], ae.prototype, "onClose", 2);
ae = je([
  Te("jds-toast")
], ae);
const le = Math.min, F = Math.max, Ri = Math.round, wi = Math.floor, be = (t) => ({
  x: t,
  y: t
}), fa = {
  left: "right",
  right: "left",
  bottom: "top",
  top: "bottom"
}, ma = {
  start: "end",
  end: "start"
};
function bo(t, e, i) {
  return F(t, le(e, i));
}
function ut(t, e) {
  return typeof t == "function" ? t(e) : t;
}
function ye(t) {
  return t.split("-")[0];
}
function jt(t) {
  return t.split("-")[1];
}
function En(t) {
  return t === "x" ? "y" : "x";
}
function Ho(t) {
  return t === "y" ? "height" : "width";
}
function Ue(t) {
  return ["top", "bottom"].includes(ye(t)) ? "y" : "x";
}
function Fo(t) {
  return En(Ue(t));
}
function wa(t, e, i) {
  i === void 0 && (i = !1);
  const o = jt(t), s = Fo(t), n = Ho(s);
  let r = s === "x" ? o === (i ? "end" : "start") ? "right" : "left" : o === "start" ? "bottom" : "top";
  return e.reference[n] > e.floating[n] && (r = Di(r)), [r, Di(r)];
}
function va(t) {
  const e = Di(t);
  return [yo(t), e, yo(e)];
}
function yo(t) {
  return t.replace(/start|end/g, (e) => ma[e]);
}
function ba(t, e, i) {
  const o = ["left", "right"], s = ["right", "left"], n = ["top", "bottom"], r = ["bottom", "top"];
  switch (t) {
    case "top":
    case "bottom":
      return i ? e ? s : o : e ? o : s;
    case "left":
    case "right":
      return e ? n : r;
    default:
      return [];
  }
}
function ya(t, e, i, o) {
  const s = jt(t);
  let n = ba(ye(t), i === "start", o);
  return s && (n = n.map((r) => r + "-" + s), e && (n = n.concat(n.map(yo)))), n;
}
function Di(t) {
  return t.replace(/left|right|bottom|top/g, (e) => fa[e]);
}
function xa(t) {
  return {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    ...t
  };
}
function qo(t) {
  return typeof t != "number" ? xa(t) : {
    top: t,
    right: t,
    bottom: t,
    left: t
  };
}
function Et(t) {
  const {
    x: e,
    y: i,
    width: o,
    height: s
  } = t;
  return {
    width: o,
    height: s,
    top: i,
    left: e,
    right: e + o,
    bottom: i + s,
    x: e,
    y: i
  };
}
function ks(t, e, i) {
  let {
    reference: o,
    floating: s
  } = t;
  const n = Ue(e), r = Fo(e), a = Ho(r), l = ye(e), c = n === "y", d = o.x + o.width / 2 - s.width / 2, h = o.y + o.height / 2 - s.height / 2, p = o[a] / 2 - s[a] / 2;
  let f;
  switch (l) {
    case "top":
      f = {
        x: d,
        y: o.y - s.height
      };
      break;
    case "bottom":
      f = {
        x: d,
        y: o.y + o.height
      };
      break;
    case "right":
      f = {
        x: o.x + o.width,
        y: h
      };
      break;
    case "left":
      f = {
        x: o.x - s.width,
        y: h
      };
      break;
    default:
      f = {
        x: o.x,
        y: o.y
      };
  }
  switch (jt(e)) {
    case "start":
      f[r] -= p * (i && c ? -1 : 1);
      break;
    case "end":
      f[r] += p * (i && c ? -1 : 1);
      break;
  }
  return f;
}
const $a = async (t, e, i) => {
  const {
    placement: o = "bottom",
    strategy: s = "absolute",
    middleware: n = [],
    platform: r
  } = i, a = n.filter(Boolean), l = await (r.isRTL == null ? void 0 : r.isRTL(e));
  let c = await r.getElementRects({
    reference: t,
    floating: e,
    strategy: s
  }), {
    x: d,
    y: h
  } = ks(c, o, l), p = o, f = {}, g = 0;
  for (let w = 0; w < a.length; w++) {
    const {
      name: m,
      fn: b
    } = a[w], {
      x: y,
      y: x,
      data: T,
      reset: k
    } = await b({
      x: d,
      y: h,
      initialPlacement: o,
      placement: p,
      strategy: s,
      middlewareData: f,
      rects: c,
      platform: r,
      elements: {
        reference: t,
        floating: e
      }
    });
    d = y ?? d, h = x ?? h, f = {
      ...f,
      [m]: {
        ...f[m],
        ...T
      }
    }, k && g <= 50 && (g++, typeof k == "object" && (k.placement && (p = k.placement), k.rects && (c = k.rects === !0 ? await r.getElementRects({
      reference: t,
      floating: e,
      strategy: s
    }) : k.rects), {
      x: d,
      y: h
    } = ks(c, p, l)), w = -1);
  }
  return {
    x: d,
    y: h,
    placement: p,
    strategy: s,
    middlewareData: f
  };
};
async function Yo(t, e) {
  var i;
  e === void 0 && (e = {});
  const {
    x: o,
    y: s,
    platform: n,
    rects: r,
    elements: a,
    strategy: l
  } = t, {
    boundary: c = "clippingAncestors",
    rootBoundary: d = "viewport",
    elementContext: h = "floating",
    altBoundary: p = !1,
    padding: f = 0
  } = ut(e, t), g = qo(f), w = a[p ? h === "floating" ? "reference" : "floating" : h], m = Et(await n.getClippingRect({
    element: (i = await (n.isElement == null ? void 0 : n.isElement(w))) == null || i ? w : w.contextElement || await (n.getDocumentElement == null ? void 0 : n.getDocumentElement(a.floating)),
    boundary: c,
    rootBoundary: d,
    strategy: l
  })), b = h === "floating" ? {
    x: o,
    y: s,
    width: r.floating.width,
    height: r.floating.height
  } : r.reference, y = await (n.getOffsetParent == null ? void 0 : n.getOffsetParent(a.floating)), x = await (n.isElement == null ? void 0 : n.isElement(y)) ? await (n.getScale == null ? void 0 : n.getScale(y)) || {
    x: 1,
    y: 1
  } : {
    x: 1,
    y: 1
  }, T = Et(n.convertOffsetParentRelativeRectToViewportRelativeRect ? await n.convertOffsetParentRelativeRectToViewportRelativeRect({
    elements: a,
    rect: b,
    offsetParent: y,
    strategy: l
  }) : b);
  return {
    top: (m.top - T.top + g.top) / x.y,
    bottom: (T.bottom - m.bottom + g.bottom) / x.y,
    left: (m.left - T.left + g.left) / x.x,
    right: (T.right - m.right + g.right) / x.x
  };
}
const Ea = (t) => ({
  name: "arrow",
  options: t,
  async fn(e) {
    const {
      x: i,
      y: o,
      placement: s,
      rects: n,
      platform: r,
      elements: a,
      middlewareData: l
    } = e, {
      element: c,
      padding: d = 0
    } = ut(t, e) || {};
    if (c == null)
      return {};
    const h = qo(d), p = {
      x: i,
      y: o
    }, f = Fo(s), g = Ho(f), w = await r.getDimensions(c), m = f === "y", b = m ? "top" : "left", y = m ? "bottom" : "right", x = m ? "clientHeight" : "clientWidth", T = n.reference[g] + n.reference[f] - p[f] - n.floating[g], k = p[f] - n.reference[f], D = await (r.getOffsetParent == null ? void 0 : r.getOffsetParent(c));
    let z = D ? D[x] : 0;
    (!z || !await (r.isElement == null ? void 0 : r.isElement(D))) && (z = a.floating[x] || n.floating[g]);
    const H = T / 2 - k / 2, Y = z / 2 - w[g] / 2 - 1, R = le(h[b], Y), te = le(h[y], Y), W = R, ie = z - w[g] - te, O = z / 2 - w[g] / 2 + H, me = bo(W, O, ie), oe = !l.arrow && jt(s) != null && O !== me && n.reference[g] / 2 - (O < W ? R : te) - w[g] / 2 < 0, se = oe ? O < W ? O - W : O - ie : 0;
    return {
      [f]: p[f] + se,
      data: {
        [f]: me,
        centerOffset: O - me - se,
        ...oe && {
          alignmentOffset: se
        }
      },
      reset: oe
    };
  }
}), Aa = function(t) {
  return t === void 0 && (t = {}), {
    name: "flip",
    options: t,
    async fn(e) {
      var i, o;
      const {
        placement: s,
        middlewareData: n,
        rects: r,
        initialPlacement: a,
        platform: l,
        elements: c
      } = e, {
        mainAxis: d = !0,
        crossAxis: h = !0,
        fallbackPlacements: p,
        fallbackStrategy: f = "bestFit",
        fallbackAxisSideDirection: g = "none",
        flipAlignment: w = !0,
        ...m
      } = ut(t, e);
      if ((i = n.arrow) != null && i.alignmentOffset)
        return {};
      const b = ye(s), y = Ue(a), x = ye(a) === a, T = await (l.isRTL == null ? void 0 : l.isRTL(c.floating)), k = p || (x || !w ? [Di(a)] : va(a)), D = g !== "none";
      !p && D && k.push(...ya(a, w, g, T));
      const z = [a, ...k], H = await Yo(e, m), Y = [];
      let R = ((o = n.flip) == null ? void 0 : o.overflows) || [];
      if (d && Y.push(H[b]), h) {
        const O = wa(s, r, T);
        Y.push(H[O[0]], H[O[1]]);
      }
      if (R = [...R, {
        placement: s,
        overflows: Y
      }], !Y.every((O) => O <= 0)) {
        var te, W;
        const O = (((te = n.flip) == null ? void 0 : te.index) || 0) + 1, me = z[O];
        if (me)
          return {
            data: {
              index: O,
              overflows: R
            },
            reset: {
              placement: me
            }
          };
        let oe = (W = R.filter((se) => se.overflows[0] <= 0).sort((se, we) => se.overflows[1] - we.overflows[1])[0]) == null ? void 0 : W.placement;
        if (!oe)
          switch (f) {
            case "bestFit": {
              var ie;
              const se = (ie = R.filter((we) => {
                if (D) {
                  const Re = Ue(we.placement);
                  return Re === y || // Create a bias to the `y` side axis due to horizontal
                  // reading directions favoring greater width.
                  Re === "y";
                }
                return !0;
              }).map((we) => [we.placement, we.overflows.filter((Re) => Re > 0).reduce((Re, Er) => Re + Er, 0)]).sort((we, Re) => we[1] - Re[1])[0]) == null ? void 0 : ie[0];
              se && (oe = se);
              break;
            }
            case "initialPlacement":
              oe = a;
              break;
          }
        if (s !== oe)
          return {
            reset: {
              placement: oe
            }
          };
      }
      return {};
    }
  };
};
function An(t) {
  const e = le(...t.map((n) => n.left)), i = le(...t.map((n) => n.top)), o = F(...t.map((n) => n.right)), s = F(...t.map((n) => n.bottom));
  return {
    x: e,
    y: i,
    width: o - e,
    height: s - i
  };
}
function ka(t) {
  const e = t.slice().sort((s, n) => s.y - n.y), i = [];
  let o = null;
  for (let s = 0; s < e.length; s++) {
    const n = e[s];
    !o || n.y - o.y > o.height / 2 ? i.push([n]) : i[i.length - 1].push(n), o = n;
  }
  return i.map((s) => Et(An(s)));
}
const Sa = function(t) {
  return t === void 0 && (t = {}), {
    name: "inline",
    options: t,
    async fn(e) {
      const {
        placement: i,
        elements: o,
        rects: s,
        platform: n,
        strategy: r
      } = e, {
        padding: a = 2,
        x: l,
        y: c
      } = ut(t, e), d = Array.from(await (n.getClientRects == null ? void 0 : n.getClientRects(o.reference)) || []), h = ka(d), p = Et(An(d)), f = qo(a);
      function g() {
        if (h.length === 2 && h[0].left > h[1].right && l != null && c != null)
          return h.find((m) => l > m.left - f.left && l < m.right + f.right && c > m.top - f.top && c < m.bottom + f.bottom) || p;
        if (h.length >= 2) {
          if (Ue(i) === "y") {
            const R = h[0], te = h[h.length - 1], W = ye(i) === "top", ie = R.top, O = te.bottom, me = W ? R.left : te.left, oe = W ? R.right : te.right, se = oe - me, we = O - ie;
            return {
              top: ie,
              bottom: O,
              left: me,
              right: oe,
              width: se,
              height: we,
              x: me,
              y: ie
            };
          }
          const m = ye(i) === "left", b = F(...h.map((R) => R.right)), y = le(...h.map((R) => R.left)), x = h.filter((R) => m ? R.left === y : R.right === b), T = x[0].top, k = x[x.length - 1].bottom, D = y, z = b, H = z - D, Y = k - T;
          return {
            top: T,
            bottom: k,
            left: D,
            right: z,
            width: H,
            height: Y,
            x: D,
            y: T
          };
        }
        return p;
      }
      const w = await n.getElementRects({
        reference: {
          getBoundingClientRect: g
        },
        floating: o.floating,
        strategy: r
      });
      return s.reference.x !== w.reference.x || s.reference.y !== w.reference.y || s.reference.width !== w.reference.width || s.reference.height !== w.reference.height ? {
        reset: {
          rects: w
        }
      } : {};
    }
  };
};
async function Ta(t, e) {
  const {
    placement: i,
    platform: o,
    elements: s
  } = t, n = await (o.isRTL == null ? void 0 : o.isRTL(s.floating)), r = ye(i), a = jt(i), l = Ue(i) === "y", c = ["left", "top"].includes(r) ? -1 : 1, d = n && l ? -1 : 1, h = ut(e, t);
  let {
    mainAxis: p,
    crossAxis: f,
    alignmentAxis: g
  } = typeof h == "number" ? {
    mainAxis: h,
    crossAxis: 0,
    alignmentAxis: null
  } : {
    mainAxis: h.mainAxis || 0,
    crossAxis: h.crossAxis || 0,
    alignmentAxis: h.alignmentAxis
  };
  return a && typeof g == "number" && (f = a === "end" ? g * -1 : g), l ? {
    x: f * d,
    y: p * c
  } : {
    x: p * c,
    y: f * d
  };
}
const _a = function(t) {
  return t === void 0 && (t = 0), {
    name: "offset",
    options: t,
    async fn(e) {
      var i, o;
      const {
        x: s,
        y: n,
        placement: r,
        middlewareData: a
      } = e, l = await Ta(e, t);
      return r === ((i = a.offset) == null ? void 0 : i.placement) && (o = a.arrow) != null && o.alignmentOffset ? {} : {
        x: s + l.x,
        y: n + l.y,
        data: {
          ...l,
          placement: r
        }
      };
    }
  };
}, Ca = function(t) {
  return t === void 0 && (t = {}), {
    name: "shift",
    options: t,
    async fn(e) {
      const {
        x: i,
        y: o,
        placement: s
      } = e, {
        mainAxis: n = !0,
        crossAxis: r = !1,
        limiter: a = {
          fn: (m) => {
            let {
              x: b,
              y
            } = m;
            return {
              x: b,
              y
            };
          }
        },
        ...l
      } = ut(t, e), c = {
        x: i,
        y: o
      }, d = await Yo(e, l), h = Ue(ye(s)), p = En(h);
      let f = c[p], g = c[h];
      if (n) {
        const m = p === "y" ? "top" : "left", b = p === "y" ? "bottom" : "right", y = f + d[m], x = f - d[b];
        f = bo(y, f, x);
      }
      if (r) {
        const m = h === "y" ? "top" : "left", b = h === "y" ? "bottom" : "right", y = g + d[m], x = g - d[b];
        g = bo(y, g, x);
      }
      const w = a.fn({
        ...e,
        [p]: f,
        [h]: g
      });
      return {
        ...w,
        data: {
          x: w.x - i,
          y: w.y - o,
          enabled: {
            [p]: n,
            [h]: r
          }
        }
      };
    }
  };
}, ja = function(t) {
  return t === void 0 && (t = {}), {
    name: "size",
    options: t,
    async fn(e) {
      var i, o;
      const {
        placement: s,
        rects: n,
        platform: r,
        elements: a
      } = e, {
        apply: l = () => {
        },
        ...c
      } = ut(t, e), d = await Yo(e, c), h = ye(s), p = jt(s), f = Ue(s) === "y", {
        width: g,
        height: w
      } = n.floating;
      let m, b;
      h === "top" || h === "bottom" ? (m = h, b = p === (await (r.isRTL == null ? void 0 : r.isRTL(a.floating)) ? "start" : "end") ? "left" : "right") : (b = h, m = p === "end" ? "top" : "bottom");
      const y = w - d.top - d.bottom, x = g - d.left - d.right, T = le(w - d[m], y), k = le(g - d[b], x), D = !e.middlewareData.shift;
      let z = T, H = k;
      if ((i = e.middlewareData.shift) != null && i.enabled.x && (H = x), (o = e.middlewareData.shift) != null && o.enabled.y && (z = y), D && !p) {
        const R = F(d.left, 0), te = F(d.right, 0), W = F(d.top, 0), ie = F(d.bottom, 0);
        f ? H = g - 2 * (R !== 0 || te !== 0 ? R + te : F(d.left, d.right)) : z = w - 2 * (W !== 0 || ie !== 0 ? W + ie : F(d.top, d.bottom));
      }
      await l({
        ...e,
        availableWidth: H,
        availableHeight: z
      });
      const Y = await r.getDimensions(a.floating);
      return g !== Y.width || w !== Y.height ? {
        reset: {
          rects: !0
        }
      } : {};
    }
  };
};
function Fi() {
  return typeof window < "u";
}
function zt(t) {
  return kn(t) ? (t.nodeName || "").toLowerCase() : "#document";
}
function G(t) {
  var e;
  return (t == null || (e = t.ownerDocument) == null ? void 0 : e.defaultView) || window;
}
function Ae(t) {
  var e;
  return (e = (kn(t) ? t.ownerDocument : t.document) || window.document) == null ? void 0 : e.documentElement;
}
function kn(t) {
  return Fi() ? t instanceof Node || t instanceof G(t).Node : !1;
}
function ce(t) {
  return Fi() ? t instanceof Element || t instanceof G(t).Element : !1;
}
function xe(t) {
  return Fi() ? t instanceof HTMLElement || t instanceof G(t).HTMLElement : !1;
}
function Ss(t) {
  return !Fi() || typeof ShadowRoot > "u" ? !1 : t instanceof ShadowRoot || t instanceof G(t).ShadowRoot;
}
function ci(t) {
  const {
    overflow: e,
    overflowX: i,
    overflowY: o,
    display: s
  } = de(t);
  return /auto|scroll|overlay|hidden|clip/.test(e + o + i) && !["inline", "contents"].includes(s);
}
function za(t) {
  return ["table", "td", "th"].includes(zt(t));
}
function qi(t) {
  return [":popover-open", ":modal"].some((e) => {
    try {
      return t.matches(e);
    } catch {
      return !1;
    }
  });
}
function Vo(t) {
  const e = Ko(), i = ce(t) ? de(t) : t;
  return i.transform !== "none" || i.perspective !== "none" || (i.containerType ? i.containerType !== "normal" : !1) || !e && (i.backdropFilter ? i.backdropFilter !== "none" : !1) || !e && (i.filter ? i.filter !== "none" : !1) || ["transform", "perspective", "filter"].some((o) => (i.willChange || "").includes(o)) || ["paint", "layout", "strict", "content"].some((o) => (i.contain || "").includes(o));
}
function Ra(t) {
  let e = Ne(t);
  for (; xe(e) && !At(e); ) {
    if (Vo(e))
      return e;
    if (qi(e))
      return null;
    e = Ne(e);
  }
  return null;
}
function Ko() {
  return typeof CSS > "u" || !CSS.supports ? !1 : CSS.supports("-webkit-backdrop-filter", "none");
}
function At(t) {
  return ["html", "body", "#document"].includes(zt(t));
}
function de(t) {
  return G(t).getComputedStyle(t);
}
function Yi(t) {
  return ce(t) ? {
    scrollLeft: t.scrollLeft,
    scrollTop: t.scrollTop
  } : {
    scrollLeft: t.scrollX,
    scrollTop: t.scrollY
  };
}
function Ne(t) {
  if (zt(t) === "html")
    return t;
  const e = (
    // Step into the shadow DOM of the parent of a slotted node.
    t.assignedSlot || // DOM Element detected.
    t.parentNode || // ShadowRoot detected.
    Ss(t) && t.host || // Fallback.
    Ae(t)
  );
  return Ss(e) ? e.host : e;
}
function Sn(t) {
  const e = Ne(t);
  return At(e) ? t.ownerDocument ? t.ownerDocument.body : t.body : xe(e) && ci(e) ? e : Sn(e);
}
function Qt(t, e, i) {
  var o;
  e === void 0 && (e = []), i === void 0 && (i = !0);
  const s = Sn(t), n = s === ((o = t.ownerDocument) == null ? void 0 : o.body), r = G(s);
  if (n) {
    const a = xo(r);
    return e.concat(r, r.visualViewport || [], ci(s) ? s : [], a && i ? Qt(a) : []);
  }
  return e.concat(s, Qt(s, [], i));
}
function xo(t) {
  return t.parent && Object.getPrototypeOf(t.parent) ? t.frameElement : null;
}
function Tn(t) {
  const e = de(t);
  let i = parseFloat(e.width) || 0, o = parseFloat(e.height) || 0;
  const s = xe(t), n = s ? t.offsetWidth : i, r = s ? t.offsetHeight : o, a = Ri(i) !== n || Ri(o) !== r;
  return a && (i = n, o = r), {
    width: i,
    height: o,
    $: a
  };
}
function Wo(t) {
  return ce(t) ? t : t.contextElement;
}
function yt(t) {
  const e = Wo(t);
  if (!xe(e))
    return be(1);
  const i = e.getBoundingClientRect(), {
    width: o,
    height: s,
    $: n
  } = Tn(e);
  let r = (n ? Ri(i.width) : i.width) / o, a = (n ? Ri(i.height) : i.height) / s;
  return (!r || !Number.isFinite(r)) && (r = 1), (!a || !Number.isFinite(a)) && (a = 1), {
    x: r,
    y: a
  };
}
const Da = /* @__PURE__ */ be(0);
function _n(t) {
  const e = G(t);
  return !Ko() || !e.visualViewport ? Da : {
    x: e.visualViewport.offsetLeft,
    y: e.visualViewport.offsetTop
  };
}
function La(t, e, i) {
  return e === void 0 && (e = !1), !i || e && i !== G(t) ? !1 : e;
}
function nt(t, e, i, o) {
  e === void 0 && (e = !1), i === void 0 && (i = !1);
  const s = t.getBoundingClientRect(), n = Wo(t);
  let r = be(1);
  e && (o ? ce(o) && (r = yt(o)) : r = yt(t));
  const a = La(n, i, o) ? _n(n) : be(0);
  let l = (s.left + a.x) / r.x, c = (s.top + a.y) / r.y, d = s.width / r.x, h = s.height / r.y;
  if (n) {
    const p = G(n), f = o && ce(o) ? G(o) : o;
    let g = p, w = xo(g);
    for (; w && o && f !== g; ) {
      const m = yt(w), b = w.getBoundingClientRect(), y = de(w), x = b.left + (w.clientLeft + parseFloat(y.paddingLeft)) * m.x, T = b.top + (w.clientTop + parseFloat(y.paddingTop)) * m.y;
      l *= m.x, c *= m.y, d *= m.x, h *= m.y, l += x, c += T, g = G(w), w = xo(g);
    }
  }
  return Et({
    width: d,
    height: h,
    x: l,
    y: c
  });
}
function Jo(t, e) {
  const i = Yi(t).scrollLeft;
  return e ? e.left + i : nt(Ae(t)).left + i;
}
function Cn(t, e, i) {
  i === void 0 && (i = !1);
  const o = t.getBoundingClientRect(), s = o.left + e.scrollLeft - (i ? 0 : (
    // RTL <body> scrollbar.
    Jo(t, o)
  )), n = o.top + e.scrollTop;
  return {
    x: s,
    y: n
  };
}
function Pa(t) {
  let {
    elements: e,
    rect: i,
    offsetParent: o,
    strategy: s
  } = t;
  const n = s === "fixed", r = Ae(o), a = e ? qi(e.floating) : !1;
  if (o === r || a && n)
    return i;
  let l = {
    scrollLeft: 0,
    scrollTop: 0
  }, c = be(1);
  const d = be(0), h = xe(o);
  if ((h || !h && !n) && ((zt(o) !== "body" || ci(r)) && (l = Yi(o)), xe(o))) {
    const f = nt(o);
    c = yt(o), d.x = f.x + o.clientLeft, d.y = f.y + o.clientTop;
  }
  const p = r && !h && !n ? Cn(r, l, !0) : be(0);
  return {
    width: i.width * c.x,
    height: i.height * c.y,
    x: i.x * c.x - l.scrollLeft * c.x + d.x + p.x,
    y: i.y * c.y - l.scrollTop * c.y + d.y + p.y
  };
}
function Ia(t) {
  return Array.from(t.getClientRects());
}
function Ma(t) {
  const e = Ae(t), i = Yi(t), o = t.ownerDocument.body, s = F(e.scrollWidth, e.clientWidth, o.scrollWidth, o.clientWidth), n = F(e.scrollHeight, e.clientHeight, o.scrollHeight, o.clientHeight);
  let r = -i.scrollLeft + Jo(t);
  const a = -i.scrollTop;
  return de(o).direction === "rtl" && (r += F(e.clientWidth, o.clientWidth) - s), {
    width: s,
    height: n,
    x: r,
    y: a
  };
}
function Oa(t, e) {
  const i = G(t), o = Ae(t), s = i.visualViewport;
  let n = o.clientWidth, r = o.clientHeight, a = 0, l = 0;
  if (s) {
    n = s.width, r = s.height;
    const c = Ko();
    (!c || c && e === "fixed") && (a = s.offsetLeft, l = s.offsetTop);
  }
  return {
    width: n,
    height: r,
    x: a,
    y: l
  };
}
function Ba(t, e) {
  const i = nt(t, !0, e === "fixed"), o = i.top + t.clientTop, s = i.left + t.clientLeft, n = xe(t) ? yt(t) : be(1), r = t.clientWidth * n.x, a = t.clientHeight * n.y, l = s * n.x, c = o * n.y;
  return {
    width: r,
    height: a,
    x: l,
    y: c
  };
}
function Ts(t, e, i) {
  let o;
  if (e === "viewport")
    o = Oa(t, i);
  else if (e === "document")
    o = Ma(Ae(t));
  else if (ce(e))
    o = Ba(e, i);
  else {
    const s = _n(t);
    o = {
      x: e.x - s.x,
      y: e.y - s.y,
      width: e.width,
      height: e.height
    };
  }
  return Et(o);
}
function jn(t, e) {
  const i = Ne(t);
  return i === e || !ce(i) || At(i) ? !1 : de(i).position === "fixed" || jn(i, e);
}
function Ua(t, e) {
  const i = e.get(t);
  if (i)
    return i;
  let o = Qt(t, [], !1).filter((a) => ce(a) && zt(a) !== "body"), s = null;
  const n = de(t).position === "fixed";
  let r = n ? Ne(t) : t;
  for (; ce(r) && !At(r); ) {
    const a = de(r), l = Vo(r);
    !l && a.position === "fixed" && (s = null), (n ? !l && !s : !l && a.position === "static" && s && ["absolute", "fixed"].includes(s.position) || ci(r) && !l && jn(t, r)) ? o = o.filter((c) => c !== r) : s = a, r = Ne(r);
  }
  return e.set(t, o), o;
}
function Na(t) {
  let {
    element: e,
    boundary: i,
    rootBoundary: o,
    strategy: s
  } = t;
  const n = [...i === "clippingAncestors" ? qi(e) ? [] : Ua(e, this._c) : [].concat(i), o], r = n[0], a = n.reduce((l, c) => {
    const d = Ts(e, c, s);
    return l.top = F(d.top, l.top), l.right = le(d.right, l.right), l.bottom = le(d.bottom, l.bottom), l.left = F(d.left, l.left), l;
  }, Ts(e, r, s));
  return {
    width: a.right - a.left,
    height: a.bottom - a.top,
    x: a.left,
    y: a.top
  };
}
function Ha(t) {
  const {
    width: e,
    height: i
  } = Tn(t);
  return {
    width: e,
    height: i
  };
}
function Fa(t, e, i) {
  const o = xe(e), s = Ae(e), n = i === "fixed", r = nt(t, !0, n, e);
  let a = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const l = be(0);
  if (o || !o && !n)
    if ((zt(e) !== "body" || ci(s)) && (a = Yi(e)), o) {
      const p = nt(e, !0, n, e);
      l.x = p.x + e.clientLeft, l.y = p.y + e.clientTop;
    } else s && (l.x = Jo(s));
  const c = s && !o && !n ? Cn(s, a) : be(0), d = r.left + a.scrollLeft - l.x - c.x, h = r.top + a.scrollTop - l.y - c.y;
  return {
    x: d,
    y: h,
    width: r.width,
    height: r.height
  };
}
function ro(t) {
  return de(t).position === "static";
}
function _s(t, e) {
  if (!xe(t) || de(t).position === "fixed")
    return null;
  if (e)
    return e(t);
  let i = t.offsetParent;
  return Ae(t) === i && (i = i.ownerDocument.body), i;
}
function zn(t, e) {
  const i = G(t);
  if (qi(t))
    return i;
  if (!xe(t)) {
    let s = Ne(t);
    for (; s && !At(s); ) {
      if (ce(s) && !ro(s))
        return s;
      s = Ne(s);
    }
    return i;
  }
  let o = _s(t, e);
  for (; o && za(o) && ro(o); )
    o = _s(o, e);
  return o && At(o) && ro(o) && !Vo(o) ? i : o || Ra(t) || i;
}
const qa = async function(t) {
  const e = this.getOffsetParent || zn, i = this.getDimensions, o = await i(t.floating);
  return {
    reference: Fa(t.reference, await e(t.floating), t.strategy),
    floating: {
      x: 0,
      y: 0,
      width: o.width,
      height: o.height
    }
  };
};
function Ya(t) {
  return de(t).direction === "rtl";
}
const Va = {
  convertOffsetParentRelativeRectToViewportRelativeRect: Pa,
  getDocumentElement: Ae,
  getClippingRect: Na,
  getOffsetParent: zn,
  getElementRects: qa,
  getClientRects: Ia,
  getDimensions: Ha,
  getScale: yt,
  isElement: ce,
  isRTL: Ya
};
function Ka(t, e) {
  let i = null, o;
  const s = Ae(t);
  function n() {
    var a;
    clearTimeout(o), (a = i) == null || a.disconnect(), i = null;
  }
  function r(a, l) {
    a === void 0 && (a = !1), l === void 0 && (l = 1), n();
    const {
      left: c,
      top: d,
      width: h,
      height: p
    } = t.getBoundingClientRect();
    if (a || e(), !h || !p)
      return;
    const f = wi(d), g = wi(s.clientWidth - (c + h)), w = wi(s.clientHeight - (d + p)), m = wi(c), b = {
      rootMargin: -f + "px " + -g + "px " + -w + "px " + -m + "px",
      threshold: F(0, le(1, l)) || 1
    };
    let y = !0;
    function x(T) {
      const k = T[0].intersectionRatio;
      if (k !== l) {
        if (!y)
          return r();
        k ? r(!1, k) : o = setTimeout(() => {
          r(!1, 1e-7);
        }, 1e3);
      }
      y = !1;
    }
    try {
      i = new IntersectionObserver(x, {
        ...b,
        // Handle <iframe>s
        root: s.ownerDocument
      });
    } catch {
      i = new IntersectionObserver(x, b);
    }
    i.observe(t);
  }
  return r(!0), n;
}
function Wa(t, e, i, o) {
  o === void 0 && (o = {});
  const {
    ancestorScroll: s = !0,
    ancestorResize: n = !0,
    elementResize: r = typeof ResizeObserver == "function",
    layoutShift: a = typeof IntersectionObserver == "function",
    animationFrame: l = !1
  } = o, c = Wo(t), d = s || n ? [...c ? Qt(c) : [], ...Qt(e)] : [];
  d.forEach((b) => {
    s && b.addEventListener("scroll", i, {
      passive: !0
    }), n && b.addEventListener("resize", i);
  });
  const h = c && a ? Ka(c, i) : null;
  let p = -1, f = null;
  r && (f = new ResizeObserver((b) => {
    let [y] = b;
    y && y.target === c && f && (f.unobserve(e), cancelAnimationFrame(p), p = requestAnimationFrame(() => {
      var x;
      (x = f) == null || x.observe(e);
    })), i();
  }), c && !l && f.observe(c), f.observe(e));
  let g, w = l ? nt(t) : null;
  l && m();
  function m() {
    const b = nt(t);
    w && (b.x !== w.x || b.y !== w.y || b.width !== w.width || b.height !== w.height) && i(), w = b, g = requestAnimationFrame(m);
  }
  return i(), () => {
    var b;
    d.forEach((y) => {
      s && y.removeEventListener("scroll", i), n && y.removeEventListener("resize", i);
    }), h == null || h(), (b = f) == null || b.disconnect(), f = null, l && cancelAnimationFrame(g);
  };
}
const Ja = _a, Ga = Ca, Xa = Aa, Qa = ja, Za = Ea, el = Sa, tl = (t, e, i) => {
  const o = /* @__PURE__ */ new Map(), s = {
    platform: Va,
    ...i
  }, n = {
    ...s.platform,
    _c: o
  };
  return $a(t, e, {
    ...s,
    platform: n
  });
};
function $o(t) {
  return typeof t == "string" ? document.getElementById(t) : t instanceof Element ? t : null;
}
const il = Ye`
  :host {
    display: contents; /* 确保宿主元素不影响子元素的布局 */
  }

  .popover {
    --arrow-display: var(--jds-popover-arrow-display, none); /* 箭头显示状态 */
    --background-color: var(
      --jds-popover-background-color,
      var(--jds-color-neutral-full)
    ); /* 背景颜色 */
    --border-color: var(
      --jds-popover-border-color,
      var(--jds-color-primary-highlight)
    ); /* 边框颜色 */
    --border-size: var(--jds-popover-border-size, 1px); /* 边框大小 */
    --padding: var(--jds-popover-padding, var(--jds-size-m)); /* 内边距 */
    --border-radius: var(
      --jds-popover-border-radius,
      var(--jds-size-3xs)
    ); /* 边角圆度 */
    --position: var(--jds-popover-position, fixed); /* 弹出框的位置属性 */
    --max-width: var(--jds-popover-max-width, 100vw); /* 最大宽度 */
    --max-viewport: calc(
      100vw - var(--jds-size-s)
    ); /* 视口最大宽度，考虑边距 */

    position: var(--position);
    box-sizing: border-box;
    border: var(--border-size) solid var(--border-color);
    padding: var(--padding);
    border-radius: var(--border-radius);
    z-index: var(--jds-index-popover); /* z-index 用于层叠顺序 */
    max-width: min(var(--max-viewport), var(--max-width)); /* 最大宽度限制 */
    width: max-content; /* 宽度根据内容自适应 */
    hyphens: auto; /* 自动断字 */
    background-color: var(--background-color);
    font: var(--jds-font-title-3-regular);
    color: var(--jds-color-neutral-darker);
  }

  .popover:not(.visible) {
    visibility: hidden; /* 不可见时隐藏 */
  }

  .arrow {
    --arrow-rotation: 45deg; /* 箭头的初始旋转角度 */
    --size: var(--jds-size-2xs); /* 箭头的大小 */
    --arrow-position: calc(
      (var(--size) / -2.5) - var(--border-size)
    ); /* 箭头的位置偏移量 */

    box-sizing: border-box;
    display: var(--arrow-display);
    position: absolute;
    background-color: var(--background-color);
    width: var(--size);
    height: var(--size);
    transform: rotate(var(--arrow-rotation));
    border: var(--border-size) solid var(--border-color);
    border-bottom: none; /* 底部边框隐藏 */
    border-inline-end: none; /* 右侧边框隐藏 */
  }

  /* 根据位置定义箭头的样式 */
  .popover[data-placement*="bottom"] .arrow {
    top: var(--arrow-position); /* 底部箭头位置 */
  }

  .popover[data-placement*="top"] .arrow {
    --arrow-rotation: 225deg; /* 顶部箭头旋转角度 */
    bottom: var(--arrow-position); /* 顶部箭头位置 */
  }

  .popover[data-placement*="left"] .arrow {
    --arrow-rotation: 135deg; /* 左侧箭头旋转角度 */
    right: var(--arrow-position); /* 左侧箭头位置 */
  }

  .popover[data-placement*="right"] .arrow {
    --arrow-rotation: 315deg; /* 右侧箭头旋转角度 */
    left: var(--arrow-position); /* 右侧箭头位置 */
  }
`;
var ol = Object.defineProperty, sl = Object.getOwnPropertyDescriptor, ue = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? sl(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && ol(e, i, s), s;
};
let Q = class extends J {
  constructor() {
    super(...arguments), this.placement = "bottom", this.fitSize = !1, this.offset = 8, this._visible = !1, this._handleClickOutside = (e) => {
      const i = e.composedPath();
      !i.includes(this._target) && !i.includes(this) && this.hide();
    };
  }
  connectedCallback() {
    super.connectedCallback(), this._handlePopoverShowEvent = this._handlePopoverShowEvent.bind(this), this._handleKeydownEvent = this._handleKeydownEvent.bind(this), this._handleClickOutside = this._handleClickOutside.bind(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.popoverAutoUpdateCleanup && this.popoverAutoUpdateCleanup();
  }
  getMiddleware() {
    const e = [];
    return e.push(
      Ja(this.offset),
      el(),
      Xa(),
      Ga({ padding: 4 })
    ), this.fitSize && e.push(
      Qa({
        apply(i) {
          i.elements.floating && i.elements.reference && Object.assign(i.elements.floating.style, {
            "min-width": `${i.elements.reference.getBoundingClientRect().width}px`
          });
        }
      })
    ), e.push(Za({ element: this.arrow, padding: 5 })), e;
  }
  setPopover() {
    this.target && (this.popoverAutoUpdateCleanup = Wa(
      this.target,
      this._popover,
      () => {
        tl(this.target, this._popover, {
          placement: this.placement,
          strategy: "fixed",
          middleware: this.getMiddleware()
        }).then(({ x: e, y: i, placement: o, middlewareData: s }) => {
          if (Object.assign(this._popover.style, {
            left: `${e}px`,
            top: `${i}px`
          }), this._popover.dataset.placement = o, s.arrow) {
            const { x: n, y: r } = s.arrow;
            Object.assign(this.arrow.style, {
              left: n != null ? `${n}px` : "",
              top: r != null ? `${r}px` : ""
            });
          }
        });
      }
    ));
  }
  get target() {
    return this._target;
  }
  set target(e) {
    const i = $o(e);
    if (!i) {
      console.warn(
        "JdsPopover 的目标仅接受 Element 实例或 DOM 元素的字符串 ID。"
      );
      return;
    }
    this._target = i;
  }
  /**
   * 显示弹出框
   */
  show() {
    this._visible = !0, this.setPopover(), this.onJdsPopoverShow(""), document.addEventListener("click", this._handleClickOutside), document.addEventListener("keydown", this._handleKeydownEvent), document.addEventListener("jds-popover-show", this._handlePopoverShowEvent);
  }
  /**
   * 隐藏弹出框
   */
  hide() {
    this._visible = !1, document.removeEventListener("click", this._handleClickOutside), document.removeEventListener("keydown", this._handleKeydownEvent), document.removeEventListener(
      "jds-popover-show",
      this._handlePopoverShowEvent
    ), this.onJdsPopoverHide("");
  }
  /**
   * 获取弹出框的可见性状态
   */
  get visible() {
    return this._visible;
  }
  _handlePopoverShowEvent(e) {
    if (e.target !== this) {
      const { parentElement: i } = e.target;
      !this.contains(i) && e.target.tagName === this.tagName && this.hide();
    }
  }
  _handleKeydownEvent(e) {
    e.key === "Escape" && this.visible && (e.preventDefault(), this.hide());
  }
  render() {
    const e = No({
      popover: !0,
      visible: this._visible
    });
    return _`<div part="popover" class=${e}>
      <slot id="popover" aria-live=${this._visible ? "polite" : "off"}></slot>
      <div class="arrow" aria-hidden="true"></div>
    </div>`;
  }
};
Q.styles = [il];
ue([
  _e(".popover")
], Q.prototype, "_popover", 2);
ue([
  _e(".arrow")
], Q.prototype, "arrow", 2);
ue([
  E({ type: String })
], Q.prototype, "placement", 2);
ue([
  Uo()
], Q.prototype, "_target", 2);
ue([
  E({ type: Boolean, attribute: "fit-size" })
], Q.prototype, "fitSize", 2);
ue([
  E({ type: Number })
], Q.prototype, "offset", 2);
ue([
  Uo()
], Q.prototype, "_visible", 2);
ue([
  Ce("jds-popover-show")
], Q.prototype, "onJdsPopoverShow", 2);
ue([
  Ce("jds-popover-hide")
], Q.prototype, "onJdsPopoverHide", 2);
ue([
  E()
], Q.prototype, "target", 1);
Q = ue([
  Te("jds-popover")
], Q);
var nl = Object.defineProperty, rl = Object.getOwnPropertyDescriptor, Ve = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? rl(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && nl(e, i, s), s;
};
let $e = class extends J {
  constructor() {
    super(...arguments), this.placement = "top";
  }
  update(e) {
    if (e.has("target")) {
      const i = e.get("target");
      i && this._removeEvents(i), this._addEvents();
    }
    super.update(e);
  }
  _addEvents() {
    const e = $o(this.target);
    e && (e.addEventListener("focus", this.show, { capture: !0 }), e.addEventListener("mouseenter", this.show), e.addEventListener("blur", this.hide, { capture: !0 }), e.addEventListener("mouseleave", this.hide));
  }
  _removeEvents(e) {
    const i = $o(e);
    i && (i.removeEventListener("focus", this.show, { capture: !0 }), i.removeEventListener("mouseenter", this.show), i.removeEventListener("blur", this.hide, { capture: !0 }), i.removeEventListener("mouseleave", this.hide));
  }
  connectedCallback() {
    super.connectedCallback(), this._timer = null, this.show = this.show.bind(this), this.hide = this.hide.bind(this), this._addEvents();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this._removeEvents(this.target);
  }
  /**
   * 显示工具提示
   */
  show() {
    this._popover.target = this.target ?? this.trigger, this._popover.show(), this.onShow(""), this._timer && clearTimeout(this._timer);
  }
  /**
   * 隐藏工具提示
   */
  hide() {
    this._timer = setTimeout(() => {
      this._popover.hide(), this.onHide("");
    }, 150);
  }
  /**
   * 获取工具提示的可见性状态
   */
  get visible() {
    return this._popover.visible;
  }
  triggerTemplate() {
    return _`<slot
      class="trigger"
      name="tooltip-trigger"
      aria-describedby="tooltip"
      @focus=${{ handleEvent: () => this.show(), capture: !0 }}
      @blur=${{ handleEvent: () => this.hide(), capture: !0 }}
      @mouseenter=${() => this.show()}
      @mouseleave=${() => this.hide()}
    >
    </slot>`;
  }
  render() {
    return _`
      ${this.target ? "" : this.triggerTemplate()}
      <jds-popover
        part="tooltip"
        .target="${this.target ?? this.trigger}"
        placement="${Le(this.placement)}"
        @jds-popover-hide="${() => this.onHide("")}"
      >
        <slot
          class="content"
          id="tooltip"
          role="tooltip"
          @mouseenter=${() => this.show()}
          @mouseleave=${() => this.hide()}
        >
        </slot>
      </jds-popover>
    `;
  }
};
$e.styles = Ye`
    :host {
      display: inline-block;
    }

    .trigger {
      display: var(--jds-tooltip-trigger-display, inline-flex);
      cursor: pointer;
    }

    jds-popover {
      --jds-popover-background-color: var(--jds-color-info);
      --jds-popover-arrow-display: block;
      --jds-popover-border-size: 0px;
      --jds-popover-max-width: 424px;
    }

    .content {
      color: var(--jds-color-neutral-full);
    }
  `;
Ve([
  _e(".trigger")
], $e.prototype, "trigger", 2);
Ve([
  _e("jds-popover")
], $e.prototype, "_popover", 2);
Ve([
  E({ type: String })
], $e.prototype, "placement", 2);
Ve([
  Ce("jds-tooltip-show")
], $e.prototype, "onShow", 2);
Ve([
  Ce("jds-tooltip-hide")
], $e.prototype, "onHide", 2);
Ve([
  E()
], $e.prototype, "target", 2);
Ve([
  E()
], $e.prototype, "_timer", 2);
$e = Ve([
  Te("jds-tooltip")
], $e);
const al = {
  back: "Back",
  cancel: "Cancel",
  changeLanguage: "Change Language",
  close: "Close",
  confirm: "Confirm",
  create: "Create",
  delete: "Delete",
  edit: "Edit",
  hello: "Hello",
  loading: "Loading...",
  more: "More",
  next: "Next",
  no: "No",
  ok: "OK",
  open: "Open",
  previous: "Previous",
  save: "Save",
  search: "Search",
  submit: "Submit",
  update: "Update",
  welcome: "Welcome {name}",
  yes: "Yes"
}, ll = {
  button: {
    hideAll: "Hide All",
    loadMore: "Load More",
    showAll: "Show All"
  },
  modal: {
    confirmContent: "Are you sure you want to perform this action?",
    confirmTitle: "Please Confirm"
  },
  table: {
    noData: "No Data Available",
    selected: "{selected} Items Selected",
    total: "Total {total} Items"
  },
  upload: {
    dragHere: "Drag files here or click to upload",
    fileTooBig: "File size cannot exceed {size}MB"
  }
}, cl = {
  label: {
    address: "Address",
    email: "Email",
    password: "Password",
    phone: "Phone Number",
    username: "Username"
  },
  placeholder: {
    email: "Enter Your Email Address",
    enterKeyword: "Enter keyword",
    password: "Enter Your Password",
    phone: "Enter Your Mobile Phone Number",
    search: "Enter Keywords to Search",
    username: "Enter Your Username"
  },
  validation: {
    email: "Please enter a valid email address",
    enterKeyword: "No search data",
    maxLength: "Please enter no more than {max} characters",
    minLength: "Please enter at least {min} characters",
    number: "Please enter a valid number",
    password: "Password must contain at least 8 characters",
    passwordConfirm: "The two passwords do not match",
    phone: "Please enter a valid phone number",
    required: "This field is required",
    termsOfService: "Please agree to the Terms of Service and Privacy policy.",
    url: "Please enter a valid URL"
  }
}, dl = {
  date: {
    long: "MMMM DD, YYYY",
    short: "MM/DD/YYYY",
    withTime: "MM/DD/YYYY HH:mm:ss"
  },
  number: {
    currency: "$",
    decimal: ".",
    thousand: ","
  },
  time: {
    hours: "{hours} hours",
    minutes: "{minutes} minutes",
    seconds: "{seconds} seconds"
  }
}, hl = {
  error: {
    403: "Access Denied",
    404: "Page Not Found",
    500: "Internal Server Error",
    default: "Oops, something went wrong, Please try again later."
  },
  login: {
    forgotPassword: "Forgot Password?",
    noAccount: "Don't have an account?",
    rememberMe: "Remember Me",
    signUp: "Sign Up",
    title: "Login"
  }
}, ul = {
  common: al,
  components: ll,
  form: cl,
  format: dl,
  pages: hl
}, pl = {
  back: "戻る",
  cancel: "キャンセル",
  changeLanguage: "言語を変更",
  close: "閉じる",
  confirm: "確認",
  create: "作成",
  delete: "削除",
  edit: "編集",
  hello: "こんにちは",
  loading: "読み込み中...",
  more: "もっと見る",
  next: "次へ",
  no: "いいえ",
  ok: "よし",
  open: "開く",
  previous: "前へ",
  save: "保存",
  search: "検索",
  submit: "送信",
  update: "更新",
  welcome: "ようこそ {name}",
  yes: "はい"
}, gl = {
  button: {
    hideAll: "すべて非表示",
    loadMore: "もっと読み込む",
    showAll: "すべて表示"
  },
  modal: {
    confirmContent: "この操作を実行してもよろしいですか？",
    confirmTitle: "確認してください"
  },
  table: {
    noData: "データがありません",
    selected: "{selected} 件選択済み",
    total: "合計 {total} 件"
  },
  upload: {
    dragHere: "ファイルをここにドラッグするか、クリックしてアップロード",
    fileTooBig: "ファイルサイズは {size}MB を超えることはできません"
  }
}, fl = {
  label: {
    address: "住所",
    email: "メールアドレス",
    password: "パスワード",
    phone: "電話番号",
    username: "ユーザー名"
  },
  placeholder: {
    email: "メールアドレスを入力してください",
    enterKeyword: "キーワードを入力",
    password: "パスワードを入力してください",
    phone: "電話番号を入力してください",
    search: "キーワードを入力して検索してください",
    username: "ユーザー名を入力してください"
  },
  validation: {
    email: "有効なメールアドレスを入力してください",
    enterKeyword: "データがありません",
    maxLength: "最大{max}文字まで入力できます",
    minLength: "最低{min}文字を入力してください",
    number: "有効な数値を入力してください",
    password: "パスワードは8文字以上である必要があります",
    passwordConfirm: "パスワードが一致しません",
    phone: "有効な電話番号を入力してください",
    required: "この項目は必須です",
    termsOfService: "利用規約とプライバシーポリシーに同意してください",
    url: "有効なURLを入力してください"
  }
}, ml = {
  date: {
    long: "YYYY年M月D日",
    short: "YYYY/MM/DD",
    withTime: "YYYY/MM/DD HH:mm:ss"
  },
  number: {
    currency: "¥",
    decimal: ".",
    thousand: ","
  },
  time: {
    hours: "{hours}時間",
    minutes: "{minutes}分",
    seconds: "{seconds}秒"
  }
}, wl = {
  error: {
    403: "アクセスが拒否されました",
    404: "ページが見つかりません",
    500: "サーバーエラー",
    default: "エラーが発生しました。後ほどもう一度お試しください。"
  },
  login: {
    forgotPassword: "パスワードをお忘れですか？",
    noAccount: "アカウントをお持ちでない方",
    rememberMe: "ログイン情報を保存",
    signUp: "新規登録",
    title: "ログイン"
  }
}, vl = {
  common: pl,
  components: gl,
  form: fl,
  format: ml,
  pages: wl
}, bl = {
  back: "Zurück",
  cancel: "Abbrechen",
  changeLanguage: "Sprache ändern",
  close: "Schließen",
  confirm: "Bestätigen",
  create: "Erstellen",
  delete: "Löschen",
  edit: "Bearbeiten",
  hello: "Hallo",
  loading: "Laden...",
  more: "Mehr",
  next: "Weiter",
  no: "Nein",
  ok: "OK",
  open: "Öffnen",
  previous: "Zurück",
  save: "Speichern",
  search: "Suchen",
  submit: "Absenden",
  update: "Aktualisieren",
  welcome: "Willkommen {name}",
  yes: "Ja"
}, yl = {
  button: {
    hideAll: "Alle ausblenden",
    loadMore: "Mehr laden",
    showAll: "Alle anzeigen"
  },
  modal: {
    confirmContent: "Sind Sie sicher, dass Sie diese Aktion ausführen möchten?",
    confirmTitle: "Bitte bestätigen"
  },
  table: {
    noData: "Keine Daten verfügbar",
    selected: "{selected} Einträge ausgewählt",
    total: "Insgesamt {total} Einträge"
  },
  upload: {
    dragHere: "Dateien hierher ziehen oder klicken zum Hochladen",
    fileTooBig: "Dateigröße darf {size}MB nicht überschreiten"
  }
}, xl = {
  label: {
    address: "Adresse",
    email: "E-Mail",
    password: "Passwort",
    phone: "Telefonnummer",
    username: "Benutzername"
  },
  placeholder: {
    email: "Geben Sie Ihre E-Mail-Adresse ein",
    enterKeyword: "Suchbegriff eingeben",
    password: "Geben Sie Ihr Passwort ein",
    phone: "Geben Sie Ihre Mobiltelefonnummer ein",
    search: "Suchbegriffe eingeben",
    username: "Geben Sie Ihren Benutzernamen ein"
  },
  validation: {
    email: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    enterKeyword: "Keine Suchdaten",
    maxLength: "Bitte geben Sie nicht mehr als {max} Zeichen ein",
    minLength: "Bitte geben Sie mindestens {min} Zeichen ein",
    number: "Bitte geben Sie eine gültige Zahl ein",
    password: "Passwort muss mindestens 8 Zeichen enthalten",
    passwordConfirm: "Die Passwörter stimmen nicht überein",
    phone: "Bitte geben Sie eine gültige Telefonnummer ein",
    required: "Dieses Feld ist erforderlich",
    termsOfService: "Bitte stimmen Sie den Nutzungsbedingungen und der Datenschutzerklärung zu",
    url: "Bitte geben Sie eine gültige URL ein"
  }
}, $l = {
  date: {
    long: "D. MMMM YYYY",
    short: "DD.MM.YYYY",
    withTime: "DD.MM.YYYY HH:mm:ss"
  },
  number: {
    currency: "€",
    decimal: ",",
    thousand: "."
  },
  time: {
    hours: "{hours} Stunden",
    minutes: "{minutes} Minuten",
    seconds: "{seconds} Sekunden"
  }
}, El = {
  error: {
    403: "Zugriff verweigert",
    404: "Seite nicht gefunden",
    500: "Interner Serverfehler",
    default: "Ups, etwas ist schiefgelaufen. Bitte versuchen Sie es später erneut."
  },
  login: {
    forgotPassword: "Passwort vergessen?",
    noAccount: "Noch kein Konto?",
    rememberMe: "Angemeldet bleiben",
    signUp: "Registrieren",
    title: "Anmelden"
  }
}, Al = {
  common: bl,
  components: yl,
  form: xl,
  format: $l,
  pages: El
}, kl = {
  back: "Indietro",
  cancel: "Annulla",
  changeLanguage: "Cambia lingua",
  close: "Chiudi",
  confirm: "Conferma",
  create: "Crea",
  delete: "Elimina",
  edit: "Modifica",
  hello: "Ciao",
  loading: "Caricamento...",
  more: "Altro",
  next: "Avanti",
  no: "No",
  ok: "OK",
  open: "Apri",
  previous: "Precedente",
  save: "Salva",
  search: "Cerca",
  submit: "Invia",
  update: "Aggiorna",
  welcome: "Benvenuto {name}",
  yes: "Sì"
}, Sl = {
  button: {
    hideAll: "Nascondi tutto",
    loadMore: "Carica altro",
    showAll: "Mostra tutto"
  },
  modal: {
    confirmContent: "Sei sicuro di voler eseguire questa azione?",
    confirmTitle: "Conferma"
  },
  table: {
    noData: "Nessun dato disponibile",
    selected: "{selected} elementi selezionati",
    total: "Totale {total} elementi"
  },
  upload: {
    dragHere: "Trascina i file qui o clicca per caricare",
    fileTooBig: "La dimensione del file non può superare {size}MB"
  }
}, Tl = {
  label: {
    address: "Indirizzo",
    email: "Email",
    password: "Password",
    phone: "Numero di telefono",
    username: "Nome utente"
  },
  placeholder: {
    email: "Inserisci il tuo indirizzo email",
    enterKeyword: "Inserisci parola chiave",
    password: "Inserisci la tua password",
    phone: "Inserisci il tuo numero di telefono cellulare",
    search: "Inserisci le parole chiave per la ricerca",
    username: "Inserisci il tuo nome utente"
  },
  validation: {
    email: "Inserisci un indirizzo email valido",
    enterKeyword: "Nessun dato di ricerca",
    maxLength: "Non inserire più di {max} caratteri",
    minLength: "Inserisci almeno {min} caratteri",
    number: "Inserisci un numero valido",
    password: "La password deve contenere almeno 8 caratteri",
    passwordConfirm: "Le password non coincidono",
    phone: "Inserisci un numero di telefono valido",
    required: "Questo campo è obbligatorio",
    termsOfService: "Accetta i Termini di servizio e l'Informativa sulla privacy",
    url: "Inserisci un URL valido"
  }
}, _l = {
  date: {
    long: "D MMMM YYYY",
    short: "DD/MM/YYYY",
    withTime: "DD/MM/YYYY HH:mm:ss"
  },
  number: {
    currency: "€",
    decimal: ",",
    thousand: "."
  },
  time: {
    hours: "{hours} ore",
    minutes: "{minutes} minuti",
    seconds: "{seconds} secondi"
  }
}, Cl = {
  error: {
    403: "Accesso negato",
    404: "Pagina non trovata",
    500: "Errore interno del server",
    default: "Ops, qualcosa è andato storto. Per favore riprova più tardi."
  },
  login: {
    forgotPassword: "Password dimenticata?",
    noAccount: "Non hai un account?",
    rememberMe: "Ricordami",
    signUp: "Registrati",
    title: "Accedi"
  }
}, jl = {
  common: kl,
  components: Sl,
  form: Tl,
  format: _l,
  pages: Cl
}, zl = {
  back: "Retour",
  cancel: "Annuler",
  changeLanguage: "Changer de langue",
  close: "Fermer",
  confirm: "Confirmer",
  create: "Créer",
  delete: "Supprimer",
  edit: "Modifier",
  hello: "Bonjour",
  loading: "Chargement...",
  more: "Plus",
  next: "Suivant",
  no: "Non",
  ok: "OK",
  open: "Ouvrir",
  previous: "Précédent",
  save: "Sauvegarder",
  search: "Rechercher",
  submit: "Envoyer",
  update: "Mettre à jour",
  welcome: "Bienvenue {name}",
  yes: "Oui"
}, Rl = {
  button: {
    hideAll: "Masquer tout",
    loadMore: "Charger plus",
    showAll: "Afficher tout"
  },
  modal: {
    confirmContent: "Êtes-vous sûr de vouloir effectuer cette action ?",
    confirmTitle: "Veuillez confirmer"
  },
  table: {
    noData: "Aucune donnée disponible",
    selected: "{selected} éléments sélectionnés",
    total: "Total {total} éléments"
  },
  upload: {
    dragHere: "Déposez les fichiers ici ou cliquez pour télécharger",
    fileTooBig: "La taille du fichier ne peut pas dépasser {size}MB"
  }
}, Dl = {
  label: {
    address: "Adresse",
    email: "E-mail",
    password: "Mot de passe",
    phone: "Numéro de téléphone",
    username: "Nom d'utilisateur"
  },
  placeholder: {
    email: "Entrez votre adresse e-mail",
    enterKeyword: "Entrez un mot-clé",
    password: "Entrez votre mot de passe",
    phone: "Entrez votre numéro de téléphone mobile",
    search: "Entrez les mots-clés pour la recherche",
    username: "Entrez votre nom d'utilisateur"
  },
  validation: {
    email: "Veuillez entrer une adresse e-mail valide",
    enterKeyword: "Aucune donnée de recherche",
    maxLength: "Veuillez ne pas entrer plus de {max} caractères",
    minLength: "Veuillez entrer au moins {min} caractères",
    number: "Veuillez entrer un nombre valide",
    password: "Le mot de passe doit contenir au moins 8 caractères",
    passwordConfirm: "Les mots de passe ne correspondent pas",
    phone: "Veuillez entrer un numéro de téléphone valide",
    required: "Ce champ est obligatoire",
    termsOfService: "Veuillez accepter les Conditions d'utilisation et la Politique de confidentialité",
    url: "Veuillez entrer une URL valide"
  }
}, Ll = {
  date: {
    long: "D MMMM YYYY",
    short: "DD/MM/YYYY",
    withTime: "DD/MM/YYYY HH:mm:ss"
  },
  number: {
    currency: "€",
    decimal: ","
  },
  time: {
    hours: "{hours} heures",
    minutes: "{minutes} minutes",
    seconds: "{seconds} secondes"
  }
}, Pl = {
  error: {
    403: "Accès refusé",
    404: "Page non trouvée",
    500: "Erreur interne du serveur",
    default: "Oups, quelque chose s'est mal passé. Veuillez réessayer plus tard."
  },
  login: {
    forgotPassword: "Mot de passe oublié ?",
    noAccount: "Vous n'avez pas de compte ?",
    rememberMe: "Se souvenir de moi",
    signUp: "S'inscrire",
    title: "Connexion"
  }
}, Il = {
  common: zl,
  components: Rl,
  form: Dl,
  format: Ll,
  pages: Pl
}, Ml = {
  back: "Volver",
  cancel: "Cancelar",
  changeLanguage: "Cambiar idioma",
  close: "Cerrar",
  confirm: "Confirmar",
  create: "Crear",
  delete: "Eliminar",
  edit: "Editar",
  hello: "Hola",
  loading: "Cargando...",
  more: "Más",
  next: "Siguiente",
  no: "No",
  ok: "OK",
  open: "Abrir",
  previous: "Anterior",
  save: "Guardar",
  search: "Buscar",
  submit: "Enviar",
  update: "Actualizar",
  welcome: "Bienvenido {name}",
  yes: "Sí"
}, Ol = {
  button: {
    hideAll: "Ocultar todo",
    loadMore: "Cargar más",
    showAll: "Mostrar todo"
  },
  modal: {
    confirmContent: "¿Está seguro de que desea realizar esta acción?",
    confirmTitle: "Por favor confirme"
  },
  table: {
    noData: "No hay datos disponibles",
    selected: "{selected} elementos seleccionados",
    total: "Total {total} elementos"
  },
  upload: {
    dragHere: "Arrastre los archivos aquí o haga clic para cargar",
    fileTooBig: "El tamaño del archivo no puede exceder {size}MB"
  }
}, Bl = {
  label: {
    address: "Dirección",
    email: "Correo electrónico",
    password: "Contraseña",
    phone: "Número de teléfono",
    username: "Nombre de usuario"
  },
  placeholder: {
    email: "Introduce tu dirección de correo electrónico",
    enterKeyword: "Introduce palabra clave",
    password: "Introduce tu contraseña",
    phone: "Introduce tu número de teléfono móvil",
    search: "Introduce palabras clave para buscar",
    username: "Introduce tu nombre de usuario"
  },
  validation: {
    email: "Por favor, introduce una dirección de correo electrónico válida",
    enterKeyword: "No hay datos de búsqueda",
    maxLength: "Por favor, no introduzcas más de {max} caracteres",
    minLength: "Por favor, introduce al menos {min} caracteres",
    number: "Por favor, introduce un número válido",
    password: "La contraseña debe contener al menos 8 caracteres",
    passwordConfirm: "Las contraseñas no coinciden",
    phone: "Por favor, introduce un número de teléfono válido",
    required: "Este campo es obligatorio",
    termsOfService: "Por favor, acepta los Términos de servicio y la Política de privacidad",
    url: "Por favor, introduce una URL válida"
  }
}, Ul = {
  date: {
    long: "D [de] MMMM [de] YYYY",
    short: "DD/MM/YYYY",
    withTime: "DD/MM/YYYY HH:mm:ss"
  },
  number: {
    currency: "€",
    decimal: ",",
    thousand: "."
  },
  time: {
    hours: "{hours} horas",
    minutes: "{minutes} minutos",
    seconds: "{seconds} segundos"
  }
}, Nl = {
  error: {
    403: "Acceso denegado",
    404: "Página no encontrada",
    500: "Error interno del servidor",
    default: "Ups, algo salió mal. Por favor, inténtelo de nuevo más tarde."
  },
  login: {
    forgotPassword: "¿Olvidaste tu contraseña?",
    noAccount: "¿No tienes una cuenta?",
    rememberMe: "Recordarme",
    signUp: "Registrarse",
    title: "Iniciar sesión"
  }
}, Hl = {
  common: Ml,
  components: Ol,
  form: Bl,
  format: Ul,
  pages: Nl
}, Fl = {
  back: "뒤로",
  cancel: "취소",
  changeLanguage: "언어 변경",
  close: "닫기",
  confirm: "확인",
  create: "생성",
  delete: "삭제",
  edit: "편집",
  hello: "안녕하세요",
  loading: "로딩 중...",
  more: "더 보기",
  next: "다음",
  no: "아니오",
  ok: "확인",
  open: "열기",
  previous: "이전",
  save: "저장",
  search: "검색",
  submit: "제출",
  update: "업데이트",
  welcome: "{name}님 환영합니다",
  yes: "예"
}, ql = {
  button: {
    hideAll: "전체 숨기기",
    loadMore: "더 불러오기",
    showAll: "전체 보기"
  },
  modal: {
    confirmContent: "이 작업을 수행하시겠습니까?",
    confirmTitle: "확인해 주세요"
  },
  table: {
    noData: "데이터가 없습니다",
    selected: "{selected}개 항목 선택됨",
    total: "총 {total}개 항목"
  },
  upload: {
    dragHere: "파일을 여기에 끌어다 놓거나 클릭하여 업로드",
    fileTooBig: "파일 크기는 {size}MB를 초과할 수 없습니다"
  }
}, Yl = {
  label: {
    address: "주소",
    email: "이메일",
    password: "비밀번호",
    phone: "전화번호",
    username: "사용자 이름"
  },
  placeholder: {
    email: "이메일 주소를 입력하세요",
    enterKeyword: "키워드를 입력하세요",
    password: "비밀번호를 입력하세요",
    phone: "휴대폰 번호를 입력하세요",
    search: "검색어를 입력하세요",
    username: "사용자 이름을 입력하세요"
  },
  validation: {
    email: "유효한 이메일 주소를 입력해 주세요",
    enterKeyword: "검색 데이터가 없습니다",
    maxLength: "{max}자를 초과할 수 없습니다",
    minLength: "최소 {min}자를 입력해 주세요",
    number: "유효한 숫자를 입력해 주세요",
    password: "비밀번호는 최소 8자 이상이어야 합니다",
    passwordConfirm: "비밀번호가 일치하지 않습니다",
    phone: "유효한 전화번호를 입력해 주세요",
    required: "이 필드는 필수입니다",
    termsOfService: "서비스 이용약관 및 개인정보 처리방침에 동의해 주세요",
    url: "유효한 URL을 입력해 주세요"
  }
}, Vl = {
  date: {
    long: "YYYY년 M월 D일",
    short: "YYYY.MM.DD",
    withTime: "YYYY.MM.DD HH:mm:ss"
  },
  number: {
    currency: "₩",
    decimal: ".",
    thousand: ","
  },
  time: {
    hours: "{hours}시간",
    minutes: "{minutes}분",
    seconds: "{seconds}초"
  }
}, Kl = {
  error: {
    403: "접근이 거부되었습니다",
    404: "페이지를 찾을 수 없습니다",
    500: "서버 내부 오류",
    default: "죄송합니다. 문제가 발생했습니다. 나중에 다시 시도해 주세요."
  },
  login: {
    forgotPassword: "비밀번호를 잊으셨나요?",
    noAccount: "계정이 없으신가요?",
    rememberMe: "로그인 상태 유지",
    signUp: "회원가입",
    title: "로그인"
  }
}, Wl = {
  common: Fl,
  components: ql,
  form: Yl,
  format: Vl,
  pages: Kl
}, Jl = {
  back: "返回",
  cancel: "取消",
  changeLanguage: "切换语言",
  close: "关闭",
  confirm: "确认",
  create: "创建",
  delete: "删除",
  edit: "编辑",
  hello: "你好",
  loading: "加载中...",
  more: "更多",
  next: "下一步",
  no: "否",
  ok: "确定",
  open: "打开",
  previous: "上一步",
  save: "保存",
  search: "搜索",
  submit: "提交",
  update: "更新",
  welcome: "欢迎 {name}",
  yes: "是"
}, Gl = {
  button: {
    hideAll: "隐藏全部",
    loadMore: "加载更多",
    showAll: "显示全部"
  },
  modal: {
    confirmContent: "确定要执行此操作吗？",
    confirmTitle: "请确认"
  },
  table: {
    noData: "暂无数据",
    selected: "已选择 {selected} 项",
    total: "共 {total} 条"
  },
  upload: {
    dragHere: "将文件拖到此处或点击上传",
    fileTooBig: "文件大小不能超过 {size}MB"
  }
}, Xl = {
  label: {
    address: "地址",
    email: "电子邮件",
    password: "密码",
    phone: "手机号码",
    username: "用户名"
  },
  placeholder: {
    email: "请输入您的电子邮件地址",
    enterKeyword: "请输入关键词",
    password: "请输入您的密码",
    phone: "请输入您的手机号码",
    search: "请输入搜索关键词",
    username: "请输入您的用户名"
  },
  validation: {
    email: "请输入有效的电子邮件地址",
    enterKeyword: "暂无搜索数据",
    maxLength: "请不要输入超过 {max} 个字符",
    minLength: "请至少输入 {min} 个字符",
    number: "请输入有效的数字",
    password: "密码必须至少包含8个字符",
    passwordConfirm: "两次输入的密码不一致",
    phone: "请输入有效的手机号码",
    required: "此字段为必填项",
    termsOfService: "请同意服务条款和隐私政策",
    url: "请输入有效的URL"
  }
}, Ql = {
  date: {
    long: "YYYY年M月D日",
    short: "YYYY/MM/DD",
    withTime: "YYYY/MM/DD HH:mm:ss"
  },
  number: {
    currency: "¥",
    decimal: ".",
    thousand: ","
  },
  time: {
    hours: "{hours}小时",
    minutes: "{minutes}分钟",
    seconds: "{seconds}秒"
  }
}, Zl = {
  error: {
    403: "访问被拒绝",
    404: "页面未找到",
    500: "服务器内部错误",
    default: "抱歉，出现了一些问题，请稍后再试。"
  },
  login: {
    forgotPassword: "忘记密码？",
    noAccount: "还没有账号？",
    rememberMe: "记住我",
    signUp: "注册",
    title: "登录"
  }
}, ec = {
  common: Jl,
  components: Gl,
  form: Xl,
  format: Ql,
  pages: Zl
}, tc = {
  back: "返回",
  cancel: "取消",
  changeLanguage: "切換語言",
  close: "關閉",
  confirm: "確認",
  create: "新增",
  delete: "刪除",
  edit: "編輯",
  hello: "你好",
  loading: "載入中...",
  more: "更多",
  next: "下一步",
  no: "否",
  ok: "確定",
  open: "開啟",
  previous: "上一步",
  save: "儲存",
  search: "搜尋",
  submit: "提交",
  update: "更新",
  welcome: "歡迎 {name}",
  yes: "是"
}, ic = {
  button: {
    hideAll: "隱藏全部",
    loadMore: "載入更多",
    showAll: "顯示全部"
  },
  modal: {
    confirmContent: "確定要執行此操作嗎？",
    confirmTitle: "請確認"
  },
  table: {
    noData: "暫無資料",
    selected: "已選擇 {selected} 筆",
    total: "共 {total} 筆"
  },
  upload: {
    dragHere: "將檔案拖曳至此處或點擊上傳",
    fileTooBig: "檔案大小不能超過 {size}MB"
  }
}, oc = {
  label: {
    address: "地址",
    email: "電子郵件",
    password: "密碼",
    phone: "手機號碼",
    username: "用戶名"
  },
  placeholder: {
    email: "請輸入您的電子郵件地址",
    enterKeyword: "請輸入關鍵字",
    password: "請輸入您的密碼",
    phone: "請輸入您的手機號碼",
    search: "請輸入關鍵字搜尋",
    username: "請輸入您的用戶名"
  },
  validation: {
    email: "請輸入有效的電子郵件地址",
    enterKeyword: "暫無搜尋資料",
    maxLength: "請不要輸入超過 {max} 個字符",
    minLength: "請至少輸入 {min} 個字符",
    number: "請輸入有效的數字",
    password: "密碼長度至少為8個字符",
    passwordConfirm: "兩次輸入的密碼不一致",
    phone: "請輸入有效的手機號碼",
    required: "此欄位為必填項",
    termsOfService: "請同意服務條款和隱私政策",
    url: "請輸入有效的網址"
  }
}, sc = {
  date: {
    long: "YYYY年MM月DD日",
    short: "MM/DD/YYYY",
    withTime: "YYYY年MM月DD日 HH:mm:ss"
  },
  number: {
    currency: "NT$",
    decimal: ".",
    thousand: ","
  },
  time: {
    hours: "{hours} 小時",
    minutes: "{minutes} 分鐘",
    seconds: "{seconds} 秒"
  }
}, nc = {
  error: {
    403: "拒絕存取",
    404: "找不到頁面",
    500: "伺服器內部錯誤",
    default: "抱歉，出現了一些問題，請稍後再試。"
  },
  login: {
    forgotPassword: "忘記密碼？",
    noAccount: "還沒有帳號？",
    rememberMe: "記住我",
    signUp: "註冊",
    title: "登入"
  }
}, rc = {
  common: tc,
  components: ic,
  form: oc,
  format: sc,
  pages: nc
}, Cs = {
  en: ul,
  ja: vl,
  de: Al,
  it: jl,
  fr: Il,
  es: Hl,
  ko: Wl,
  "zh-CN": ec,
  "zh-TW": rc
}, Zt = "i18n-language-change", Oe = {
  defaultLanguage: "en",
  fallbackLanguage: "en",
  cacheSize: 1e3,
  errorHandler: (t) => {
    console.warn(`[i18n] ${t.type}: ${t.message}`, t);
  }
};
class ac {
  constructor(e) {
    this.cache = /* @__PURE__ */ new Map(), this.maxSize = e;
  }
  get(e) {
    const i = this.cache.get(e);
    return i !== void 0 && (this.cache.delete(e), this.cache.set(e, i)), i;
  }
  set(e, i) {
    if (this.cache.size >= this.maxSize) {
      const o = this.cache.keys().next().value;
      o !== void 0 && this.cache.delete(o);
    }
    this.cache.set(e, i);
  }
  clear() {
    this.cache.clear();
  }
}
function lc(t) {
  const e = document.cookie.match(new RegExp("(^| )" + t + "=([^;]+)"));
  return e ? decodeURIComponent(e[2]) : null;
}
function cc(t, e, i = 7) {
  const o = window.location.hostname, s = new Date(Date.now() + i * 864e5).toUTCString();
  document.cookie = `${t}=${encodeURIComponent(e)};expires=${s};path=/;domain=${o}`;
}
const dc = /\{([^}]+)\}/g;
let ei = Oe.defaultLanguage;
const Eo = new ac(Oe.cacheSize);
function vi(t) {
  var e;
  return (e = Oe == null ? void 0 : Oe.errorHandler) == null || e.call(Oe, t), t.key;
}
function Rn() {
  return ei;
}
function Dn(t) {
  ei !== t && (ei = t, Eo.clear(), window.dispatchEvent(new CustomEvent(Zt, { detail: t })));
}
function Vi(t, e) {
  const i = e ? `${t}:${JSON.stringify(e)}` : t;
  try {
    const o = Eo.get(i);
    if (o) return o;
    const s = t.split(".");
    let n = Cs[ei];
    if (n = s.reduce((a, l) => a && typeof a == "object" ? a[l] : void 0, n), typeof n != "string")
      if (ei !== Oe.fallbackLanguage) {
        const a = s.reduce(
          (l, c) => l && typeof l == "object" ? l[c] : void 0,
          Cs[Oe.fallbackLanguage || "en"]
        );
        if (a !== void 0 && typeof a == "string")
          n = a;
        else
          return vi({
            type: "MISSING_KEY",
            key: t,
            message: `Translation key not found: ${t}`
          });
      } else
        return vi({
          type: "MISSING_KEY",
          key: t,
          message: `Translation key not found: ${t}`
        });
    let r = n;
    return e && (r = n.replace(dc, (a, l) => l in e ? String(e[l]) : vi({
      type: "INVALID_PARAMS",
      key: t,
      message: `Missing parameter: ${l}`,
      params: e
    }))), Eo.set(i, r), r;
  } catch (o) {
    return vi({
      type: "INTERNAL_ERROR",
      key: t,
      message: o instanceof Error ? o.message : "Unknown error",
      params: e
    });
  }
}
const hc = {
  t: Vi,
  getCurrLanguage: Rn,
  setCurrLanguage: Dn
};
function uc() {
  const t = lc("jds-language") || Oe.defaultLanguage || "en";
  Dn(t), window.addEventListener(Zt, (e) => {
    const i = e.detail;
    cc("jds-language", i);
  }), typeof window < "u" && (window.i18n = hc);
}
class pc {
  /**
   * 构造函数
   * @param host 宿主元素
   */
  constructor(e) {
    this.isConnected = !1, this.host = e, this.onLanguageChange = () => {
      this.isConnected && "requestUpdate" in this.host && typeof this.host.requestUpdate == "function" && this.host.requestUpdate();
    }, this.connect();
  }
  /**
   * 连接到宿主元素
   */
  connect() {
    this.isConnected || (this.isConnected = !0, window.addEventListener(Zt, this.onLanguageChange));
  }
  /**
   * 断开与宿主元素的连接
   */
  hostDisconnected() {
    this.isConnected && (this.isConnected = !1, window.removeEventListener(Zt, this.onLanguageChange));
  }
  /**
   * 翻译函数
   * @param key 翻译键
   * @param params 参数
   * @returns 翻译后的字符串
   */
  t(e, i) {
    return Vi(e, i);
  }
  /**
   * 当前语言
   * @returns 当前语言
   */
  get currentLanguage() {
    return Rn();
  }
}
var gc = Object.defineProperty, fc = Object.getOwnPropertyDescriptor, Go = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? fc(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && gc(e, i, s), s;
};
let Li = class extends J {
  constructor() {
    super(), this.key = "", this.params = {}, this.i18n = new pc(this);
  }
  render() {
    return _`${this.i18n.t(this.key, this.params) || this.key}`;
  }
  updated(e) {
    (e.has("key") || e.has("params")) && this.requestUpdate();
  }
  disconnectedCallback() {
    super.disconnectedCallback(), this.i18n.hostDisconnected();
  }
  createRenderRoot() {
    return this;
  }
};
Go([
  E({ type: String })
], Li.prototype, "key", 2);
Go([
  E({ type: Object })
], Li.prototype, "params", 2);
Li = Go([
  Te("jds-i18n")
], Li);
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const $i = globalThis, Xo = $i.ShadowRoot && ($i.ShadyCSS === void 0 || $i.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, Qo = Symbol(), js = /* @__PURE__ */ new WeakMap();
let Ln = class {
  constructor(t, e, i) {
    if (this._$cssResult$ = !0, i !== Qo) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = t, this.t = e;
  }
  get styleSheet() {
    let t = this.o;
    const e = this.t;
    if (Xo && t === void 0) {
      const i = e !== void 0 && e.length === 1;
      i && (t = js.get(e)), t === void 0 && ((this.o = t = new CSSStyleSheet()).replaceSync(this.cssText), i && js.set(e, t));
    }
    return t;
  }
  toString() {
    return this.cssText;
  }
};
const mc = (t) => new Ln(typeof t == "string" ? t : t + "", void 0, Qo), wc = (t, ...e) => {
  const i = t.length === 1 ? t[0] : e.reduce((o, s, n) => o + ((r) => {
    if (r._$cssResult$ === !0) return r.cssText;
    if (typeof r == "number") return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(s) + t[n + 1], t[0]);
  return new Ln(i, t, Qo);
}, vc = (t, e) => {
  if (Xo) t.adoptedStyleSheets = e.map((i) => i instanceof CSSStyleSheet ? i : i.styleSheet);
  else for (const i of e) {
    const o = document.createElement("style"), s = $i.litNonce;
    s !== void 0 && o.setAttribute("nonce", s), o.textContent = i.cssText, t.appendChild(o);
  }
}, zs = Xo ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let i = "";
  for (const o of e.cssRules) i += o.cssText;
  return mc(i);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: bc, defineProperty: yc, getOwnPropertyDescriptor: xc, getOwnPropertyNames: $c, getOwnPropertySymbols: Ec, getPrototypeOf: Ac } = Object, kt = globalThis, Rs = kt.trustedTypes, kc = Rs ? Rs.emptyScript : "", Ds = kt.reactiveElementPolyfillSupport, Ft = (t, e) => t, Pi = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? kc : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let i = t;
  switch (e) {
    case Boolean:
      i = t !== null;
      break;
    case Number:
      i = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        i = JSON.parse(t);
      } catch {
        i = null;
      }
  }
  return i;
} }, Zo = (t, e) => !bc(t, e), Ls = { attribute: !0, type: String, converter: Pi, reflect: !1, hasChanged: Zo };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), kt.litPropertyMetadata ?? (kt.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
let mt = class extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, i = Ls) {
    if (i.state && (i.attribute = !1), this._$Ei(), this.elementProperties.set(e, i), !i.noAccessor) {
      const o = Symbol(), s = this.getPropertyDescriptor(e, o, i);
      s !== void 0 && yc(this.prototype, e, s);
    }
  }
  static getPropertyDescriptor(e, i, o) {
    const { get: s, set: n } = xc(this.prototype, e) ?? { get() {
      return this[i];
    }, set(r) {
      this[i] = r;
    } };
    return { get() {
      return s == null ? void 0 : s.call(this);
    }, set(r) {
      const a = s == null ? void 0 : s.call(this);
      n.call(this, r), this.requestUpdate(e, a, o);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? Ls;
  }
  static _$Ei() {
    if (this.hasOwnProperty(Ft("elementProperties"))) return;
    const e = Ac(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(Ft("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(Ft("properties"))) {
      const i = this.properties, o = [...$c(i), ...Ec(i)];
      for (const s of o) this.createProperty(s, i[s]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const i = litPropertyMetadata.get(e);
      if (i !== void 0) for (const [o, s] of i) this.elementProperties.set(o, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [i, o] of this.elementProperties) {
      const s = this._$Eu(i, o);
      s !== void 0 && this._$Eh.set(s, i);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const i = [];
    if (Array.isArray(e)) {
      const o = new Set(e.flat(1 / 0).reverse());
      for (const s of o) i.unshift(zs(s));
    } else e !== void 0 && i.push(zs(e));
    return i;
  }
  static _$Eu(e, i) {
    const o = i.attribute;
    return o === !1 ? void 0 : typeof o == "string" ? o : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((i) => this.enableUpdating = i), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((i) => i(this));
  }
  addController(e) {
    var i;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((i = e.hostConnected) == null || i.call(e));
  }
  removeController(e) {
    var i;
    (i = this._$EO) == null || i.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), i = this.constructor.elementProperties;
    for (const o of i.keys()) this.hasOwnProperty(o) && (e.set(o, this[o]), delete this[o]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return vc(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostConnected) == null ? void 0 : o.call(i);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostDisconnected) == null ? void 0 : o.call(i);
    });
  }
  attributeChangedCallback(e, i, o) {
    this._$AK(e, o);
  }
  _$EC(e, i) {
    var o;
    const s = this.constructor.elementProperties.get(e), n = this.constructor._$Eu(e, s);
    if (n !== void 0 && s.reflect === !0) {
      const r = (((o = s.converter) == null ? void 0 : o.toAttribute) !== void 0 ? s.converter : Pi).toAttribute(i, s.type);
      this._$Em = e, r == null ? this.removeAttribute(n) : this.setAttribute(n, r), this._$Em = null;
    }
  }
  _$AK(e, i) {
    var o;
    const s = this.constructor, n = s._$Eh.get(e);
    if (n !== void 0 && this._$Em !== n) {
      const r = s.getPropertyOptions(n), a = typeof r.converter == "function" ? { fromAttribute: r.converter } : ((o = r.converter) == null ? void 0 : o.fromAttribute) !== void 0 ? r.converter : Pi;
      this._$Em = n, this[n] = a.fromAttribute(i, r.type), this._$Em = null;
    }
  }
  requestUpdate(e, i, o) {
    if (e !== void 0) {
      if (o ?? (o = this.constructor.getPropertyOptions(e)), !(o.hasChanged ?? Zo)(this[e], i)) return;
      this.P(e, i, o);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, i, o) {
    this._$AL.has(e) || this._$AL.set(e, i), o.reflect === !0 && this._$Em !== e && (this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Set())).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (i) {
      Promise.reject(i);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var e;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [n, r] of this._$Ep) this[n] = r;
        this._$Ep = void 0;
      }
      const s = this.constructor.elementProperties;
      if (s.size > 0) for (const [n, r] of s) r.wrapped !== !0 || this._$AL.has(n) || this[n] === void 0 || this.P(n, this[n], r);
    }
    let i = !1;
    const o = this._$AL;
    try {
      i = this.shouldUpdate(o), i ? (this.willUpdate(o), (e = this._$EO) == null || e.forEach((s) => {
        var n;
        return (n = s.hostUpdate) == null ? void 0 : n.call(s);
      }), this.update(o)) : this._$EU();
    } catch (s) {
      throw i = !1, this._$EU(), s;
    }
    i && this._$AE(o);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var i;
    (i = this._$EO) == null || i.forEach((o) => {
      var s;
      return (s = o.hostUpdated) == null ? void 0 : s.call(o);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((i) => this._$EC(i, this[i]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
};
mt.elementStyles = [], mt.shadowRootOptions = { mode: "open" }, mt[Ft("elementProperties")] = /* @__PURE__ */ new Map(), mt[Ft("finalized")] = /* @__PURE__ */ new Map(), Ds == null || Ds({ ReactiveElement: mt }), (kt.reactiveElementVersions ?? (kt.reactiveElementVersions = [])).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ii = globalThis, Mi = Ii.trustedTypes, Ps = Mi ? Mi.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, Pn = "$lit$", Ie = `lit$${Math.random().toFixed(9).slice(2)}$`, In = "?" + Ie, Sc = `<${In}>`, rt = document, ti = () => rt.createComment(""), ii = (t) => t === null || typeof t != "object" && typeof t != "function", es = Array.isArray, Tc = (t) => es(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", ao = `[ 	
\f\r]`, Ot = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, Is = /-->/g, Ms = />/g, Je = RegExp(`>|${ao}(?:([^\\s"'>=/]+)(${ao}*=${ao}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), Os = /'/g, Bs = /"/g, Mn = /^(?:script|style|textarea|title)$/i, _c = (t) => (e, ...i) => ({ _$litType$: t, strings: e, values: i }), Cc = _c(1), at = Symbol.for("lit-noChange"), P = Symbol.for("lit-nothing"), Us = /* @__PURE__ */ new WeakMap(), Qe = rt.createTreeWalker(rt, 129);
function On(t, e) {
  if (!es(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return Ps !== void 0 ? Ps.createHTML(e) : e;
}
const jc = (t, e) => {
  const i = t.length - 1, o = [];
  let s, n = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", r = Ot;
  for (let a = 0; a < i; a++) {
    const l = t[a];
    let c, d, h = -1, p = 0;
    for (; p < l.length && (r.lastIndex = p, d = r.exec(l), d !== null); ) p = r.lastIndex, r === Ot ? d[1] === "!--" ? r = Is : d[1] !== void 0 ? r = Ms : d[2] !== void 0 ? (Mn.test(d[2]) && (s = RegExp("</" + d[2], "g")), r = Je) : d[3] !== void 0 && (r = Je) : r === Je ? d[0] === ">" ? (r = s ?? Ot, h = -1) : d[1] === void 0 ? h = -2 : (h = r.lastIndex - d[2].length, c = d[1], r = d[3] === void 0 ? Je : d[3] === '"' ? Bs : Os) : r === Bs || r === Os ? r = Je : r === Is || r === Ms ? r = Ot : (r = Je, s = void 0);
    const f = r === Je && t[a + 1].startsWith("/>") ? " " : "";
    n += r === Ot ? l + Sc : h >= 0 ? (o.push(c), l.slice(0, h) + Pn + l.slice(h) + Ie + f) : l + Ie + (h === -2 ? a : f);
  }
  return [On(t, n + (t[i] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), o];
};
let Ao = class Bn {
  constructor({ strings: e, _$litType$: i }, o) {
    let s;
    this.parts = [];
    let n = 0, r = 0;
    const a = e.length - 1, l = this.parts, [c, d] = jc(e, i);
    if (this.el = Bn.createElement(c, o), Qe.currentNode = this.el.content, i === 2 || i === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (s = Qe.nextNode()) !== null && l.length < a; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const h of s.getAttributeNames()) if (h.endsWith(Pn)) {
          const p = d[r++], f = s.getAttribute(h).split(Ie), g = /([.?@])?(.*)/.exec(p);
          l.push({ type: 1, index: n, name: g[2], strings: f, ctor: g[1] === "." ? Rc : g[1] === "?" ? Dc : g[1] === "@" ? Lc : Ki }), s.removeAttribute(h);
        } else h.startsWith(Ie) && (l.push({ type: 6, index: n }), s.removeAttribute(h));
        if (Mn.test(s.tagName)) {
          const h = s.textContent.split(Ie), p = h.length - 1;
          if (p > 0) {
            s.textContent = Mi ? Mi.emptyScript : "";
            for (let f = 0; f < p; f++) s.append(h[f], ti()), Qe.nextNode(), l.push({ type: 2, index: ++n });
            s.append(h[p], ti());
          }
        }
      } else if (s.nodeType === 8) if (s.data === In) l.push({ type: 2, index: n });
      else {
        let h = -1;
        for (; (h = s.data.indexOf(Ie, h + 1)) !== -1; ) l.push({ type: 7, index: n }), h += Ie.length - 1;
      }
      n++;
    }
  }
  static createElement(e, i) {
    const o = rt.createElement("template");
    return o.innerHTML = e, o;
  }
};
function St(t, e, i = t, o) {
  var s, n;
  if (e === at) return e;
  let r = o !== void 0 ? (s = i._$Co) == null ? void 0 : s[o] : i._$Cl;
  const a = ii(e) ? void 0 : e._$litDirective$;
  return (r == null ? void 0 : r.constructor) !== a && ((n = r == null ? void 0 : r._$AO) == null || n.call(r, !1), a === void 0 ? r = void 0 : (r = new a(t), r._$AT(t, i, o)), o !== void 0 ? (i._$Co ?? (i._$Co = []))[o] = r : i._$Cl = r), r !== void 0 && (e = St(t, r._$AS(t, e.values), r, o)), e;
}
class zc {
  constructor(e, i) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = i;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: i }, parts: o } = this._$AD, s = ((e == null ? void 0 : e.creationScope) ?? rt).importNode(i, !0);
    Qe.currentNode = s;
    let n = Qe.nextNode(), r = 0, a = 0, l = o[0];
    for (; l !== void 0; ) {
      if (r === l.index) {
        let c;
        l.type === 2 ? c = new di(n, n.nextSibling, this, e) : l.type === 1 ? c = new l.ctor(n, l.name, l.strings, this, e) : l.type === 6 && (c = new Pc(n, this, e)), this._$AV.push(c), l = o[++a];
      }
      r !== (l == null ? void 0 : l.index) && (n = Qe.nextNode(), r++);
    }
    return Qe.currentNode = rt, s;
  }
  p(e) {
    let i = 0;
    for (const o of this._$AV) o !== void 0 && (o.strings !== void 0 ? (o._$AI(e, o, i), i += o.strings.length - 2) : o._$AI(e[i])), i++;
  }
}
class di {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, i, o, s) {
    this.type = 2, this._$AH = P, this._$AN = void 0, this._$AA = e, this._$AB = i, this._$AM = o, this.options = s, this._$Cv = (s == null ? void 0 : s.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const i = this._$AM;
    return i !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = i.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, i = this) {
    e = St(this, e, i), ii(e) ? e === P || e == null || e === "" ? (this._$AH !== P && this._$AR(), this._$AH = P) : e !== this._$AH && e !== at && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : Tc(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== P && ii(this._$AH) ? this._$AA.nextSibling.data = e : this.T(rt.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var i;
    const { values: o, _$litType$: s } = e, n = typeof s == "number" ? this._$AC(e) : (s.el === void 0 && (s.el = Ao.createElement(On(s.h, s.h[0]), this.options)), s);
    if (((i = this._$AH) == null ? void 0 : i._$AD) === n) this._$AH.p(o);
    else {
      const r = new zc(n, this), a = r.u(this.options);
      r.p(o), this.T(a), this._$AH = r;
    }
  }
  _$AC(e) {
    let i = Us.get(e.strings);
    return i === void 0 && Us.set(e.strings, i = new Ao(e)), i;
  }
  k(e) {
    es(this._$AH) || (this._$AH = [], this._$AR());
    const i = this._$AH;
    let o, s = 0;
    for (const n of e) s === i.length ? i.push(o = new di(this.O(ti()), this.O(ti()), this, this.options)) : o = i[s], o._$AI(n), s++;
    s < i.length && (this._$AR(o && o._$AB.nextSibling, s), i.length = s);
  }
  _$AR(e = this._$AA.nextSibling, i) {
    var o;
    for ((o = this._$AP) == null ? void 0 : o.call(this, !1, !0, i); e && e !== this._$AB; ) {
      const s = e.nextSibling;
      e.remove(), e = s;
    }
  }
  setConnected(e) {
    var i;
    this._$AM === void 0 && (this._$Cv = e, (i = this._$AP) == null || i.call(this, e));
  }
}
class Ki {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, i, o, s, n) {
    this.type = 1, this._$AH = P, this._$AN = void 0, this.element = e, this.name = i, this._$AM = s, this.options = n, o.length > 2 || o[0] !== "" || o[1] !== "" ? (this._$AH = Array(o.length - 1).fill(new String()), this.strings = o) : this._$AH = P;
  }
  _$AI(e, i = this, o, s) {
    const n = this.strings;
    let r = !1;
    if (n === void 0) e = St(this, e, i, 0), r = !ii(e) || e !== this._$AH && e !== at, r && (this._$AH = e);
    else {
      const a = e;
      let l, c;
      for (e = n[0], l = 0; l < n.length - 1; l++) c = St(this, a[o + l], i, l), c === at && (c = this._$AH[l]), r || (r = !ii(c) || c !== this._$AH[l]), c === P ? e = P : e !== P && (e += (c ?? "") + n[l + 1]), this._$AH[l] = c;
    }
    r && !s && this.j(e);
  }
  j(e) {
    e === P ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class Rc extends Ki {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === P ? void 0 : e;
  }
}
class Dc extends Ki {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== P);
  }
}
class Lc extends Ki {
  constructor(e, i, o, s, n) {
    super(e, i, o, s, n), this.type = 5;
  }
  _$AI(e, i = this) {
    if ((e = St(this, e, i, 0) ?? P) === at) return;
    const o = this._$AH, s = e === P && o !== P || e.capture !== o.capture || e.once !== o.once || e.passive !== o.passive, n = e !== P && (o === P || s);
    s && this.element.removeEventListener(this.name, this, o), n && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var i;
    typeof this._$AH == "function" ? this._$AH.call(((i = this.options) == null ? void 0 : i.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class Pc {
  constructor(e, i, o) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = i, this.options = o;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    St(this, e);
  }
}
const Ns = Ii.litHtmlPolyfillSupport;
Ns == null || Ns(Ao, di), (Ii.litHtmlVersions ?? (Ii.litHtmlVersions = [])).push("3.2.1");
const Ic = (t, e, i) => {
  const o = (i == null ? void 0 : i.renderBefore) ?? e;
  let s = o._$litPart$;
  if (s === void 0) {
    const n = (i == null ? void 0 : i.renderBefore) ?? null;
    o._$litPart$ = s = new di(e.insertBefore(ti(), n), n, void 0, i ?? {});
  }
  return s._$AI(t), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let qt = class extends mt {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var e;
    const i = super.createRenderRoot();
    return (e = this.renderOptions).renderBefore ?? (e.renderBefore = i.firstChild), i;
  }
  update(e) {
    const i = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = Ic(i, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return at;
  }
};
var Hs;
qt._$litElement$ = !0, qt.finalized = !0, (Hs = globalThis.litElementHydrateSupport) == null || Hs.call(globalThis, { LitElement: qt });
const Fs = globalThis.litElementPolyfillSupport;
Fs == null || Fs({ LitElement: qt });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Mc = (t) => (e, i) => {
  i !== void 0 ? i.addInitializer(() => {
    customElements.define(t, e);
  }) : customElements.define(t, e);
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Oc = { attribute: !0, type: String, converter: Pi, reflect: !1, hasChanged: Zo }, Bc = (t = Oc, e, i) => {
  const { kind: o, metadata: s } = i;
  let n = globalThis.litPropertyMetadata.get(s);
  if (n === void 0 && globalThis.litPropertyMetadata.set(s, n = /* @__PURE__ */ new Map()), n.set(i.name, t), o === "accessor") {
    const { name: r } = i;
    return { set(a) {
      const l = e.get.call(this);
      e.set.call(this, a), this.requestUpdate(r, l, t);
    }, init(a) {
      return a !== void 0 && this.P(r, void 0, t), a;
    } };
  }
  if (o === "setter") {
    const { name: r } = i;
    return function(a) {
      const l = this[r];
      e.call(this, a), this.requestUpdate(r, l, t);
    };
  }
  throw Error("Unsupported decorator location: " + o);
};
function Un(t) {
  return (e, i) => typeof i == "object" ? Bc(t, e, i) : ((o, s, n) => {
    const r = s.hasOwnProperty(n);
    return s.constructor.createProperty(n, r ? { ...o, wrapped: !0 } : o), r ? Object.getOwnPropertyDescriptor(s, n) : void 0;
  })(t, e, i);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function Uc(t) {
  return Un({ ...t, state: !0, attribute: !1 });
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Nc = { CHILD: 2 }, Hc = (t) => (...e) => ({ _$litDirective$: t, values: e });
class Fc {
  constructor(e) {
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AT(e, i, o) {
    this._$Ct = e, this._$AM = i, this._$Ci = o;
  }
  _$AS(e, i) {
    return this.update(e, i);
  }
  update(e, i) {
    return this.render(...i);
  }
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
class ko extends Fc {
  constructor(e) {
    if (super(e), this.it = P, e.type !== Nc.CHILD) throw Error(this.constructor.directiveName + "() can only be used in child bindings");
  }
  render(e) {
    if (e === P || e == null) return this._t = void 0, this.it = e;
    if (e === at) return e;
    if (typeof e != "string") throw Error(this.constructor.directiveName + "() called with a non-string value");
    if (e === this.it) return this._t;
    this.it = e;
    const i = [e];
    return i.raw = i, this._t = { _$litType$: this.constructor.resultType, strings: i, values: [] };
  }
}
ko.directiveName = "unsafeHTML", ko.resultType = 1;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let So = class extends ko {
};
So.directiveName = "unsafeSVG", So.resultType = 2;
const qc = Hc(So);
function Yc(t, e) {
  return function(i, o) {
    const s = new CustomEvent(e, {
      detail: i,
      // 事件的详细信息
      bubbles: !0,
      // 默认允许冒泡
      cancelable: !1,
      // 默认不可取消
      composed: !0,
      // 默认允许穿透到 shadow DOM
      ...o
      // 允许覆盖默认选项
    });
    return t.dispatchEvent(s), s;
  };
}
function Nn(t) {
  return (e, i) => {
    Object.defineProperty(e, i, {
      get() {
        return Yc(this, t || i);
      },
      enumerable: !0,
      // 使属性可枚举
      configurable: !0
      // 使属性可配置
    });
  };
}
const Vc = [
  "alert-fill",
  "alert-line",
  "arrow-left",
  "arrow-right",
  "arrow-up",
  "arrow-down",
  "check-fill",
  "check-line",
  "check",
  "close-fill",
  "close-line",
  "close",
  "info-fill",
  "info-line",
  "loading"
], Kc = [
  "facebook",
  "facebook-color"
], ts = /* @__PURE__ */ new Map();
Vc.forEach((t) => ts.set(t, "system"));
Kc.forEach((t) => ts.set(t, "social-media"));
function Wc(t) {
  const e = ts.get(t);
  if (!e)
    throw new Error(`未找到图标 "${t}" 对应的类别`);
  return e;
}
function Jc(t, e) {
  return `../icons/${t}/${e}.svg`;
}
var Gc = Object.defineProperty, Xc = Object.getOwnPropertyDescriptor, hi = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Xc(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Gc(e, i, s), s;
};
const bi = /* @__PURE__ */ new Map();
let lt = class extends qt {
  constructor() {
    super(...arguments), this.svg = "";
  }
  get name() {
    return this._iconName;
  }
  set name(e) {
    e !== this._iconName && (this._iconName = e, this.load());
  }
  async load() {
    const e = /* @__PURE__ */ Object.assign({ "../icons/social-media/facebook-color.svg": () => import("./facebook-color.es-rJXe6Hu7.js").then((s) => s.default), "../icons/social-media/facebook.svg": () => import("./facebook.es-AJ3WqCpH.js").then((s) => s.default), "../icons/system/alert-fill.svg": () => import("./alert-fill.es-DwZ3lRLL.js").then((s) => s.default), "../icons/system/alert-line.svg": () => import("./alert-line.es-C330XYdL.js").then((s) => s.default), "../icons/system/arrow-down.svg": () => import("./arrow-down.es-BAIHdBJX.js").then((s) => s.default), "../icons/system/arrow-left.svg": () => import("./arrow-left.es-DyQh8wom.js").then((s) => s.default), "../icons/system/arrow-right.svg": () => import("./arrow-right.es-DQ0y3q2u.js").then((s) => s.default), "../icons/system/arrow-up.svg": () => import("./arrow-up.es-BKyXcjR4.js").then((s) => s.default), "../icons/system/check-fill.svg": () => import("./check-fill.es-DRYeYNcH.js").then((s) => s.default), "../icons/system/check-line.svg": () => import("./check-line.es-DPL-j_gG.js").then((s) => s.default), "../icons/system/check.svg": () => import("./check.es-juGVHa96.js").then((s) => s.default), "../icons/system/close-fill.svg": () => import("./close-fill.es-CvA4Qc5D.js").then((s) => s.default), "../icons/system/close-line.svg": () => import("./close-line.es-mK-IolDy.js").then((s) => s.default), "../icons/system/close.svg": () => import("./close.es-BYmVEyOJ.js").then((s) => s.default), "../icons/system/info-fill.svg": () => import("./info-fill.es-C5sobxLx.js").then((s) => s.default), "../icons/system/info-line.svg": () => import("./info-line.es-BOwPMbJk.js").then((s) => s.default), "../icons/system/loading.svg": () => import("./loading.es-hZBh5I_P.js").then((s) => s.default) }), i = Wc(this.name), o = Jc(i, this.name);
    if (!e[o])
      throw new Error(`图标 "${this.name}" 不存在`);
    bi.has(o) || bi.set(o, e[o]());
    try {
      const s = await bi.get(o);
      if (!s)
        throw new Error("SVG content is empty");
      this.svg = s, this.onLoad(`${this.name} 图标加载完成`);
    } catch (s) {
      this.onError(`${this.name} 图标加载失败 [${s}]`), bi.delete(o);
    }
  }
  render() {
    return Cc`<div aria-hidden="true">${qc(this.svg)}</div>`;
  }
};
lt.styles = wc`
    :host {
      display: inline-block;
      width: max-content;
    }

    :host div {
      display: flex;
      align-items: stretch;
      width: 1em;
      height: 1em;
      min-width: 1em;
      min-height: 1em;
      overflow: hidden;
      transform: translateZ(0);
    }

    :host svg {
      width: 1em;
      height: 1em;
    }
  `;
hi([
  Un({ type: String, reflect: !0 })
], lt.prototype, "name", 1);
hi([
  Nn("jds-load")
], lt.prototype, "onLoad", 2);
hi([
  Nn("jds-error")
], lt.prototype, "onError", 2);
hi([
  Uc()
], lt.prototype, "svg", 2);
lt = hi([
  Mc("jds-icon")
], lt);
const vt = "data-i18n-key", Hn = "data-i18n-params", Qc = /\$\{window\.i18n\.t\(['"](.*?)['"](?:,\s*([^)]+))?\)\}/g;
function qs(t) {
  const e = t.textContent;
  if (!e || !e.includes("${window.i18n.t(")) return;
  const i = e.replace(Qc, (o, s, n) => {
    try {
      let r = {};
      if (n)
        try {
          r = Function(`return ${n}`)();
        } catch (a) {
          console.warn(`无效的参数，key: ${s}`, a);
        }
      return Vi(s, r);
    } catch (r) {
      return console.warn(`翻译失败，key: ${s}`, r), s;
    }
  });
  e !== i && (t.textContent = i);
}
function Ei(t) {
  const e = t.getAttribute(vt);
  if (e)
    try {
      const i = t.getAttribute(Hn);
      let o = {};
      if (i)
        try {
          o = JSON.parse(i);
        } catch (s) {
          console.warn(`无效的参数格式，key: ${e}`, s);
        }
      t.textContent = Vi(e, o);
    } catch (i) {
      console.warn(`翻译更新失败，key: ${e}`, i), t.textContent = e;
    }
}
function lo() {
  document.querySelectorAll(`[${vt}]`).forEach((t) => {
    t instanceof HTMLElement && Ei(t);
  });
}
function Zc() {
  const t = new MutationObserver((o) => {
    o.forEach((s) => {
      s.type === "attributes" && s.attributeName === vt && s.target instanceof HTMLElement ? Ei(s.target) : s.type === "childList" && s.addedNodes.forEach((n) => {
        n instanceof HTMLElement ? (n.hasAttribute(vt) && Ei(n), n.querySelectorAll(`[${vt}]`).forEach((r) => {
          r instanceof HTMLElement && Ei(r);
        })) : n.nodeType === Node.TEXT_NODE && qs(n);
      });
    });
  }), e = {
    attributes: !0,
    childList: !0,
    subtree: !0,
    attributeFilter: [vt, Hn]
    // 添加对参数属性的监听
  };
  function i() {
    const o = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      null
    );
    let s;
    for (; s = o.nextNode(); )
      qs(s);
  }
  return document.readyState === "loading" ? document.addEventListener("DOMContentLoaded", () => {
    t.observe(document.body, e), lo(), i();
  }) : (t.observe(document.body, e), lo(), i()), window.addEventListener(Zt, () => {
    lo(), i();
  }), () => t.disconnect();
}
uc();
Zc();
/**
 * @license
 * Copyright 2019 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Ai = globalThis, is = Ai.ShadowRoot && (Ai.ShadyCSS === void 0 || Ai.ShadyCSS.nativeShadow) && "adoptedStyleSheets" in Document.prototype && "replace" in CSSStyleSheet.prototype, os = Symbol(), Ys = /* @__PURE__ */ new WeakMap();
let Fn = class {
  constructor(e, i, o) {
    if (this._$cssResult$ = !0, o !== os) throw Error("CSSResult is not constructable. Use `unsafeCSS` or `css` instead.");
    this.cssText = e, this.t = i;
  }
  get styleSheet() {
    let e = this.o;
    const i = this.t;
    if (is && e === void 0) {
      const o = i !== void 0 && i.length === 1;
      o && (e = Ys.get(i)), e === void 0 && ((this.o = e = new CSSStyleSheet()).replaceSync(this.cssText), o && Ys.set(i, e));
    }
    return e;
  }
  toString() {
    return this.cssText;
  }
};
const ki = (t) => new Fn(typeof t == "string" ? t : t + "", void 0, os), N = (t, ...e) => {
  const i = t.length === 1 ? t[0] : e.reduce((o, s, n) => o + ((r) => {
    if (r._$cssResult$ === !0) return r.cssText;
    if (typeof r == "number") return r;
    throw Error("Value passed to 'css' function must be a 'css' function result: " + r + ". Use 'unsafeCSS' to pass non-literal values, but take care to ensure page security.");
  })(s) + t[n + 1], t[0]);
  return new Fn(i, t, os);
}, ed = (t, e) => {
  if (is) t.adoptedStyleSheets = e.map((i) => i instanceof CSSStyleSheet ? i : i.styleSheet);
  else for (const i of e) {
    const o = document.createElement("style"), s = Ai.litNonce;
    s !== void 0 && o.setAttribute("nonce", s), o.textContent = i.cssText, t.appendChild(o);
  }
}, Vs = is ? (t) => t : (t) => t instanceof CSSStyleSheet ? ((e) => {
  let i = "";
  for (const o of e.cssRules) i += o.cssText;
  return ki(i);
})(t) : t;
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const { is: td, defineProperty: id, getOwnPropertyDescriptor: od, getOwnPropertyNames: sd, getOwnPropertySymbols: nd, getPrototypeOf: rd } = Object, Be = globalThis, Ks = Be.trustedTypes, ad = Ks ? Ks.emptyScript : "", co = Be.reactiveElementPolyfillSupport, Yt = (t, e) => t, Oi = { toAttribute(t, e) {
  switch (e) {
    case Boolean:
      t = t ? ad : null;
      break;
    case Object:
    case Array:
      t = t == null ? t : JSON.stringify(t);
  }
  return t;
}, fromAttribute(t, e) {
  let i = t;
  switch (e) {
    case Boolean:
      i = t !== null;
      break;
    case Number:
      i = t === null ? null : Number(t);
      break;
    case Object:
    case Array:
      try {
        i = JSON.parse(t);
      } catch {
        i = null;
      }
  }
  return i;
} }, ss = (t, e) => !td(t, e), Ws = { attribute: !0, type: String, converter: Oi, reflect: !1, hasChanged: ss };
Symbol.metadata ?? (Symbol.metadata = Symbol("metadata")), Be.litPropertyMetadata ?? (Be.litPropertyMetadata = /* @__PURE__ */ new WeakMap());
class wt extends HTMLElement {
  static addInitializer(e) {
    this._$Ei(), (this.l ?? (this.l = [])).push(e);
  }
  static get observedAttributes() {
    return this.finalize(), this._$Eh && [...this._$Eh.keys()];
  }
  static createProperty(e, i = Ws) {
    if (i.state && (i.attribute = !1), this._$Ei(), this.elementProperties.set(e, i), !i.noAccessor) {
      const o = Symbol(), s = this.getPropertyDescriptor(e, o, i);
      s !== void 0 && id(this.prototype, e, s);
    }
  }
  static getPropertyDescriptor(e, i, o) {
    const { get: s, set: n } = od(this.prototype, e) ?? { get() {
      return this[i];
    }, set(r) {
      this[i] = r;
    } };
    return { get() {
      return s == null ? void 0 : s.call(this);
    }, set(r) {
      const a = s == null ? void 0 : s.call(this);
      n.call(this, r), this.requestUpdate(e, a, o);
    }, configurable: !0, enumerable: !0 };
  }
  static getPropertyOptions(e) {
    return this.elementProperties.get(e) ?? Ws;
  }
  static _$Ei() {
    if (this.hasOwnProperty(Yt("elementProperties"))) return;
    const e = rd(this);
    e.finalize(), e.l !== void 0 && (this.l = [...e.l]), this.elementProperties = new Map(e.elementProperties);
  }
  static finalize() {
    if (this.hasOwnProperty(Yt("finalized"))) return;
    if (this.finalized = !0, this._$Ei(), this.hasOwnProperty(Yt("properties"))) {
      const i = this.properties, o = [...sd(i), ...nd(i)];
      for (const s of o) this.createProperty(s, i[s]);
    }
    const e = this[Symbol.metadata];
    if (e !== null) {
      const i = litPropertyMetadata.get(e);
      if (i !== void 0) for (const [o, s] of i) this.elementProperties.set(o, s);
    }
    this._$Eh = /* @__PURE__ */ new Map();
    for (const [i, o] of this.elementProperties) {
      const s = this._$Eu(i, o);
      s !== void 0 && this._$Eh.set(s, i);
    }
    this.elementStyles = this.finalizeStyles(this.styles);
  }
  static finalizeStyles(e) {
    const i = [];
    if (Array.isArray(e)) {
      const o = new Set(e.flat(1 / 0).reverse());
      for (const s of o) i.unshift(Vs(s));
    } else e !== void 0 && i.push(Vs(e));
    return i;
  }
  static _$Eu(e, i) {
    const o = i.attribute;
    return o === !1 ? void 0 : typeof o == "string" ? o : typeof e == "string" ? e.toLowerCase() : void 0;
  }
  constructor() {
    super(), this._$Ep = void 0, this.isUpdatePending = !1, this.hasUpdated = !1, this._$Em = null, this._$Ev();
  }
  _$Ev() {
    var e;
    this._$ES = new Promise((i) => this.enableUpdating = i), this._$AL = /* @__PURE__ */ new Map(), this._$E_(), this.requestUpdate(), (e = this.constructor.l) == null || e.forEach((i) => i(this));
  }
  addController(e) {
    var i;
    (this._$EO ?? (this._$EO = /* @__PURE__ */ new Set())).add(e), this.renderRoot !== void 0 && this.isConnected && ((i = e.hostConnected) == null || i.call(e));
  }
  removeController(e) {
    var i;
    (i = this._$EO) == null || i.delete(e);
  }
  _$E_() {
    const e = /* @__PURE__ */ new Map(), i = this.constructor.elementProperties;
    for (const o of i.keys()) this.hasOwnProperty(o) && (e.set(o, this[o]), delete this[o]);
    e.size > 0 && (this._$Ep = e);
  }
  createRenderRoot() {
    const e = this.shadowRoot ?? this.attachShadow(this.constructor.shadowRootOptions);
    return ed(e, this.constructor.elementStyles), e;
  }
  connectedCallback() {
    var e;
    this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this.enableUpdating(!0), (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostConnected) == null ? void 0 : o.call(i);
    });
  }
  enableUpdating(e) {
  }
  disconnectedCallback() {
    var e;
    (e = this._$EO) == null || e.forEach((i) => {
      var o;
      return (o = i.hostDisconnected) == null ? void 0 : o.call(i);
    });
  }
  attributeChangedCallback(e, i, o) {
    this._$AK(e, o);
  }
  _$EC(e, i) {
    var n;
    const o = this.constructor.elementProperties.get(e), s = this.constructor._$Eu(e, o);
    if (s !== void 0 && o.reflect === !0) {
      const r = (((n = o.converter) == null ? void 0 : n.toAttribute) !== void 0 ? o.converter : Oi).toAttribute(i, o.type);
      this._$Em = e, r == null ? this.removeAttribute(s) : this.setAttribute(s, r), this._$Em = null;
    }
  }
  _$AK(e, i) {
    var n;
    const o = this.constructor, s = o._$Eh.get(e);
    if (s !== void 0 && this._$Em !== s) {
      const r = o.getPropertyOptions(s), a = typeof r.converter == "function" ? { fromAttribute: r.converter } : ((n = r.converter) == null ? void 0 : n.fromAttribute) !== void 0 ? r.converter : Oi;
      this._$Em = s, this[s] = a.fromAttribute(i, r.type), this._$Em = null;
    }
  }
  requestUpdate(e, i, o) {
    if (e !== void 0) {
      if (o ?? (o = this.constructor.getPropertyOptions(e)), !(o.hasChanged ?? ss)(this[e], i)) return;
      this.P(e, i, o);
    }
    this.isUpdatePending === !1 && (this._$ES = this._$ET());
  }
  P(e, i, o) {
    this._$AL.has(e) || this._$AL.set(e, i), o.reflect === !0 && this._$Em !== e && (this._$Ej ?? (this._$Ej = /* @__PURE__ */ new Set())).add(e);
  }
  async _$ET() {
    this.isUpdatePending = !0;
    try {
      await this._$ES;
    } catch (i) {
      Promise.reject(i);
    }
    const e = this.scheduleUpdate();
    return e != null && await e, !this.isUpdatePending;
  }
  scheduleUpdate() {
    return this.performUpdate();
  }
  performUpdate() {
    var o;
    if (!this.isUpdatePending) return;
    if (!this.hasUpdated) {
      if (this.renderRoot ?? (this.renderRoot = this.createRenderRoot()), this._$Ep) {
        for (const [n, r] of this._$Ep) this[n] = r;
        this._$Ep = void 0;
      }
      const s = this.constructor.elementProperties;
      if (s.size > 0) for (const [n, r] of s) r.wrapped !== !0 || this._$AL.has(n) || this[n] === void 0 || this.P(n, this[n], r);
    }
    let e = !1;
    const i = this._$AL;
    try {
      e = this.shouldUpdate(i), e ? (this.willUpdate(i), (o = this._$EO) == null || o.forEach((s) => {
        var n;
        return (n = s.hostUpdate) == null ? void 0 : n.call(s);
      }), this.update(i)) : this._$EU();
    } catch (s) {
      throw e = !1, this._$EU(), s;
    }
    e && this._$AE(i);
  }
  willUpdate(e) {
  }
  _$AE(e) {
    var i;
    (i = this._$EO) == null || i.forEach((o) => {
      var s;
      return (s = o.hostUpdated) == null ? void 0 : s.call(o);
    }), this.hasUpdated || (this.hasUpdated = !0, this.firstUpdated(e)), this.updated(e);
  }
  _$EU() {
    this._$AL = /* @__PURE__ */ new Map(), this.isUpdatePending = !1;
  }
  get updateComplete() {
    return this.getUpdateComplete();
  }
  getUpdateComplete() {
    return this._$ES;
  }
  shouldUpdate(e) {
    return !0;
  }
  update(e) {
    this._$Ej && (this._$Ej = this._$Ej.forEach((i) => this._$EC(i, this[i]))), this._$EU();
  }
  updated(e) {
  }
  firstUpdated(e) {
  }
}
wt.elementStyles = [], wt.shadowRootOptions = { mode: "open" }, wt[Yt("elementProperties")] = /* @__PURE__ */ new Map(), wt[Yt("finalized")] = /* @__PURE__ */ new Map(), co == null || co({ ReactiveElement: wt }), (Be.reactiveElementVersions ?? (Be.reactiveElementVersions = [])).push("2.0.4");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const Vt = globalThis, Bi = Vt.trustedTypes, Js = Bi ? Bi.createPolicy("lit-html", { createHTML: (t) => t }) : void 0, qn = "$lit$", Me = `lit$${Math.random().toFixed(9).slice(2)}$`, Yn = "?" + Me, ld = `<${Yn}>`, ct = document, oi = () => ct.createComment(""), si = (t) => t === null || typeof t != "object" && typeof t != "function", ns = Array.isArray, cd = (t) => ns(t) || typeof (t == null ? void 0 : t[Symbol.iterator]) == "function", ho = `[ 	
\f\r]`, Bt = /<(?:(!--|\/[^a-zA-Z])|(\/?[a-zA-Z][^>\s]*)|(\/?$))/g, Gs = /-->/g, Xs = />/g, Ge = RegExp(`>|${ho}(?:([^\\s"'>=/]+)(${ho}*=${ho}*(?:[^ 	
\f\r"'\`<>=]|("|')|))|$)`, "g"), Qs = /'/g, Zs = /"/g, Vn = /^(?:script|style|textarea|title)$/i, dd = (t) => (e, ...i) => ({ _$litType$: t, strings: e, values: i }), v = dd(1), Tt = Symbol.for("lit-noChange"), B = Symbol.for("lit-nothing"), en = /* @__PURE__ */ new WeakMap(), Ze = ct.createTreeWalker(ct, 129);
function Kn(t, e) {
  if (!ns(t) || !t.hasOwnProperty("raw")) throw Error("invalid template strings array");
  return Js !== void 0 ? Js.createHTML(e) : e;
}
const hd = (t, e) => {
  const i = t.length - 1, o = [];
  let s, n = e === 2 ? "<svg>" : e === 3 ? "<math>" : "", r = Bt;
  for (let a = 0; a < i; a++) {
    const l = t[a];
    let c, d, h = -1, p = 0;
    for (; p < l.length && (r.lastIndex = p, d = r.exec(l), d !== null); ) p = r.lastIndex, r === Bt ? d[1] === "!--" ? r = Gs : d[1] !== void 0 ? r = Xs : d[2] !== void 0 ? (Vn.test(d[2]) && (s = RegExp("</" + d[2], "g")), r = Ge) : d[3] !== void 0 && (r = Ge) : r === Ge ? d[0] === ">" ? (r = s ?? Bt, h = -1) : d[1] === void 0 ? h = -2 : (h = r.lastIndex - d[2].length, c = d[1], r = d[3] === void 0 ? Ge : d[3] === '"' ? Zs : Qs) : r === Zs || r === Qs ? r = Ge : r === Gs || r === Xs ? r = Bt : (r = Ge, s = void 0);
    const f = r === Ge && t[a + 1].startsWith("/>") ? " " : "";
    n += r === Bt ? l + ld : h >= 0 ? (o.push(c), l.slice(0, h) + qn + l.slice(h) + Me + f) : l + Me + (h === -2 ? a : f);
  }
  return [Kn(t, n + (t[i] || "<?>") + (e === 2 ? "</svg>" : e === 3 ? "</math>" : "")), o];
};
class ni {
  constructor({ strings: e, _$litType$: i }, o) {
    let s;
    this.parts = [];
    let n = 0, r = 0;
    const a = e.length - 1, l = this.parts, [c, d] = hd(e, i);
    if (this.el = ni.createElement(c, o), Ze.currentNode = this.el.content, i === 2 || i === 3) {
      const h = this.el.content.firstChild;
      h.replaceWith(...h.childNodes);
    }
    for (; (s = Ze.nextNode()) !== null && l.length < a; ) {
      if (s.nodeType === 1) {
        if (s.hasAttributes()) for (const h of s.getAttributeNames()) if (h.endsWith(qn)) {
          const p = d[r++], f = s.getAttribute(h).split(Me), g = /([.?@])?(.*)/.exec(p);
          l.push({ type: 1, index: n, name: g[2], strings: f, ctor: g[1] === "." ? pd : g[1] === "?" ? gd : g[1] === "@" ? fd : Wi }), s.removeAttribute(h);
        } else h.startsWith(Me) && (l.push({ type: 6, index: n }), s.removeAttribute(h));
        if (Vn.test(s.tagName)) {
          const h = s.textContent.split(Me), p = h.length - 1;
          if (p > 0) {
            s.textContent = Bi ? Bi.emptyScript : "";
            for (let f = 0; f < p; f++) s.append(h[f], oi()), Ze.nextNode(), l.push({ type: 2, index: ++n });
            s.append(h[p], oi());
          }
        }
      } else if (s.nodeType === 8) if (s.data === Yn) l.push({ type: 2, index: n });
      else {
        let h = -1;
        for (; (h = s.data.indexOf(Me, h + 1)) !== -1; ) l.push({ type: 7, index: n }), h += Me.length - 1;
      }
      n++;
    }
  }
  static createElement(e, i) {
    const o = ct.createElement("template");
    return o.innerHTML = e, o;
  }
}
function _t(t, e, i = t, o) {
  var r, a;
  if (e === Tt) return e;
  let s = o !== void 0 ? (r = i._$Co) == null ? void 0 : r[o] : i._$Cl;
  const n = si(e) ? void 0 : e._$litDirective$;
  return (s == null ? void 0 : s.constructor) !== n && ((a = s == null ? void 0 : s._$AO) == null || a.call(s, !1), n === void 0 ? s = void 0 : (s = new n(t), s._$AT(t, i, o)), o !== void 0 ? (i._$Co ?? (i._$Co = []))[o] = s : i._$Cl = s), s !== void 0 && (e = _t(t, s._$AS(t, e.values), s, o)), e;
}
class ud {
  constructor(e, i) {
    this._$AV = [], this._$AN = void 0, this._$AD = e, this._$AM = i;
  }
  get parentNode() {
    return this._$AM.parentNode;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  u(e) {
    const { el: { content: i }, parts: o } = this._$AD, s = ((e == null ? void 0 : e.creationScope) ?? ct).importNode(i, !0);
    Ze.currentNode = s;
    let n = Ze.nextNode(), r = 0, a = 0, l = o[0];
    for (; l !== void 0; ) {
      if (r === l.index) {
        let c;
        l.type === 2 ? c = new ui(n, n.nextSibling, this, e) : l.type === 1 ? c = new l.ctor(n, l.name, l.strings, this, e) : l.type === 6 && (c = new md(n, this, e)), this._$AV.push(c), l = o[++a];
      }
      r !== (l == null ? void 0 : l.index) && (n = Ze.nextNode(), r++);
    }
    return Ze.currentNode = ct, s;
  }
  p(e) {
    let i = 0;
    for (const o of this._$AV) o !== void 0 && (o.strings !== void 0 ? (o._$AI(e, o, i), i += o.strings.length - 2) : o._$AI(e[i])), i++;
  }
}
class ui {
  get _$AU() {
    var e;
    return ((e = this._$AM) == null ? void 0 : e._$AU) ?? this._$Cv;
  }
  constructor(e, i, o, s) {
    this.type = 2, this._$AH = B, this._$AN = void 0, this._$AA = e, this._$AB = i, this._$AM = o, this.options = s, this._$Cv = (s == null ? void 0 : s.isConnected) ?? !0;
  }
  get parentNode() {
    let e = this._$AA.parentNode;
    const i = this._$AM;
    return i !== void 0 && (e == null ? void 0 : e.nodeType) === 11 && (e = i.parentNode), e;
  }
  get startNode() {
    return this._$AA;
  }
  get endNode() {
    return this._$AB;
  }
  _$AI(e, i = this) {
    e = _t(this, e, i), si(e) ? e === B || e == null || e === "" ? (this._$AH !== B && this._$AR(), this._$AH = B) : e !== this._$AH && e !== Tt && this._(e) : e._$litType$ !== void 0 ? this.$(e) : e.nodeType !== void 0 ? this.T(e) : cd(e) ? this.k(e) : this._(e);
  }
  O(e) {
    return this._$AA.parentNode.insertBefore(e, this._$AB);
  }
  T(e) {
    this._$AH !== e && (this._$AR(), this._$AH = this.O(e));
  }
  _(e) {
    this._$AH !== B && si(this._$AH) ? this._$AA.nextSibling.data = e : this.T(ct.createTextNode(e)), this._$AH = e;
  }
  $(e) {
    var n;
    const { values: i, _$litType$: o } = e, s = typeof o == "number" ? this._$AC(e) : (o.el === void 0 && (o.el = ni.createElement(Kn(o.h, o.h[0]), this.options)), o);
    if (((n = this._$AH) == null ? void 0 : n._$AD) === s) this._$AH.p(i);
    else {
      const r = new ud(s, this), a = r.u(this.options);
      r.p(i), this.T(a), this._$AH = r;
    }
  }
  _$AC(e) {
    let i = en.get(e.strings);
    return i === void 0 && en.set(e.strings, i = new ni(e)), i;
  }
  k(e) {
    ns(this._$AH) || (this._$AH = [], this._$AR());
    const i = this._$AH;
    let o, s = 0;
    for (const n of e) s === i.length ? i.push(o = new ui(this.O(oi()), this.O(oi()), this, this.options)) : o = i[s], o._$AI(n), s++;
    s < i.length && (this._$AR(o && o._$AB.nextSibling, s), i.length = s);
  }
  _$AR(e = this._$AA.nextSibling, i) {
    var o;
    for ((o = this._$AP) == null ? void 0 : o.call(this, !1, !0, i); e && e !== this._$AB; ) {
      const s = e.nextSibling;
      e.remove(), e = s;
    }
  }
  setConnected(e) {
    var i;
    this._$AM === void 0 && (this._$Cv = e, (i = this._$AP) == null || i.call(this, e));
  }
}
class Wi {
  get tagName() {
    return this.element.tagName;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  constructor(e, i, o, s, n) {
    this.type = 1, this._$AH = B, this._$AN = void 0, this.element = e, this.name = i, this._$AM = s, this.options = n, o.length > 2 || o[0] !== "" || o[1] !== "" ? (this._$AH = Array(o.length - 1).fill(new String()), this.strings = o) : this._$AH = B;
  }
  _$AI(e, i = this, o, s) {
    const n = this.strings;
    let r = !1;
    if (n === void 0) e = _t(this, e, i, 0), r = !si(e) || e !== this._$AH && e !== Tt, r && (this._$AH = e);
    else {
      const a = e;
      let l, c;
      for (e = n[0], l = 0; l < n.length - 1; l++) c = _t(this, a[o + l], i, l), c === Tt && (c = this._$AH[l]), r || (r = !si(c) || c !== this._$AH[l]), c === B ? e = B : e !== B && (e += (c ?? "") + n[l + 1]), this._$AH[l] = c;
    }
    r && !s && this.j(e);
  }
  j(e) {
    e === B ? this.element.removeAttribute(this.name) : this.element.setAttribute(this.name, e ?? "");
  }
}
class pd extends Wi {
  constructor() {
    super(...arguments), this.type = 3;
  }
  j(e) {
    this.element[this.name] = e === B ? void 0 : e;
  }
}
class gd extends Wi {
  constructor() {
    super(...arguments), this.type = 4;
  }
  j(e) {
    this.element.toggleAttribute(this.name, !!e && e !== B);
  }
}
class fd extends Wi {
  constructor(e, i, o, s, n) {
    super(e, i, o, s, n), this.type = 5;
  }
  _$AI(e, i = this) {
    if ((e = _t(this, e, i, 0) ?? B) === Tt) return;
    const o = this._$AH, s = e === B && o !== B || e.capture !== o.capture || e.once !== o.once || e.passive !== o.passive, n = e !== B && (o === B || s);
    s && this.element.removeEventListener(this.name, this, o), n && this.element.addEventListener(this.name, this, e), this._$AH = e;
  }
  handleEvent(e) {
    var i;
    typeof this._$AH == "function" ? this._$AH.call(((i = this.options) == null ? void 0 : i.host) ?? this.element, e) : this._$AH.handleEvent(e);
  }
}
class md {
  constructor(e, i, o) {
    this.element = e, this.type = 6, this._$AN = void 0, this._$AM = i, this.options = o;
  }
  get _$AU() {
    return this._$AM._$AU;
  }
  _$AI(e) {
    _t(this, e);
  }
}
const uo = Vt.litHtmlPolyfillSupport;
uo == null || uo(ni, ui), (Vt.litHtmlVersions ?? (Vt.litHtmlVersions = [])).push("3.2.1");
const wd = (t, e, i) => {
  const o = (i == null ? void 0 : i.renderBefore) ?? e;
  let s = o._$litPart$;
  if (s === void 0) {
    const n = (i == null ? void 0 : i.renderBefore) ?? null;
    o._$litPart$ = s = new ui(e.insertBefore(oi(), n), n, void 0, i ?? {});
  }
  return s._$AI(t), s;
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
let re = class extends wt {
  constructor() {
    super(...arguments), this.renderOptions = { host: this }, this._$Do = void 0;
  }
  createRenderRoot() {
    var i;
    const e = super.createRenderRoot();
    return (i = this.renderOptions).renderBefore ?? (i.renderBefore = e.firstChild), e;
  }
  update(e) {
    const i = this.render();
    this.hasUpdated || (this.renderOptions.isConnected = this.isConnected), super.update(e), this._$Do = wd(i, this.renderRoot, this.renderOptions);
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), (e = this._$Do) == null || e.setConnected(!0);
  }
  disconnectedCallback() {
    var e;
    super.disconnectedCallback(), (e = this._$Do) == null || e.setConnected(!1);
  }
  render() {
    return Tt;
  }
};
var fn;
re._$litElement$ = !0, re.finalized = !0, (fn = globalThis.litElementHydrateSupport) == null || fn.call(globalThis, { LitElement: re });
const po = globalThis.litElementPolyfillSupport;
po == null || po({ LitElement: re });
(globalThis.litElementVersions ?? (globalThis.litElementVersions = [])).push("4.1.1");
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const pe = (t) => (e, i) => {
  i !== void 0 ? i.addInitializer(() => {
    customElements.define(t, e);
  }) : customElements.define(t, e);
};
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
const vd = { attribute: !0, type: String, converter: Oi, reflect: !1, hasChanged: ss }, bd = (t = vd, e, i) => {
  const { kind: o, metadata: s } = i;
  let n = globalThis.litPropertyMetadata.get(s);
  if (n === void 0 && globalThis.litPropertyMetadata.set(s, n = /* @__PURE__ */ new Map()), n.set(i.name, t), o === "accessor") {
    const { name: r } = i;
    return { set(a) {
      const l = e.get.call(this);
      e.set.call(this, a), this.requestUpdate(r, l, t);
    }, init(a) {
      return a !== void 0 && this.P(r, void 0, t), a;
    } };
  }
  if (o === "setter") {
    const { name: r } = i;
    return function(a) {
      const l = this[r];
      e.call(this, a), this.requestUpdate(r, l, t);
    };
  }
  throw Error("Unsupported decorator location: " + o);
};
function $(t) {
  return (e, i) => typeof i == "object" ? bd(t, e, i) : ((o, s, n) => {
    const r = s.hasOwnProperty(n);
    return s.constructor.createProperty(n, r ? { ...o, wrapped: !0 } : o), r ? Object.getOwnPropertyDescriptor(s, n) : void 0;
  })(t, e, i);
}
/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
function S(t) {
  return $({ ...t, state: !0, attribute: !1 });
}
function Wn(t, e) {
  return function() {
    return t.apply(e, arguments);
  };
}
const { toString: yd } = Object.prototype, { getPrototypeOf: rs } = Object, Ji = /* @__PURE__ */ ((t) => (e) => {
  const i = yd.call(e);
  return t[i] || (t[i] = i.slice(8, -1).toLowerCase());
})(/* @__PURE__ */ Object.create(null)), ge = (t) => (t = t.toLowerCase(), (e) => Ji(e) === t), Gi = (t) => (e) => typeof e === t, { isArray: Rt } = Array, ri = Gi("undefined");
function xd(t) {
  return t !== null && !ri(t) && t.constructor !== null && !ri(t.constructor) && X(t.constructor.isBuffer) && t.constructor.isBuffer(t);
}
const Jn = ge("ArrayBuffer");
function $d(t) {
  let e;
  return typeof ArrayBuffer < "u" && ArrayBuffer.isView ? e = ArrayBuffer.isView(t) : e = t && t.buffer && Jn(t.buffer), e;
}
const Ed = Gi("string"), X = Gi("function"), Gn = Gi("number"), Xi = (t) => t !== null && typeof t == "object", Ad = (t) => t === !0 || t === !1, Si = (t) => {
  if (Ji(t) !== "object")
    return !1;
  const e = rs(t);
  return (e === null || e === Object.prototype || Object.getPrototypeOf(e) === null) && !(Symbol.toStringTag in t) && !(Symbol.iterator in t);
}, kd = ge("Date"), Sd = ge("File"), Td = ge("Blob"), _d = ge("FileList"), Cd = (t) => Xi(t) && X(t.pipe), jd = (t) => {
  let e;
  return t && (typeof FormData == "function" && t instanceof FormData || X(t.append) && ((e = Ji(t)) === "formdata" || // detect form-data instance
  e === "object" && X(t.toString) && t.toString() === "[object FormData]"));
}, zd = ge("URLSearchParams"), [Rd, Dd, Ld, Pd] = ["ReadableStream", "Request", "Response", "Headers"].map(ge), Id = (t) => t.trim ? t.trim() : t.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, "");
function pi(t, e, { allOwnKeys: i = !1 } = {}) {
  if (t === null || typeof t > "u")
    return;
  let o, s;
  if (typeof t != "object" && (t = [t]), Rt(t))
    for (o = 0, s = t.length; o < s; o++)
      e.call(null, t[o], o, t);
  else {
    const n = i ? Object.getOwnPropertyNames(t) : Object.keys(t), r = n.length;
    let a;
    for (o = 0; o < r; o++)
      a = n[o], e.call(null, t[a], a, t);
  }
}
function Xn(t, e) {
  e = e.toLowerCase();
  const i = Object.keys(t);
  let o = i.length, s;
  for (; o-- > 0; )
    if (s = i[o], e === s.toLowerCase())
      return s;
  return null;
}
const et = typeof globalThis < "u" ? globalThis : typeof self < "u" ? self : typeof window < "u" ? window : global, Qn = (t) => !ri(t) && t !== et;
function To() {
  const { caseless: t } = Qn(this) && this || {}, e = {}, i = (o, s) => {
    const n = t && Xn(e, s) || s;
    Si(e[n]) && Si(o) ? e[n] = To(e[n], o) : Si(o) ? e[n] = To({}, o) : Rt(o) ? e[n] = o.slice() : e[n] = o;
  };
  for (let o = 0, s = arguments.length; o < s; o++)
    arguments[o] && pi(arguments[o], i);
  return e;
}
const Md = (t, e, i, { allOwnKeys: o } = {}) => (pi(e, (s, n) => {
  i && X(s) ? t[n] = Wn(s, i) : t[n] = s;
}, { allOwnKeys: o }), t), Od = (t) => (t.charCodeAt(0) === 65279 && (t = t.slice(1)), t), Bd = (t, e, i, o) => {
  t.prototype = Object.create(e.prototype, o), t.prototype.constructor = t, Object.defineProperty(t, "super", {
    value: e.prototype
  }), i && Object.assign(t.prototype, i);
}, Ud = (t, e, i, o) => {
  let s, n, r;
  const a = {};
  if (e = e || {}, t == null) return e;
  do {
    for (s = Object.getOwnPropertyNames(t), n = s.length; n-- > 0; )
      r = s[n], (!o || o(r, t, e)) && !a[r] && (e[r] = t[r], a[r] = !0);
    t = i !== !1 && rs(t);
  } while (t && (!i || i(t, e)) && t !== Object.prototype);
  return e;
}, Nd = (t, e, i) => {
  t = String(t), (i === void 0 || i > t.length) && (i = t.length), i -= e.length;
  const o = t.indexOf(e, i);
  return o !== -1 && o === i;
}, Hd = (t) => {
  if (!t) return null;
  if (Rt(t)) return t;
  let e = t.length;
  if (!Gn(e)) return null;
  const i = new Array(e);
  for (; e-- > 0; )
    i[e] = t[e];
  return i;
}, Fd = /* @__PURE__ */ ((t) => (e) => t && e instanceof t)(typeof Uint8Array < "u" && rs(Uint8Array)), qd = (t, e) => {
  const o = (t && t[Symbol.iterator]).call(t);
  let s;
  for (; (s = o.next()) && !s.done; ) {
    const n = s.value;
    e.call(t, n[0], n[1]);
  }
}, Yd = (t, e) => {
  let i;
  const o = [];
  for (; (i = t.exec(e)) !== null; )
    o.push(i);
  return o;
}, Vd = ge("HTMLFormElement"), Kd = (t) => t.toLowerCase().replace(
  /[-_\s]([a-z\d])(\w*)/g,
  function(i, o, s) {
    return o.toUpperCase() + s;
  }
), tn = (({ hasOwnProperty: t }) => (e, i) => t.call(e, i))(Object.prototype), Wd = ge("RegExp"), Zn = (t, e) => {
  const i = Object.getOwnPropertyDescriptors(t), o = {};
  pi(i, (s, n) => {
    let r;
    (r = e(s, n, t)) !== !1 && (o[n] = r || s);
  }), Object.defineProperties(t, o);
}, Jd = (t) => {
  Zn(t, (e, i) => {
    if (X(t) && ["arguments", "caller", "callee"].indexOf(i) !== -1)
      return !1;
    const o = t[i];
    if (X(o)) {
      if (e.enumerable = !1, "writable" in e) {
        e.writable = !1;
        return;
      }
      e.set || (e.set = () => {
        throw Error("Can not rewrite read-only method '" + i + "'");
      });
    }
  });
}, Gd = (t, e) => {
  const i = {}, o = (s) => {
    s.forEach((n) => {
      i[n] = !0;
    });
  };
  return Rt(t) ? o(t) : o(String(t).split(e)), i;
}, Xd = () => {
}, Qd = (t, e) => t != null && Number.isFinite(t = +t) ? t : e;
function Zd(t) {
  return !!(t && X(t.append) && t[Symbol.toStringTag] === "FormData" && t[Symbol.iterator]);
}
const eh = (t) => {
  const e = new Array(10), i = (o, s) => {
    if (Xi(o)) {
      if (e.indexOf(o) >= 0)
        return;
      if (!("toJSON" in o)) {
        e[s] = o;
        const n = Rt(o) ? [] : {};
        return pi(o, (r, a) => {
          const l = i(r, s + 1);
          !ri(l) && (n[a] = l);
        }), e[s] = void 0, n;
      }
    }
    return o;
  };
  return i(t, 0);
}, th = ge("AsyncFunction"), ih = (t) => t && (Xi(t) || X(t)) && X(t.then) && X(t.catch), er = ((t, e) => t ? setImmediate : e ? ((i, o) => (et.addEventListener("message", ({ source: s, data: n }) => {
  s === et && n === i && o.length && o.shift()();
}, !1), (s) => {
  o.push(s), et.postMessage(i, "*");
}))(`axios@${Math.random()}`, []) : (i) => setTimeout(i))(
  typeof setImmediate == "function",
  X(et.postMessage)
), oh = typeof queueMicrotask < "u" ? queueMicrotask.bind(et) : typeof process < "u" && process.nextTick || er, u = {
  isArray: Rt,
  isArrayBuffer: Jn,
  isBuffer: xd,
  isFormData: jd,
  isArrayBufferView: $d,
  isString: Ed,
  isNumber: Gn,
  isBoolean: Ad,
  isObject: Xi,
  isPlainObject: Si,
  isReadableStream: Rd,
  isRequest: Dd,
  isResponse: Ld,
  isHeaders: Pd,
  isUndefined: ri,
  isDate: kd,
  isFile: Sd,
  isBlob: Td,
  isRegExp: Wd,
  isFunction: X,
  isStream: Cd,
  isURLSearchParams: zd,
  isTypedArray: Fd,
  isFileList: _d,
  forEach: pi,
  merge: To,
  extend: Md,
  trim: Id,
  stripBOM: Od,
  inherits: Bd,
  toFlatObject: Ud,
  kindOf: Ji,
  kindOfTest: ge,
  endsWith: Nd,
  toArray: Hd,
  forEachEntry: qd,
  matchAll: Yd,
  isHTMLForm: Vd,
  hasOwnProperty: tn,
  hasOwnProp: tn,
  // an alias to avoid ESLint no-prototype-builtins detection
  reduceDescriptors: Zn,
  freezeMethods: Jd,
  toObjectSet: Gd,
  toCamelCase: Kd,
  noop: Xd,
  toFiniteNumber: Qd,
  findKey: Xn,
  global: et,
  isContextDefined: Qn,
  isSpecCompliantForm: Zd,
  toJSONObject: eh,
  isAsyncFn: th,
  isThenable: ih,
  setImmediate: er,
  asap: oh
};
function A(t, e, i, o, s) {
  Error.call(this), Error.captureStackTrace ? Error.captureStackTrace(this, this.constructor) : this.stack = new Error().stack, this.message = t, this.name = "AxiosError", e && (this.code = e), i && (this.config = i), o && (this.request = o), s && (this.response = s, this.status = s.status ? s.status : null);
}
u.inherits(A, Error, {
  toJSON: function() {
    return {
      // Standard
      message: this.message,
      name: this.name,
      // Microsoft
      description: this.description,
      number: this.number,
      // Mozilla
      fileName: this.fileName,
      lineNumber: this.lineNumber,
      columnNumber: this.columnNumber,
      stack: this.stack,
      // Axios
      config: u.toJSONObject(this.config),
      code: this.code,
      status: this.status
    };
  }
});
const tr = A.prototype, ir = {};
[
  "ERR_BAD_OPTION_VALUE",
  "ERR_BAD_OPTION",
  "ECONNABORTED",
  "ETIMEDOUT",
  "ERR_NETWORK",
  "ERR_FR_TOO_MANY_REDIRECTS",
  "ERR_DEPRECATED",
  "ERR_BAD_RESPONSE",
  "ERR_BAD_REQUEST",
  "ERR_CANCELED",
  "ERR_NOT_SUPPORT",
  "ERR_INVALID_URL"
  // eslint-disable-next-line func-names
].forEach((t) => {
  ir[t] = { value: t };
});
Object.defineProperties(A, ir);
Object.defineProperty(tr, "isAxiosError", { value: !0 });
A.from = (t, e, i, o, s, n) => {
  const r = Object.create(tr);
  return u.toFlatObject(t, r, function(l) {
    return l !== Error.prototype;
  }, (a) => a !== "isAxiosError"), A.call(r, t.message, e, i, o, s), r.cause = t, r.name = t.name, n && Object.assign(r, n), r;
};
const sh = null;
function _o(t) {
  return u.isPlainObject(t) || u.isArray(t);
}
function or(t) {
  return u.endsWith(t, "[]") ? t.slice(0, -2) : t;
}
function on(t, e, i) {
  return t ? t.concat(e).map(function(s, n) {
    return s = or(s), !i && n ? "[" + s + "]" : s;
  }).join(i ? "." : "") : e;
}
function nh(t) {
  return u.isArray(t) && !t.some(_o);
}
const rh = u.toFlatObject(u, {}, null, function(e) {
  return /^is[A-Z]/.test(e);
});
function Qi(t, e, i) {
  if (!u.isObject(t))
    throw new TypeError("target must be an object");
  e = e || new FormData(), i = u.toFlatObject(i, {
    metaTokens: !0,
    dots: !1,
    indexes: !1
  }, !1, function(w, m) {
    return !u.isUndefined(m[w]);
  });
  const o = i.metaTokens, s = i.visitor || d, n = i.dots, r = i.indexes, l = (i.Blob || typeof Blob < "u" && Blob) && u.isSpecCompliantForm(e);
  if (!u.isFunction(s))
    throw new TypeError("visitor must be a function");
  function c(g) {
    if (g === null) return "";
    if (u.isDate(g))
      return g.toISOString();
    if (!l && u.isBlob(g))
      throw new A("Blob is not supported. Use a Buffer instead.");
    return u.isArrayBuffer(g) || u.isTypedArray(g) ? l && typeof Blob == "function" ? new Blob([g]) : Buffer.from(g) : g;
  }
  function d(g, w, m) {
    let b = g;
    if (g && !m && typeof g == "object") {
      if (u.endsWith(w, "{}"))
        w = o ? w : w.slice(0, -2), g = JSON.stringify(g);
      else if (u.isArray(g) && nh(g) || (u.isFileList(g) || u.endsWith(w, "[]")) && (b = u.toArray(g)))
        return w = or(w), b.forEach(function(x, T) {
          !(u.isUndefined(x) || x === null) && e.append(
            // eslint-disable-next-line no-nested-ternary
            r === !0 ? on([w], T, n) : r === null ? w : w + "[]",
            c(x)
          );
        }), !1;
    }
    return _o(g) ? !0 : (e.append(on(m, w, n), c(g)), !1);
  }
  const h = [], p = Object.assign(rh, {
    defaultVisitor: d,
    convertValue: c,
    isVisitable: _o
  });
  function f(g, w) {
    if (!u.isUndefined(g)) {
      if (h.indexOf(g) !== -1)
        throw Error("Circular reference detected in " + w.join("."));
      h.push(g), u.forEach(g, function(b, y) {
        (!(u.isUndefined(b) || b === null) && s.call(
          e,
          b,
          u.isString(y) ? y.trim() : y,
          w,
          p
        )) === !0 && f(b, w ? w.concat(y) : [y]);
      }), h.pop();
    }
  }
  if (!u.isObject(t))
    throw new TypeError("data must be an object");
  return f(t), e;
}
function sn(t) {
  const e = {
    "!": "%21",
    "'": "%27",
    "(": "%28",
    ")": "%29",
    "~": "%7E",
    "%20": "+",
    "%00": "\0"
  };
  return encodeURIComponent(t).replace(/[!'()~]|%20|%00/g, function(o) {
    return e[o];
  });
}
function as(t, e) {
  this._pairs = [], t && Qi(t, this, e);
}
const sr = as.prototype;
sr.append = function(e, i) {
  this._pairs.push([e, i]);
};
sr.toString = function(e) {
  const i = e ? function(o) {
    return e.call(this, o, sn);
  } : sn;
  return this._pairs.map(function(s) {
    return i(s[0]) + "=" + i(s[1]);
  }, "").join("&");
};
function ah(t) {
  return encodeURIComponent(t).replace(/%3A/gi, ":").replace(/%24/g, "$").replace(/%2C/gi, ",").replace(/%20/g, "+").replace(/%5B/gi, "[").replace(/%5D/gi, "]");
}
function nr(t, e, i) {
  if (!e)
    return t;
  const o = i && i.encode || ah;
  u.isFunction(i) && (i = {
    serialize: i
  });
  const s = i && i.serialize;
  let n;
  if (s ? n = s(e, i) : n = u.isURLSearchParams(e) ? e.toString() : new as(e, i).toString(o), n) {
    const r = t.indexOf("#");
    r !== -1 && (t = t.slice(0, r)), t += (t.indexOf("?") === -1 ? "?" : "&") + n;
  }
  return t;
}
class nn {
  constructor() {
    this.handlers = [];
  }
  /**
   * Add a new interceptor to the stack
   *
   * @param {Function} fulfilled The function to handle `then` for a `Promise`
   * @param {Function} rejected The function to handle `reject` for a `Promise`
   *
   * @return {Number} An ID used to remove interceptor later
   */
  use(e, i, o) {
    return this.handlers.push({
      fulfilled: e,
      rejected: i,
      synchronous: o ? o.synchronous : !1,
      runWhen: o ? o.runWhen : null
    }), this.handlers.length - 1;
  }
  /**
   * Remove an interceptor from the stack
   *
   * @param {Number} id The ID that was returned by `use`
   *
   * @returns {Boolean} `true` if the interceptor was removed, `false` otherwise
   */
  eject(e) {
    this.handlers[e] && (this.handlers[e] = null);
  }
  /**
   * Clear all interceptors from the stack
   *
   * @returns {void}
   */
  clear() {
    this.handlers && (this.handlers = []);
  }
  /**
   * Iterate over all the registered interceptors
   *
   * This method is particularly useful for skipping over any
   * interceptors that may have become `null` calling `eject`.
   *
   * @param {Function} fn The function to call for each interceptor
   *
   * @returns {void}
   */
  forEach(e) {
    u.forEach(this.handlers, function(o) {
      o !== null && e(o);
    });
  }
}
const rr = {
  silentJSONParsing: !0,
  forcedJSONParsing: !0,
  clarifyTimeoutError: !1
}, lh = typeof URLSearchParams < "u" ? URLSearchParams : as, ch = typeof FormData < "u" ? FormData : null, dh = typeof Blob < "u" ? Blob : null, hh = {
  isBrowser: !0,
  classes: {
    URLSearchParams: lh,
    FormData: ch,
    Blob: dh
  },
  protocols: ["http", "https", "file", "blob", "url", "data"]
}, ls = typeof window < "u" && typeof document < "u", Co = typeof navigator == "object" && navigator || void 0, uh = ls && (!Co || ["ReactNative", "NativeScript", "NS"].indexOf(Co.product) < 0), ph = typeof WorkerGlobalScope < "u" && // eslint-disable-next-line no-undef
self instanceof WorkerGlobalScope && typeof self.importScripts == "function", gh = ls && window.location.href || "http://localhost", fh = /* @__PURE__ */ Object.freeze(/* @__PURE__ */ Object.defineProperty({
  __proto__: null,
  hasBrowserEnv: ls,
  hasStandardBrowserEnv: uh,
  hasStandardBrowserWebWorkerEnv: ph,
  navigator: Co,
  origin: gh
}, Symbol.toStringTag, { value: "Module" })), q = {
  ...fh,
  ...hh
};
function mh(t, e) {
  return Qi(t, new q.classes.URLSearchParams(), Object.assign({
    visitor: function(i, o, s, n) {
      return q.isNode && u.isBuffer(i) ? (this.append(o, i.toString("base64")), !1) : n.defaultVisitor.apply(this, arguments);
    }
  }, e));
}
function wh(t) {
  return u.matchAll(/\w+|\[(\w*)]/g, t).map((e) => e[0] === "[]" ? "" : e[1] || e[0]);
}
function vh(t) {
  const e = {}, i = Object.keys(t);
  let o;
  const s = i.length;
  let n;
  for (o = 0; o < s; o++)
    n = i[o], e[n] = t[n];
  return e;
}
function ar(t) {
  function e(i, o, s, n) {
    let r = i[n++];
    if (r === "__proto__") return !0;
    const a = Number.isFinite(+r), l = n >= i.length;
    return r = !r && u.isArray(s) ? s.length : r, l ? (u.hasOwnProp(s, r) ? s[r] = [s[r], o] : s[r] = o, !a) : ((!s[r] || !u.isObject(s[r])) && (s[r] = []), e(i, o, s[r], n) && u.isArray(s[r]) && (s[r] = vh(s[r])), !a);
  }
  if (u.isFormData(t) && u.isFunction(t.entries)) {
    const i = {};
    return u.forEachEntry(t, (o, s) => {
      e(wh(o), s, i, 0);
    }), i;
  }
  return null;
}
function bh(t, e, i) {
  if (u.isString(t))
    try {
      return (e || JSON.parse)(t), u.trim(t);
    } catch (o) {
      if (o.name !== "SyntaxError")
        throw o;
    }
  return (i || JSON.stringify)(t);
}
const gi = {
  transitional: rr,
  adapter: ["xhr", "http", "fetch"],
  transformRequest: [function(e, i) {
    const o = i.getContentType() || "", s = o.indexOf("application/json") > -1, n = u.isObject(e);
    if (n && u.isHTMLForm(e) && (e = new FormData(e)), u.isFormData(e))
      return s ? JSON.stringify(ar(e)) : e;
    if (u.isArrayBuffer(e) || u.isBuffer(e) || u.isStream(e) || u.isFile(e) || u.isBlob(e) || u.isReadableStream(e))
      return e;
    if (u.isArrayBufferView(e))
      return e.buffer;
    if (u.isURLSearchParams(e))
      return i.setContentType("application/x-www-form-urlencoded;charset=utf-8", !1), e.toString();
    let a;
    if (n) {
      if (o.indexOf("application/x-www-form-urlencoded") > -1)
        return mh(e, this.formSerializer).toString();
      if ((a = u.isFileList(e)) || o.indexOf("multipart/form-data") > -1) {
        const l = this.env && this.env.FormData;
        return Qi(
          a ? { "files[]": e } : e,
          l && new l(),
          this.formSerializer
        );
      }
    }
    return n || s ? (i.setContentType("application/json", !1), bh(e)) : e;
  }],
  transformResponse: [function(e) {
    const i = this.transitional || gi.transitional, o = i && i.forcedJSONParsing, s = this.responseType === "json";
    if (u.isResponse(e) || u.isReadableStream(e))
      return e;
    if (e && u.isString(e) && (o && !this.responseType || s)) {
      const r = !(i && i.silentJSONParsing) && s;
      try {
        return JSON.parse(e);
      } catch (a) {
        if (r)
          throw a.name === "SyntaxError" ? A.from(a, A.ERR_BAD_RESPONSE, this, null, this.response) : a;
      }
    }
    return e;
  }],
  /**
   * A timeout in milliseconds to abort a request. If set to 0 (default) a
   * timeout is not created.
   */
  timeout: 0,
  xsrfCookieName: "XSRF-TOKEN",
  xsrfHeaderName: "X-XSRF-TOKEN",
  maxContentLength: -1,
  maxBodyLength: -1,
  env: {
    FormData: q.classes.FormData,
    Blob: q.classes.Blob
  },
  validateStatus: function(e) {
    return e >= 200 && e < 300;
  },
  headers: {
    common: {
      Accept: "application/json, text/plain, */*",
      "Content-Type": void 0
    }
  }
};
u.forEach(["delete", "get", "head", "post", "put", "patch"], (t) => {
  gi.headers[t] = {};
});
const yh = u.toObjectSet([
  "age",
  "authorization",
  "content-length",
  "content-type",
  "etag",
  "expires",
  "from",
  "host",
  "if-modified-since",
  "if-unmodified-since",
  "last-modified",
  "location",
  "max-forwards",
  "proxy-authorization",
  "referer",
  "retry-after",
  "user-agent"
]), xh = (t) => {
  const e = {};
  let i, o, s;
  return t && t.split(`
`).forEach(function(r) {
    s = r.indexOf(":"), i = r.substring(0, s).trim().toLowerCase(), o = r.substring(s + 1).trim(), !(!i || e[i] && yh[i]) && (i === "set-cookie" ? e[i] ? e[i].push(o) : e[i] = [o] : e[i] = e[i] ? e[i] + ", " + o : o);
  }), e;
}, rn = Symbol("internals");
function Ut(t) {
  return t && String(t).trim().toLowerCase();
}
function Ti(t) {
  return t === !1 || t == null ? t : u.isArray(t) ? t.map(Ti) : String(t);
}
function $h(t) {
  const e = /* @__PURE__ */ Object.create(null), i = /([^\s,;=]+)\s*(?:=\s*([^,;]+))?/g;
  let o;
  for (; o = i.exec(t); )
    e[o[1]] = o[2];
  return e;
}
const Eh = (t) => /^[-_a-zA-Z0-9^`|~,!#$%&'*+.]+$/.test(t.trim());
function go(t, e, i, o, s) {
  if (u.isFunction(o))
    return o.call(this, e, i);
  if (s && (e = i), !!u.isString(e)) {
    if (u.isString(o))
      return e.indexOf(o) !== -1;
    if (u.isRegExp(o))
      return o.test(e);
  }
}
function Ah(t) {
  return t.trim().toLowerCase().replace(/([a-z\d])(\w*)/g, (e, i, o) => i.toUpperCase() + o);
}
function kh(t, e) {
  const i = u.toCamelCase(" " + e);
  ["get", "set", "has"].forEach((o) => {
    Object.defineProperty(t, o + i, {
      value: function(s, n, r) {
        return this[o].call(this, e, s, n, r);
      },
      configurable: !0
    });
  });
}
let K = class {
  constructor(e) {
    e && this.set(e);
  }
  set(e, i, o) {
    const s = this;
    function n(a, l, c) {
      const d = Ut(l);
      if (!d)
        throw new Error("header name must be a non-empty string");
      const h = u.findKey(s, d);
      (!h || s[h] === void 0 || c === !0 || c === void 0 && s[h] !== !1) && (s[h || l] = Ti(a));
    }
    const r = (a, l) => u.forEach(a, (c, d) => n(c, d, l));
    if (u.isPlainObject(e) || e instanceof this.constructor)
      r(e, i);
    else if (u.isString(e) && (e = e.trim()) && !Eh(e))
      r(xh(e), i);
    else if (u.isHeaders(e))
      for (const [a, l] of e.entries())
        n(l, a, o);
    else
      e != null && n(i, e, o);
    return this;
  }
  get(e, i) {
    if (e = Ut(e), e) {
      const o = u.findKey(this, e);
      if (o) {
        const s = this[o];
        if (!i)
          return s;
        if (i === !0)
          return $h(s);
        if (u.isFunction(i))
          return i.call(this, s, o);
        if (u.isRegExp(i))
          return i.exec(s);
        throw new TypeError("parser must be boolean|regexp|function");
      }
    }
  }
  has(e, i) {
    if (e = Ut(e), e) {
      const o = u.findKey(this, e);
      return !!(o && this[o] !== void 0 && (!i || go(this, this[o], o, i)));
    }
    return !1;
  }
  delete(e, i) {
    const o = this;
    let s = !1;
    function n(r) {
      if (r = Ut(r), r) {
        const a = u.findKey(o, r);
        a && (!i || go(o, o[a], a, i)) && (delete o[a], s = !0);
      }
    }
    return u.isArray(e) ? e.forEach(n) : n(e), s;
  }
  clear(e) {
    const i = Object.keys(this);
    let o = i.length, s = !1;
    for (; o--; ) {
      const n = i[o];
      (!e || go(this, this[n], n, e, !0)) && (delete this[n], s = !0);
    }
    return s;
  }
  normalize(e) {
    const i = this, o = {};
    return u.forEach(this, (s, n) => {
      const r = u.findKey(o, n);
      if (r) {
        i[r] = Ti(s), delete i[n];
        return;
      }
      const a = e ? Ah(n) : String(n).trim();
      a !== n && delete i[n], i[a] = Ti(s), o[a] = !0;
    }), this;
  }
  concat(...e) {
    return this.constructor.concat(this, ...e);
  }
  toJSON(e) {
    const i = /* @__PURE__ */ Object.create(null);
    return u.forEach(this, (o, s) => {
      o != null && o !== !1 && (i[s] = e && u.isArray(o) ? o.join(", ") : o);
    }), i;
  }
  [Symbol.iterator]() {
    return Object.entries(this.toJSON())[Symbol.iterator]();
  }
  toString() {
    return Object.entries(this.toJSON()).map(([e, i]) => e + ": " + i).join(`
`);
  }
  get [Symbol.toStringTag]() {
    return "AxiosHeaders";
  }
  static from(e) {
    return e instanceof this ? e : new this(e);
  }
  static concat(e, ...i) {
    const o = new this(e);
    return i.forEach((s) => o.set(s)), o;
  }
  static accessor(e) {
    const o = (this[rn] = this[rn] = {
      accessors: {}
    }).accessors, s = this.prototype;
    function n(r) {
      const a = Ut(r);
      o[a] || (kh(s, r), o[a] = !0);
    }
    return u.isArray(e) ? e.forEach(n) : n(e), this;
  }
};
K.accessor(["Content-Type", "Content-Length", "Accept", "Accept-Encoding", "User-Agent", "Authorization"]);
u.reduceDescriptors(K.prototype, ({ value: t }, e) => {
  let i = e[0].toUpperCase() + e.slice(1);
  return {
    get: () => t,
    set(o) {
      this[i] = o;
    }
  };
});
u.freezeMethods(K);
function fo(t, e) {
  const i = this || gi, o = e || i, s = K.from(o.headers);
  let n = o.data;
  return u.forEach(t, function(a) {
    n = a.call(i, n, s.normalize(), e ? e.status : void 0);
  }), s.normalize(), n;
}
function lr(t) {
  return !!(t && t.__CANCEL__);
}
function Dt(t, e, i) {
  A.call(this, t ?? "canceled", A.ERR_CANCELED, e, i), this.name = "CanceledError";
}
u.inherits(Dt, A, {
  __CANCEL__: !0
});
function cr(t, e, i) {
  const o = i.config.validateStatus;
  !i.status || !o || o(i.status) ? t(i) : e(new A(
    "Request failed with status code " + i.status,
    [A.ERR_BAD_REQUEST, A.ERR_BAD_RESPONSE][Math.floor(i.status / 100) - 4],
    i.config,
    i.request,
    i
  ));
}
function Sh(t) {
  const e = /^([-+\w]{1,25})(:?\/\/|:)/.exec(t);
  return e && e[1] || "";
}
function Th(t, e) {
  t = t || 10;
  const i = new Array(t), o = new Array(t);
  let s = 0, n = 0, r;
  return e = e !== void 0 ? e : 1e3, function(l) {
    const c = Date.now(), d = o[n];
    r || (r = c), i[s] = l, o[s] = c;
    let h = n, p = 0;
    for (; h !== s; )
      p += i[h++], h = h % t;
    if (s = (s + 1) % t, s === n && (n = (n + 1) % t), c - r < e)
      return;
    const f = d && c - d;
    return f ? Math.round(p * 1e3 / f) : void 0;
  };
}
function _h(t, e) {
  let i = 0, o = 1e3 / e, s, n;
  const r = (c, d = Date.now()) => {
    i = d, s = null, n && (clearTimeout(n), n = null), t.apply(null, c);
  };
  return [(...c) => {
    const d = Date.now(), h = d - i;
    h >= o ? r(c, d) : (s = c, n || (n = setTimeout(() => {
      n = null, r(s);
    }, o - h)));
  }, () => s && r(s)];
}
const Ui = (t, e, i = 3) => {
  let o = 0;
  const s = Th(50, 250);
  return _h((n) => {
    const r = n.loaded, a = n.lengthComputable ? n.total : void 0, l = r - o, c = s(l), d = r <= a;
    o = r;
    const h = {
      loaded: r,
      total: a,
      progress: a ? r / a : void 0,
      bytes: l,
      rate: c || void 0,
      estimated: c && a && d ? (a - r) / c : void 0,
      event: n,
      lengthComputable: a != null,
      [e ? "download" : "upload"]: !0
    };
    t(h);
  }, i);
}, an = (t, e) => {
  const i = t != null;
  return [(o) => e[0]({
    lengthComputable: i,
    total: t,
    loaded: o
  }), e[1]];
}, ln = (t) => (...e) => u.asap(() => t(...e)), Ch = q.hasStandardBrowserEnv ? /* @__PURE__ */ ((t, e) => (i) => (i = new URL(i, q.origin), t.protocol === i.protocol && t.host === i.host && (e || t.port === i.port)))(
  new URL(q.origin),
  q.navigator && /(msie|trident)/i.test(q.navigator.userAgent)
) : () => !0, jh = q.hasStandardBrowserEnv ? (
  // Standard browser envs support document.cookie
  {
    write(t, e, i, o, s, n) {
      const r = [t + "=" + encodeURIComponent(e)];
      u.isNumber(i) && r.push("expires=" + new Date(i).toGMTString()), u.isString(o) && r.push("path=" + o), u.isString(s) && r.push("domain=" + s), n === !0 && r.push("secure"), document.cookie = r.join("; ");
    },
    read(t) {
      const e = document.cookie.match(new RegExp("(^|;\\s*)(" + t + ")=([^;]*)"));
      return e ? decodeURIComponent(e[3]) : null;
    },
    remove(t) {
      this.write(t, "", Date.now() - 864e5);
    }
  }
) : (
  // Non-standard browser env (web workers, react-native) lack needed support.
  {
    write() {
    },
    read() {
      return null;
    },
    remove() {
    }
  }
);
function zh(t) {
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(t);
}
function Rh(t, e) {
  return e ? t.replace(/\/?\/$/, "") + "/" + e.replace(/^\/+/, "") : t;
}
function dr(t, e, i) {
  let o = !zh(e);
  return t && o || i == !1 ? Rh(t, e) : e;
}
const cn = (t) => t instanceof K ? { ...t } : t;
function dt(t, e) {
  e = e || {};
  const i = {};
  function o(c, d, h, p) {
    return u.isPlainObject(c) && u.isPlainObject(d) ? u.merge.call({ caseless: p }, c, d) : u.isPlainObject(d) ? u.merge({}, d) : u.isArray(d) ? d.slice() : d;
  }
  function s(c, d, h, p) {
    if (u.isUndefined(d)) {
      if (!u.isUndefined(c))
        return o(void 0, c, h, p);
    } else return o(c, d, h, p);
  }
  function n(c, d) {
    if (!u.isUndefined(d))
      return o(void 0, d);
  }
  function r(c, d) {
    if (u.isUndefined(d)) {
      if (!u.isUndefined(c))
        return o(void 0, c);
    } else return o(void 0, d);
  }
  function a(c, d, h) {
    if (h in e)
      return o(c, d);
    if (h in t)
      return o(void 0, c);
  }
  const l = {
    url: n,
    method: n,
    data: n,
    baseURL: r,
    transformRequest: r,
    transformResponse: r,
    paramsSerializer: r,
    timeout: r,
    timeoutMessage: r,
    withCredentials: r,
    withXSRFToken: r,
    adapter: r,
    responseType: r,
    xsrfCookieName: r,
    xsrfHeaderName: r,
    onUploadProgress: r,
    onDownloadProgress: r,
    decompress: r,
    maxContentLength: r,
    maxBodyLength: r,
    beforeRedirect: r,
    transport: r,
    httpAgent: r,
    httpsAgent: r,
    cancelToken: r,
    socketPath: r,
    responseEncoding: r,
    validateStatus: a,
    headers: (c, d, h) => s(cn(c), cn(d), h, !0)
  };
  return u.forEach(Object.keys(Object.assign({}, t, e)), function(d) {
    const h = l[d] || s, p = h(t[d], e[d], d);
    u.isUndefined(p) && h !== a || (i[d] = p);
  }), i;
}
const hr = (t) => {
  const e = dt({}, t);
  let { data: i, withXSRFToken: o, xsrfHeaderName: s, xsrfCookieName: n, headers: r, auth: a } = e;
  e.headers = r = K.from(r), e.url = nr(dr(e.baseURL, e.url), t.params, t.paramsSerializer), a && r.set(
    "Authorization",
    "Basic " + btoa((a.username || "") + ":" + (a.password ? unescape(encodeURIComponent(a.password)) : ""))
  );
  let l;
  if (u.isFormData(i)) {
    if (q.hasStandardBrowserEnv || q.hasStandardBrowserWebWorkerEnv)
      r.setContentType(void 0);
    else if ((l = r.getContentType()) !== !1) {
      const [c, ...d] = l ? l.split(";").map((h) => h.trim()).filter(Boolean) : [];
      r.setContentType([c || "multipart/form-data", ...d].join("; "));
    }
  }
  if (q.hasStandardBrowserEnv && (o && u.isFunction(o) && (o = o(e)), o || o !== !1 && Ch(e.url))) {
    const c = s && n && jh.read(n);
    c && r.set(s, c);
  }
  return e;
}, Dh = typeof XMLHttpRequest < "u", Lh = Dh && function(t) {
  return new Promise(function(i, o) {
    const s = hr(t);
    let n = s.data;
    const r = K.from(s.headers).normalize();
    let { responseType: a, onUploadProgress: l, onDownloadProgress: c } = s, d, h, p, f, g;
    function w() {
      f && f(), g && g(), s.cancelToken && s.cancelToken.unsubscribe(d), s.signal && s.signal.removeEventListener("abort", d);
    }
    let m = new XMLHttpRequest();
    m.open(s.method.toUpperCase(), s.url, !0), m.timeout = s.timeout;
    function b() {
      if (!m)
        return;
      const x = K.from(
        "getAllResponseHeaders" in m && m.getAllResponseHeaders()
      ), k = {
        data: !a || a === "text" || a === "json" ? m.responseText : m.response,
        status: m.status,
        statusText: m.statusText,
        headers: x,
        config: t,
        request: m
      };
      cr(function(z) {
        i(z), w();
      }, function(z) {
        o(z), w();
      }, k), m = null;
    }
    "onloadend" in m ? m.onloadend = b : m.onreadystatechange = function() {
      !m || m.readyState !== 4 || m.status === 0 && !(m.responseURL && m.responseURL.indexOf("file:") === 0) || setTimeout(b);
    }, m.onabort = function() {
      m && (o(new A("Request aborted", A.ECONNABORTED, t, m)), m = null);
    }, m.onerror = function() {
      o(new A("Network Error", A.ERR_NETWORK, t, m)), m = null;
    }, m.ontimeout = function() {
      let T = s.timeout ? "timeout of " + s.timeout + "ms exceeded" : "timeout exceeded";
      const k = s.transitional || rr;
      s.timeoutErrorMessage && (T = s.timeoutErrorMessage), o(new A(
        T,
        k.clarifyTimeoutError ? A.ETIMEDOUT : A.ECONNABORTED,
        t,
        m
      )), m = null;
    }, n === void 0 && r.setContentType(null), "setRequestHeader" in m && u.forEach(r.toJSON(), function(T, k) {
      m.setRequestHeader(k, T);
    }), u.isUndefined(s.withCredentials) || (m.withCredentials = !!s.withCredentials), a && a !== "json" && (m.responseType = s.responseType), c && ([p, g] = Ui(c, !0), m.addEventListener("progress", p)), l && m.upload && ([h, f] = Ui(l), m.upload.addEventListener("progress", h), m.upload.addEventListener("loadend", f)), (s.cancelToken || s.signal) && (d = (x) => {
      m && (o(!x || x.type ? new Dt(null, t, m) : x), m.abort(), m = null);
    }, s.cancelToken && s.cancelToken.subscribe(d), s.signal && (s.signal.aborted ? d() : s.signal.addEventListener("abort", d)));
    const y = Sh(s.url);
    if (y && q.protocols.indexOf(y) === -1) {
      o(new A("Unsupported protocol " + y + ":", A.ERR_BAD_REQUEST, t));
      return;
    }
    m.send(n || null);
  });
}, Ph = (t, e) => {
  const { length: i } = t = t ? t.filter(Boolean) : [];
  if (e || i) {
    let o = new AbortController(), s;
    const n = function(c) {
      if (!s) {
        s = !0, a();
        const d = c instanceof Error ? c : this.reason;
        o.abort(d instanceof A ? d : new Dt(d instanceof Error ? d.message : d));
      }
    };
    let r = e && setTimeout(() => {
      r = null, n(new A(`timeout ${e} of ms exceeded`, A.ETIMEDOUT));
    }, e);
    const a = () => {
      t && (r && clearTimeout(r), r = null, t.forEach((c) => {
        c.unsubscribe ? c.unsubscribe(n) : c.removeEventListener("abort", n);
      }), t = null);
    };
    t.forEach((c) => c.addEventListener("abort", n));
    const { signal: l } = o;
    return l.unsubscribe = () => u.asap(a), l;
  }
}, Ih = function* (t, e) {
  let i = t.byteLength;
  if (i < e) {
    yield t;
    return;
  }
  let o = 0, s;
  for (; o < i; )
    s = o + e, yield t.slice(o, s), o = s;
}, Mh = async function* (t, e) {
  for await (const i of Oh(t))
    yield* Ih(i, e);
}, Oh = async function* (t) {
  if (t[Symbol.asyncIterator]) {
    yield* t;
    return;
  }
  const e = t.getReader();
  try {
    for (; ; ) {
      const { done: i, value: o } = await e.read();
      if (i)
        break;
      yield o;
    }
  } finally {
    await e.cancel();
  }
}, dn = (t, e, i, o) => {
  const s = Mh(t, e);
  let n = 0, r, a = (l) => {
    r || (r = !0, o && o(l));
  };
  return new ReadableStream({
    async pull(l) {
      try {
        const { done: c, value: d } = await s.next();
        if (c) {
          a(), l.close();
          return;
        }
        let h = d.byteLength;
        if (i) {
          let p = n += h;
          i(p);
        }
        l.enqueue(new Uint8Array(d));
      } catch (c) {
        throw a(c), c;
      }
    },
    cancel(l) {
      return a(l), s.return();
    }
  }, {
    highWaterMark: 2
  });
}, Zi = typeof fetch == "function" && typeof Request == "function" && typeof Response == "function", ur = Zi && typeof ReadableStream == "function", Bh = Zi && (typeof TextEncoder == "function" ? /* @__PURE__ */ ((t) => (e) => t.encode(e))(new TextEncoder()) : async (t) => new Uint8Array(await new Response(t).arrayBuffer())), pr = (t, ...e) => {
  try {
    return !!t(...e);
  } catch {
    return !1;
  }
}, Uh = ur && pr(() => {
  let t = !1;
  const e = new Request(q.origin, {
    body: new ReadableStream(),
    method: "POST",
    get duplex() {
      return t = !0, "half";
    }
  }).headers.has("Content-Type");
  return t && !e;
}), hn = 64 * 1024, jo = ur && pr(() => u.isReadableStream(new Response("").body)), Ni = {
  stream: jo && ((t) => t.body)
};
Zi && ((t) => {
  ["text", "arrayBuffer", "blob", "formData", "stream"].forEach((e) => {
    !Ni[e] && (Ni[e] = u.isFunction(t[e]) ? (i) => i[e]() : (i, o) => {
      throw new A(`Response type '${e}' is not supported`, A.ERR_NOT_SUPPORT, o);
    });
  });
})(new Response());
const Nh = async (t) => {
  if (t == null)
    return 0;
  if (u.isBlob(t))
    return t.size;
  if (u.isSpecCompliantForm(t))
    return (await new Request(q.origin, {
      method: "POST",
      body: t
    }).arrayBuffer()).byteLength;
  if (u.isArrayBufferView(t) || u.isArrayBuffer(t))
    return t.byteLength;
  if (u.isURLSearchParams(t) && (t = t + ""), u.isString(t))
    return (await Bh(t)).byteLength;
}, Hh = async (t, e) => {
  const i = u.toFiniteNumber(t.getContentLength());
  return i ?? Nh(e);
}, Fh = Zi && (async (t) => {
  let {
    url: e,
    method: i,
    data: o,
    signal: s,
    cancelToken: n,
    timeout: r,
    onDownloadProgress: a,
    onUploadProgress: l,
    responseType: c,
    headers: d,
    withCredentials: h = "same-origin",
    fetchOptions: p
  } = hr(t);
  c = c ? (c + "").toLowerCase() : "text";
  let f = Ph([s, n && n.toAbortSignal()], r), g;
  const w = f && f.unsubscribe && (() => {
    f.unsubscribe();
  });
  let m;
  try {
    if (l && Uh && i !== "get" && i !== "head" && (m = await Hh(d, o)) !== 0) {
      let k = new Request(e, {
        method: "POST",
        body: o,
        duplex: "half"
      }), D;
      if (u.isFormData(o) && (D = k.headers.get("content-type")) && d.setContentType(D), k.body) {
        const [z, H] = an(
          m,
          Ui(ln(l))
        );
        o = dn(k.body, hn, z, H);
      }
    }
    u.isString(h) || (h = h ? "include" : "omit");
    const b = "credentials" in Request.prototype;
    g = new Request(e, {
      ...p,
      signal: f,
      method: i.toUpperCase(),
      headers: d.normalize().toJSON(),
      body: o,
      duplex: "half",
      credentials: b ? h : void 0
    });
    let y = await fetch(g);
    const x = jo && (c === "stream" || c === "response");
    if (jo && (a || x && w)) {
      const k = {};
      ["status", "statusText", "headers"].forEach((Y) => {
        k[Y] = y[Y];
      });
      const D = u.toFiniteNumber(y.headers.get("content-length")), [z, H] = a && an(
        D,
        Ui(ln(a), !0)
      ) || [];
      y = new Response(
        dn(y.body, hn, z, () => {
          H && H(), w && w();
        }),
        k
      );
    }
    c = c || "text";
    let T = await Ni[u.findKey(Ni, c) || "text"](y, t);
    return !x && w && w(), await new Promise((k, D) => {
      cr(k, D, {
        data: T,
        headers: K.from(y.headers),
        status: y.status,
        statusText: y.statusText,
        config: t,
        request: g
      });
    });
  } catch (b) {
    throw w && w(), b && b.name === "TypeError" && /fetch/i.test(b.message) ? Object.assign(
      new A("Network Error", A.ERR_NETWORK, t, g),
      {
        cause: b.cause || b
      }
    ) : A.from(b, b && b.code, t, g);
  }
}), zo = {
  http: sh,
  xhr: Lh,
  fetch: Fh
};
u.forEach(zo, (t, e) => {
  if (t) {
    try {
      Object.defineProperty(t, "name", { value: e });
    } catch {
    }
    Object.defineProperty(t, "adapterName", { value: e });
  }
});
const un = (t) => `- ${t}`, qh = (t) => u.isFunction(t) || t === null || t === !1, gr = {
  getAdapter: (t) => {
    t = u.isArray(t) ? t : [t];
    const { length: e } = t;
    let i, o;
    const s = {};
    for (let n = 0; n < e; n++) {
      i = t[n];
      let r;
      if (o = i, !qh(i) && (o = zo[(r = String(i)).toLowerCase()], o === void 0))
        throw new A(`Unknown adapter '${r}'`);
      if (o)
        break;
      s[r || "#" + n] = o;
    }
    if (!o) {
      const n = Object.entries(s).map(
        ([a, l]) => `adapter ${a} ` + (l === !1 ? "is not supported by the environment" : "is not available in the build")
      );
      let r = e ? n.length > 1 ? `since :
` + n.map(un).join(`
`) : " " + un(n[0]) : "as no adapter specified";
      throw new A(
        "There is no suitable adapter to dispatch the request " + r,
        "ERR_NOT_SUPPORT"
      );
    }
    return o;
  },
  adapters: zo
};
function mo(t) {
  if (t.cancelToken && t.cancelToken.throwIfRequested(), t.signal && t.signal.aborted)
    throw new Dt(null, t);
}
function pn(t) {
  return mo(t), t.headers = K.from(t.headers), t.data = fo.call(
    t,
    t.transformRequest
  ), ["post", "put", "patch"].indexOf(t.method) !== -1 && t.headers.setContentType("application/x-www-form-urlencoded", !1), gr.getAdapter(t.adapter || gi.adapter)(t).then(function(o) {
    return mo(t), o.data = fo.call(
      t,
      t.transformResponse,
      o
    ), o.headers = K.from(o.headers), o;
  }, function(o) {
    return lr(o) || (mo(t), o && o.response && (o.response.data = fo.call(
      t,
      t.transformResponse,
      o.response
    ), o.response.headers = K.from(o.response.headers))), Promise.reject(o);
  });
}
const fr = "1.8.2", eo = {};
["object", "boolean", "number", "function", "string", "symbol"].forEach((t, e) => {
  eo[t] = function(o) {
    return typeof o === t || "a" + (e < 1 ? "n " : " ") + t;
  };
});
const gn = {};
eo.transitional = function(e, i, o) {
  function s(n, r) {
    return "[Axios v" + fr + "] Transitional option '" + n + "'" + r + (o ? ". " + o : "");
  }
  return (n, r, a) => {
    if (e === !1)
      throw new A(
        s(r, " has been removed" + (i ? " in " + i : "")),
        A.ERR_DEPRECATED
      );
    return i && !gn[r] && (gn[r] = !0, console.warn(
      s(
        r,
        " has been deprecated since v" + i + " and will be removed in the near future"
      )
    )), e ? e(n, r, a) : !0;
  };
};
eo.spelling = function(e) {
  return (i, o) => (console.warn(`${o} is likely a misspelling of ${e}`), !0);
};
function Yh(t, e, i) {
  if (typeof t != "object")
    throw new A("options must be an object", A.ERR_BAD_OPTION_VALUE);
  const o = Object.keys(t);
  let s = o.length;
  for (; s-- > 0; ) {
    const n = o[s], r = e[n];
    if (r) {
      const a = t[n], l = a === void 0 || r(a, n, t);
      if (l !== !0)
        throw new A("option " + n + " must be " + l, A.ERR_BAD_OPTION_VALUE);
      continue;
    }
    if (i !== !0)
      throw new A("Unknown option " + n, A.ERR_BAD_OPTION);
  }
}
const _i = {
  assertOptions: Yh,
  validators: eo
}, ve = _i.validators;
let tt = class {
  constructor(e) {
    this.defaults = e, this.interceptors = {
      request: new nn(),
      response: new nn()
    };
  }
  /**
   * Dispatch a request
   *
   * @param {String|Object} configOrUrl The config specific for this request (merged with this.defaults)
   * @param {?Object} config
   *
   * @returns {Promise} The Promise to be fulfilled
   */
  async request(e, i) {
    try {
      return await this._request(e, i);
    } catch (o) {
      if (o instanceof Error) {
        let s = {};
        Error.captureStackTrace ? Error.captureStackTrace(s) : s = new Error();
        const n = s.stack ? s.stack.replace(/^.+\n/, "") : "";
        try {
          o.stack ? n && !String(o.stack).endsWith(n.replace(/^.+\n.+\n/, "")) && (o.stack += `
` + n) : o.stack = n;
        } catch {
        }
      }
      throw o;
    }
  }
  _request(e, i) {
    typeof e == "string" ? (i = i || {}, i.url = e) : i = e || {}, i = dt(this.defaults, i);
    const { transitional: o, paramsSerializer: s, headers: n } = i;
    o !== void 0 && _i.assertOptions(o, {
      silentJSONParsing: ve.transitional(ve.boolean),
      forcedJSONParsing: ve.transitional(ve.boolean),
      clarifyTimeoutError: ve.transitional(ve.boolean)
    }, !1), s != null && (u.isFunction(s) ? i.paramsSerializer = {
      serialize: s
    } : _i.assertOptions(s, {
      encode: ve.function,
      serialize: ve.function
    }, !0)), i.allowAbsoluteUrls !== void 0 || (this.defaults.allowAbsoluteUrls !== void 0 ? i.allowAbsoluteUrls = this.defaults.allowAbsoluteUrls : i.allowAbsoluteUrls = !0), _i.assertOptions(i, {
      baseUrl: ve.spelling("baseURL"),
      withXsrfToken: ve.spelling("withXSRFToken")
    }, !0), i.method = (i.method || this.defaults.method || "get").toLowerCase();
    let r = n && u.merge(
      n.common,
      n[i.method]
    );
    n && u.forEach(
      ["delete", "get", "head", "post", "put", "patch", "common"],
      (g) => {
        delete n[g];
      }
    ), i.headers = K.concat(r, n);
    const a = [];
    let l = !0;
    this.interceptors.request.forEach(function(w) {
      typeof w.runWhen == "function" && w.runWhen(i) === !1 || (l = l && w.synchronous, a.unshift(w.fulfilled, w.rejected));
    });
    const c = [];
    this.interceptors.response.forEach(function(w) {
      c.push(w.fulfilled, w.rejected);
    });
    let d, h = 0, p;
    if (!l) {
      const g = [pn.bind(this), void 0];
      for (g.unshift.apply(g, a), g.push.apply(g, c), p = g.length, d = Promise.resolve(i); h < p; )
        d = d.then(g[h++], g[h++]);
      return d;
    }
    p = a.length;
    let f = i;
    for (h = 0; h < p; ) {
      const g = a[h++], w = a[h++];
      try {
        f = g(f);
      } catch (m) {
        w.call(this, m);
        break;
      }
    }
    try {
      d = pn.call(this, f);
    } catch (g) {
      return Promise.reject(g);
    }
    for (h = 0, p = c.length; h < p; )
      d = d.then(c[h++], c[h++]);
    return d;
  }
  getUri(e) {
    e = dt(this.defaults, e);
    const i = dr(e.baseURL, e.url, e.allowAbsoluteUrls);
    return nr(i, e.params, e.paramsSerializer);
  }
};
u.forEach(["delete", "get", "head", "options"], function(e) {
  tt.prototype[e] = function(i, o) {
    return this.request(dt(o || {}, {
      method: e,
      url: i,
      data: (o || {}).data
    }));
  };
});
u.forEach(["post", "put", "patch"], function(e) {
  function i(o) {
    return function(n, r, a) {
      return this.request(dt(a || {}, {
        method: e,
        headers: o ? {
          "Content-Type": "multipart/form-data"
        } : {},
        url: n,
        data: r
      }));
    };
  }
  tt.prototype[e] = i(), tt.prototype[e + "Form"] = i(!0);
});
let Vh = class mr {
  constructor(e) {
    if (typeof e != "function")
      throw new TypeError("executor must be a function.");
    let i;
    this.promise = new Promise(function(n) {
      i = n;
    });
    const o = this;
    this.promise.then((s) => {
      if (!o._listeners) return;
      let n = o._listeners.length;
      for (; n-- > 0; )
        o._listeners[n](s);
      o._listeners = null;
    }), this.promise.then = (s) => {
      let n;
      const r = new Promise((a) => {
        o.subscribe(a), n = a;
      }).then(s);
      return r.cancel = function() {
        o.unsubscribe(n);
      }, r;
    }, e(function(n, r, a) {
      o.reason || (o.reason = new Dt(n, r, a), i(o.reason));
    });
  }
  /**
   * Throws a `CanceledError` if cancellation has been requested.
   */
  throwIfRequested() {
    if (this.reason)
      throw this.reason;
  }
  /**
   * Subscribe to the cancel signal
   */
  subscribe(e) {
    if (this.reason) {
      e(this.reason);
      return;
    }
    this._listeners ? this._listeners.push(e) : this._listeners = [e];
  }
  /**
   * Unsubscribe from the cancel signal
   */
  unsubscribe(e) {
    if (!this._listeners)
      return;
    const i = this._listeners.indexOf(e);
    i !== -1 && this._listeners.splice(i, 1);
  }
  toAbortSignal() {
    const e = new AbortController(), i = (o) => {
      e.abort(o);
    };
    return this.subscribe(i), e.signal.unsubscribe = () => this.unsubscribe(i), e.signal;
  }
  /**
   * Returns an object that contains a new `CancelToken` and a function that, when called,
   * cancels the `CancelToken`.
   */
  static source() {
    let e;
    return {
      token: new mr(function(s) {
        e = s;
      }),
      cancel: e
    };
  }
};
function Kh(t) {
  return function(i) {
    return t.apply(null, i);
  };
}
function Wh(t) {
  return u.isObject(t) && t.isAxiosError === !0;
}
const Ro = {
  Continue: 100,
  SwitchingProtocols: 101,
  Processing: 102,
  EarlyHints: 103,
  Ok: 200,
  Created: 201,
  Accepted: 202,
  NonAuthoritativeInformation: 203,
  NoContent: 204,
  ResetContent: 205,
  PartialContent: 206,
  MultiStatus: 207,
  AlreadyReported: 208,
  ImUsed: 226,
  MultipleChoices: 300,
  MovedPermanently: 301,
  Found: 302,
  SeeOther: 303,
  NotModified: 304,
  UseProxy: 305,
  Unused: 306,
  TemporaryRedirect: 307,
  PermanentRedirect: 308,
  BadRequest: 400,
  Unauthorized: 401,
  PaymentRequired: 402,
  Forbidden: 403,
  NotFound: 404,
  MethodNotAllowed: 405,
  NotAcceptable: 406,
  ProxyAuthenticationRequired: 407,
  RequestTimeout: 408,
  Conflict: 409,
  Gone: 410,
  LengthRequired: 411,
  PreconditionFailed: 412,
  PayloadTooLarge: 413,
  UriTooLong: 414,
  UnsupportedMediaType: 415,
  RangeNotSatisfiable: 416,
  ExpectationFailed: 417,
  ImATeapot: 418,
  MisdirectedRequest: 421,
  UnprocessableEntity: 422,
  Locked: 423,
  FailedDependency: 424,
  TooEarly: 425,
  UpgradeRequired: 426,
  PreconditionRequired: 428,
  TooManyRequests: 429,
  RequestHeaderFieldsTooLarge: 431,
  UnavailableForLegalReasons: 451,
  InternalServerError: 500,
  NotImplemented: 501,
  BadGateway: 502,
  ServiceUnavailable: 503,
  GatewayTimeout: 504,
  HttpVersionNotSupported: 505,
  VariantAlsoNegotiates: 506,
  InsufficientStorage: 507,
  LoopDetected: 508,
  NotExtended: 510,
  NetworkAuthenticationRequired: 511
};
Object.entries(Ro).forEach(([t, e]) => {
  Ro[e] = t;
});
function wr(t) {
  const e = new tt(t), i = Wn(tt.prototype.request, e);
  return u.extend(i, tt.prototype, e, { allOwnKeys: !0 }), u.extend(i, e, null, { allOwnKeys: !0 }), i.create = function(s) {
    return wr(dt(t, s));
  }, i;
}
const C = wr(gi);
C.Axios = tt;
C.CanceledError = Dt;
C.CancelToken = Vh;
C.isCancel = lr;
C.VERSION = fr;
C.toFormData = Qi;
C.AxiosError = A;
C.Cancel = C.CanceledError;
C.all = function(e) {
  return Promise.all(e);
};
C.spread = Kh;
C.isAxiosError = Wh;
C.mergeConfig = dt;
C.AxiosHeaders = K;
C.formToJSON = (t) => ar(u.isHTMLForm(t) ? new FormData(t) : t);
C.getAdapter = gr.getAdapter;
C.HttpStatusCode = Ro;
C.default = C;
const {
  Axios: dp,
  AxiosError: hp,
  CanceledError: up,
  isCancel: pp,
  CancelToken: gp,
  VERSION: fp,
  all: mp,
  Cancel: wp,
  isAxiosError: vp,
  spread: bp,
  toFormData: yp,
  AxiosHeaders: xp,
  HttpStatusCode: $p,
  formToJSON: Ep,
  getAdapter: Ap,
  mergeConfig: kp
} = C, fi = N`
  * {
    margin: 0;
    padding:0;
    word-break: break-word;
    font-family: var(--jds-font-family);
  }
  li {
    list-style:none;
  }
  .text-overflow-1 {
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text-overflow-2 {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .text-overflow-3 {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }


  jds-dialog .detail::-webkit-scrollbar-thumb {
    background-color: #C9C9CB;
    border-radius: 10px;
  }
  jds-dialog .detail::-webkit-scrollbar-track {
    background-color: transparent;
  }
  jds-dialog .detail::-webkit-scrollbar {
    width: 6px;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-container){
    background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
    background-size: 70px 70px;
    position: relative;
    height: 560px;
    border-radius: 20px;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
    display: block;
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    top: 8px;
    bottom: 8px;
    background: #fff;
    border-radius: 20px;
    z-index: -1;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog:not(#rulesDialog)::part(dialog-container){
      background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
      background-size: 4.86111111111111vw 4.86111111111111vw;
      position: relative;
      height: 39.8888888888889vw;
      border-radius: 1.38888888888889vw;
    }
    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      display: block;
      content: '';
      position: absolute;
      left: 0.555555555555556vw;
      right: 0.555555555555556vw;
      top: 0.555555555555556vw;
      bottom: 0.555555555555556vw;
      background: #fff;
      border-radius: 1.38888888888889vw;
      z-index: -1;
    }
    jds-dialog .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 0.694444444444444vw;
    }
    jds-dialog .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    jds-dialog .detail::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog:not(#rulesDialog)::part(dialog-container){
      background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
      background-size: 70px 70px;
      position: relative;
      height: max-content;
      border-radius: 5.33333333333333vw;
    }
    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      display: block;
      content: '';
      position: absolute;
      left: 1.06666666666667vw;
      right: 1.06666666666667vw;
      top: 1.06666666666667vw;
      bottom: 1.06666666666667vw;
      background: #fff;
      border-radius: 5.33333333333333vw;
      z-index: -1;
    }
    jds-dialog .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 1.33333333333333vw;
    }
    jds-dialog .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    jds-dialog .detail::-webkit-scrollbar {
      width: 0.8vw;
    }
  }
`, Jh = N`
  jds-dialog#rulesDialog{
    text-align: left;
  }
  jds-dialog#rulesDialog::part(dialog-container){
    min-height: 320px;
    max-height: 560px;
  }
  jds-dialog#rulesDialog::part(dialog-content) {
    padding-left: 48px;
    padding-right: 10px;
    margin-right: 0;
  }
  jds-dialog#rulesDialog .dialog-content .detail {
    padding-right: 38px;
    max-height: 436px;
    overflow-y: auto;
  }
  jds-dialog#rulesDialog .dialog-content .detail img {
    width: auto;
    max-width: 100% !important;
    height: auto;
  }
  jds-dialog::part(dialog-content) {
    overflow: unset;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog#rulesDialog{
      text-align: left;
    }
    jds-dialog#rulesDialog::part(dialog-container){
      min-height: 22.2222222222222vw;
      max-height: 39.8888888888889vw;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 0.694444444444444vw;
      margin-right: 0;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.63888888888889vw;
      max-height: 30.2777777777778vw;
      overflow-y: auto;
    }
    jds-dialog#rulesDialog .dialog-content .detail img {
      width: auto;
      max-width: 100% !important;
      height: auto;
    }
    jds-dialog::part(dialog-content) {
      overflow: unset;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog#rulesDialog::part(dialog-container){
      min-height: 85.3333333333333vw;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
      margin-right: 0;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 106.666666666667vw;
      overflow-y: auto;
    }
    jds-dialog#rulesDialog .dialog-content .detail img {
      width: auto;
      max-width: 100% !important;
      height: auto;
    }
    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }
  }
`, Gh = N`
  jds-dialog:not(#rulesDialog) {
    text-align:center;
  }
  jds-dialog:not(#rulesDialog)::part(dialog-content) {
    padding-left: 48px;
    padding-right: 18px;
    margin-right: 0;
    overflow: unset;
  }
  jds-dialog:not(#rulesDialog) .detail {
    padding-right: 30px;
    max-height: 380px;
    overflow-y: auto;
  }
  jds-dialog:not(#rulesDialog)#thsDialog .detail {
    max-height: 360px;
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 1.25vw;
      margin-right: 0;
      overflow: unset;
    }
    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.08333333333333vw;
      max-height: 26.3888888888889vw;
      overflow-y: auto;
    }
    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 25vw;
    }
  }
  @media screen and (max-width: 1023.98px){
    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
      margin-right: 0;
      overflow: unset;
    }
    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.66666666666667vw;
      max-height: 88vw;
      overflow-y: auto;
    }
    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }
  }
`, Xh = N`
  :host {
    --jds-rules-dialog-width: 600px;
    --jds-prize-dialog-width: 640px;
  }
  :host .jds-container{
    width: 1200px;
    height: 600px;
    margin: 0 auto;
    padding: 48px 46px;
    position: relative;
    z-index: 1;
    display:flex;
    justify-content: space-around;
    background-repeat: no-repeat;
    background-size: cover;
    box-sizing: border-box;
  }
  :host .wrapper{
    padding: 88px 0 66px;
    background-color: #FDF3E5;
  }
  .bg-img{
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }
  .rg-box{
    display:flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: 100%;
  }
  jds-dialog .dialog-tips {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  jds-dialog .dialog-title{
    margin-right: 30px;
    height: 72px;
    margin-bottom: 18px;
    font-size: 28px;
    line-height: 36px;
    font-weight: 600;
    color: #1A1A1A;
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog#rulesDialog .dialog-title {
    height: auto;
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog .ths-tips .dialog-title {
    height: 100px;
    font-size: 24px;
    line-height: 32px;
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  jds-dialog .dialog-image{
    margin-top: 24px;
    margin-bottom: 16px;
    width: 248px;
    height: 166px;
    object-fit: contain;
  }
  jds-dialog .notes-full {
    font-size:14px;
    line-height: 22px;
    width: 100%;
    color: rgba(12,11,11,1);
  }
  jds-dialog .notes-box {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 24px;
    margin-top: 16px;
    margin-bottom: 10px;
  }
  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules  {
    flex-shrink: 0;
    position: relative;
    overflow: visible;
    display: flex;
  }
  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules {
    font-size: 16px;
    font-weight: 500;
    text-decoration-line: underline;
    text-underline-offset: 4px;
    color: #D98B24;
    cursor: pointer;
  }
  .rules-content {
    display: none;
    box-sizing: border-box;
    width: 420px;
    text-align: left;
    color: #444444;
    font-size: 16px;
    line-height: 24px;
    font-weight: 400;
    background-color: #fff;
    box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.27);
    border-radius: 12px;
    border: 1px solid #EEEEEE;
    padding: 20px 10px 26px 32px;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 501;
  }
  .rules-content::after {
    display: block;
    content: '';
    position: absolute;
    left: 100%;
    bottom: 10px;
    z-index: 0;
    transform: translateY(-50%);
    border-color: transparent;
    border-left-color: rgba(255, 255, 255, 1);
    border-style: solid;
    background-color: transparent;
    filter: drop-shadow(5px 0px 3px rgba(0, 0, 0, 0.17));
    border-width: 13px;
    width: 0vw;
    height: 0vw
  }
  .rules-content h4.title {
    text-align: left;
    font-weight: 600;
    font-size: 24px;
    color: #666;
    padding-bottom: 10px;
    border-bottom: 1px solid #DDD;
    margin-bottom: 14px;
    margin-right: 6px;
  }
  .rules-content .detail {
    padding-right: 6px;
    max-height: 360px;
    overflow-y: auto;
  }
  .rules-content .detail::-webkit-scrollbar-thumb {
    background-color: #C9C9CB;
    border-radius: 10px;
  }
  .rules-content .detail::-webkit-scrollbar-track {
    background-color: transparent;
  }
  .rules-content .detail::-webkit-scrollbar {
    width: 6px;
  }
  jds-dialog .notes-box .notes{
    text-align: left;
    font-size:14px;
    line-height: 22px;
    width: fit-content;
    max-width: 364px;
    color: rgba(12,11,11,1);
  }
  jds-dialog jds-button::part(button) {
    height: 48px;
    line-height: 48px;
    min-width: 180px;
    font-size: 16px;
    font-weight: 600;
    margin-top: 38px;
  }


  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    :host {
      --jds-rules-dialog-width: 41.6666666666667vw;
      --jds-prize-dialog-width: 44.4444444444444vw;
    }
    :host .jds-container{
      width: 83.3333333333333vw;
      height: 41.6666666666667vw;
      margin: 0 auto;
      padding: 3.33333333333333vw 3.19444444444444vw;
      position: relative;
      z-index: 1;
      display:flex;
      justify-content: space-around;
      background-repeat: no-repeat;
      background-size: cover;
      box-sizing: border-box;
    }
    :host .wrapper{
      padding: 6.11111111111111vw 0 4.58333333333333vw;
      background-color: #FDF3E5;
    }
    .bg-img{
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -1;
    }
    .rg-box{
      display:flex;
      justify-content: center;
      align-items: center;
      width: auto;
      height: 100%;
    }
    jds-dialog .dialog-tips {
      display: flex;
      justify-content: center;
      align-items: center;
    }
    jds-dialog .dialog-title{
      margin-right: 2.08333333333333vw;
      height: 5vw;
      margin-bottom: 1.25vw;
      font-size: 1.94444444444444vw;
      line-height: 2.5vw;
      font-weight: 600;
      color: #1A1A1A;
    }
    jds-dialog#rulesDialog .dialog-title {
      height: auto;
    }
    jds-dialog .ths-tips .dialog-title {
      height: 6.94444444444444vw;
      font-size: 1.66666666666667vw;
      line-height: 2.22222222222222vw;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog .dialog-image{
      margin-top: 1.66666666666667vw;
      margin-bottom: 1.11111111111111vw;
      width: 17.2222222222222vw;
      height: 11.5277777777778vw;
      object-fit: contain;
    }
    jds-dialog .notes-full {
      font-size: 0.972222222222222vw;
      line-height: 1.52777777777778vw;
      width: 100%;
      color: rgba(12,11,11,1);
    }
    jds-dialog .notes-box {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 1.66666666666667vw;
      margin-top: 1.11111111111111vw;
      margin-bottom: 0.694444444444444vw;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules  {
      flex-shrink: 0;
      position: relative;
      overflow: visible;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 1.11111111111111vw;
      font-weight: 500;
      text-decoration-line: underline;
      text-underline-offset: 0.277777777777778vw;
      color: #D98B24;
      cursor: pointer;
    }
    .rules-content {
      display: none;
      box-sizing: border-box;
      width: 29.1666666666667vw;
      text-align: left;
      color: #444444;
      font-size: 1.11111111111111vw;
      line-height: 1.66666666666667vw;
      font-weight: 400;
      background-color: #fff;
      box-shadow: 0px 0.138888888888889vw 0.555555555555556vw 0px rgba(0,0,0,0.27);
      border-radius: 0.833333333333333vw;
      border: 1px solid #EEEEEE;
      padding: 1.38888888888889vw 0.694444444444444vw 1.80555555555556vw 2.22222222222222vw;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 501;
    }
    .rules-content::after {
      display: block;
      content: '';
      position: absolute;
      left: 100%;
      bottom: 0.972222222222222vw;
      z-index: 0;
      transform: translateY(-50%);
      border-color: transparent;
      border-left-color: rgba(255, 255, 255, 1);
      border-style: solid;
      background-color: transparent;
      filter: drop-shadow(0.347222222222222vw 0px 0.208333333333333vw rgba(0, 0, 0, 0.17));
      border-width: 0.902777777777778vw;
      width: 0vw;
      height: 0vw
    }
    .rules-content h4.title {
      text-align: left;
      font-weight: 600;
      font-size: 1.66666666666667vw;
      color: #666;
      padding-bottom: 0.694444444444444vw;
      border-bottom: 1px solid #DDD;
      margin-bottom: 0.972222222222222vw;
      margin-right: 0.416666666666667vw;
    }
    .rules-content .detail {
      padding-right: 0.416666666666667vw;
      max-height: 25vw;
      overflow-y: auto;
    }
    .rules-content .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 0.694444444444444vw;
    }
    .rules-content .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    .rules-content .detail::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
    jds-dialog .notes-box .notes{
      text-align: left;
      font-size: 0.972222222222222vw;
      line-height: 1.52777777777778vw;
      width: fit-content;
      max-width: 25.2777777777778vw;
      color: rgba(12,11,11,1);
    }
    jds-dialog jds-button::part(button) {
      height: 3.33333333333333vw;
      line-height: 3.33333333333333vw;
      min-width: 12.5vw;
      font-size: 1.11111111111111vw;
      font-weight: 600;
      margin-top: 2.63888888888889vw;
    }
  }


  @media screen and (max-width: 1023.98px){
    :host {
      --jds-rules-dialog-width: 600px;
      --jds-prize-dialog-width: 640px;
    }
    :host .jds-container{
      width: 91.4666666666667vw;
      height: 170.666666666667vw;
      margin: 0 auto;
      padding: 4.26666666666667vw 4.8vw;
      position: relative;
      z-index: 1;
      display:flex;
      align-items: center;
      flex-direction: column;
      justify-content: space-around;
      background-repeat: no-repeat;
      background-size: cover;
      box-sizing: border-box;
    }
    :host .wrapper{
      padding: 15.4666666666667vw 0 16.9333333333333vw;
      background-color: #FDF3E5;
    }
    jds-dialog .dialog-title {
      margin-right: 2.66666666666667vw;
      margin-bottom: 5.33333333333333vw;
      font-weight: 600;
      font-size: 5.33333333333333vw;
      line-height: 6.4vw;
      color: #1A1A1A;
      height: unset;
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog#rulesDialog .dialog-title {
      height: auto;
    }
    jds-dialog .ths-tips .dialog-title {
      height: 25.6vw;
      font-size: 5.33333333333333vw;
      line-height: 6.4vw;
      display: -webkit-box;
      line-clamp: 4;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    jds-dialog .dialog-image {
      margin-bottom: 4.26666666666667vw;
      width: 53.3333333333333vw;
      height: 35.7333333333333vw;
      object-fit: contain;
    }
    jds-dialog .notes-box {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 6.4vw;
      margin-top: 4.26666666666667vw;
      margin-bottom: 6.4vw;
      padding-left: 1.33333333333333vw;
      padding-right: 4vw;
    }
    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 3.73333333333333vw;
      font-weight: 500;
      text-decoration-line: underline;
      text-underline-offset: 4px;
      color: #D98B24;
      cursor: pointer;
    }
    jds-dialog .notes-box .notes {
      font-size: 3.46666666666667vw;
      line-height: 4.26666666666667vw;
      color: rgba(12,11,11,1);
    }

    .rules-content {
      display: none;
      box-sizing: border-box;
      width: 77.3333333333333vw;
      text-align: left;
      color: #444444;
      font-size: 3.73333333333333vw;
      line-height: 5.33333333333333vw;
      font-weight: 400;
      background-color: #fff;
      box-shadow: 0px 2px 8px 0px rgba(0,0,0,0.27);
      border-radius: 2.66666666666667vw;
      border: 1px solid #EEEEEE;
      padding: 4.26666666666667vw 1.6vw 5.33333333333333vw 4.26666666666667vw;
      position: fixed;
      left: 0;
      top: 0;
      z-index: 501;
    }
    .rules-content::after {
      display: none;
      content: '';
      position: absolute;
      top: calc(100% + 10px);
      left: 80%;
      z-index: 0;
      transform: translateY(-50%);
      border-color: transparent;
      border-top-color: rgba(255, 255, 255, 1);
      border-style: solid;
      background-color: transparent;
      filter: drop-shadow(0px 5px 3px rgba(0, 0, 0, 0.17));
      border-width: 10px;
      width: 0vw;
      height: 0vw
    }
    .rules-content h4.title {
      text-align: left;
      font-weight: 600;
      font-size: 5.33333333333333vw;
      color: #333;
      padding-bottom: 2.66666666666667vw;
      border-bottom: 1px solid #DDD;
      margin-bottom: 2.66666666666667vw;
      margin-right: 2.66666666666667vw;
    }
    .rules-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 80vw;
      overflow-y: auto;
    }
    .rules-content .detail::-webkit-scrollbar-thumb {
      background-color: #C9C9CB;
      border-radius: 1.33333333333333vw;
    }
    .rules-content .detail::-webkit-scrollbar-track {
      background-color: transparent;
    }
    .rules-content .detail::-webkit-scrollbar {
      width: 0.8vw;
    }
    jds-dialog .notes-full {
      font-size: 3.46666666666667vw;
      line-height: 4.26666666666667vw;
      width: 100%;
      color: rgba(12,11,11,1);
    }
    jds-dialog jds-button::part(button) {
      height: 10.6666666666667vw;
      line-height: 10.6666666666667vw;
      min-width: 42.6666666666667vw;
      font-size: 3.73333333333333vw;
      font-weight: 600;
      margin-top: 6.4vw;
    }
  }
`;
function Nt(t) {
  const e = new RegExp(`(^| )${t}=([^;]*)(;|$)`), i = document.cookie.match(e);
  return i ? decodeURI(i[2]) : null;
}
function ht(t, e = 100) {
  let i = null;
  return function(...o) {
    i && clearTimeout(i), i = setTimeout(() => {
      t.apply(this, o);
    }, e);
  };
}
function vr(t) {
  return new Promise((e, i) => {
    t && (typeof t == "number" || typeof t == "string") ? navigator.clipboard.writeText(String(t)).then(() => e(String(t))).catch(i) : i("The parameter must be Number or String.");
  });
}
function gt(t) {
  return new Promise((e) => setTimeout(e, t));
}
function br(t, e) {
  var i;
  if (window.dataLayer || Array.isArray(window.dataLayer)) {
    const o = ((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr) || Nt("_jk_cr") || localStorage.getItem("JACKERY_ID_CR") || "US";
    Object.assign(e, { CR: o, country: o }), window.dataLayer.push({ event_parameters: null }), window.dataLayer.push({
      event: "ga4Event",
      event_name: t,
      event_parameters: {
        current_page: window.location.href,
        ...e
      }
    });
  }
}
function De(t) {
  return br("section_pointslottery_user_clicks", t);
}
function yr(t = window.location.href) {
  const e = new URL(t), i = new URLSearchParams(e.search), o = {};
  for (const [s, n] of i.entries())
    if (s in o) {
      const r = o[s];
      Array.isArray(r) ? r.push(n) : o[s] = [r, n];
    } else
      o[s] = n;
  return o;
}
function Qh(t) {
  return /^[a-zA-Z0-9]+([._%+-][a-zA-Z0-9]+)*@([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/.test(t);
}
function Kt(t) {
  return t ? t.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/\s+/g, " ").trim() : "";
}
function ne(t, e = "info", i = 3) {
  const o = `<jds-toast variant="${e}" countdownTime="${i}" icon closed>${t}</jds-toast>`, r = new DOMParser().parseFromString(o, "text/html").body.firstChild;
  r ? (document.body.appendChild(r), Object.assign(r.style, {
    position: "fixed",
    top: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: "var(--jds-index-notification)"
  }), r.open(), setTimeout(() => {
    r.remove();
  }, i * 1e3)) : console.error("创建Toast失败！");
}
window.showToast = ne;
var Zh = Object.defineProperty, eu = Object.getOwnPropertyDescriptor, to = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? eu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Zh(e, i, s), s;
};
let Ct = class extends re {
  constructor() {
    super(...arguments), this.pcBgImg = "", this.mbBgImg = "", this.alt = "";
  }
  render() {
    return v`
      <picture>
        <source media="(min-width: 1024px)" srcset="${this.pcBgImg}">
        <source media="(max-width: 1023.98px)" srcset="${this.mbBgImg}">
        <img class="lucky-bg-img" src="${this.mbBgImg}" alt="${this.alt}" loading="lazy">
      </picture>
    `;
  }
};
Ct.styles = N`
    .lucky-bg-img {
      width: 1200px;
      min-height: 600px;
      height: 100%;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      .lucky-bg-img {
        width: 83.3333333333333vw;
        min-height: 41.6666666666667vw;
      }
    }
    @media screen and (max-width: 1023.98px){
      .lucky-bg-img {
        width: 91.4666666666667vw;
        min-height: 170.666666666667vw;
      }
    }
  `;
to([
  $({ type: String, reflect: !1 })
], Ct.prototype, "pcBgImg", 2);
to([
  $({ type: String, reflect: !1 })
], Ct.prototype, "mbBgImg", 2);
to([
  $({ type: String, reflect: !1 })
], Ct.prototype, "alt", 2);
Ct = to([
  pe("lucky-bg-img")
], Ct);
var tu = Object.defineProperty, iu = Object.getOwnPropertyDescriptor, cs = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? iu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && tu(e, i, s), s;
};
let ai = class extends re {
  constructor() {
    super(...arguments), this.isLogined = !1, this.activityInfo = {
      mainTitleType: 0,
      mainTitle: "",
      subTitle: "",
      btnTitleNologin: "",
      btnTitleLogin: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return v`
      <div class="lf-info">
        <div class="title">
          ${this.activityInfo.mainTitleType === 0 ? v`<h2 class="title-content text-overflow-3" .innerHTML="${this.activityInfo.mainTitle}"></h2>` : v`<img src="${this.activityInfo.mainTitle}" loading="lazy"/>`}
        </div>
        <div class="subtitle text-overflow-2" .innerHTML="${this.activityInfo.subTitle}"></div>
        ${this.isLogined ? v`<div class="tips tips-one text-overflow-2" .innerHTML="${this.activityInfo.btnTitleLogin}"></div>` : v`<div class="tips tips-one text-overflow-2" .innerHTML="${this.activityInfo.btnTitleNologin}"></div>`}
        <div class="pc-show">
          <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
          ${this.activityInfo.ruleTitle ? v`
                <div class="rule-text" @click="${this.showRulesDialog}">
                  <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                  <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                    <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                  </svg>
                </div>
              ` : ""}
        </div>
      </div>
    `;
  }
};
ai.styles = [fi, N`
    .pc-show {
      display: block;
    }
    .lf-info{
      width: 460px;
      height: 100% ;
      text-align: center;
    }
    .title{
      display: flex;
      justify-content: center;
      align-items: center;
      width: 460px;
      height: 238px;
      overflow: hidden;
    }
    .title img{
      width: 100%;
      height: auto;
    }
    .title-content{
      padding: 0 45px;
      font-weight: bold;
      font-style: normal;
      font-size: 56px;
      color: #FD5000;
      line-height: 64px;
      height: 200px;
      place-content: center;
    }
    .subtitle{
      margin-top: 6px;
      width: 460px;
      font-weight: bold;
      font-size: 28px;
      color: #FD5000;
      line-height: 36px;
      font-style: normal;
    }
    .tips{
      width: 460px;
      font-weight: 500;
      font-size: 16px;
      line-height: 24px;
      font-style: normal;
    }
    .tips-one{
      margin-top: 8px;
      color: #fd5000;
    }
    .tips-two{
      margin-top: 40px;
      color: #1A1A1A;
      text-align: center;
    }
    .rule-text {
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 10px;
      margin-top: 16px;
      width:100%;
      font-weight: 500;
      font-size: 20px;
      color: #1A1A1A;
      line-height: 20px;
      font-style: normal;
      cursor: pointer;
    }
    .arrow {
      width: 20px;
      height: 20px;
      color: #1A1A1A;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      .pc-show {
        display: block;
      }
      .lf-info{
        width: 31.9444444444444vw;
        height: 100% ;
        text-align: center;
      }
      .title{
        display: flex;
        justify-content: center;
        align-items: center;
        width: 31.9444444444444vw;
        height: 16.5277777777778vw;
        overflow: hidden;
      }
      .title img{
        width: 100%;
        height: auto;
      }
      .title-content{
        padding: 0 3.125vw;
        font-weight: bold;
        font-style: normal;
        font-size: 3.88888888888889vw;
        color: #FD5000;
        line-height: 4.44444444444444vw;
        height: 13.8888888888889vw;
        place-content: center;
      }
      .subtitle{
        margin-top: 0.416666666666667vw;
        width: 31.9444444444444vw;
        font-weight: bold;
        font-size: 1.94444444444444vw;
        color: #FD5000;
        line-height: 2.5vw;
        font-style: normal;
      }
      .tips{
        width: 31.9444444444444vw;
        font-weight: 500;
        font-size: 1.11111111111111vw;
        line-height: 1.66666666666667vw;
        font-style: normal;
      }
      .tips-one{
        margin-top: 0.555555555555556vw;
        color: #fd5000;
      }
      .tips-two{
        margin-top: 2.77777777777778vw;
        color: #1A1A1A;
        text-align: center;
      }
      .rule-text {
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 0.694444444444444vw;
        margin-top: 1.11111111111111vw;
        width:100%;
        font-weight: 500;
        font-size: 1.38888888888889vw;
        color: #1A1A1A;
        line-height: 1.38888888888889vw;
        font-style: normal;
        cursor: pointer;
      }
      .arrow {
        width: 1.38888888888889vw;
        height: 1.38888888888889vw;
        color: #1A1A1A;
      }
    }
    @media screen and (max-width: 1023.98px){
      .pc-show {
        display: none;
      }
      .lf-info{
        width: 80.8vw;
        display: flex;
        align-items: center;
        flex-direction: column;
      }
      .title{
        width: 61.3333333333333vw;
        height: 31.7333333333333vw;
        overflow: hidden;
      }
      .title-content{
        padding: 0 6vw;
        font-weight: bold;
        font-style: normal;
        font-size: 7.46666666666667vw;
        color: #FD5000;
        line-height: 8.53333333333333vw;
        height: 26.6666666666667vw;
        place-content: center;
      }
      .subtitle{
        margin-top: 0.8vw;
        width: 80.8vw;
        font-weight: bold;
        font-size: 4.8vw;
        color: #FD5000;
        line-height: 5.86666666666667vw;
        font-style: normal;
      }
      .tips{
        width: 61.3333333333333vw;
        font-weight: 500;
        font-size: 3.46666666666667vw;
        line-height: 4.26666666666667vw;
        font-style: normal;
      }
      .tips-one{
        margin: 1.33333333333333vw 0;
        color: #fd5000;
      }
      .tips-two{
        margin-top: 4.26666666666667vw;
        color: #1A1A1A;
        font-size: 3.46666666666667vw;
        line-height: 5.33333333333333vw;
      }
      .rule-text{
        display: flex;
        align-items: center;
        justify-content: center;
        column-gap: 1.6vw;
        margin-top: 3.2vw;
        width:100%;
        font-weight: 500;
        font-size: 4vw;
        color: #1A1A1A;
        line-height: 4vw;
        font-style: normal;
        cursor: pointer;
      }
    }
  `];
cs([
  $({ type: Boolean })
], ai.prototype, "isLogined", 2);
cs([
  $({ type: Object, reflect: !1 })
], ai.prototype, "activityInfo", 2);
ai = cs([
  pe("snippet-left-info")
], ai);
const wo = "data:image/png;base64,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", ds = N`
  .tips-two{
    margin-top: 40px;
    color: #1A1A1A;
    text-align: center;
  }
  .rule-text {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 10px;
    margin-top: 16px;
    width:100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 1;
    color: #1A1A1A;
    font-style: normal;
    cursor: pointer;
  }
  .grid {
    display: grid;
    gap: 12px;
    position: relative;
    margin: 0;
    text-align: center;
    box-sizing: border-box;
  }
  .prize {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 5px solid #fff;
    border-radius: 12px;
    padding: 8px 8px 6px;
    transition: transform 0.3s ease, border-color 0.3s ease;
    background-color: #fff;
    box-sizing: border-box;
  }
  .prize img {
    margin-bottom: 6px;
    width: 138px;
    height: 92px;
    object-fit: cover;
  }
  .prize .name, 
  .login-btn-tips {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 44px;
    font-size: 16px;
    font-weight: 600;
    line-height: 20px;
    color:#FE651F;
  }
  .prize .name p,
  .login-btn-tips p {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .selected {
    border:5px solid #FD891C;
    background-color:#FFEFB5;
  }
  .btn {
    display: flex;
    align-items: center;
    justify-content: center;
    position:relative;
    padding: 0 20px;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    color: #FFFFFF;
    background-color: #fd5000;
    cursor: pointer;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
  }
  .btn::before{
    content: " ";
    display: block;
    width: 67px;
    height: 43px;
    background-image: url(${ki(wo)});
    background-size: cover;
    position: absolute;
    top:6px;
    left:6px;
    z-index:1;
  }
  .btn:disabled {
    background-color: #ef6114;
    cursor: wait;
  }
  .btn:active {
    transform: scale(1.1);
  }
  @media screen and (min-width: 1024px) and (max-width: 1440px) {
    .tips-two{
      margin-top: 2.77777777777778vw;
    }
    .rule-text {
      column-gap: 0.694444444444444vw;
      margin-top: 1.11111111111111vw;
      font-size: 1.38888888888889vw;
    }
    .grid {
      gap: 0.833333333333333vw;
    }
    .prize {
      border: 0.347222222222222vw solid #fff;
      border-radius: 0.833333333333333vw;
      padding: 0.555555555555556vw 0.555555555555556vw 0.416666666666667vw;
    }
    .prize img {
      margin-bottom: 0.416666666666667vw;
      width: 9.58333333333333vw;
      height: 6.38888888888889vw;
    }
    .prize .name, 
    .login-btn-tips {
      height: 3.05555555555556vw;
      font-size: 1.11111111111111vw;
      line-height: 1.38888888888889vw;
    }
    .selected {
      border: 0.347222222222222vw solid #FD891C;
    }
    .btn {
      padding: 0 1.38888888888889vw;
      border-radius: 0.555555555555556vw;
      box-shadow: 0 0.138888888888889vw 0.347222222222222vw rgba(0, 0, 0, 0.1);
    }
    .btn::before{
      content: " ";
      display: block;
      width: 4.65277777777778vw;
      height: 2.98611111111111vw;
      background-image: url(${ki(wo)});
      background-size: cover;
      position: absolute;
      top: 0.416666666666667vw;
      left: 0.416666666666667vw;
      z-index: 1;
    }
    .btn:active {
      transform: scale(1.1);
    }
  }
  @media screen and (max-width: 1023.98px){
    .tips-two{
      margin-top: 4.26666666666667vw;
      color: #1A1A1A;
      font-size: 3.46666666666667vw;
      line-height: 5.33333333333333vw;
    }
    .rule-text{
      display: flex;
      align-items: center;
      justify-content: center;
      column-gap: 1.6vw;
      margin-top: 3.2vw;
      width:100%;
      font-weight: 500;
      font-size: 4vw;
      color: #1A1A1A;
      line-height: 4vw;
      font-style: normal;
      cursor: pointer;
    }
    .arrow {
      width: 4vw;
      height: 4vw;
      color: #1A1A1A;
    }
    .grid {
      gap: 1.6vw;
    }
    .prize {
      border: 0.666666666666667vw solid #fff;
      border-radius: 1.6vw;
      padding: 1.06666666666667vw 1.06666666666667vw 0.8vw;
    }
    .prize img {
      margin-bottom: 0.266666666666667vw;
      width: 18.4vw;
      height: 12.2666666666667vw;
      object-fit: cover;
    }
    .prize .name,
    .login-btn-tips {
      width: 100%;
      height: 10.6666666666667vw;
      font-weight: 600;
      font-size: 3.2vw;
      line-height: 3.73333333333333vw;
      color:#FE651F;
    }
    .prize .name p,
    .login-btn-tips p {
      display: -webkit-box;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .selected {
      border:5px solid #FD891C;
      background-color:#FFEFB5;
    }
    .btn {
      position:relative;
      padding: 0 20px;
      border: none;
      border-radius: 8px;
      font-weight: bold;
      color: #FFFFFF;
      background-color: #fd5000;
      cursor: pointer;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }
    .btn::before{
      content: " ";
      display: block;
      width: 67px;
      height: 43px;
      background-image: url(${ki(wo)});
      background-size: cover;
      position: absolute;
      top:6px;
      left:6px;
      z-index:1;
    }
    .btn:disabled {
      background-color: #ef6114;
      cursor: wait
    }
    .btn:active {
      transform: scale(1.1);
    }
  }
`;
var ou = Object.defineProperty, su = Object.getOwnPropertyDescriptor, Lt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? su(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && ou(e, i, s), s;
};
let He = class extends re {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("start-draw"));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return v`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;" >
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 3; grid-row: 1;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 3; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
        <button class="btn" @click="${this.startDraw}" ?disabled="${this.isDrawing}" style="background-color:${this.activityInfo.btnBgValue};">
          ${this.isDrawing ? v`<jds-spinner size="var(--btn-size)" color="${this.activityInfo.btnColor || "#FFF"}"></jds-spinner>` : v`
                <div class="btn-txt text-overflow-1 ${this.isLogined || !this.activityInfo.guideText ? "btn-text-center" : "btn-text-end"}" style="color:${this.activityInfo.btnColor || "#FFF"}">${this.activityInfo.btnTitle}</div>
                ${this.isLogined || !this.activityInfo.guideText ? "" : v`<div class="login-btn-tips" .innerHTML="${this.activityInfo.guideText}"></div>`}
              `}
        </button>
        <div class="prize ${this.currentIndex === 4 ? "selected" : ""}" style="grid-column: 3; grid-row: 3;">
          <img src="${this.prizes[4].image}" alt="${this.prizes[4].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[4].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 5 ? "selected" : ""}" style="grid-column: 2; grid-row: 3;">
          <img src="${this.prizes[5].image}" alt="${this.prizes[5].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[5].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 6 ? "selected" : ""}" style="grid-column: 1; grid-row: 3;">
          <img src="${this.prizes[6].image}" alt="${this.prizes[6].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[6].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 7 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[7].image}" alt="${this.prizes[7].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[7].name}</p>
          </div>
        </div>
      </div>
      <div class="mb-show">
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? v`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
He.styles = [fi, ds, N`
    :host{
      width: 606px;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(3, 1fr);
      width: 606px;
      height: 505px;
    }
    .prize{
      padding: 4px 8px 4px;
    }
    .btn {
      --btn-size: 40px;
      grid-column: 2;
      grid-row: 2;
      border-radius: 16px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .btn-txt{
      width: calc(100% + 40px);
      font-size: var(--btn-size);
      line-height: 56px;
      margin-top: 32px;
      margin-bottom: 6px;
    }
    .btn-text-center {
      align-items: center;
      margin-top: 0;
      margin-bottom: 0;
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 42.0833333333333vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(3, 1fr);
        width: 42.0833333333333vw;
        height: 35.0694444444444vw;
      }
      .prize{
        padding: 0.277777777777778vw 0.555555555555556vw 0.277777777777778vw;
      }
      .btn {
        --btn-size: 2.77777777777778vw;
        grid-column: 2;
        grid-row: 2;
        border-radius: 1.11111111111111vw;
      }
      .btn-txt{
        width: calc(100% + 2.77777777777778vw);
        font-size: var(--btn-size);
        line-height: 3.88888888888889vw;
        margin-top: 2.22222222222222vw;
        margin-bottom: 0.416666666666667vw;
      }
      .btn-text-center {
        align-items: center;
        margin-top: 0;
        margin-bottom: 0;
      }
    }
    @media screen and (max-width: 1023.98px){
      :host{
        width: 80.8vw;
      }
      .mb-show {
        display: block;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 80.8vw;
        height: 80.8vw;
      }
      .prize {
        height: 25.3333333333333vw;
      }
      .btn {
        --btn-size: 4.8vw;
        padding: 1.6vw 2.66666666666667vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 5.86666666666667vw;
        height: 25.3333333333333vw;
        border-radius: 2.13333333333333vw;
      }
      .btn::before{
        content: " ";
        display: block;
        width: 8.93333333333333vw;
        height: 5.2vw;
        background-image: url('https://f2e-cdn.jackery.com/@jackery/biz-components/latest/dist/assets/lucky-draw/icon-corner.png');
        background-size: cover;
        position: absolute;
        top: 0.666666666666667vw;
        left: 0.8vw;
        z-index:1;
      }
      .btn-txt{
        width: calc(100% + 5.33333333333333vw);
        font-size: 5.33333333333333vw;
        line-height: 7.46666666666667vw;
        margin-top: 0;
        margin-bottom: 0;
      }
      .btn-txt.btn-text-end {
        margin-top: 4.80000000000003vw;
      }
    }
  `];
Lt([
  $({ type: Number, reflect: !0 })
], He.prototype, "currentIndex", 2);
Lt([
  $({ type: Array, reflect: !1 })
], He.prototype, "prizes", 2);
Lt([
  $({ type: Boolean, reflect: !0 })
], He.prototype, "isDrawing", 2);
Lt([
  $({ type: Boolean, reflect: !0 })
], He.prototype, "isLogined", 2);
Lt([
  $({ type: Object, reflect: !1 })
], He.prototype, "activityInfo", 2);
He = Lt([
  pe("snippet-right-eight-prizes")
], He);
var nu = Object.defineProperty, ru = Object.getOwnPropertyDescriptor, Pt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? ru(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && nu(e, i, s), s;
};
let Fe = class extends re {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("start-draw"));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return v`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;" >
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 3; grid-row: 1;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 3; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 4 ? "selected" : ""}" style="grid-column: 2; grid-row: 2;">
          <img src="${this.prizes[4].image}" alt="${this.prizes[4].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[4].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 5 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[5].image}" alt="${this.prizes[5].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[5].name}</p>
          </div>
        </div>
      </div>
      <button class="btn" @click="${this.startDraw}" ?disabled="${this.isDrawing}" style="background-color:${this.activityInfo.btnBgValue};">
        ${this.isDrawing ? v`<jds-spinner size="var(--btn-size)" color="${this.activityInfo.btnColor || "#FFF"}"></jds-spinner>` : v`<div class="btn-txt text-overflow-1" style="color:${this.activityInfo.btnColor || "#FFF"}">${this.activityInfo.btnTitle}</div>`}
      </button>
      ${this.isLogined ? "" : v`<div class="login-tips text-overflow-1" .innerHTML="${this.activityInfo.guideText}"></div>`}
      <div class="mb-show">
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? v`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
Fe.styles = [fi, ds, N`
    :host{
      width: 606px;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(2, 1fr);
      width: 606px;
      height: 345px;
    }
    .prize {
      padding: 8px 8px 6px;
    }
    .btn {
      --btn-size:36px;
      margin-top: 12px;
      padding: 12px 20px;
      width: 100%;
      font-size: var(--btn-size);
      line-height: 44px;
      height: 72px;
      border-radius: 16px;
    }
    .login-tips{
      margin-top:14px;
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 27px;
      color: var(--jds-color-text-l1);
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 42.0833333333333vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 42.0833333333333vw;
        height: 23.9583333333333vw;
      }
      .prize {
        padding: 0.555555555555556vw 0.555555555555556vw 0.416666666666667vw;
      }
      .btn {
        --btn-size: 2.5vw;
        margin-top: 0.833333333333333vw;
        padding: 0.833333333333333vw 1.38888888888889vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 3.05555555555556vw;
        height: 5vw;
        border-radius: 1.11111111111111vw;
      }
      .login-tips{
        margin-top: 0.972222222222222vw;
        width: 100%;
        text-align: center;
        font-weight: 500;
        font-size: 1.25vw;
        line-height: 1.875vw;
        color: var(--jds-color-text-l1);
      }
    }
    @media screen and (max-width: 1023.98px){
      :host{
        width: 80.8vw;
      }

      .mb-show {
        display: block;
      }
  
      .grid {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 80.8vw;
        height: 53.3333333333333vw;
      }

      .prize {
        height: 25.3333333333333vw;
      }
  
      .btn {
        --btn-size: 4.8vw;
        margin-top: 2.13333333333333vw;
        padding: 1.6vw 2.66666666666667vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 5.86666666666667vw;
        height: 12.6666666666667vw;
        border-radius: 2.13333333333333vw;
      }

      .btn::before{
        content: " ";
        display: block;
        width: 8.93333333333333vw;
        height: 5.2vw;
        background-image: url('https://f2e-cdn.jackery.com/@jackery/biz-components/latest/dist/assets/lucky-draw/icon-corner.png');
        background-size: cover;
        position: absolute;
        top: 0.666666666666667vw;
        left: 0.8vw;
        z-index:1;
      }
  
      .login-tips{
        margin-top: 3.2vw;
        width: 100%;
        text-align: center;
        font-weight: 600;
        font-size: 3.46666666666667vw;
        color: var(--jds-color-text-l1);
      }
    }
  `];
Pt([
  $({ type: Number, reflect: !0 })
], Fe.prototype, "currentIndex", 2);
Pt([
  $({ type: Array, reflect: !1 })
], Fe.prototype, "prizes", 2);
Pt([
  $({ type: Boolean, reflect: !0 })
], Fe.prototype, "isDrawing", 2);
Pt([
  $({ type: Boolean, reflect: !0 })
], Fe.prototype, "isLogined", 2);
Pt([
  $({ type: Object, reflect: !1 })
], Fe.prototype, "activityInfo", 2);
Fe = Pt([
  pe("snippet-right-six-prizes")
], Fe);
var au = Object.defineProperty, lu = Object.getOwnPropertyDescriptor, It = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? lu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && au(e, i, s), s;
};
let qe = class extends re {
  constructor() {
    super(...arguments), this.currentIndex = 0, this.prizes = [], this.isDrawing = !1, this.isLogined = !1, this.activityInfo = {
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      joinMaxText: "",
      ruleTitle: ""
    };
  }
  startDraw() {
    this.dispatchEvent(new CustomEvent("start-draw"));
  }
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog"));
  }
  render() {
    return v`
      <div class="grid">
        <div class="prize ${this.currentIndex === 0 ? "selected" : ""}" style="grid-column: 1; grid-row: 1;">
          <img src="${this.prizes[0].image}" alt="${this.prizes[0].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[0].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 1 ? "selected" : ""}" style="grid-column: 2; grid-row: 1;">
          <img src="${this.prizes[1].image}" alt="${this.prizes[1].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[1].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 2 ? "selected" : ""}" style="grid-column: 2; grid-row: 2;">
          <img src="${this.prizes[2].image}" alt="${this.prizes[2].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[2].name}</p>
          </div>
        </div>
        <div class="prize ${this.currentIndex === 3 ? "selected" : ""}" style="grid-column: 1; grid-row: 2;">
          <img src="${this.prizes[3].image}" alt="${this.prizes[3].name}" loading="lazy"/>
          <div class="name">
            <p>${this.prizes[3].name}</p>
          </div>
        </div>
      </div>
      <button class="btn" @click="${this.startDraw}" ?disabled="${this.isDrawing}" style="background-color:${this.activityInfo.btnBgValue};">
        ${this.isDrawing ? v`<jds-spinner size="var(--btn-size)" color="${this.activityInfo.btnColor || "#FFF"}"></jds-spinner>` : v`<div class="btn-txt" style="color:${this.activityInfo.btnColor || "#FFF"}">${this.activityInfo.btnTitle}</div>`}
      </button>
      ${this.isLogined ? "" : v`<div class="login-tips text-overflow-1" .innerHTML="${this.activityInfo.guideText}"></div>`}
      <div class="mb-show">
        <div class="tips tips-two text-overflow-3" .innerHTML="${this.activityInfo.joinMaxText}"></div>
        ${this.activityInfo.ruleTitle ? v`
              <div class="rule-text" @click="${this.showRulesDialog}">
                <span .innerHTML="${this.activityInfo.ruleTitle}"></span>
                <svg class="arrow" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2530" width="20" height="20">
                  <path d="M512 671.288889L785.066667 398.222222l-56.888889-62.577778L512 551.822222 295.822222 335.644444 233.244444 398.222222z" fill="currentColor" p-id="2531" transform="rotate(-90) scale(1.5)" style="transform-origin:center"></path>
                </svg>
              </div>
            ` : ""}
      </div>
    `;
  }
};
qe.styles = [fi, ds, N`
    :host{
      width: 400px;
    }
    .mb-show {
      display: none;
    }
    .grid {
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
      width: 400px;
      height: 345px;
    }
    .btn {
      --btn-size: 36px;
      margin-top: 12px;
      padding: 12px 20px;
      width: 100%;
      font-size: var(--btn-size);
      line-height: 44px;
      height: 72px;
    }
    .login-tips{
      margin-top:14px;
      width: 100%;
      text-align: center;
      font-weight: 500;
      font-size: 18px;
      line-height: 27px;
      color: var(--jds-color-text-l1);
    }
    @media screen and (min-width: 1024px) and (max-width: 1440px) {
      :host{
        width: 27.7777777777778vw;
      }
      .mb-show {
        display: none;
      }
      .grid {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 27.7777777777778vw;
        height: 23.9583333333333vw;
      }
      .btn {
        --btn-size: 2.5vw;
        margin-top: 0.833333333333333vw;
        padding: 0.833333333333333vw 1.38888888888889vw;
        width: 100%;
        font-size: var(--btn-size);
        line-height: 3.05555555555556vw;
        height: 5vw;
      }
      .login-tips{
        margin-top: 0.972222222222222vw;
        width: 100%;
        text-align: center;
        font-weight: 500;
        font-size: 1.25vw;
        line-height: 1.875vw;
        color: var(--jds-color-text-l1);
      }
    }
    @media screen and (max-width: 1023.98px){
      .mb-show {
        display: block;
      }
      :host{
        width: 80.8vw;
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      .grid {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(2, 1fr);
        width: 53.3333333333333vw;
        height: 52.2666666666667vw;
      }
      .prize {
        height: 25.3333333333333vw;
      }
      .btn {
        --btn-size: 5.33333333333333vw;
        margin-top: 2.66666666666667vw;
        padding: 0 2.66666666666667vw;
        width: 53.3333333333333vw;
        font-size: var(--btn-size);
        height: 12.8vw;
      }
      .login-tips{
        margin-top: 3.2vw;
        width: 100%;
        text-align: center;
        font-weight: 600;
        font-size: 3.46666666666667vw;
        color: var(--jds-color-text-l1);
      }
    }
  `];
It([
  $({ type: Number, reflect: !0 })
], qe.prototype, "currentIndex", 2);
It([
  $({ type: Array, reflect: !1 })
], qe.prototype, "prizes", 2);
It([
  $({ type: Boolean, reflect: !0 })
], qe.prototype, "isDrawing", 2);
It([
  $({ type: Boolean, reflect: !0 })
], qe.prototype, "isLogined", 2);
It([
  $({ type: Object, reflect: !1 })
], qe.prototype, "activityInfo", 2);
qe = It([
  pe("snippet-right-four-prizes")
], qe);
var cu = Object.defineProperty, du = Object.getOwnPropertyDescriptor, L = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? du(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && cu(e, i, s), s;
};
let j = class extends re {
  constructor() {
    super(...arguments), this.platform = "membership", this.actId = "", this.shopifyShopId = "", this.customTag = "", this.showLuckyDraw = !1, this.prizes = [], this.currentIndex = -1, this.targetIndex = -1, this.isDrawing = !1, this.isLogined = !1, this.showRulesModal = !1, this.showTKSModal = !1, this.showXNKModal = !1, this.showYHQModal = !1, this.showJFModal = !1, this.timer = void 0, this.isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0, this.drawStartTime = 0, this.activityInfo = {
      shopId: "",
      busId: "",
      backgroundColor: "",
      pcBgImg: "",
      mbBgImg: "",
      mainTitleType: 0,
      mainTitle: "",
      subTitle: "",
      balance: 0,
      btnTitleNologin: "",
      btnTitleLogin: "",
      joinTimes: 0,
      joinDays: 0,
      joinMaxText: "",
      ruleTitle: "",
      ruleText: "",
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      guideText: "",
      tksTitle: "",
      tksImg: "",
      tipsOther: ""
    }, this.drawResults = {
      itemId: 1,
      balance: 1,
      btnText: "按钮",
      image: "",
      btnLink: "https://www.jackery.com/",
      tipsLoseWindowTitle: "未中奖-弹窗标题",
      tipsLoseContent: "未中奖-备注文案",
      tipsWindowTitle: "中奖-优惠券和礼品卡标题",
      tipsCopyBtnText: "中奖-复制按钮code",
      code: "",
      tipsCopySuccessText: "中奖-复制成功提示语",
      tipsRuleBtnText: "中奖-规则按钮rules",
      tipsWindowTitleVc: "中奖-虚拟卡标题",
      tipsContentVc: "中奖-虚拟卡备注文案",
      tipsRuleBtnTextVc: "中奖-虚拟卡-规则按钮rules",
      tipsWindowTitlePoint: "中奖-积分标题",
      tipsContentPoint: "中奖-积分备注文案",
      welfareType: "default",
      couponRules: ""
    }, this.debounceOnClick = ht(() => this.loginCheck(), 300);
  }
  // mock data
  // async mockData() {
  //   await delay(300)
  //   this.drawResults = {
  //     "balance": 5,
  //     "welfareType": "virtual_card",
  //     "image": "https://cdn.shopify.com/s/files/1/0550/0524/9633/files/gift_card.png?v=1693815666",
  //     "tipsLoseWindowTitle": "奖品弹窗",
  //     "tipsLoseContent": "备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案备注文案",
  //     "tipsWindowTitle": "优惠券和礼品卡标题（含变量）title： {item_title}优惠券和礼品卡标题（含变量）title： {item_title}优惠券和礼品卡标题（含变量）title： {item_title}",
  //     "tipsCopyBtnText": "Code",
  //     "tipsCopySuccessText": "Copy Success",
  //     "tipsRuleBtnText": "Rules",
  //     "tipsWindowTitleVc": "虚拟卡标题（含变量）： gj虚拟奖品001（对外）虚拟卡标题（含变量）： gj虚拟奖品001（对外）虚拟卡标题（含变量）： gj虚拟奖品001（对外）",
  //     "tipsContentVc": "虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案虚拟卡备注文案",
  //     "tipsRuleBtnTextVc": "虚拟卡Rules",
  //     "tipsWindowTitlePoint": "积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}积分标题 （含变量）： {item_title}",
  //     "tipsContentPoint": "积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案积分备注文案",
  //     "code": "44DDGGTYX8HFG",
  //     "couponRules": "<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>2.虚拟电视剧的撒娇，虚拟电视剧的撒娇</p>\n<p>3.虚拟电视剧的撒娇</p>\n<p>4.哇哈哈</p>\n<p>5.3434223@##！#！</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>2.虚拟电视剧的撒娇，虚拟电视剧的撒娇</p>\n<p>3.虚拟电视剧的撒娇</p>\n<p>4.哇哈哈</p>\n<p>5.3434223@##！#！</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>\n<p>1.虚拟电视剧的撒娇,虚拟电视剧的撒娇</p>",
  //     "btnText": "Spin Again",
  //     "btnLink": "",
  //     "itemId": 113
  //   }
  //   await delay(200)
  //   this.currentIndex = 1
  //   this.showDialog(this.drawResults.welfareType);
  // }
  async connectedCallback() {
    super.connectedCallback(), this.isLogined = Nt("_jk_user") != null, await this.getActivityInfo();
    const { lucky_act_id: t } = yr();
    t && setTimeout(() => {
      const e = document.querySelector(`jbc-lucky-draw[actId=${t}]`) || document.querySelector("jbc-lucky-draw");
      window.scrollTo({ top: (e == null ? void 0 : e.offsetTop) || 0 });
    }, 1e3);
  }
  // 登录态校验
  async loginCheck() {
    var t;
    if (!this.isDrawing) {
      if (this.isDrawing = !0, Nt("_jk_user"))
        this.startDraw();
      else if (window != null && window.pageInfo) {
        if (window.pageInfo.platform === "AEM") {
          if (!window.pageInfo.enablePwdFreeLoginOrReg) {
            ne("The password-free login function is not enabled. Please use normal login.", "warning");
            return;
          }
          const s = document.querySelector(".header .login-popup");
          s && (s.style.display = "block");
        }
        const e = window.location.search ? window.location.href + `#lucky_act_id=${this.actId || this.activityInfo.busId}` : window.location.href + `?lucky_act_id=${this.actId || this.activityInfo.busId}`, i = ["JFCJ-Register"], o = this.customTag ? i.concat(((t = this.customTag) == null ? void 0 : t.split(",")) || []).filter(Boolean) : i;
        window != null && window.jackeryLoginApp && (window == null || window.jackeryLoginApp.modalLogin({
          register_act: o.join(","),
          return_url: e
          // 返回当前页面
        })), await gt(500), this.isDrawing = !1;
      } else
        ne("Please log in first.", "warning"), await gt(500), this.isDrawing = !1;
      De({
        module: "Lottery button",
        // 固定模块文案
        button_name: this.activityInfo.btnTitle,
        // 抽奖按钮文案
        status: this.isLogined ? "logged_in" : "tourist"
        // 登录状态
      });
    }
  }
  // 动画执行
  startDraw() {
    this.drawStartTime = Date.now(), this.currentIndex = 0, this.requestUpdate();
    const t = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.prizes.length, this.requestUpdate();
    }, 200), e = setInterval(() => {
      this.targetIndex != -1 && (clearInterval(t), clearInterval(e), this.finalAnimation());
    }, 200);
    this.joinActivity(t, e);
  }
  // 最后选中动画
  finalAnimation() {
    const t = this.targetIndex, e = this.prizes.length;
    let i = 0;
    t > this.currentIndex ? i = t - this.currentIndex : t === this.currentIndex ? i = 0 : i = e + t - this.currentIndex;
    const o = setInterval(() => {
      this.currentIndex = (this.currentIndex + 1) % this.prizes.length, this.requestUpdate(), i--, i <= 0 && (clearInterval(o), this.currentIndex = t, this.getActivityInfo(), this.requestUpdate(), this.isDrawing = !1, this.showDialog(this.drawResults.welfareType), this.newCustomEvent());
    }, 200);
  }
  // 重置动画
  resetAnimation(t, e) {
    t && clearInterval(t), e && clearInterval(e), this.currentIndex = -1, this.targetIndex = -1, this.requestUpdate();
  }
  // 显示 规则弹窗
  showRulesDialog() {
    var i, o;
    this.showRulesModal = !0;
    const t = (i = this.shadowRoot) == null ? void 0 : i.querySelector("jds-dialog#rulesDialog"), e = (o = t == null ? void 0 : t.querySelector(".dialog-title")) == null ? void 0 : o.innerText;
    De({
      module: "Lottery button",
      // 固定模块文案
      button_name: e,
      // 规则按钮文案
      status: this.isLogined ? "logged_in" : "tourist"
      // 登录状态
    });
  }
  // 关闭所有弹窗，并重置动画 + 移除事件
  closeAllDialog() {
    ["showRulesModal", "showTKSModal", "showYHQModal", "showJFModal", "showXNKModal"].forEach((e) => this[e] = !1), this.resetAnimation(), this.removeRulesEvent();
  }
  // 处理对话框关闭事件
  handleDialogClose() {
    De({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      // "button_name": this.drawResults.btnText, // 奖品按钮文案
      button_name: "close"
      // 关闭按钮文案
    }), this.closeAllDialog();
  }
  // 处理对话框跳转链接
  handleDialogLink() {
    De({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.btnText,
      // 奖品按钮文案
      link: this.drawResults.btnLink
      // 跳转链接
    });
  }
  // 自定义抽奖结束事件用于外部监听
  newCustomEvent() {
    const t = new CustomEvent("luckyDrawDone", {
      detail: {
        status: !0
      }
    });
    document.dispatchEvent(t);
  }
  // 获取活动基本信息
  async getActivityInfo() {
    var s, n, r;
    const e = `${((s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/getCjActivityInfo`, i = {};
    !this.platform || this.platform === "membership" ? i.shopifyShopId = this.shopifyShopId : (i.platform = this.platform, i.actId = this.actId, i.shopifyShopId = this.shopifyShopId);
    const o = Nt("_jk_user") || localStorage.getItem("JACKERY_USER_TOKEN");
    try {
      const a = await C.get(e, {
        params: i,
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          jackeryToken: o,
          Cr: (n = window == null ? void 0 : window.pageInfo) == null ? void 0 : n.cr,
          Lang: (r = window == null ? void 0 : window.pageInfo) == null ? void 0 : r.cr
        }
      }), { data: l, success: c } = a.data;
      if (c && l !== null) {
        const { bgValue: d, bgValue1: h, items: p, ...f } = l;
        this.activityInfo = {
          ...f,
          pcBgImg: d,
          mbBgImg: h
        }, this.prizes = p, this.showLuckyDraw = !0;
      } else
        this.showLuckyDraw = !1;
    } catch (a) {
      this.showLuckyDraw = !1, a.code === "ECONNABORTED" ? console.error("请求超时:", a.message) : console.error("接口异常:", a);
    }
  }
  // 抽奖接口
  async joinActivity(t, e) {
    var r, a, l;
    const o = `${((r = window == null ? void 0 : window.pageInfo) == null ? void 0 : r.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/cjActivityJoin`, s = {};
    !this.platform || this.platform === "membership" ? (s.platform = "member", s.actType = "point", s.shopId = this.activityInfo.shopId, s.actId = this.activityInfo.busId) : (s.platform = this.platform, s.actType = "point", s.actId = this.actId, s.shopId = this.activityInfo.shopId);
    const n = Nt("_jk_user") || localStorage.getItem("JACKERY_USER_TOKEN");
    try {
      const c = await C.post(o, s, {
        withCredentials: !0,
        // 允许携带 Cookies
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          jackeryToken: n,
          Cr: (a = window == null ? void 0 : window.pageInfo) == null ? void 0 : a.cr,
          Lang: (l = window == null ? void 0 : window.pageInfo) == null ? void 0 : l.cr
        }
      }), { data: d, code: h, success: p, message: f } = c.data;
      if (p && d !== null)
        if (this.drawResults = d, this.drawResults.welfareType) {
          await gt(Math.max(Date.now() - this.drawStartTime, 3e3));
          const g = d.itemId;
          this.targetIndex = this.prizes.findIndex((w) => w.itemId === g);
        } else
          await gt(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, ne(this.activityInfo.tipsOther, "waring"), this.resetAnimation(t, e);
      else
        await gt(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, ne(f, "warning"), this.resetAnimation(t, e), h === 500209 && De({
          module: "Lottery button",
          // 固定模块文案
          button_name: "Lack of points",
          // 积分不足文案
          status: "logged_in"
          // 登录状态
        });
    } catch {
      await gt(Math.max(Date.now() - this.drawStartTime, 3e3)), this.isDrawing = !1, ne(this.activityInfo.tipsOther, "waring"), this.resetAnimation(t, e);
    }
  }
  // 中奖后，展示对应弹窗
  showDialog(t) {
    const i = {
      coupon: "showYHQModal",
      gift_card: "showYHQModal",
      point: "showJFModal",
      virtual_card: "showXNKModal",
      default: "showTKSModal"
    }[t] || "showTKSModal";
    this[i] = !0, this.registerRulesEvent(), De({
      module: "Awards",
      // 固定模块文案
      position: t !== "default" ? this.prizes[this.currentIndex].name : "Thanks！"
      // 奖品名称
    });
  }
  // 复制 code 码
  copyAndUseCode() {
    this.drawResults.code && (vr(this.drawResults.code).then(() => ne(this.drawResults.tipsCopySuccessText, "success")).catch(() => console.log("Copy Failed")), De({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.code
      // 奖品券码
    }));
  }
  // 注册 rules 交互事件
  registerRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (this.mouseEnterEvent = this.mouseEnterEvent.bind(this), this.mouseLeaveEvent = this.mouseLeaveEvent.bind(this), this.clearTimeoutEvent = this.clearTimeoutEvent.bind(this), t.addEventListener("mouseover", this.clearTimeoutEvent), t.addEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.addEventListener("touchend", this.mouseEnterEvent) : o.addEventListener("mouseenter", this.mouseEnterEvent), o.addEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 移除 rules 交互事件
  removeRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (t.removeEventListener("mouseover", this.clearTimeoutEvent), t.removeEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.removeEventListener("touchend", this.mouseEnterEvent) : o.removeEventListener("mouseenter", this.mouseEnterEvent), o.removeEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 清除 timer 事件
  clearTimeoutEvent() {
    clearTimeout(this.timer);
  }
  // Rules 规则，鼠标移入 | touchend
  mouseEnterEvent(t) {
    var l, c, d, h;
    clearTimeout(this.timer);
    const e = t.target, i = (l = this.shadowRoot) == null ? void 0 : l.querySelector(".rules-content");
    i.style.display = "block";
    const { left: o, top: s, bottom: n } = e.getBoundingClientRect(), { width: r, height: a } = i.getBoundingClientRect();
    if (this.isTouchDevice) {
      const p = (d = (c = e.closest("jds-dialog")) == null ? void 0 : c.shadowRoot) == null ? void 0 : d.querySelector('[part="dialog-container"]'), f = Math.max(((h = p == null ? void 0 : p.getBoundingClientRect()) == null ? void 0 : h.left) || 0, 0), g = Math.max(s - a - 5, 0);
      i.style.left = f + "px", i.style.top = g + "px";
    } else
      i.style.left = Math.max(o - r - 20, 0) + "px", i.style.top = Math.max(n - a + 26, 0) + "px";
    De({
      module: "Awards",
      // 固定模块文案
      position: this.drawResults.welfareType !== "default" ? this.prizes[this.currentIndex].name : "Thanks！",
      button_name: this.drawResults.tipsRuleBtnText
      // 奖品规则按钮文案
    });
  }
  // Rules 规则，鼠标移出
  mouseLeaveEvent() {
    var e;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    this.timer = setTimeout(() => {
      t.style.display = "none";
    }, 150);
  }
  // 获取对应抽奖区DOM
  getPrizeContent() {
    switch (this.prizes.length) {
      case 8:
        return v`
        <snippet-right-eight-prizes
          @start-draw="${this.loginCheck}"
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
          .isLogined="${this.isLogined}"
         ></snippet-right-eight-prizes>`;
      case 6:
        return v`
        <snippet-right-six-prizes
          @start-draw="${this.loginCheck}"
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
          .isLogined="${this.isLogined}"
        ></snippet-right-six-prizes>`;
      case 4:
        return v`
        <snippet-right-four-prizes
          @start-draw="${this.loginCheck}"
          @show-rules-dialog="${this.showRulesDialog}"
          .activityInfo="${this.activityInfo}"
          .prizes="${this.prizes}"
          .currentIndex="${this.currentIndex}"
          .isDrawing="${this.isDrawing}"
          .isLogined="${this.isLogined}"
        ></snippet-right-four-prizes>`;
      default:
        return v``;
    }
  }
  render() {
    return v`${this.showLuckyDraw ? v`
          <div class="wrapper" style="background-color:${this.activityInfo.backgroundColor};">
            <div class="jds-container">
              <!-- 背景图 -->
              <lucky-bg-img class="bg-img" .pcBgImg="${this.activityInfo.pcBgImg}" .mbBgImg="${this.activityInfo.mbBgImg}" alt="Lucky Draw Background Image"></lucky-bg-img>

              <!-- 左侧文案 -->
              <snippet-left-info @show-rules-dialog="${this.showRulesDialog}" .activityInfo="${this.activityInfo}" .isLogined="${this.isLogined}"></snippet-left-info>

              <!-- 右侧抽奖区 -->
              <div class="rg-box">
                ${this.getPrizeContent()}
              </div>
            </div>

            <!-- 规则弹窗 -->
            <jds-dialog id="rulesDialog" caption="&nbsp;" ?open=${this.showRulesModal} style="--jds-dialog-width:var(--jds-rules-dialog-width)" critical @closed="${this.closeAllDialog}">
              <h2 class="dialog-title" .innerHTML="${this.activityInfo.ruleTitle}"></h2>
              <section class="dialog-content">
                <div class="detail" .innerHTML="${this.activityInfo.ruleText}"></div>
              </section>
            </jds-dialog>

            <!-- 中奖弹窗 - 谢谢参与 -->
            <jds-dialog id="thsDialog" caption="&nbsp;" ?open=${this.showTKSModal} style="--jds-dialog-width:var(--jds-prize-dialog-width)" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips ths-tips">
                <div class="dialog-title">${this.drawResults.tipsLoseWindowTitle}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-full">${this.drawResults.tipsLoseContent}</div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? v`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : v`<jds-button name="primary-action" @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 虚拟卡 -->
            <jds-dialog id="xnkDialog" caption="&nbsp;" ?open=${this.showXNKModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);"  critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips xnk-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitleVc}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-box">
                  <div class="notes">${this.drawResults.tipsContentVc}</div>
                  ${this.drawResults.couponRules ? v`<div class="rules" data-rules-wrap>${this.drawResults.tipsRuleBtnTextVc}</div>` : ""}
                </div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? v`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : v`<jds-button @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 优惠券礼品卡 -->
            <jds-dialog id="yhqDialog" caption="&nbsp;" ?open=${this.showYHQModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips yhq-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitle}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-box">
                  ${this.drawResults.code ? v`<div class="code" @click="${this.copyAndUseCode}">${this.drawResults.tipsCopyBtnText}:${this.drawResults.code}</div>` : ""}
                  ${this.drawResults.couponRules ? v`<div class="rules" data-rules-wrap>${this.drawResults.tipsRuleBtnText}</div>` : ""}
                </div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? v`<jds-button class="btn" href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : v`<jds-button class="btn" @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 积分 -->
            <jds-dialog id="jfDialog" caption="&nbsp;" ?open=${this.showJFModal} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${this.handleDialogClose}">
              <div class="dialog-tips jf-tips">
                <div class="dialog-title">${this.drawResults.tipsWindowTitlePoint}</div>
              </div>
              <div class="detail">
                <img class="dialog-image" src="${this.drawResults.image}" loading="lazy">
                <div class="notes-full">${this.drawResults.tipsContentPoint}</div>
                ${this.drawResults.btnText ? this.drawResults.btnLink ? v`<jds-button href="${this.drawResults.btnLink}" target="_blank" @click="${this.handleDialogLink}">${this.drawResults.btnText}</jds-button>` : v`<jds-button @click="${this.handleDialogClose}">${this.drawResults.btnText}</jds-button>` : ""}
              </div>
            </jds-dialog>

            <!-- 中奖弹窗 - 规则 - tooltip -->
            ${this.drawResults.couponRules ? v`
                  <div class="rules-content">
                    <h4 class="title">${this.drawResults.tipsRuleBtnText}</h4>
                    <div class="detail" .innerHTML="${this.drawResults.couponRules}"></div>
                  </div>
                ` : ""}
          </div>
        ` : ""}`;
  }
};
j.styles = [fi, Jh, Gh, Xh];
L([
  $({ type: String, reflect: !0 })
], j.prototype, "platform", 2);
L([
  $({ type: String, reflect: !0 })
], j.prototype, "actId", 2);
L([
  $({ type: String, reflect: !0 })
], j.prototype, "shopifyShopId", 2);
L([
  $({ type: String, reflect: !0 })
], j.prototype, "customTag", 2);
L([
  $({ type: Boolean })
], j.prototype, "showLuckyDraw", 2);
L([
  $({ type: Array })
], j.prototype, "prizes", 2);
L([
  $({ type: Number })
], j.prototype, "currentIndex", 2);
L([
  $({ type: Number })
], j.prototype, "targetIndex", 2);
L([
  $({ type: Boolean })
], j.prototype, "isDrawing", 2);
L([
  $({ type: Boolean })
], j.prototype, "isLogined", 2);
L([
  S()
], j.prototype, "showRulesModal", 2);
L([
  S()
], j.prototype, "showTKSModal", 2);
L([
  S()
], j.prototype, "showXNKModal", 2);
L([
  S()
], j.prototype, "showYHQModal", 2);
L([
  S()
], j.prototype, "showJFModal", 2);
L([
  S()
], j.prototype, "timer", 2);
L([
  S()
], j.prototype, "isTouchDevice", 2);
L([
  S()
], j.prototype, "drawStartTime", 2);
L([
  $({ type: Object })
], j.prototype, "activityInfo", 2);
L([
  $({ type: Object })
], j.prototype, "drawResults", 2);
j = L([
  pe("jbc-lucky-draw")
], j);
var hu = Object.defineProperty, io = (t, e, i, o) => {
  for (var s = void 0, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = r(e, i, s) || s);
  return s && hu(e, i, s), s;
};
class mi extends re {
  constructor() {
    super(...arguments), this.platform = "membership", this.actId = "", this.shopifyShopId = "", this.customTag = "";
  }
  connectedCallback() {
    var e;
    super.connectedCallback(), window.i18n && window.i18n.setCurrLanguage(((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.lang) || "en");
  }
}
io([
  $({ type: String, reflect: !0 })
], mi.prototype, "platform");
io([
  $({ type: String, reflect: !0 })
], mi.prototype, "actId");
io([
  $({ type: String, reflect: !0 })
], mi.prototype, "shopifyShopId");
io([
  $({ type: String, reflect: !0 })
], mi.prototype, "customTag");
var uu = Object.defineProperty, pu = Object.getOwnPropertyDescriptor, oo = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? pu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && uu(e, i, s), s;
};
let ke = class extends mi {
  constructor() {
    super(...arguments), this.showLuckyComponent = !1, this.activityInfo = {
      shopId: "",
      busId: "",
      backgroundColor: "",
      pcBgImg: "",
      mbBgImg: "",
      mainTitle: "",
      subTitle: "",
      guideText: "",
      highlightErrTips: "",
      showPhoneInput: 0,
      privacyPolicy: "",
      btnTitle: "",
      btnColor: "",
      btnBgValue: "",
      joinTimes: 0,
      joinDays: 0,
      ruleTitle: "",
      ruleText: "",
      btnPlayText: "",
      drawGuideText: "",
      scratchBoxBgImg: "",
      noSubscribeMaskImg: "",
      subscribeMaskImg: "",
      showPrizeBtnText: "",
      tryAgainBtnText: "",
      tksTitle: "",
      tksImg: ""
    }, this.drawResults = {
      itemId: -1,
      balance: -1,
      btnText: "",
      title: "",
      image: "",
      btnLink: "",
      tipsLoseWindowTitle: "",
      tipsLoseContent: "",
      tipsWindowTitle: "",
      tipsCopyBtnText: "",
      code: "",
      tipsCopySuccessText: "",
      tipsRuleBtnText: "",
      tipsWindowTitleVc: "",
      tipsContentVc: "",
      tipsRuleBtnTextVc: "",
      tipsWindowTitlePoint: "",
      tipsContentPoint: "",
      welfareType: "default",
      couponRules: "",
      token: "",
      userJoinTimes: 0
    };
  }
};
oo([
  S()
], ke.prototype, "showLuckyComponent", 2);
oo([
  S()
], ke.prototype, "activityInfo", 2);
oo([
  S()
], ke.prototype, "drawResults", 2);
ke = oo([
  pe("common-lucky-component")
], ke);
const so = N`
  * {
    margin: 0;
    padding: 0;
    word-break: break-word;
    font-family: var(--jds-font-family);
  }

  li {
    list-style: none;
  }

  .hidden,
  .pc-show,
  .mb-show {
    display: none;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #c9c9cb;
  }

  ::-webkit-scrollbar-track {
    background-color: transparent;
  }

  .text-overflow-1 {
    display: -webkit-box;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .text-overflow-2 {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .text-overflow-3 {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media screen and (min-width: 1440px) {
    ::-webkit-scrollbar-thumb {
      border-radius: 10px;
    }

    ::-webkit-scrollbar {
      width: 6px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    ::-webkit-scrollbar-thumb {
      border-radius: 0.694444444444444vw;
    }

    ::-webkit-scrollbar {
      width: 0.416666666666667vw;
    }
  }

  @media screen and (min-width: 1024px) {
    .pc-show {
      display: block;
    }
  }

  @media screen and (max-width: 1023.98px) {
    .mb-show {
      display: block;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 1.33333333333333vw;
    }

    ::-webkit-scrollbar {
      width: 0.8vw;
    }
  }
`, gu = N`
  :host .jds-container {
    margin: 0 auto;
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-repeat: no-repeat;
    background-size: cover;
    box-sizing: border-box;
  }

  :host .wrapper {
    background-color: #FDF3E5;
  }

  .bg-img {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: -1;
  }

  .rg-box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: auto;
    height: 100%;
  }

  @media screen and (min-width: 1440px) {
    :host {
      --jds-rules-dialog-width: 600px;
      --jds-prize-dialog-width: 640px;
    }

    :host .jds-container {
      width: 1200px;
      min-height: 600px;
      padding: 100px 120px 100px 80px;
    }

    :host .wrapper {
      padding: 88px 0 66px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    :host {
      --jds-rules-dialog-width: 41.666666666667vw;
      --jds-prize-dialog-width: 44.444444444444vw;
    }

    :host .jds-container {
      width: 83.333333333333vw;
      min-height: 41.666666666667vw;
      margin: 0 auto;
      padding: 6.944444444444vw 8.333333333333vw 6.944444444444vw 5.555555555556vw;
    }

    :host .wrapper {
      padding: 6.111111111111vw 0 4.583333333333vw;
    }
  }

  @media screen and (max-width: 1023.98px) {
    :host {
      --jds-rules-dialog-width: 80vw;
      --jds-prize-dialog-width: 85.333333333333vw;
    }

    :host .wrapper {
      padding: 11.733333333333vw 0 8.8vw;
    }

    :host .jds-container {
      margin: 0 auto;
      flex-direction: column;
      width: 91.466666666667vw;
      min-height: 90.666666666667vw;
      padding: 10.666666666667vw 8.533333333333vw;
    }
  }
`, fu = function(t) {
  return v`
  ${t.showLuckyComponent ? v`
      <div class="wrapper" style="background-color:${t.activityInfo.backgroundColor};">
        <div class="jds-container">
          <!-- 背景图 -->
          <lucky-bg-img class="bg-img"
            .pcBgImg="${t.activityInfo.pcBgImg}"
            .mbBgImg="${t.activityInfo.mbBgImg}"
            alt="Lucky Draw Background Image">
          </lucky-bg-img>

          <!-- 左侧文案 -->
          <scratch-left-info
            @show-rules-dialog="${t.handleShowRulesDialog}"
            @subscribe-success="${t.handleSubscribeSuccess}"
            .customTag="${t.customTag}"
            .activityInfo="${t.activityInfo}"
            .hightLightUnlockTips = "${t.hightLightUnlockTips}">
          </scratch-left-info>

          <!-- 右侧抽奖区 -->
          <scratch-right-box
            .props=${{ platform: t.platform, actId: t.activityInfo.busId, shopifyShopId: t.activityInfo.shopId, customTag: t.customTag }}
            .activityInfo="${t.activityInfo}"
            .drawResults="${t.drawResults}"
            .email="${t.email}"
            .drawState="${t.drawState}"
            @update-prize-details="${t.handleUpdatePrizeDetails}"
            @show-prize-details="${t.handleShowPrizeDetails}"
            @unlock-click="${t.showUnlockTips}"
            @draw-state-update="${t.handleDrawStateUpdate}"
          ></scratch-right-box>
        </div>

        <!-- 所有弹窗 -->
        <lucky-all-dialog
          .activityInfo=${t.activityInfo}
          .drawResults="${t.drawResults}"
          .showRulesDialog="${t.showRulesDialog}"
          .showCouponDialog="${t.showCouponDialog}"
          .showPointsDialog="${t.showPointsDialog}"
          .showThanksDialog="${t.showThanksDialog}"
          .showVirtualCardDialog="${t.showVirtualCardDialog}"
          @modal-state-update="${t.handleModalStateUpdate}"
        ></lucky-all-dialog>

      </div>
    ` : ""}`;
}, mu = N`
  jds-dialog .dialog-tips {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  jds-dialog .dialog-title {
    font-weight: 600;
    color: #1A1A1A;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  jds-dialog#rulesDialog .dialog-title {
    height: auto;
    line-clamp: 1;
    -webkit-line-clamp: 1;
  }

  jds-dialog .dialog-image {
    object-fit: contain;
  }

  jds-dialog .notes-full {
    width: 100%;
    color: rgba(12, 11, 11, 1);
  }

  jds-dialog .notes-box {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }

  jds-dialog .notes-box .code,
  jds-dialog .notes-box .rules {
    flex-shrink: 0;
    position: relative;
    overflow: visible;
    display: flex;
    font-weight: 500;
    text-decoration-line: underline;
    color: #D98B24;
    cursor: pointer;
  }

  .rules-content {
    display: none;
    box-sizing: border-box;
    text-align: left;
    color: #444444;
    font-weight: 400;
    background-color: #fff;
    border: 1px solid #EEEEEE;
    position: fixed;
    left: 0;
    top: 0;
    z-index: 501;
  }

  .rules-content::after {
    display: block;
    content: '';
    position: absolute;
    left: 100%;
    z-index: 0;
    transform: translateY(-50%);
    border-color: transparent;
    border-left-color: rgba(255, 255, 255, 1);
    border-style: solid;
    background-color: transparent;
    width: 0;
    height: 0;
  }

  .rules-content h4.title {
    text-align: left;
    font-weight: 600;
    color: #666;
    border-bottom: 1px solid #DDD;
  }

  .rules-content .detail {
    overflow-y: auto;
  }

  .rules-content .detail ul,
  .rules-content .detail ol{
    padding: revert;
  }

  .rules-content .detail li{
    list-style: inherit;
  }

  jds-dialog .notes-box .notes {
    text-align: left;
    width: fit-content;
    color: rgba(12, 11, 11, 1);
  }

  jds-dialog jds-button::part(button) {
    font-weight: 600;
  }

  jds-dialog:not(#rulesDialog) {
    text-align: center;
  }

  jds-dialog:not(#rulesDialog)::part(dialog-content) {
    margin-right: 0;
    overflow: unset;
  }

  jds-dialog:not(#rulesDialog) .detail {
    overflow-y: auto;
  }

  jds-dialog:not(#rulesDialog)::part(dialog-container) {
    position: relative;
    background: linear-gradient(135deg, #fd891c 12.5%, #fbfaf5 0, #fbfaf5 25%, #ffd07a 0, #ffd07a 37.5%, #fbfaf5 0, #fbfaf5 50%, #fd891c 0, #fd891c 62.5%, #fbfaf5 0, #fbfaf5 75%, #ffd07a 0, #ffd07a 87.5%, #fbfaf5 0, #fbfaf5);
  }

  jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
    display: block;
    content: '';
    position: absolute;
    background: #fff;
    z-index: -1;
  }

  @media screen and (min-width: 1440px) {
    jds-dialog .dialog-tips {
      margin-bottom: 18px;
      height: 72px;
    }

    jds-dialog .dialog-title {
      margin-right: 30px;
      font-size: 28px;
      line-height: 36px;
    }

    jds-dialog .dialog-tips.ths-tips{
      height: 100px;
    }

    jds-dialog .ths-tips .dialog-title {
      font-size: 24px;
      line-height: 32px;
    }

    jds-dialog .dialog-image {
      margin-top: 24px;
      margin-bottom: 16px;
      width: 248px;
      height: 166px;
    }

    jds-dialog .notes-full {
      font-size: 14px;
      line-height: 22px;
    }

    jds-dialog .notes-box {
      column-gap: 24px;
      row-gap: 34px;
      margin-top: 16px;
      margin-bottom: 10px;
    }

    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: 16px;
      text-underline-offset: 4px;
    }

    .rules-content {
      width: 420px;
      font-size: 16px;
      line-height: 24px;
      box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.27);
      border-radius: 12px;
      padding: 20px 10px 26px 32px;
    }

    .rules-content::after {
      bottom: 10px;
      filter: drop-shadow(5px 0px 3px rgba(0, 0, 0, 0.17));
      border-width: 13px;
    }

    .rules-content h4.title {
      font-size: 24px;
      padding-bottom: 10px;
      margin-bottom: 14px;
      margin-right: 6px;
    }

    .rules-content .detail {
      padding-right: 6px;
      max-height: 360px;
    }

    jds-dialog .notes-box .notes {
      font-size: 14px;
      line-height: 22px;
      max-width: 364px;
    }

    jds-dialog jds-button::part(button) {
      height: 48px;
      line-height: 48px;
      min-width: 180px;
      font-size: 16px;
      margin-top: 38px;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 48px;
      padding-right: 18px;
    }

    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 30px;
      max-height: 380px;
    }

    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 360px;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container) {
      background-size: 70px 70px;
      height: 560px;
      border-radius: 20px;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      left: 8px;
      right: 8px;
      top: 8px;
      bottom: 8px;
      border-radius: 20px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    jds-dialog .dialog-tips {
      margin-bottom: 1.25vw;
      height: 5vw;
    }

    jds-dialog .dialog-title {
      margin-right: 2.08333333333333vw;
      font-size: max(12px, 1.94444444444444vw);
      line-height: 2.5vw;
    }

    jds-dialog .dialog-tips.ths-tips {
      height: 6.94444444444444vw;
    }

    jds-dialog .ths-tips .dialog-title {
      font-size: max(12px, 1.66666666666667vw);
      line-height: 2.22222222222222vw;
    }

    jds-dialog .dialog-image {
      margin-top: 1.66666666666667vw;
      margin-bottom: 1.11111111111111vw;
      width: 17.2222222222222vw;
      height: 11.5277777777778vw;
    }

    jds-dialog .notes-full {
      font-size: max(12px, 0.972222222222222vw);
      line-height: 1.52777777777778vw;
    }

    jds-dialog .notes-box {
      column-gap: 1.66666666666667vw;
      row-gap: 2.36111111111111vw;
      margin-top: 1.11111111111111vw;
      margin-bottom: 0.694444444444444vw;
    }

    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: max(12px, 1.11111111111111vw);
      text-underline-offset: 0.277777777777778vw;
    }

    .rules-content {
      width: 29.1666666666667vw;
      font-size: max(12px, 1.11111111111111vw);
      line-height: 1.66666666666667vw;
      box-shadow: 0px 0.138888888888889vw 0.555555555555556vw 0px rgba(0, 0, 0, 0.27);
      border-radius: 0.833333333333333vw;
      padding: 1.38888888888889vw 0.694444444444444vw 1.80555555555556vw 2.22222222222222vw;
    }

    .rules-content::after {
      bottom: 0.972222222222222vw;
      filter: drop-shadow(0.347222222222222vw 0px 0.208333333333333vw rgba(0, 0, 0, 0.17));
      border-width: 0.902777777777778vw;
    }

    .rules-content h4.title {
      font-size: max(12px, 1.66666666666667vw);
      padding-bottom: 0.694444444444444vw;
      margin-bottom: 0.972222222222222vw;
      margin-right: 0.416666666666667vw;
    }

    .rules-content .detail {
      padding-right: 0.416666666666667vw;
      max-height: 25vw;
    }

    jds-dialog .notes-box .notes {
      font-size: max(12px, 0.972222222222222vw);
      line-height: 1.52777777777778vw;
      max-width: 25.2777777777778vw;
    }

    jds-dialog jds-button::part(button) {
      height: 3.33333333333333vw;
      line-height: 3.33333333333333vw;
      min-width: 12.5vw;
      font-size: max(12px, 1.11111111111111vw);
      margin-top: 2.63888888888889vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 1.25vw;
    }

    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.08333333333333vw;
      max-height: 26.3888888888889vw;
    }

    jds-dialog:not(#rulesDialog)#thsDialog .detail {
      max-height: 25vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container) {
      background-size: 4.86111111111111vw 4.86111111111111vw;
      height: 39.8888888888889vw;
      border-radius: 1.38888888888889vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      left: 0.555555555555556vw;
      right: 0.555555555555556vw;
      top: 0.555555555555556vw;
      bottom: 0.555555555555556vw;
      border-radius: 1.38888888888889vw;
    }
  }

  @media screen and (min-width: 1024px) {
    jds-dialog .dialog-title {
      line-clamp: 2;
      -webkit-line-clamp: 2;
    }

    jds-dialog .ths-tips .dialog-title {
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }
  }

  @media screen and (max-width: 1023.98px) {
    jds-dialog .dialog-tips {
      margin-bottom: 5.33333333333333vw;
      height: unset;
    }

    jds-dialog .dialog-title {
      margin-right: 2.66666666666667vw;
      font-size: max(12px, 5.33333333333333vw);
      line-height: 6.4vw;
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }

    jds-dialog .dialog-tips.ths-tips {
      height: 25.6vw;
    }

    jds-dialog .ths-tips .dialog-title {
      font-size: max(12px, 5.33333333333333vw);
      line-height: 6.4vw;
      line-clamp: 4;
      -webkit-line-clamp: 4;
    }

    jds-dialog .dialog-image {
      margin-bottom: 4.26666666666667vw;
      width: 53.3333333333333vw;
      height: 35.7333333333333vw;
    }

    jds-dialog .notes-full {
      font-size: max(12px, 3.46666666666667vw);
      line-height: 4.26666666666667vw;
    }

    jds-dialog .notes-box {
      column-gap: 6.4vw;
      row-gap: 5.86666666666667vw;
      margin-top: 4.26666666666667vw;
      margin-bottom: 6.4vw;
      padding-left: 1.33333333333333vw;
      padding-right: 4vw;
    }

    jds-dialog .notes-box .code,
    jds-dialog .notes-box .rules {
      font-size: max(12px, 3.73333333333333vw);
      text-underline-offset: 0.53333333333333vw;
    }

    jds-dialog .notes-box .notes {
      font-size: max(12px, 3.46666666666667vw);
      line-height: 4.26666666666667vw;
      color: rgba(12, 11, 11, 1);
    }

    .rules-content {
      width: 77.3333333333333vw;
      font-size: max(12px, 3.73333333333333vw);
      line-height: 5.33333333333333vw;
      box-shadow: 0 0.26666666666667vw 1.06666666666667vw 0 rgba(0, 0, 0, 0.27);
      border-radius: 2.66666666666667vw;
      padding: 4.26666666666667vw 1.6vw 5.33333333333333vw 4.26666666666667vw;
    }

    .rules-content::after {
      display: none;
      top: calc(100% + 1.33333333333333vw);
      left: 80%;
      filter: drop-shadow(0 0.66666666666667vw 0.4vw rgba(0, 0, 0, 0.17));
      border-width: 1.33333333333333vw;
    }

    .rules-content h4.title {
      font-size: max(12px, 5.33333333333333vw);
      color: #333;
      padding-bottom: 2.66666666666667vw;
      margin-bottom: 2.66666666666667vw;
      margin-right: 2.66666666666667vw;
    }

    .rules-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 80vw;
    }

    jds-dialog jds-button::part(button) {
      height: 10.6666666666667vw;
      line-height: 10.6666666666667vw;
      min-width: 42.6666666666667vw;
      font-size: max(12px, 3.73333333333333vw);
      margin-top: 6.4vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
    }

    jds-dialog:not(#rulesDialog) .detail {
      padding-right: 2.66666666666667vw;
      max-height: 88vw;
    }

    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
      overflow: unset;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container) {
      background-size: 9.33333333333333vw 9.33333333333333vw;
      height: max-content;
      border-radius: 5.33333333333333vw;
    }

    jds-dialog:not(#rulesDialog)::part(dialog-container)::after {
      left: 1.06666666666667vw;
      right: 1.06666666666667vw;
      top: 1.06666666666667vw;
      bottom: 1.06666666666667vw;
      border-radius: 5.33333333333333vw;
    }
  }
`, wu = N`
  jds-dialog#rulesDialog {
    text-align: left;
  }

  jds-dialog#rulesDialog::part(dialog-content) {
    margin-right: 0;
  }

  jds-dialog#rulesDialog .dialog-content .detail {
    overflow-y: auto;
  }

  jds-dialog#rulesDialog .dialog-content .detail img {
    width: auto;
    max-width: 100% !important;
    height: auto;
  }

  jds-dialog::part(dialog-content) {
    overflow: unset;
  }

  @media screen and (min-width: 1440px) {
    jds-dialog#rulesDialog::part(dialog-container) {
      min-height: 320px;
      max-height: 560px;
    }
    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 48px;
      padding-right: 10px;
    }
    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 38px;
      max-height: 436px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    jds-dialog#rulesDialog::part(dialog-container) {
      min-height: 22.2222222222222vw;
      max-height: 39.8888888888889vw;
    }

    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 3.33333333333333vw;
      padding-right: 0.694444444444444vw;
      margin-right: 0;
    }

    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.63888888888889vw;
      max-height: 30.2777777777778vw;
    }
  }

  @media screen and (max-width: 1023.98px) {
    jds-dialog#rulesDialog::part(dialog-container) {
      min-height: 85.3333333333333vw;
    }

    jds-dialog#rulesDialog::part(dialog-content) {
      padding-left: 4.26666666666667vw;
      padding-right: 1.6vw;
    }

    jds-dialog#rulesDialog .dialog-content .detail {
      padding-right: 2.66666666666667vw;
      max-height: 106.666666666667vw;
    }

    jds-dialog::part(dialog-content) {
      padding: 0 5.6vw;
      max-height: 144vw;
    }
  }
`, vu = function(t) {
  return v`
    <!-- 规则弹窗 -->
    <jds-dialog id="rulesDialog" caption="&nbsp;" ?open=${t.showRulesDialog} style="--jds-dialog-width:var(--jds-rules-dialog-width)" critical @closed="${t.closeAllDialog}">
      <h2 class="dialog-title" .innerHTML="${t.activityInfo.ruleTitle}"></h2>
      <section class="dialog-content">
        <div class="detail" .innerHTML="${t.activityInfo.ruleText}"></div>
      </section>
    </jds-dialog>

    <!-- 中奖弹窗 - 谢谢参与 -->
    <jds-dialog id="thsDialog" caption="&nbsp;" ?open=${t.showThanksDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width)" critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips ths-tips">
        <div class="dialog-title">${t.drawResults.tipsLoseWindowTitle}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-full">${t.drawResults.tipsLoseContent}</div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? v`<jds-button href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : v`<jds-button name="primary-action" @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 虚拟卡 -->
    <jds-dialog id="xnkDialog" caption="&nbsp;" ?open=${t.showVirtualCardDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width);"  critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips xnk-tips">
        <div class="dialog-title">${t.drawResults.tipsWindowTitleVc}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-box">
          <div class="notes">${t.drawResults.tipsContentVc}</div>
          ${t.drawResults.couponRules ? v`<div class="rules" data-rules-wrap>${t.drawResults.tipsRuleBtnTextVc}</div>` : ""}
        </div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? v`<jds-button href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : v`<jds-button @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 优惠券礼品卡 -->
    <jds-dialog id="yhqDialog" caption="&nbsp;" ?open=${t.showCouponDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips yhq-tips">
        <div class="dialog-title">${t.drawResults.tipsWindowTitle}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-box">
          ${t.drawResults.code ? v`<div class="code" @click="${t.copyAndUseCode}">${t.drawResults.tipsCopyBtnText}:${t.drawResults.code}</div>` : ""}
          ${t.drawResults.couponRules ? v`<div class="rules" data-rules-wrap>${t.drawResults.tipsRuleBtnText}</div>` : ""}
        </div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? v`<jds-button class="btn" href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : v`<jds-button class="btn" @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 积分 -->
    <jds-dialog id="jfDialog" caption="&nbsp;" ?open=${t.showPointsDialog} style="--jds-dialog-width:var(--jds-prize-dialog-width);" critical @closed="${t.handleDialogClose}">
      <div class="dialog-tips jf-tips">
        <div class="dialog-title">${t.drawResults.tipsWindowTitlePoint}</div>
      </div>
      <div class="detail">
        <img class="dialog-image" src="${t.drawResults.image}" loading="lazy">
        <div class="notes-full">${t.drawResults.tipsContentPoint}</div>
        ${t.drawResults.btnText ? t.drawResults.btnLink ? v`<jds-button href="${t.drawResults.btnLink}" target="_blank">${t.drawResults.btnText}</jds-button>` : v`<jds-button @click="${t.handleDialogClose}">${t.drawResults.btnText}</jds-button>` : ""}
      </div>
    </jds-dialog>

    <!-- 中奖弹窗 - 规则 - tooltip -->
    ${t.drawResults.couponRules ? v`
        <div class="rules-content">
          <h4 class="title"></h4>
          <div class="detail" .innerHTML="${t.drawResults.couponRules}"></div>
        </div>
      ` : ""}
  `;
};
var bu = Object.defineProperty, yu = Object.getOwnPropertyDescriptor, Ke = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? yu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && bu(e, i, s), s;
};
let Ee = class extends ke {
  constructor() {
    super(...arguments), this.showRulesDialog = !1, this.showThanksDialog = !1, this.showVirtualCardDialog = !1, this.showCouponDialog = !1, this.showPointsDialog = !1, this.isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0, this.timer = void 0;
  }
  // 鼠标移入|移出，延时执行
  // 注册 rules 交互事件
  registerRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (this.mouseEnterEvent = this.mouseEnterEvent.bind(this), this.mouseLeaveEvent = this.mouseLeaveEvent.bind(this), this.clearTimeoutEvent = this.clearTimeoutEvent.bind(this), t.addEventListener("mouseover", this.clearTimeoutEvent), t.addEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.addEventListener("touchend", this.mouseEnterEvent) : o.addEventListener("mouseenter", this.mouseEnterEvent), o.addEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 展示对应弹窗
  showDialog(t) {
    const i = {
      coupon: "showCouponDialog",
      gift_card: "showCouponDialog",
      point: "showPointsDialog",
      virtual_card: "showVirtualCardDialog",
      default: "showThanksDialog"
    }[t] || "showThanksDialog";
    this[i] = !0, this.registerRulesEvent();
  }
  // 复制 code 码
  copyAndUseCode() {
    this.drawResults.code && vr(this.drawResults.code).then(() => ne(this.drawResults.tipsCopySuccessText, "success")).catch(() => console.log("Copy Failed"));
  }
  // 关闭所有弹窗， 移除事件
  closeAllDialog() {
    ["showRulesDialog", "showCouponDialog", "showPointsDialog", "showVirtualCardDialog", "showThanksDialog"].forEach((e) => this[e] = !1), this.dispatchEvent(new CustomEvent("modal-state-update", {
      detail: {
        showRulesDialog: !1,
        showCouponDialog: !1,
        showPointsDialog: !1,
        showVirtualCardDialog: !1,
        showThanksDialog: !1
      }
    })), this.removeRulesEvent();
  }
  // 处理对话框关闭事件
  handleDialogClose() {
    this.closeAllDialog();
  }
  // 移除 rules 交互事件
  removeRulesEvent() {
    var e, i;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    t && (t.removeEventListener("mouseover", this.clearTimeoutEvent), t.removeEventListener("mouseleave", this.mouseLeaveEvent), (i = this.shadowRoot) == null || i.querySelectorAll("[data-rules-wrap]").forEach((o) => {
      this.isTouchDevice ? o.removeEventListener("touchend", this.mouseEnterEvent) : o.removeEventListener("mouseenter", this.mouseEnterEvent), o.removeEventListener("mouseleave", this.mouseLeaveEvent);
    }));
  }
  // 清除 timer 事件
  clearTimeoutEvent() {
    clearTimeout(this.timer);
  }
  // Rules 规则，鼠标移入 | touchend
  mouseEnterEvent(t) {
    var d, h, p, f;
    clearTimeout(this.timer);
    const e = t.target, i = (d = this.shadowRoot) == null ? void 0 : d.querySelector(".rules-content"), o = e.textContent || "", s = i.querySelector(".title");
    s && (s.textContent = o), i.style.display = "block";
    const { left: n, top: r, bottom: a } = e.getBoundingClientRect(), { width: l, height: c } = i.getBoundingClientRect();
    if (this.isTouchDevice) {
      const g = (p = (h = e.closest("jds-dialog")) == null ? void 0 : h.shadowRoot) == null ? void 0 : p.querySelector('[part="dialog-container"]'), w = Math.max(((f = g == null ? void 0 : g.getBoundingClientRect()) == null ? void 0 : f.left) || 0, 0), m = Math.max(r - c - 5, 0);
      i.style.left = w + "px", i.style.top = m + "px";
    } else
      i.style.left = Math.max(n - l - 20, 0) + "px", i.style.top = Math.max(a - c + 26, 0) + "px";
  }
  // Rules 规则，鼠标移出
  mouseLeaveEvent() {
    var e;
    const t = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".rules-content");
    this.timer = setTimeout(() => {
      t.style.display = "none";
    }, 150);
  }
  render() {
    return vu(this);
  }
};
Ee.styles = [so, mu, wu];
Ke([
  $({ type: Boolean })
], Ee.prototype, "showRulesDialog", 2);
Ke([
  $({ type: Boolean })
], Ee.prototype, "showThanksDialog", 2);
Ke([
  $({ type: Boolean })
], Ee.prototype, "showVirtualCardDialog", 2);
Ke([
  $({ type: Boolean })
], Ee.prototype, "showCouponDialog", 2);
Ke([
  $({ type: Boolean })
], Ee.prototype, "showPointsDialog", 2);
Ke([
  S()
], Ee.prototype, "isTouchDevice", 2);
Ke([
  S()
], Ee.prototype, "timer", 2);
Ee = Ke([
  pe("lucky-all-dialog")
], Ee);
const xr = N`
  .info-box .title {
    display: flex;
    text-align: center;
    position: relative;
    justify-content: center;
    align-items: center;
    font-weight: 600;
    font-style: normal;
    color: #fbf5db;
  }

  .info-box .subtitle,
  .info-box .tips {
    font-weight: 500;
    font-style: normal;
    color: #fff;
  }

  .info-box .tips {
    text-align: center;
  }

  @media screen and (min-width: 1440px) {
    .info-box .title {
      width: 480px;
      font-size: 32px;
      line-height: 42px;
    }

    .info-box .subtitle {
      margin-top: 12px;
      font-size: 16px;
      line-height: 24px;
    }

    .info-box .subtitle,
    .info-box .tips {
      width: 400px;
    }

    .info-box .tips {
      margin-top: 28px;
      height: 16px;
      font-size: 12px;
      line-height: 16px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .info-box .title {
      width: 33.33vw;
      font-size: max(12px, 2.22vw);
      line-height: 2.92vw;
    }

    .info-box .subtitle {
      margin-top: 0.83vw;
      font-size: max(12px, 1.11vw);
      line-height: 1.67vw;
    }

    .info-box .subtitle,
    .info-box .tips {
      width: 27.78vw;
    }

    .info-box .tips {
      margin-top: 1.94vw;
      height: 1.11vw;
      font-size: max(12px, 0.83vw);
      line-height: 1.11vw;
    }
  }

  @media screen and (max-width: 1023.98px) {

    .info-box .title {
      font-size: max(12px, 8vw);
      line-height: 10.13333333333333vw;
    }

    .info-box .subtitle {
      margin-top: 1.06666666666667vw;
      font-size: max(12px, 4.26666666666667vw);
      line-height: 6.4vw;
      font-weight: 600;
    }

    .info-box .title,
    .info-box .subtitle,
    .info-box .tips {
      width: 74.4vw;
    }

    .info-box .tips {
      display: -webkit-box;
      font-size: max(12px, 3.73333333333333vw);
      line-height: 4.8vw;
      height: 9.6vw;
      font-weight: 600;
    }
  }
`, xu = N`
  .lf-info {
    flex-direction: column;
    height: 100%;
  }

  .lf-info {
    display: flex;
    text-align: center;
    align-items: center;
  }
  .info-box{
    display: none;
    flex-direction: column;
    align-items: center;
  }
  .rule-text {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-weight: 500;
    color: rgba(255, 255, 255, .6);
    font-style: normal;
    cursor: pointer;
  }

  .item-box {
    position: relative;
    display: flex;
    box-sizing: border-box;
  }

  input,
  input:focus,
  input:focus-visible {
    width: 100%;
    background-color: transparent;
    border: none;
    outline: none;
  }

  .email-box,
  .phone-box {
    background-color: #fff;
  }

  .item-box.sub-success {
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
  }

  .status-check {
    display: none;
    color: #fd5000;
  }

  .email-box label {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
  }

  .item-box.sub-success .status-required {
    display: none;
  }

  .item-box.sub-success .status-check {
    display: block;
  }

  .email-box .box-inp {
    width: 100%;
  }

  .item-box.sub-success .box-inp {
    color: #fff;
  }

  .item-box .error-tips {
    position: absolute;
    top: 106%;
    font-weight: 400;
    color: #ff3b30;
    text-align: left;
  }

  .email-box label>span {
    position: absolute;
    line-height: 1;
    color: rgba(0, 0, 0, .6);
  }

  .checkbox {
    position: relative;
    display: flex;
    align-items: flex-start;
    align-self: flex-start;
    cursor: pointer;
  }

  .item-box.checkbox-box {
    height: auto;
  }

  .checkbox .checkbox-original {
    position: absolute;
    left: 0;
    margin: 0;
    color: #fff;
    background: transparent;
    transition: border .3s ease;
    opacity: 0;
    outline: none;
    transform: none;
  }

  .checkbox .checkbox-original:checked:after {
    background-color: #ff5000;
    border: none;
    color: #fff;
  }

  .checkbox .checkbox-original:checked~.checkbox-inner:before {
    background: #fd5000;
    border-color: #fd5000;
    color: #fff;
  }

  .checkbox .jackery-icon-checkbox:before {
    color: transparent;
  }

  .checkbox .checkbox-inner .icon-check {
    display: none;
    color: #fff;
    align-items: center;
    justify-content: center;
  }

  .checkbox .checkbox-original:checked~.checkbox-inner .icon-check {
    display: flex;
  }

  .checkbox .checkbox-inner {
    position: relative;
    display: flex;
  }

  .checkbox .checkbox-inner:after,
  .checkbox .checkbox-inner:before {
    content: "";
    letter-spacing: -.02em;
    line-height: 1.33;
    position: absolute;
  }

  .checkbox .checkbox-inner:before {
    left: 0;
    box-sizing: border-box;
    border-color: hsla(0, 0%, 100%, .6);
    border-style: solid;
  }

  .checkbox .checkbox-input:hover .checkbox-inner:before {
    border-color: #fd5000;
  }

  .checkbox .checkbox-inner:after {
    color: #fff;
    cursor: pointer;
    font-weight: 900;
    pointer-events: none;
    z-index: 1;
  }

  .checkbox .checkbox-inner .jackery-icon-checkbox {
    opacity: 0;
    position: relative;
    transition: all .3s ease;
    z-index: 9;
  }

  .checkbox .checkbox-label {
    letter-spacing: 0;
    font-weight: 400;
    color: hsla(0, 0%, 100%, .6);
    text-align: left;
    font-style: normal;
  }

  .checkbox a {
    text-decoration: underline;
    color: hsla(0, 0%, 100%, .6);
  }

  .checkbox a:hover {
    color: #fff;
  }

  jds-button[class~=btn-subscribe] {
    width: 100%;
  }

  jds-button[class~=disabled] {
    width: 100%;
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }

  jds-button[class~=btn-subscribe]::part(button) {
    width: 100%;
    font-weight: 600;
    transition: all .3s ease;
    color: var(--btn-text-color);
    background-color: var(--btn-bg-color);
    border-color: var(--btn-bg-color);
  }

  jds-button[class=btn-subscribe]::part(button):hover {
    background-color: color-mix(in srgb, var(--btn-bg-color) 85%, #fff);
    border-color: color-mix(in srgb, var(--btn-bg-color) 85%, #fff);
  }

  jds-button[class=btn-subscribe]::part(button):active {
    background-color: color-mix(in srgb, var(--btn-bg-color) 90%, #000);
    border-color: color-mix(in srgb, var(--btn-bg-color) 90%, #000);
  }

  @media screen and (min-width: 1440px) {
    .lf-info {
      width: 480px;
    }

    .btn-subscribe {
      margin-bottom: 20px;
    }

    .rule-text {
      column-gap: 6px;
      font-size: 16px;
      line-height: 20px;
    }

    .form {
      margin-top: 12px;
      width: 400px;
    }

    .item-box {
      height: 48px;
    }

    input,
    input:focus,
    input:focus-visible {
      font-size: 14px;
    }

    .email-box,
    .phone-box {
      margin-bottom: 20px;
      padding: 12px;
      border-radius: 8px;
    }

    .status-check {
      font-size: 20px;
    }

    .email-box .box-inp {
      margin-left: 24px;
    }

    .item-box .error-tips {
      font-size: 12px;
      line-height: 14px;
    }

    .email-box label>span {
      font-size: 28px;
      top: 4px;
      height: 16px;
    }

    .checkbox-box {
      min-height: 24px;
      margin-bottom: 26px;
    }

    .checkbox .checkbox-original {
      border: 1px solid hsla(0, 0%, 100%, .6);
      border-radius: 2px;
    }

    .checkbox .checkbox-original:checked:after {
      font-size: 12px;
    }

    .checkbox .checkbox-inner .icon-check {
      width: 16px;
      font-size: 14px;
    }

    .checkbox .checkbox-input {
      height: 16px;
    }

    .checkbox .checkbox-inner:before,
    .checkbox .checkbox-original {
      width: 16px;
      height: 16px;
    }

    .checkbox .checkbox-inner {
      font-size: 12px;
      width: 16px;
      height: 16px;
    }

    .checkbox .checkbox-inner:after,
    .checkbox .checkbox-inner:before {
      font-size: 12px;
    }

    .checkbox .checkbox-inner:before {
      border-width: 1px;
      border-radius: 2px;
    }

    .checkbox .checkbox-label {
      margin-left: 12px;
      min-height: 14px;
      font-size: 12px;
      line-height: 14px;
    }

    jds-button[class~=btn-subscribe]::part(button) {
      height: 48px;
      font-size: 18px;
      line-height: 24px;
      border-radius: 8px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .lf-info {
      width: 33.33vw;
    }

    .btn-subscribe {
      margin-bottom: 1.39vw;
    }

    .rule-text {
      column-gap: 0.42vw;
      font-size: max(12px, 1.11vw);
      line-height: 1.39vw;
    }

    .form {
      margin-top: 0.83vw;
      width: 27.78vw;
    }

    .item-box {
      height: 3.33vw;
    }

    input,
    input:focus,
    input:focus-visible {
      font-size: max(12px, 0.97vw);
    }

    .email-box,
    .phone-box {
      margin-bottom: 1.39vw;
      padding: 0.83vw;
      border-radius: 0.56vw;
    }

    .status-check {
      font-size: max(12px, 1.39vw);
    }

    .email-box .box-inp {
      margin-left: 1.67vw;
    }

    .item-box .error-tips {
      font-size: max(12px, 0.83vw);
      line-height: 0.97vw;
    }

    .email-box label>span {
      font-size: max(12px, 1.94vw);
      top: 0.28vw;
      height: 1.11vw;
    }

    .checkbox-box {
      min-height: 1.67vw;
      margin-bottom: 1.81vw;
    }

    .checkbox .checkbox-original {
      border: 0.14vw solid hsla(0, 0%, 100%, .6);
      border-radius: 0.14vw;
    }

    .checkbox .checkbox-original:checked:after {
      font-size: max(12px, 0.83vw);
    }

    .checkbox .checkbox-inner .icon-check {
      width: 1.11vw;
      font-size: max(12px, 0.97vw);
    }

    .checkbox .checkbox-input {
      height: 1.11vw;
    }

    .checkbox .checkbox-inner:before,
    .checkbox .checkbox-original {
      width: 1.11vw;
      height: 1.11vw;
    }

    .checkbox .checkbox-inner {
      font-size: max(12px, 0.83vw);
      width: 1.11vw;
      height: 1.11vw;
    }

    .checkbox .checkbox-inner:after,
    .checkbox .checkbox-inner:before {
      font-size: max(12px, 0.83vw);
    }

    .checkbox .checkbox-inner:before {
      border-width: 0.14vw;
      border-radius: 0.14vw;
    }

    .checkbox .checkbox-label {
      margin-left: 0.83vw;
      min-height: 0.97vw;
      font-size: max(12px, 0.83vw);
      line-height: 0.97vw;
    }

    jds-button[class~=btn-subscribe]::part(button) {
      height: 3.33vw;
      font-size: max(12px, 1.25vw);
      line-height: 1.67vw;
      border-radius: 0.56vw;
    }
  }

  @media screen and (min-width:1024px) {
    .info-box {
      display: flex;
    }
  }

  @media screen and (max-width:1023.98px) {
    :host{
      order: 2;
    }

    .lf-info {
      width: 74.4vw;
    }

    .form {
      margin-top: 4.27vw;
      width: 74.4vw;
    }

    .item-box {
      height: 12.8vw;
    }

    input,
    input:focus,
    input:focus-visible {
      font-size: max(12px, 3.2vw);
    }

    .email-box,
    .phone-box {
      margin-bottom: 4.27vw;
      padding: 2.67vw 3.2vw;
      border-radius: 1.6vw;
    }

    .status-check {
      font-size: max(12px, 5.33vw);
    }

    .email-box .box-inp {
      margin-left: 4.67vw;
    }

    .item-box .error-tips {
      top: 102%;
      font-size: max(12px, 2.67vw);
      line-height: 1.4;
    }

    .email-box label>span {
      font-size: max(12px, 5.33vw);
      top: 2vw;
      height: 3.2vw;
      color:#FD5000;
    }

    .checkbox{
      align-items: flex-start;
    }

    .checkbox-box {
      min-height: 4.27vw;
      margin-bottom: 9.33vw;
    }

    .checkbox-box .error-tips {
      margin-left: 3.2vw;
    }

    .checkbox .checkbox-original {
      border: 0.27vw solid hsla(0, 0%, 100%, .6);
      border-radius: 0.27vw;
    }

    .checkbox .checkbox-original:checked:after {
      font-size: max(12px, 4vw);
    }

    .checkbox .checkbox-inner .icon-check {
      width: 4.27vw;
      font-size: max(12px, 3.47vw);
    }

    .checkbox .checkbox-input {
      height: 4.27vw;
    }

    .checkbox .checkbox-inner:before,
    .checkbox .checkbox-original {
      width: 4.27vw;
      height: 4.27vw;
    }

    .checkbox .checkbox-inner {
      font-size: max(12px, 4vw);
      width: 4.27vw;
      height: 4.27vw;
    }

    .checkbox .checkbox-inner:after,
    .checkbox .checkbox-inner:before {
      font-size: max(12px, 4vw);
    }

    .checkbox .checkbox-inner:before {
      border-width: 0.27vw;
      border-radius: 0.53vw;
    }

    .checkbox .checkbox-label {
      margin-left: 3.2vw;
      font-size: max(12px, 2.67vw);
      line-height: 1.4;
    }

    .btn-subscribe {
      margin-bottom: 5.33vw;
    }

    jds-button[class~=btn-subscribe]::part(button) {
      height: 12.8vw;
      font-size: max(12px, 4vw);
      line-height: 1.25;
      border-radius: 1.6vw;
    }

    .rule-text {
      column-gap: 1.6vw;
      font-size: max(12px, 4.8vw);
      line-height: 1.25;
    }
  }
`, $u = N`
  .phone-box .country-area-num {
    color: rgba(0, 0, 0, 0.7);
    word-break: normal;
    min-width: fit-content;
  }

  .phone-box .country-area-select {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
  }

  .phone-box .country-area-select .change-area {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
  }

  .country-area-select .change-area.active .select-arrow {
    transform: rotate(180deg);
    -webkit-transform: rotate(180deg);
    transition: transform .5s;
  }

  .phone-box .selected-flag {
    height: auto;
    background-color: #fff;
    box-shadow: 0 0 2px #ddd;
  }

  .phone-box .selected-item {
    display: block;
    position: relative;
    color: rgba(0, 0, 0, 0.7);
    word-break: normal;
  }

  /* 移除数字输入框的上下箭头 */
  .box-inp::-webkit-outer-spin-button,
  .box-inp::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .box-inp[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .phone-box .select-arrow {
    color: rgba(0, 0, 0, 0.7);
  }

  .item-box.sub-success .country-area-num,
  .item-box.sub-success .selected-item,
  .item-box.sub-success .select-arrow {
    color: #fff;
  }

  .phone-box .country-area-list {
    position: absolute;
    background-color: #fff;
    max-height: 30vh;
    overflow-y: auto;
    transition: all .2s ease;
    z-index: 1;
  }

  .country-area-list .search-swrapper {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  .country-area-list .search-swrapper input {
    width: 100%;
    color: #333;
    background: transparent;
    border: 1px solid #b3b3b3;
    box-sizing: border-box;
  }

  .country-area-list .no-data {
    text-align: center;
  }

  .country-area-list .country-item {
    position: relative;
    color: #202223;
    transition: all .3s;
    cursor: pointer;
    font-weight: 400;
    text-align: left;
  }

  .country-area-list .country-item:hover {
    background: #f6f6f7;
  }

  .country-area-list .country-item.active {
    background: #f2f7fe;
  }

  .country-area-list .country-item.active:before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background: #fd5000;
  }

  .phone-box .box-inp {
    width: 100%;
    border: none;
  }

  @media screen and (min-width: 1440px) {
    .phone-box .selected-flag {
      width: 32px;
    }

    .phone-box .selected-item {
      min-width: 20px;
    }

    .phone-box .selected-item,
    .phone-box .country-area-num{
      font-size: 16px;
    }

    .phone-box .select-arrow {
      margin-right: 10px;
    }

    .phone-box .country-area-list {
      border-radius: 8px;
      left: -12px;
      bottom: calc(100% + 20px);
      box-shadow: 2px 2px 8px rgba(23, 24, 24, .051), 2px 2px 4px rgba(0, 0, 0, .149);
      width: 300px;
      min-height: 260px;
    }

    .country-area-list .search-swrapper {
      padding: 8px;
    }

    .country-area-list .search-swrapper input {
      padding: 8px 4px;
      border-radius: 4px;
    }

    .country-area-list .no-data {
      line-height: 60px;
    }

    .country-area-list .country-item {
      padding: 8px;
      font-size: 14px;
    }

    .country-area-list .country-item.active:before {
      width: 4px;
    }

    .phone-box .box-inp {
      margin-left: 16px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    .phone-box .selected-flag {
      width: 2.22vw;
    }

    .phone-box .selected-item {
      min-width: 1.39vw;
    }
    .phone-box .selected-item,
    .phone-box .country-area-num{
      font-size: max(12px, 1.11vw);
    }

    .phone-box .select-arrow {
      margin-right: 0.69vw;
    }

    .phone-box .country-area-list {
      border-radius: 0.56vw;
      left: -0.83vw;
      bottom: calc(100% + 1.39vw);
      box-shadow: 0.14vw 0.14vw 0.56vw rgba(23, 24, 24, .051), 0.14vw 0.14vw 0.28vw rgba(0, 0, 0, .149);
      width: 20.83vw;
      min-height: 18.06vw;
    }

    .country-area-list .search-swrapper {
      padding: 0.56vw;
    }

    .country-area-list .search-swrapper input {
      padding: 0.56vw 0.28vw;
      border-radius: 0.28vw;
      font-size: max(12px, 0.97vw);
    }

    .country-area-list .no-data {
      line-height: 4.17vw;
      font-size: max(12px, 0.97vw);
    }

    .country-area-list .country-item {
      padding: 0.56vw;
      font-size: max(12px, 0.97vw);
    }

    .country-area-list .country-item.active:before {
      width: 0.28vw;
    }

    .phone-box .box-inp {
      margin-left: 1.11vw;
    }
  }

  @media screen and (max-width:1023.98px) {
    .phone-box .selected-flag {
      width: 5.33vw;
    }

    .phone-box .selected-item {
      min-width: 5.33vw;
    }

    .phone-box .selected-item,
    .phone-box .country-area-num{
      font-size: max(12px, 3.73vw);
    }

    .phone-box .select-arrow {
      margin-right: 1.33vw;
      font-size: max(12px, 3.73vw);
    }

    .country-area-overlay{
      position: fixed;
      left: 0;
      right: 0;
      top: 0;
      bottom: 0;
      z-index: 10000;
      background: rgba(0, 0, 0, .4);
    }

    .phone-box .country-area-list {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10001;
      width: 100%;
      height: 40vh;
      box-shadow: 0.27vw 0.27vw 1.07vw rgba(23, 24, 24, .051), 0.27vw 0.27vw 0.53vw rgba(0, 0, 0, .149);
      min-height: 34.67vw;
    }

    .country-area-list .search-swrapper {
      padding: 1.6vw;
    }

    .country-area-list .search-swrapper input {
      padding: 1.07vw 0.53vw;
      font-size: max(12px, 2.67vw);
      border-radius: 0.53vw;
    }

    .country-area-list .no-data {
      font-size: max(12px, 2.67vw);
      line-height: 13.33vw;
    }

    .country-area-list .country-item {
      padding: 1.07vw 1.6vw;
      font-size: max(12px, 2.67vw);
    }

    .country-area-list .country-item.active:before {
      width: 0.8vw;
    }

    .phone-box .box-inp {
      margin-left: 2.13vw;
    }
  }
`, Eu = function(t) {
  return v`
      <div class="lf-info">
        <div class="info-box pc-show">
          <div class="title" .innerHTML="${t.activityInfo.mainTitle}"></div>
          <div class="subtitle text-overflow-1" .innerHTML="${t.activityInfo.subTitle}"></div>
          <div class="tips text-overflow-1" .innerHTML="${t.activityInfo.guideText}"></div>
        </div>
        <div class="form">
          <div class="item-box email-box">
            <label>
              <span class="status-required">*</span>
              <input class="box-inp" data-flag="email" type="text" placeholder="${window.i18n.t("form.placeholder.email")}">
              <jds-icon class="status-check" name="check-fill"></jds-icon>
            </label>
            <div class="error-tips email-error-tips hidden">${window.i18n.t("form.validation.email")}</div>
          </div>
          ${t.activityInfo.showPhoneInput ? v`
              <div class="item-box phone-box">
                <div class="country-area-select">
                  <div class="change-area">
                    <!-- 国家或区域旗帜 -->
                    <img class="selected-flag" src="" alt="" loading="lazy">

                    <!-- 国家或区域编码 -->
                    <span class="selected-item"></span>
                    <jds-icon class="select-arrow" name="arrow-down"></jds-icon>
                  </div>
                  <!-- 国家或区域列表 -->
                  <div class="country-area-overlay hidden"></div>
                  <div class="country-area-list hidden">
                    <div class="search-swrapper">
                      <input
                          type="text"
                          class="country-area-search"
                          placeholder="${window.i18n.t("form.placeholder.enterKeyword")}"
                      >
                    </div>
                    <div class="no-data">${window.i18n.t("form.validation.enterKeyword")}</div>
                    <div class="list-box"></div>
                  </div>
                  <span class="country-area-num"></span>
                  <input class="box-inp" data-flag="phone" type="number" placeholder="${window.i18n.t("form.placeholder.phone")}">
                  <jds-icon class="status-check" name="check-fill"></jds-icon>
                </div>
                <div class="error-tips phone-error-tips hidden">${window.i18n.t("form.validation.phone")}</div>
              </div>
            ` : ""}

          <div class="item-box checkbox-box">
            <label class="checkbox">
              <span class="checkbox-input">
                <input
                    type="checkbox"
                    class="checkbox-original"
                    data-section-class="subscribe-content"
                >
                <span class="checkbox-inner">
                  <jds-icon class="icon-check" name="check"></jds-icon>
                </span>
              </span>
              <div class="checkbox-label" .innerHTML="${t.activityInfo.privacyPolicy}"></div>
            </label>
            <div class="error-tips checkbox-error-tips hidden">${window.i18n.t("form.validation.termsOfService")}</div>
          </div>
          <jds-button
            class="btn-subscribe"
            size="medium"
            .innerHTML="${t.activityInfo.btnTitle}"
            style="--btn-text-color: ${t.activityInfo.btnColor}; --btn-bg-color: ${t.activityInfo.btnBgValue};"
          ></jds-button>
        </div>
        ${t.activityInfo.ruleTitle ? v`
              <div class="rule-text" @click="${t.showRulesDialog}">
                <span .innerHTML="${t.activityInfo.ruleTitle}"></span>
                <jds-icon name="arrow-right"></jds-icon>
              </div>
          ` : ""}
      </div>
    `;
}, yi = [
  {
    country: "Afghanistan",
    code: "93",
    iso: "AF"
  },
  {
    country: "Albania",
    code: "355",
    iso: "AL"
  },
  {
    country: "Algeria",
    code: "213",
    iso: "DZ"
  },
  {
    country: "American Samoa",
    code: "1-684",
    iso: "AS"
  },
  {
    country: "Andorra",
    code: "376",
    iso: "AD"
  },
  {
    country: "Angola",
    code: "244",
    iso: "AO"
  },
  {
    country: "Anguilla",
    code: "1-264",
    iso: "AI"
  },
  {
    country: "Antarctica",
    code: "672",
    iso: "AQ"
  },
  {
    country: "Antigua and Barbuda",
    code: "1-268",
    iso: "AG"
  },
  {
    country: "Argentina",
    code: "54",
    iso: "AR"
  },
  {
    country: "Armenia",
    code: "374",
    iso: "AM"
  },
  {
    country: "Aruba",
    code: "297",
    iso: "AW"
  },
  {
    country: "Australia",
    code: "61",
    iso: "AU"
  },
  {
    country: "Austria",
    code: "43",
    iso: "AT"
  },
  {
    country: "Azerbaijan",
    code: "994",
    iso: "AZ"
  },
  {
    country: "Bahamas",
    code: "1-242",
    iso: "BS"
  },
  {
    country: "Bahrain",
    code: "973",
    iso: "BH"
  },
  {
    country: "Bangladesh",
    code: "880",
    iso: "BD"
  },
  {
    country: "Barbados",
    code: "1-246",
    iso: "BB"
  },
  {
    country: "Belarus",
    code: "375",
    iso: "BY"
  },
  {
    country: "Belgium",
    code: "32",
    iso: "BE"
  },
  {
    country: "Belize",
    code: "501",
    iso: "BZ"
  },
  {
    country: "Benin",
    code: "229",
    iso: "BJ"
  },
  {
    country: "Bermuda",
    code: "1-441",
    iso: "BM"
  },
  {
    country: "Bhutan",
    code: "975",
    iso: "BT"
  },
  {
    country: "Bolivia",
    code: "591",
    iso: "BO"
  },
  {
    country: "Bosnia and Herzegovina",
    code: "387",
    iso: "BA"
  },
  {
    country: "Botswana",
    code: "267",
    iso: "BW"
  },
  {
    country: "Brazil",
    code: "55",
    iso: "BR"
  },
  {
    country: "British Indian Ocean Territory",
    code: "246",
    iso: "IO"
  },
  {
    country: "British Virgin Islands",
    code: "1-284",
    iso: "VG"
  },
  {
    country: "Brunei",
    code: "673",
    iso: "BN"
  },
  {
    country: "Bulgaria",
    code: "359",
    iso: "BG"
  },
  {
    country: "Burkina Faso",
    code: "226",
    iso: "BF"
  },
  {
    country: "Burundi",
    code: "257",
    iso: "BI"
  },
  {
    country: "Cambodia",
    code: "855",
    iso: "KH"
  },
  {
    country: "Cameroon",
    code: "237",
    iso: "CM"
  },
  {
    country: "Canada",
    code: "1",
    iso: "CA"
  },
  {
    country: "Cape Verde",
    code: "238",
    iso: "CV"
  },
  {
    country: "Cayman Islands",
    code: "1-345",
    iso: "KY"
  },
  {
    country: "Central African Republic",
    code: "236",
    iso: "CF"
  },
  {
    country: "Chad",
    code: "235",
    iso: "TD"
  },
  {
    country: "Chile",
    code: "56",
    iso: "CL"
  },
  {
    country: "China",
    code: "86",
    iso: "CN"
  },
  {
    country: "Christmas Island",
    code: "61",
    iso: "CX"
  },
  {
    country: "Cocos Islands",
    code: "61",
    iso: "CC"
  },
  {
    country: "Colombia",
    code: "57",
    iso: "CO"
  },
  {
    country: "Comoros",
    code: "269",
    iso: "KM"
  },
  {
    country: "Cook Islands",
    code: "682",
    iso: "CK"
  },
  {
    country: "Costa Rica",
    code: "506",
    iso: "CR"
  },
  {
    country: "Croatia",
    code: "385",
    iso: "HR"
  },
  {
    country: "Cuba",
    code: "53",
    iso: "CU"
  },
  {
    country: "Curacao",
    code: "599",
    iso: "CW"
  },
  {
    country: "Cyprus",
    code: "357",
    iso: "CY"
  },
  {
    country: "Czech Republic",
    code: "420",
    iso: "CZ"
  },
  {
    country: "Democratic Republic of the Congo",
    code: "243",
    iso: "CD"
  },
  {
    country: "Denmark",
    code: "45",
    iso: "DK"
  },
  {
    country: "Djibouti",
    code: "253",
    iso: "DJ"
  },
  {
    country: "Dominica",
    code: "1-767",
    iso: "DM"
  },
  {
    country: "Dominican Republic",
    code: "1-809, 1-829, 1-849",
    iso: "DO"
  },
  {
    country: "East Timor",
    code: "670",
    iso: "TL"
  },
  {
    country: "Ecuador",
    code: "593",
    iso: "EC"
  },
  {
    country: "Egypt",
    code: "20",
    iso: "EG"
  },
  {
    country: "El Salvador",
    code: "503",
    iso: "SV"
  },
  {
    country: "Equatorial Guinea",
    code: "240",
    iso: "GQ"
  },
  {
    country: "Eritrea",
    code: "291",
    iso: "ER"
  },
  {
    country: "Estonia",
    code: "372",
    iso: "EE"
  },
  {
    country: "Ethiopia",
    code: "251",
    iso: "ET"
  },
  {
    country: "Falkland Islands",
    code: "500",
    iso: "FK"
  },
  {
    country: "Faroe Islands",
    code: "298",
    iso: "FO"
  },
  {
    country: "Fiji",
    code: "679",
    iso: "FJ"
  },
  {
    country: "Finland",
    code: "358",
    iso: "FI"
  },
  {
    country: "France",
    code: "33",
    iso: "FR"
  },
  {
    country: "French Polynesia",
    code: "689",
    iso: "PF"
  },
  {
    country: "Gabon",
    code: "241",
    iso: "GA"
  },
  {
    country: "Gambia",
    code: "220",
    iso: "GM"
  },
  {
    country: "Georgia",
    code: "995",
    iso: "GE"
  },
  {
    country: "Germany",
    code: "49",
    iso: "DE"
  },
  {
    country: "Ghana",
    code: "233",
    iso: "GH"
  },
  {
    country: "Gibraltar",
    code: "350",
    iso: "GI"
  },
  {
    country: "Greece",
    code: "30",
    iso: "GR"
  },
  {
    country: "Greenland",
    code: "299",
    iso: "GL"
  },
  {
    country: "Grenada",
    code: "1-473",
    iso: "GD"
  },
  {
    country: "Guam",
    code: "1-671",
    iso: "GU"
  },
  {
    country: "Guatemala",
    code: "502",
    iso: "GT"
  },
  {
    country: "Guernsey",
    code: "44-1481",
    iso: "GG"
  },
  {
    country: "Guinea",
    code: "224",
    iso: "GN"
  },
  {
    country: "Guinea-Bissau",
    code: "245",
    iso: "GW"
  },
  {
    country: "Guyana",
    code: "592",
    iso: "GY"
  },
  {
    country: "Haiti",
    code: "509",
    iso: "HT"
  },
  {
    country: "Honduras",
    code: "504",
    iso: "HN"
  },
  {
    country: "Hong Kong",
    code: "852",
    iso: "HK"
  },
  {
    country: "Hungary",
    code: "36",
    iso: "HU"
  },
  {
    country: "Iceland",
    code: "354",
    iso: "IS"
  },
  {
    country: "India",
    code: "91",
    iso: "IN"
  },
  {
    country: "Indonesia",
    code: "62",
    iso: "ID"
  },
  {
    country: "Iran",
    code: "98",
    iso: "IR"
  },
  {
    country: "Iraq",
    code: "964",
    iso: "IQ"
  },
  {
    country: "Ireland",
    code: "353",
    iso: "IE"
  },
  {
    country: "Isle of Man",
    code: "44-1624",
    iso: "IM"
  },
  {
    country: "Israel",
    code: "972",
    iso: "IL"
  },
  {
    country: "Italy",
    code: "39",
    iso: "IT"
  },
  {
    country: "Ivory Coast",
    code: "225",
    iso: "CI"
  },
  {
    country: "Jamaica",
    code: "1-876",
    iso: "JM"
  },
  {
    country: "Japan",
    code: "81",
    iso: "JP"
  },
  {
    country: "Jersey",
    code: "44-1534",
    iso: "JE"
  },
  {
    country: "Jordan",
    code: "962",
    iso: "JO"
  },
  {
    country: "Kazakhstan",
    code: "7",
    iso: "KZ"
  },
  {
    country: "Kenya",
    code: "254",
    iso: "KE"
  },
  {
    country: "Kiribati",
    code: "686",
    iso: "KI"
  },
  {
    country: "Kosovo",
    code: "383",
    iso: "XK"
  },
  {
    country: "Kuwait",
    code: "965",
    iso: "KW"
  },
  {
    country: "Kyrgyzstan",
    code: "996",
    iso: "KG"
  },
  {
    country: "Laos",
    code: "856",
    iso: "LA"
  },
  {
    country: "Latvia",
    code: "371",
    iso: "LV"
  },
  {
    country: "Lebanon",
    code: "961",
    iso: "LB"
  },
  {
    country: "Lesotho",
    code: "266",
    iso: "LS"
  },
  {
    country: "Liberia",
    code: "231",
    iso: "LR"
  },
  {
    country: "Libya",
    code: "218",
    iso: "LY"
  },
  {
    country: "Liechtenstein",
    code: "423",
    iso: "LI"
  },
  {
    country: "Lithuania",
    code: "370",
    iso: "LT"
  },
  {
    country: "Luxembourg",
    code: "352",
    iso: "LU"
  },
  {
    country: "Macao",
    code: "853",
    iso: "MO"
  },
  {
    country: "Macedonia",
    code: "389",
    iso: "MK"
  },
  {
    country: "Madagascar",
    code: "261",
    iso: "MG"
  },
  {
    country: "Malawi",
    code: "265",
    iso: "MW"
  },
  {
    country: "Malaysia",
    code: "60",
    iso: "MY"
  },
  {
    country: "Maldives",
    code: "960",
    iso: "MV"
  },
  {
    country: "Mali",
    code: "223",
    iso: "ML"
  },
  {
    country: "Malta",
    code: "356",
    iso: "MT"
  },
  {
    country: "Marshall Islands",
    code: "692",
    iso: "MH"
  },
  {
    country: "Mauritania",
    code: "222",
    iso: "MR"
  },
  {
    country: "Mauritius",
    code: "230",
    iso: "MU"
  },
  {
    country: "Mayotte",
    code: "262",
    iso: "YT"
  },
  {
    country: "Mexico",
    code: "52",
    iso: "MX"
  },
  {
    country: "Micronesia",
    code: "691",
    iso: "FM"
  },
  {
    country: "Moldova",
    code: "373",
    iso: "MD"
  },
  {
    country: "Monaco",
    code: "377",
    iso: "MC"
  },
  {
    country: "Mongolia",
    code: "976",
    iso: "MN"
  },
  {
    country: "Montenegro",
    code: "382",
    iso: "ME"
  },
  {
    country: "Montserrat",
    code: "1-664",
    iso: "MS"
  },
  {
    country: "Morocco",
    code: "212",
    iso: "MA"
  },
  {
    country: "Mozambique",
    code: "258",
    iso: "MZ"
  },
  {
    country: "Myanmar",
    code: "95",
    iso: "MM"
  },
  {
    country: "Namibia",
    code: "264",
    iso: "NA"
  },
  {
    country: "Nauru",
    code: "674",
    iso: "NR"
  },
  {
    country: "Nepal",
    code: "977",
    iso: "NP"
  },
  {
    country: "Netherlands",
    code: "31",
    iso: "NL"
  },
  {
    country: "Netherlands Antilles",
    code: "599",
    iso: "AN"
  },
  {
    country: "New Caledonia",
    code: "687",
    iso: "NC"
  },
  {
    country: "New Zealand",
    code: "64",
    iso: "NZ"
  },
  {
    country: "Nicaragua",
    code: "505",
    iso: "NI"
  },
  {
    country: "Niger",
    code: "227",
    iso: "NE"
  },
  {
    country: "Nigeria",
    code: "234",
    iso: "NG"
  },
  {
    country: "Niue",
    code: "683",
    iso: "NU"
  },
  {
    country: "North Korea",
    code: "850",
    iso: "KP"
  },
  {
    country: "Northern Mariana Islands",
    code: "1-670",
    iso: "MP"
  },
  {
    country: "Norway",
    code: "47",
    iso: "NO"
  },
  {
    country: "Oman",
    code: "968",
    iso: "OM"
  },
  {
    country: "Pakistan",
    code: "92",
    iso: "PK"
  },
  {
    country: "Palau",
    code: "680",
    iso: "PW"
  },
  {
    country: "Palestine",
    code: "970",
    iso: "PS"
  },
  {
    country: "Panama",
    code: "507",
    iso: "PA"
  },
  {
    country: "Papua New Guinea",
    code: "675",
    iso: "PG"
  },
  {
    country: "Paraguay",
    code: "595",
    iso: "PY"
  },
  {
    country: "Peru",
    code: "51",
    iso: "PE"
  },
  {
    country: "Philippines",
    code: "63",
    iso: "PH"
  },
  {
    country: "Pitcairn",
    code: "64",
    iso: "PN"
  },
  {
    country: "Poland",
    code: "48",
    iso: "PL"
  },
  {
    country: "Portugal",
    code: "351",
    iso: "PT"
  },
  {
    country: "Puerto Rico",
    code: "1-787, 1-939",
    iso: "PR"
  },
  {
    country: "Qatar",
    code: "974",
    iso: "QA"
  },
  {
    country: "Republic of the Congo",
    code: "242",
    iso: "CG"
  },
  {
    country: "Reunion",
    code: "262",
    iso: "RE"
  },
  {
    country: "Romania",
    code: "40",
    iso: "RO"
  },
  {
    country: "Russia",
    code: "7",
    iso: "RU"
  },
  {
    country: "Rwanda",
    code: "250",
    iso: "RW"
  },
  {
    country: "Saint Barthelemy",
    code: "590",
    iso: "BL"
  },
  {
    country: "Saint Helena",
    code: "290",
    iso: "SH"
  },
  {
    country: "Saint Kitts and Nevis",
    code: "1-869",
    iso: "KN"
  },
  {
    country: "Saint Lucia",
    code: "1-758",
    iso: "LC"
  },
  {
    country: "Saint Martin",
    code: "590",
    iso: "MF"
  },
  {
    country: "Saint Pierre and Miquelon",
    code: "508",
    iso: "PM"
  },
  {
    country: "Saint Vincent and the Grenadines",
    code: "1-784",
    iso: "VC"
  },
  {
    country: "Samoa",
    code: "685",
    iso: "WS"
  },
  {
    country: "San Marino",
    code: "378",
    iso: "SM"
  },
  {
    country: "Sao Tome and Principe",
    code: "239",
    iso: "ST"
  },
  {
    country: "Saudi Arabia",
    code: "966",
    iso: "SA"
  },
  {
    country: "Senegal",
    code: "221",
    iso: "SN"
  },
  {
    country: "Serbia",
    code: "381",
    iso: "RS"
  },
  {
    country: "Seychelles",
    code: "248",
    iso: "SC"
  },
  {
    country: "Sierra Leone",
    code: "232",
    iso: "SL"
  },
  {
    country: "Singapore",
    code: "65",
    iso: "SG"
  },
  {
    country: "Sint Maarten",
    code: "1-721",
    iso: "SX"
  },
  {
    country: "Slovakia",
    code: "421",
    iso: "SK"
  },
  {
    country: "Slovenia",
    code: "386",
    iso: "SI"
  },
  {
    country: "Solomon Islands",
    code: "677",
    iso: "SB"
  },
  {
    country: "Somalia",
    code: "252",
    iso: "SO"
  },
  {
    country: "South Africa",
    code: "27",
    iso: "ZA"
  },
  {
    country: "South Korea",
    code: "82",
    iso: "KR"
  },
  {
    country: "South Sudan",
    code: "211",
    iso: "SS"
  },
  {
    country: "Spain",
    code: "34",
    iso: "ES"
  },
  {
    country: "Sri Lanka",
    code: "94",
    iso: "LK"
  },
  {
    country: "Sudan",
    code: "249",
    iso: "SD"
  },
  {
    country: "Suriname",
    code: "597",
    iso: "SR"
  },
  {
    country: "Svalbard and Jan Mayen",
    code: "47",
    iso: "SJ"
  },
  {
    country: "Swaziland",
    code: "268",
    iso: "SZ"
  },
  {
    country: "Sweden",
    code: "46",
    iso: "SE"
  },
  {
    country: "Switzerland",
    code: "41",
    iso: "CH"
  },
  {
    country: "Syria",
    code: "963",
    iso: "SY"
  },
  {
    country: "Taiwan",
    code: "886",
    iso: "TW"
  },
  {
    country: "Tajikistan",
    code: "992",
    iso: "TJ"
  },
  {
    country: "Tanzania",
    code: "255",
    iso: "TZ"
  },
  {
    country: "Thailand",
    code: "66",
    iso: "TH"
  },
  {
    country: "Togo",
    code: "228",
    iso: "TG"
  },
  {
    country: "Tokelau",
    code: "690",
    iso: "TK"
  },
  {
    country: "Tonga",
    code: "676",
    iso: "TO"
  },
  {
    country: "Trinidad and Tobago",
    code: "1-868",
    iso: "TT"
  },
  {
    country: "Tunisia",
    code: "216",
    iso: "TN"
  },
  {
    country: "Turkey",
    code: "90",
    iso: "TR"
  },
  {
    country: "Turkmenistan",
    code: "993",
    iso: "TM"
  },
  {
    country: "Turks and Caicos Islands",
    code: "1-649",
    iso: "TC"
  },
  {
    country: "Tuvalu",
    code: "688",
    iso: "TV"
  },
  {
    country: "U.S. Virgin Islands",
    code: "1-340",
    iso: "VI"
  },
  {
    country: "Uganda",
    code: "256",
    iso: "UG"
  },
  {
    country: "Ukraine",
    code: "380",
    iso: "UA"
  },
  {
    country: "United Arab Emirates",
    code: "971",
    iso: "AE"
  },
  {
    country: "United Kingdom",
    code: "44",
    iso: "GB"
  },
  {
    country: "United States",
    code: "1",
    iso: "US"
  },
  {
    country: "Uruguay",
    code: "598",
    iso: "UY"
  },
  {
    country: "Uzbekistan",
    code: "998",
    iso: "UZ"
  },
  {
    country: "Vanuatu",
    code: "678",
    iso: "VU"
  },
  {
    country: "Vatican",
    code: "379",
    iso: "VA"
  },
  {
    country: "Venezuela",
    code: "58",
    iso: "VE"
  },
  {
    country: "Vietnam",
    code: "84",
    iso: "VN"
  },
  {
    country: "Wallis and Futuna",
    code: "681",
    iso: "WF"
  },
  {
    country: "Western Sahara",
    code: "212",
    iso: "EH"
  },
  {
    country: "Yemen",
    code: "967",
    iso: "YE"
  },
  {
    country: "Zambia",
    code: "260",
    iso: "ZM"
  },
  {
    country: "Zimbabwe",
    code: "263",
    iso: "ZW"
  }
];
function bt(t) {
  return br("Section_Scratch_Card_user_clicks", t);
}
var Au = Object.defineProperty, ku = Object.getOwnPropertyDescriptor, pt = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? ku(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Au(e, i, s), s;
};
let Se = class extends ke {
  constructor() {
    super(...arguments), this.hightLightUnlockTips = !1, this.currEmail = "", this.currPhone = "", this.selectedPhoneCountry = "US", this.selectedAreaCode = "1", this.customTag = "";
  }
  /**
   * 组件首次更新完成后的回调
   */
  firstUpdated() {
    this.initDefaultCountry(), this.renderCountryList(yi), this.setupEventListeners(), this.setupSubscribeButton(), this.setupCheckboxListener(), this.setupInputFocusEvents();
  }
  /**
   * @method updated
   * @param {Map<string, any>} changedProperties - 变更的属性集合
   * @description 组件更新后的生命周期钩子，监听state变化以初始化Canvas
   */
  updated(t) {
    var e;
    if (t.has("hightLightUnlockTips") && this.hightLightUnlockTips === !0) {
      const i = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".tips");
      i && (i.style.color = this.activityInfo.highlightErrTips);
    }
  }
  /**
   * 初始化默认国家/地区设置
   */
  initDefaultCountry() {
    try {
      const t = yi.find((i) => {
        var o;
        return i.iso === (((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.cr) || "US");
      });
      if (!t) return;
      const e = {
        iso: t.iso || "US",
        flag: this.getCountryFlagUrl(t.iso),
        code: t.code || "1"
      };
      this.selectedPhoneCountry = e.iso, this.selectedAreaCode = e.code, this.updateSelectedCountry(e);
    } catch (t) {
      console.error("初始化默认国家/地区失败:", t);
    }
  }
  /**
   * 渲染国家/地区列表
   * @param listData 国家/地区数据列表
   */
  renderCountryList(t) {
    var e, i;
    try {
      const o = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".list-box"), s = (i = this.shadowRoot) == null ? void 0 : i.querySelector(".no-data");
      if (!(t != null && t.length)) {
        s == null || s.classList.remove("hidden"), o == null || o.classList.add("hidden");
        return;
      }
      s == null || s.classList.add("hidden"), o == null || o.classList.remove("hidden");
      const n = t.map((r) => this.renderCountryItem(r)).join("");
      o && (o.innerHTML = n);
    } catch (o) {
      console.error("渲染国家/地区列表失败:", o);
    }
  }
  /**
   * 渲染单个国家/地区项
   * @param country 国家/地区数据
   */
  renderCountryItem(t) {
    return `
      <div class="country-item ${this.selectedPhoneCountry === t.iso ? "active" : ""}"
           data-code="${t.code}"
           data-iso="${t.iso}">
        ${t.country}
      </div>
    `;
  }
  /**
   * 更新选中的国家/地区显示
   * @param options 国家/地区选项
   */
  updateSelectedCountry(t) {
    var e, i, o;
    try {
      const s = {
        flagImg: (e = this.shadowRoot) == null ? void 0 : e.querySelector(".selected-flag"),
        isoSpan: (i = this.shadowRoot) == null ? void 0 : i.querySelector(".selected-item"),
        codeSpan: (o = this.shadowRoot) == null ? void 0 : o.querySelector(".country-area-num")
      };
      s.flagImg && (s.flagImg.src = t.flag), s.isoSpan && (s.isoSpan.textContent = t.iso), s.codeSpan && (s.codeSpan.textContent = `+${t.code}`);
    } catch (s) {
      console.error("更新选中的国家/地区显示失败:", s);
    }
  }
  /**
   * 设置事件监听器
   */
  setupEventListeners() {
    try {
      this.setupCountryListToggle(), this.setupCountrySelection();
    } catch (t) {
      console.error("设置事件监听器失败:", t);
    }
  }
  /**
   * 设置国家/地区列表切换事件
   */
  setupCountryListToggle() {
    var t, e;
    try {
      (e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".change-area")) == null || e.addEventListener("click", (i) => {
        var n, r;
        const o = i.currentTarget, s = (n = this.shadowRoot) == null ? void 0 : n.querySelector(".country-area-list");
        if (o == null || o.classList.toggle("active"), s == null || s.classList.toggle("hidden"), window.innerWidth < 1024) {
          const a = (r = this.shadowRoot) == null ? void 0 : r.querySelector(".country-area-overlay");
          a == null || a.classList.toggle("hidden");
        }
        this.renderCountryList(yi);
      });
    } catch (i) {
      console.error("设置国家/地区列表切换事件失败:", i);
    }
  }
  /**
   * 设置国家/地区选择事件
   */
  setupCountrySelection() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".list-box");
      e == null || e.addEventListener("click", (i) => {
        var r, a, l, c, d, h;
        const o = i.target;
        if (!o.classList.contains("country-item")) return;
        e.querySelectorAll(".country-item").forEach((p) => {
          p.classList.remove("active");
        }), this.selectedPhoneCountry = o.dataset.iso || "US", this.selectedAreaCode = o.dataset.code || "1", o.classList.add("active");
        const s = {
          iso: this.selectedPhoneCountry,
          flag: this.getCountryFlagUrl(this.selectedPhoneCountry),
          code: this.selectedAreaCode
        };
        (a = (r = this.shadowRoot) == null ? void 0 : r.querySelector(".country-area-list")) == null || a.classList.add("hidden");
        const n = (l = this.shadowRoot) == null ? void 0 : l.querySelector(".country-area-search");
        if (n && (n.value = ""), (d = (c = this.shadowRoot) == null ? void 0 : c.querySelector(".change-area")) == null || d.classList.remove("active"), window.innerWidth < 1024) {
          const p = (h = this.shadowRoot) == null ? void 0 : h.querySelector(".country-area-overlay");
          p == null || p.classList.add("hidden");
        }
        this.updateSelectedCountry(s);
      }), this.setupCountrySearch(), this.setupClickOutside();
    } catch (e) {
      console.error("设置国家/地区选择事件失败:", e);
    }
  }
  /**
   * 设置国家/地区搜索事件
   */
  setupCountrySearch() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".country-area-search");
      if (!e) return;
      e.addEventListener("input", () => {
        const i = e.value.trim().toLowerCase(), o = this.searchCountries(i);
        this.renderCountryList(o);
      });
    } catch (e) {
      console.error("设置国家/地区搜索事件失败:", e);
    }
  }
  /**
   * 设置点击外部区域事件
   */
  setupClickOutside() {
    try {
      document.addEventListener("click", (t) => {
        var r, a;
        const e = (r = this.shadowRoot) == null ? void 0 : r.querySelector(".change-area"), i = (a = this.shadowRoot) == null ? void 0 : a.querySelector(".country-area-list"), o = t.composedPath().find(
          (l) => {
            var c;
            return l instanceof HTMLElement && ((c = this.shadowRoot) == null ? void 0 : c.contains(l));
          }
        ), s = !(e != null && e.contains(o)) && e !== o, n = !(i != null && i.contains(o)) && i !== o;
        s && n && this.hideCountryList();
      });
    } catch (t) {
      console.error("设置点击外部区域事件失败:", t);
    }
  }
  /**
   * 获取国家/地区旗帜URL
   * @param iso 国家/地区代码
   */
  getCountryFlagUrl(t) {
    return t ? `https://cdn.shopify.com/static/images/flags/${t.toLowerCase()}.svg` : "";
  }
  /**
   * 搜索国家/地区
   * @param keyword 搜索关键词
   */
  searchCountries(t) {
    return yi.filter(
      (e) => e.country.toLowerCase().includes(t) || e.iso.toLowerCase().includes(t)
    );
  }
  /**
   * 隐藏国家/地区列表
   */
  hideCountryList() {
    var o, s, n, r;
    const t = (o = this.shadowRoot) == null ? void 0 : o.querySelector(".country-area-list"), e = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".country-area-search"), i = (n = this.shadowRoot) == null ? void 0 : n.querySelector(".change-area");
    if (t == null || t.classList.add("hidden"), e && (e.value = ""), i == null || i.classList.remove("active"), window.innerWidth < 1024) {
      const a = (r = this.shadowRoot) == null ? void 0 : r.querySelector(".country-area-overlay");
      a == null || a.classList.add("hidden");
    }
  }
  /**
   * 显示规则对话框
   */
  showRulesDialog() {
    this.dispatchEvent(new CustomEvent("show-rules-dialog", {
      detail: {
        showRulesDialog: !0
      }
    })), bt({
      module: "Subscribe",
      // 点击模块固定名称Subscribe
      button_name: Kt(this.activityInfo.ruleTitle)
      // 点击规则按钮，识别当地文本text
    });
  }
  /**
   * 错误提示控制
   * @param errorType 错误类型：email/phone/checkbox
   * @param toggleType 显示/隐藏类型：show/hide
   */
  errorTipsControl(t, e) {
    var i;
    try {
      const o = (i = this.shadowRoot) == null ? void 0 : i.querySelector(`.${t}-error-tips`);
      o && (e === "show" ? o.classList.remove("hidden") : o.classList.add("hidden"));
    } catch (o) {
      console.error("错误提示控制失败:", o);
    }
  }
  /**
   * 校验表单
   * @returns 表单校验状态 true/false
   */
  checkForm() {
    var t, e, i, o, s, n;
    try {
      const r = {
        emailInput: (t = this.shadowRoot) == null ? void 0 : t.querySelector(".email-box .box-inp"),
        phoneInput: (e = this.shadowRoot) == null ? void 0 : e.querySelector(".phone-box .box-inp"),
        checkbox: (i = this.shadowRoot) == null ? void 0 : i.querySelector(".checkbox-box .checkbox-original")
      };
      return (o = r.checkbox) != null && o.checked ? (this.errorTipsControl("checkbox", "hide"), this.currEmail = ((s = r.emailInput) == null ? void 0 : s.value) || "", this.currEmail ? Qh(this.currEmail) ? (this.errorTipsControl("email", "hide"), this.currPhone = ((n = r.phoneInput) == null ? void 0 : n.value) || "", !0) : (this.errorTipsControl("email", "show"), !1) : (this.errorTipsControl("email", "show"), !1)) : (this.errorTipsControl("checkbox", "show"), !1);
    } catch {
      return !1;
    }
  }
  /**
   * 订阅功能
   */
  async subscribe() {
    var t, e, i, o;
    try {
      if (!this.checkForm())
        return;
      const s = {
        shopify_shop_id: ((t = window.pageInfo) == null ? void 0 : t.shopId) || "55005249633",
        email: this.currEmail,
        tags: this.customTag
      };
      this.currPhone && (s.phone_cr = this.selectedPhoneCountry, s.phone = this.selectedAreaCode + this.currPhone);
      const r = `${((e = window == null ? void 0 : window.pageInfo) == null ? void 0 : e.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/notice/subscribe`, a = await C.post(r, s, {
        withCredentials: !0,
        // 允许携带 Cookies
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          Cr: (i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.cr,
          Lang: (o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.lang
        }
      }), { code: l, success: c, message: d } = a.data;
      c ? l === 200 && this.handleSubscribeSuccess(s) : ne(d, "danger");
    } catch {
      ne(window.i18n.t("pages.error.default"), "danger");
    }
  }
  /**
   * 处理订阅成功
   */
  handleSubscribeSuccess(t) {
    var e, i, o;
    try {
      if (this.errorTipsControl("email", "hide"), t.email) {
        const n = (e = this.shadowRoot) == null ? void 0 : e.querySelector(".email-box");
        n && n.classList.add("sub-success");
      }
      if (t.phone) {
        const n = (i = this.shadowRoot) == null ? void 0 : i.querySelector(".phone-box");
        n && n.classList.add("sub-success");
      }
      const s = (o = this.shadowRoot) == null ? void 0 : o.querySelector(".btn-subscribe");
      s && s.classList.add("disabled"), sessionStorage.setItem("plus-event-email-subscribe", this.currEmail), this.currPhone && sessionStorage.setItem("plus-event-phone-subscribe", this.selectedAreaCode + this.currPhone), this.dispatchEvent(new CustomEvent("subscribe-success", {
        detail: {
          email: this.currEmail,
          drawState: "initial"
        }
      })), bt({
        module: "Subscribe",
        //点击模块固定名称Subscribe   
        position: t.phone ? "phone" : "",
        // 记录是否填写电话，若填写则为phone，若无则为Null
        button_name: Kt(this.activityInfo.btnTitle)
        // 点击按钮，识别按钮文本text；
      });
    } catch (s) {
      console.error("处理订阅成功失败:", s);
    }
  }
  /**
   * 监听协议勾选状态
   */
  setupCheckboxListener() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".checkbox-original");
      if (!e) return;
      e.addEventListener("change", () => {
        e.checked ? this.errorTipsControl("checkbox", "hide") : this.errorTipsControl("checkbox", "show");
      });
    } catch (e) {
      console.error("设置协议勾选监听失败:", e);
    }
  }
  /**
   * 设置订阅按钮点击事件
   */
  setupSubscribeButton() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".btn-subscribe");
      e == null || e.addEventListener("click", ht(() => this.subscribe(), 300));
    } catch (e) {
      console.error("设置订阅按钮点击事件失败:", e);
    }
  }
  /**
   * 设置输入框聚焦事件
   */
  setupInputFocusEvents() {
    var t;
    try {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelectorAll(".box-inp");
      e == null || e.forEach((i) => {
        i.addEventListener("focus", () => {
          var s;
          const o = i.closest(".item-box");
          if (o != null && o.classList.contains("sub-success") && o.classList.remove("sub-success"), i.dataset.flag === "email") {
            const n = (s = this.shadowRoot) == null ? void 0 : s.querySelector(".btn-subscribe");
            n == null || n.classList.remove("disabled");
          }
        });
      });
    } catch (e) {
      console.error("设置输入框聚焦事件失败:", e);
    }
  }
  render() {
    return Eu(this);
  }
};
Se.styles = [so, xr, xu, $u];
pt([
  S()
], Se.prototype, "hightLightUnlockTips", 2);
pt([
  S()
], Se.prototype, "currEmail", 2);
pt([
  S()
], Se.prototype, "currPhone", 2);
pt([
  S()
], Se.prototype, "selectedPhoneCountry", 2);
pt([
  S()
], Se.prototype, "selectedAreaCode", 2);
pt([
  $({ type: String, reflect: !1 })
], Se.prototype, "customTag", 2);
Se = pt([
  pe("scratch-left-info")
], Se);
const Su = N`
  .inline-block {
    display: inline-block;
  }
  .right-box {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    background-size: cover;
    background-position: center;
  }

  .btn {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: flex-start;
    font-weight: 600;
    color: #872017;
    text-align: center;
    font-style: normal;
    background: linear-gradient(180deg, #FFE68A 0%, #FFF5B7 43%, #FFE18B 100%);
    border-style: solid;
    border-color: transparent;
    cursor: pointer;
  }

  .btn::before,
  .btn::after {
    content: "";
    position: absolute;
    left: 8%;
    right: 8%;
    display:block;
    background: linear-gradient(90deg, transparent 0%, #fff 50%, transparent 100%);
  }

  .btn::before{
    top: 0;
  }

  .btn::after{
    bottom: 0;
  }

  .btn * {
    word-break: break-all;
  }

  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .btn-play {
    position: absolute;
    z-index: 1;
  }

  .btn-group {
    position: absolute;
    z-index: 1;
    display: flex;
  }

  .btn-group .btn {
    overflow: hidden;
  }

  .initial-state {
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }

  .scratch-area {
    position: relative;
    background-color: #fff;
  }

  .initial-state img {
    width: 100%;
    height: auto;
    object-fit: cover;
  }

  .scratch-canvas {
    position: absolute;
    top: -1px;
    left: -1px;
    right: -1px;
    bottom: -1px;
    width: calc(100% + 2px);
    height: calc(100% + 2px);
    cursor: pointer;
  }

  .prize-content {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  .prize-image {
    object-fit: contain;
  }

  .prize-name {
    width: 100%;
    font-weight: 500;
    color: #5C5C5C;
    line-height: 1.4;
    text-align: center;
  }

  .draw-tip {
    position: absolute;
    color: #FFEDA6;
    font-weight: 600;
  }

  @media screen and (min-width: 1440px) {
    :host {
      width: 446px;
      height: 400px;
    }

    .right-box,
    .btn,
    .scratch-area,
    .scratch-canvas {
      border-radius: 24px;
    }

    .btn {
      padding: 0 28px;
      height: 40px;
      line-height: 40px;
      font-size: 16px;
    }

    .btn::before,
    .btn::after{
      height: 4px;
    }

    .btn-play {
      bottom: -28px;
    }

    .btn-group {
      bottom: -28px;
      gap: 12px;
    }

    .btn-group .btn{
      width: 180px;
      padding-left: 20px;
      padding-right: 20px;
    }

    .btn-play-again {
      gap: 2px;
    }

    .initial-state,
    .scratch-area {
      margin-bottom: 22px;
      width: 392px;
      height: 216px;
    }

    .prize-image {
      margin-bottom: 8px;
      width: 248px;
      height: 166px;
    }

    .prize-name {
      font-size: 16px;
    }

    .draw-tip {
      bottom: -38px;
      font-size: 16px;
    }
  }

  @media screen and (min-width: 1024px) and (max-width: 1439.98px) {
    :host {
      width: 30.97vw;
      height: 27.78vw;
    }

    .right-box,
    .btn,
    .scratch-area,
    .scratch-canvas {
      border-radius: 1.67vw;
    }

    .btn {
      padding: 0 1.94vw;
      height: 2.78vw;
      line-height: 2.78vw;
      font-size: max(12px, 1.11vw);
    }

    .btn::before,
    .btn::after{
      height: 0.28vw;
    }

    .btn-play {
      bottom: -1.94vw;
    }

    .btn-group {
      bottom: -1.94vw;
      gap: 0.83vw;
    }

    .btn-group .btn{
      width: 12.5vw;
      padding-left: 1.39vw;
      padding-right: 1.39vw;
    }

    .btn-play-again {
      gap: 0.14vw;
    }

    .initial-state,
    .scratch-area {
      margin-bottom: 1.53vw;
      width: 27.22vw;
      height: 15vw;
    }

    .prize-image {
      margin-bottom: 0.56vw;
      width: 17.22vw;
      height: 11.53vw;
    }

    .prize-name {
      font-size: max(12px, 1.11vw);
    }

    .draw-tip {
      bottom: -2.64vw;
      font-size: max(12px, 1.11vw);
    }
  }

  @media screen and (min-width: 1024px) {
    .tips{
      display: none;
    }
  }

  @media screen and (max-width: 1023.98px) {
    :host {
      order: 1;
      width: 74.4vw;
      min-height: 53.33vw;
    }

    .opacity-hide{
      opacity: 0;
    }

    .right-info{
      text-align: center;
    }

    .tips{
      display: -webkit-box;
    }

    .right-box{
      margin-top: 8.53vw;
      margin-bottom: 6.4vw;
      height: 66.67vw;
    }

    .right-box,
    .btn,
    .scratch-area,
    .scratch-canvas {
      border-radius: 3.2vw;
    }

    .btn {
      height: 10.67vw;
      line-height: 10.67vw;
      padding: 0 5.33vw;
      font-size: max(12px, 3.73vw);
      font-weight: 500;
      border-radius: 5.87vw;
    }

    .btn::before,
    .btn::after {
        height: 0.93vw;
    }

    .btn-play {
      bottom: -15vw;
    }

    .btn-group {
      bottom: -15vw;
      gap: 1.6vw;
    }

    .btn-group .btn{
      width: 35.6vw;
      padding-left: 2.67vw;
      padding-right: 2.67vw;
    }

    .btn-play-again {
      gap: 0.53vw;
    }

    .initial-state,
    .scratch-area {
      margin-bottom: 3.2vw;
      width: 68vw;
      height: 36vw;
    }

    .prize-image {
      margin-bottom: 0.8vw;
      width: 43.07vw;
      height: 28.8vw;
    }

    .prize-name {
      font-size: max(12px, 3.2vw);
    }

    .draw-tip {
      bottom: -11.73vw;
      font-size: max(12px, 3.73vw);
      height: 4.26vw;
    }
  }
`, Tu = (t) => v`
    <div class="initial-state"
      @touchstart="${ht(() => t.handleUnlockClick(), 300)}"
      @mousedown="${ht(() => t.handleUnlockClick(), 300)}"
    >
      <img src="${t.scratchOverlayImg}" alt="Locked" />
    </div>
  `, _u = (t) => v`
    <div class="initial-state">
      <img src="${t.scratchOverlayImg}" alt="Initial" />
    </div>
    <button class="btn btn-play"
     @click="${ht(() => t.startScratch(), 300)}"
     .innerHTML="${t.activityInfo.btnPlayText}"
    ></button>
  `, Cu = (t) => v`
    <div class="scratch-area">
      ${$r(t)}
      <canvas class="scratch-canvas"></canvas>
    </div>
    <div class="draw-tip text-overflow-1" .innerHTML="${t.activityInfo.drawGuideText}"></div>
  `, $r = (t) => {
  var e, i, o;
  return v`
    <div class="prize-content">
      <img class="prize-image" src="${(e = t.currentPrize) == null ? void 0 : e.image}" alt="${(i = t.currentPrize) == null ? void 0 : i.name}" />
      <div class="prize-name text-overflow-1">${(o = t.currentPrize) == null ? void 0 : o.name}</div>
    </div>
  `;
}, ju = (t) => v`
    ${t.showBtnGroup ? v`
      <div class="btn-group">
        <button class="btn"
          @click="${ht(() => t.showPrizeDetails(), 300)}"
          .innerHTML="${t.activityInfo.showPrizeBtnText}"
        ></button>

        ${t.remainingTimes === -1 || t.remainingTimes > 0 ? v`
          <button class="btn btn-play-again" @click="${ht(() => t.playAgain(), 300)}">
            <div class="inline-block" .innerHTML="${t.activityInfo.tryAgainBtnText}"></div>
            ${t.remainingTimes > 0 ? v`(${t.remainingTimes})` : ""}
          </button>
        ` : ""}

      </div>` : ""}
  `, zu = (t) => v`
    <div class="scratch-area">
      ${$r(t)}
    </div>
    ${ju(t)}
  `, Ru = (t) => v`
    <div class="right-info info-box mb-show">
      <div class="title" .innerHTML="${t.activityInfo.mainTitle}"></div>
      <div class="subtitle text-overflow-1" .innerHTML="${t.activityInfo.subTitle}"></div>
    </div>
    <div class="right-box" style="background-image: url(${t.scratchRgBoxBg});">
      ${(() => {
  switch (t.drawState) {
    case "locked":
      return Tu(t);
    case "initial":
      return _u(t);
    case "scratching":
      return Cu(t);
    case "revealed":
      return zu(t);
    default:
      return "";
  }
})()}
    </div>
    <div class="info-box">
      <div
        class="tips text-overflow-2 ${t.hideMBUnlockTips ? "opacity-hide" : ""}"
        .innerHTML="${t.activityInfo.guideText}">
      </div>
    </div>
  `;
var Du = Object.defineProperty, Lu = Object.getOwnPropertyDescriptor, fe = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Lu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Du(e, i, s), s;
};
let Z = class extends ke {
  constructor() {
    super(...arguments), this.props = {
      shopifyShopId: "",
      platform: "membership",
      actId: "",
      customTag: ""
    }, this.email = "", this.showBtnGroup = !1, this.hideMBUnlockTips = !1, this.scratchRgBoxBg = "", this.scratchOverlayImg = "", this.currentPrize = {
      itemId: 0,
      name: "",
      image: ""
    }, this.remainingTimes = 0, this.drawState = "locked", this.token = "", this.canvas = null, this.ctx = null, this.isDrawing = !1, this.lastX = 0, this.lastY = 0, this.totalPixels = 0;
  }
  // 组件渲染模板
  connectedCallback() {
    super.connectedCallback(), this.scratchRgBoxBg = this.activityInfo.scratchBoxBgImg, this.scratchOverlayImg = this.activityInfo.noSubscribeMaskImg;
  }
  /**
   * @method firstUpdated
   * @description 组件首次更新完成后的生命周期钩子，初始化Canvas
   */
  firstUpdated() {
    this.drawState === "scratching" && this.initCanvas();
  }
  /**
   * @method updated
   * @param {Map<string, any>} changedProperties - 变更的属性集合
   * @description 组件更新后的生命周期钩子，监听state变化以初始化Canvas
   */
  updated(t) {
    super.updated(t), t.has("drawState") && (this.drawState === "initial" ? (this.unlock(), this.clearCanvas()) : this.drawState === "scratching" ? this.initCanvas() : this.drawState === "locked" && (this.hideMBUnlockTips = !1, this.scratchOverlayImg = this.activityInfo.noSubscribeMaskImg));
  }
  /**
   * @method clearCanvas
   * @private
   * @description 清除画布内容和事件监听
   */
  clearCanvas() {
    this.canvas && (this.canvas.removeEventListener("mousedown", this.handleMouseDown.bind(this)), this.canvas.removeEventListener("mousemove", this.handleMouseMove.bind(this)), this.canvas.removeEventListener("mouseup", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("mouseleave", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("touchstart", this.handleTouchStart.bind(this)), this.canvas.removeEventListener("touchmove", this.handleTouchMove.bind(this)), this.canvas.removeEventListener("touchend", this.handleTouchEnd.bind(this))), this.canvas && this.ctx && (this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height), this.canvas.width = 0, this.canvas.height = 0, this.isDrawing = !1, this.lastX = 0, this.lastY = 0, this.totalPixels = 0);
  }
  /**
   * @method initCanvas
   * @private
   * @description 初始化Canvas画布，设置尺寸和事件监听
   */
  initCanvas() {
    var t;
    if (this.canvas = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".scratch-canvas"), !this.canvas) {
      console.warn("Canvas element not found");
      return;
    }
    if (this.ctx = this.canvas.getContext("2d"), !this.ctx) {
      console.warn("Failed to get canvas context");
      return;
    }
    if (this.canvas.offsetWidth <= 0 || this.canvas.offsetHeight <= 0) {
      console.warn("Invalid canvas dimensions");
      return;
    }
    this.canvas.width = this.canvas.offsetWidth, this.canvas.height = this.canvas.offsetHeight, this.totalPixels = this.canvas.width * this.canvas.height, this.ctx.fillStyle = "#7B7B7B", this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height), this.canvas.addEventListener("mousedown", this.handleMouseDown.bind(this)), this.canvas.addEventListener("mousemove", this.handleMouseMove.bind(this)), this.canvas.addEventListener("mouseup", this.handleMouseUp.bind(this)), this.canvas.addEventListener("mouseleave", this.handleMouseUp.bind(this)), this.canvas.addEventListener("touchstart", this.handleTouchStart.bind(this)), this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this)), this.canvas.addEventListener("touchend", this.handleTouchEnd.bind(this));
  }
  /**
   * @method handleMouseDown
   * @private
   * @param {MouseEvent} e - 鼠标事件对象
   * @description 处理鼠标按下事件，开始刮奖
   */
  handleMouseDown(t) {
    this.isDrawing = !0, [this.lastX, this.lastY] = [t.offsetX, t.offsetY];
  }
  /**
   * @method handleMouseMove
   * @private
   * @param {MouseEvent} e - 鼠标事件对象
   * @description 处理鼠标移动事件，执行刮奖动作
   */
  handleMouseMove(t) {
    !this.isDrawing || !this.ctx || !this.canvas || this.scratch(t.offsetX, t.offsetY);
  }
  /**
   * @method handleMouseUp
   * @private
   * @description 处理鼠标松开事件，结束刮奖动作
   */
  handleMouseUp() {
    this.isDrawing = !1;
  }
  /**
   * @method handleTouchStart
   * @private
   * @param {TouchEvent} e - 触摸事件对象
   * @description 处理触摸开始事件，支持移动端刮奖
   */
  handleTouchStart(t) {
    if (t.preventDefault(), !this.canvas) return;
    const e = t.touches[0], i = this.canvas.getBoundingClientRect();
    this.isDrawing = !0, this.lastX = e.clientX - i.left, this.lastY = e.clientY - i.top;
  }
  /**
   * @method handleTouchMove
   * @private
   * @param {TouchEvent} e - 触摸事件对象
   * @description 处理触摸移动事件，执行移动端刮奖动作
   */
  handleTouchMove(t) {
    if (t.preventDefault(), !this.isDrawing || !this.ctx || !this.canvas) return;
    const e = t.touches[0], i = this.canvas.getBoundingClientRect();
    this.scratch(e.clientX - i.left, e.clientY - i.top);
  }
  /**
   * @method handleTouchEnd
   * @private
   * @param {TouchEvent} e - 触摸事件对象
   * @description 处理触摸结束事件，结束移动端刮奖动作
   */
  handleTouchEnd(t) {
    t.preventDefault(), this.isDrawing = !1;
  }
  /**
   * @method scratch
   * @private
   * @param {number} x - 当前刮奖位置的X坐标
   * @param {number} y - 当前刮奖位置的Y坐标
   * @description 执行刮奖动作，计算已刮开区域并触发相应事件
   */
  scratch(t, e) {
    if (!this.ctx || !this.canvas) return;
    this.ctx.globalCompositeOperation = "destination-out", this.ctx.lineWidth = 35, this.ctx.lineCap = "round", this.ctx.lineJoin = "round", this.ctx.beginPath(), this.ctx.moveTo(this.lastX, this.lastY), this.ctx.lineTo(t, e), this.ctx.stroke(), [this.lastX, this.lastY] = [t, e];
    const i = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
    let o = 0;
    for (let n = 0; n < i.data.length; n += 4)
      i.data[n + 3] === 0 && o++;
    o / this.totalPixels * 100 > 40 && this.drawState !== "revealed" && (this.drawState = "revealed", this.dispatchEvent(new CustomEvent("prize-revealed")), this.drawPrize("check"));
  }
  /**
   * @method drawPrize
   * @private
   * @description 调用抽奖接口获取奖品
   */
  async drawPrize(t) {
    var e, i, o, s;
    try {
      const n = {
        shopId: this.props.shopifyShopId || ((e = window.pageInfo) == null ? void 0 : e.shopId) || "55005249633",
        platform: this.props.platform === "membership" ? "member" : this.props.platform,
        actId: this.props.actId,
        tags: this.props.customTag,
        email: this.email,
        actType: "subscribe"
        // 活动类型：订阅抽奖
      };
      this.token && (n.token = this.token);
      const a = `${((i = window == null ? void 0 : window.pageInfo) == null ? void 0 : i.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/cjActivityJoin`, l = await C.post(a, n, {
        withCredentials: !0,
        timeout: 15e3,
        headers: {
          "Content-Type": "application/json",
          Cr: (o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.cr,
          Lang: (s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.cr
        }
      }), { code: c, success: d, message: h, data: p } = l.data;
      d && c === 200 ? (this.token ? (this.token = "", this.drawResults = p, this.dispatchEvent(new CustomEvent("update-prize-details", {
        detail: {
          drawResults: this.drawResults
        }
      })), this.showBtnGroup = !0) : (this.token = p.token, this.remainingTimes = p.userJoinTimes === null ? -1 : p.userJoinTimes, this.drawState = "scratching", this.dispatchEvent(new CustomEvent("draw-state-update", {
        detail: {
          drawState: this.drawState
        }
      }))), this.currentPrize = {
        itemId: p.itemId,
        name: p.title,
        image: p.image
      }, t === "check" && bt({
        module: "Lottery",
        //点击模块固定名称Lottery   
        position: p.title
        // 奖品文本text
      })) : (c === 0 && (this.remainingTimes = 0), this.drawState === "initial" && (this.drawState = "locked", this.dispatchEvent(new CustomEvent("draw-state-update", {
        detail: {
          drawState: this.drawState
        }
      }))), this.token = "", ne(h, "danger"));
    } catch {
      ne(window.i18n.t("pages.error.default"), "danger");
    }
  }
  /**
   * @method unlock
   * @public
   * @description 解锁刮奖区域，切换到初始状态
   */
  unlock() {
    this.token = "", this.hideMBUnlockTips = !0, this.scratchOverlayImg = this.activityInfo.subscribeMaskImg, this.dispatchEvent(new CustomEvent("unlock-scratch"));
  }
  /**
   * @method handleUnlockClick
   * @public
   * @description 处理解锁按钮点击事件，触发相应事件
   */
  handleUnlockClick() {
    var t;
    if (window.innerWidth < 1024) {
      const e = (t = this.shadowRoot) == null ? void 0 : t.querySelector(".tips");
      e && (e.style.color = this.activityInfo.highlightErrTips);
    }
    this.dispatchEvent(new CustomEvent("unlock-click"));
  }
  /**
   * @method startScratch
   * @public
   * @description 开始刮奖，切换到刮奖状态
   */
  startScratch() {
    this.drawPrize("play"), bt({
      module: "Lottery",
      //点击模块固定名称Lottery
      button_name: Kt(this.activityInfo.btnPlayText)
      // 点击按钮，识别按钮文本text；
    });
  }
  /**
   * @method showPrizeDetails
   * @public
   * @description 显示奖品详情
   */
  showPrizeDetails() {
    this.dispatchEvent(new CustomEvent("show-prize-details", {
      detail: {
        welfareType: this.drawResults.welfareType
      }
    })), bt({
      module: "Lottery",
      //点击模块固定名称Lottery
      button_name: Kt(this.activityInfo.showPrizeBtnText)
      //点击文本 查看奖品/再玩一次（记录次数），识别文本text。
    });
  }
  /**
   * @method playAgain
   * @public
   * @description 再次进行刮奖，需要剩余次数大于0
   */
  playAgain() {
    (this.remainingTimes > 0 || this.remainingTimes === -1) && (this.canvas && (this.canvas.removeEventListener("mousedown", this.handleMouseDown.bind(this)), this.canvas.removeEventListener("mousemove", this.handleMouseMove.bind(this)), this.canvas.removeEventListener("mouseup", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("mouseleave", this.handleMouseUp.bind(this)), this.canvas.removeEventListener("touchstart", this.handleTouchStart.bind(this)), this.canvas.removeEventListener("touchmove", this.handleTouchMove.bind(this)), this.canvas.removeEventListener("touchend", this.handleTouchEnd.bind(this))), this.canvas && this.ctx && (this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height), this.canvas.width = 0, this.canvas.height = 0), this.isDrawing = !1, this.lastX = 0, this.lastY = 0, this.totalPixels = 0, this.showBtnGroup = !1, this.drawPrize("playAgain"), bt({
      module: "Lottery",
      //点击模块固定名称Lottery
      button_name: `${Kt(this.activityInfo.tryAgainBtnText)}(${this.remainingTimes})`
      //点击文本 再玩一次（记录次数），识别文本text。
    }));
  }
  /**
   * @method render
   * @returns {TemplateResult} 组件的渲染模板
   * @description 渲染组件界面
   */
  render() {
    return Ru(this);
  }
};
Z.styles = [so, xr, Su];
fe([
  $({ type: Object, reflect: !1 })
], Z.prototype, "props", 2);
fe([
  $({ type: String })
], Z.prototype, "email", 2);
fe([
  S()
], Z.prototype, "showBtnGroup", 2);
fe([
  S()
], Z.prototype, "hideMBUnlockTips", 2);
fe([
  S()
], Z.prototype, "scratchRgBoxBg", 2);
fe([
  S()
], Z.prototype, "scratchOverlayImg", 2);
fe([
  S()
], Z.prototype, "currentPrize", 2);
fe([
  $({ type: Number })
], Z.prototype, "remainingTimes", 2);
fe([
  $({ type: String })
], Z.prototype, "drawState", 2);
fe([
  S()
], Z.prototype, "token", 2);
Z = fe([
  pe("scratch-right-box")
], Z);
var Pu = Object.defineProperty, Iu = Object.getOwnPropertyDescriptor, ze = (t, e, i, o) => {
  for (var s = o > 1 ? void 0 : o ? Iu(e, i) : e, n = t.length - 1, r; n >= 0; n--)
    (r = t[n]) && (s = (o ? r(e, i, s) : r(s)) || s);
  return o && s && Pu(e, i, s), s;
};
let he = class extends ke {
  constructor() {
    super(...arguments), this.email = "", this.drawState = "locked", this.hightLightUnlockTips = !1, this.showRulesDialog = !1, this.showThanksDialog = !1, this.showVirtualCardDialog = !1, this.showCouponDialog = !1, this.showPointsDialog = !1;
  }
  // 是否显示积分弹窗
  // 生命周期 - 组件挂载后
  async connectedCallback() {
    super.connectedCallback(), await this.getActivityInfo();
    const { lucky_act_id: t } = yr();
    t && setTimeout(() => {
      const e = document.querySelector(`jbc-lucky-scratch[actId=${t}]`) || document.querySelector("jbc-lucky-scratch");
      window.scrollTo({ top: (e == null ? void 0 : e.offsetTop) || 0 });
    }, 1e3);
  }
  // 高亮显示 未解锁 提示内容
  showUnlockTips() {
    this.hightLightUnlockTips = !0;
  }
  // 订阅成功
  handleSubscribeSuccess(t) {
    this.email = t.detail.email, this.drawState = t.detail.drawState, this.requestUpdate();
  }
  // 监听刮奖状态更新
  handleDrawStateUpdate(t) {
    this.drawState = t.detail.drawState, this.requestUpdate();
  }
  // 显示规则弹窗
  handleShowRulesDialog(t) {
    this.showRulesDialog = t.detail.showRulesDialog, this.requestUpdate();
  }
  // 监听奖品详情更新事件
  handleUpdatePrizeDetails(t) {
    this.drawResults = t.detail.drawResults, this.requestUpdate();
  }
  // 监听显示奖品详情事件
  handleShowPrizeDetails(t) {
    var i;
    const e = (i = this.shadowRoot) == null ? void 0 : i.querySelector("lucky-all-dialog");
    e && e.showDialog(t.detail.welfareType);
  }
  // 监听弹窗状态更新事件
  handleModalStateUpdate(t) {
    this.showRulesDialog = t.detail.showRulesDialog, this.showCouponDialog = t.detail.showCouponDialog, this.showPointsDialog = t.detail.showPointsDialog, this.showVirtualCardDialog = t.detail.showVirtualCardDialog, this.showThanksDialog = t.detail.showThanksDialog, this.requestUpdate();
  }
  // 自定义抽奖结束事件用于外部监听
  newCustomEvent() {
    const t = new CustomEvent("LuckyScratchDone", {
      detail: {
        status: !0
      }
    });
    document.dispatchEvent(t);
  }
  // 获取活动基本信息
  async getActivityInfo() {
    var o, s, n;
    const e = `${((o = window == null ? void 0 : window.pageInfo) == null ? void 0 : o.api) || "https://demo-api.myjackery.com"}/osconsumerapi/v1/active/getCjActivityInfo`, i = {};
    !this.platform || this.platform === "membership" ? i.shopifyShopId = this.shopifyShopId : (i.platform = this.platform, i.actId = this.actId, i.shopifyShopId = this.shopifyShopId), i.actType = "subscribe";
    try {
      const r = await C.get(e, {
        params: i,
        timeout: 15e3,
        // 设置超时为15秒
        headers: {
          "Content-Type": "application/json",
          Cr: (s = window == null ? void 0 : window.pageInfo) == null ? void 0 : s.cr,
          Lang: (n = window == null ? void 0 : window.pageInfo) == null ? void 0 : n.cr
        }
      }), { data: a, success: l } = r.data;
      if (l && a !== null) {
        const {
          bgValue: c,
          bgValue1: d,
          btnBgType: h,
          joinMaxText: p,
          btnTitleNologin: f,
          bgValue2: g,
          btnTitleLogin: w,
          ...m
        } = a;
        this.activityInfo = {
          pcBgImg: c,
          mbBgImg: d,
          showPhoneInput: h,
          privacyPolicy: p,
          btnPlayText: f,
          scratchBoxBgImg: g,
          tryAgainBtnText: w,
          ...m
        }, this.showLuckyComponent = !0;
      } else
        this.showLuckyComponent = !1;
    } catch (r) {
      this.showLuckyComponent = !1, r.code === "ECONNABORTED" ? console.error("请求超时:", r.message) : console.error("接口异常:", r);
    }
  }
  render() {
    return fu(this);
  }
};
he.styles = [so, gu];
ze([
  S()
], he.prototype, "email", 2);
ze([
  S()
], he.prototype, "drawState", 2);
ze([
  S()
], he.prototype, "hightLightUnlockTips", 2);
ze([
  S()
], he.prototype, "showRulesDialog", 2);
ze([
  S()
], he.prototype, "showThanksDialog", 2);
ze([
  S()
], he.prototype, "showVirtualCardDialog", 2);
ze([
  S()
], he.prototype, "showCouponDialog", 2);
ze([
  S()
], he.prototype, "showPointsDialog", 2);
he = ze([
  pe("jbc-lucky-scratch")
], he);
export {
  j as JbcLuckyDraw,
  he as JbcLuckyScratch
};
//# sourceMappingURL=index.es.js.map
